import { RpcBudgetSchema, DrillDownRuleSchema, ContractStateResultSchema, ProfileSettingsSchema, TransactionStateInsightSchema, ProfileDefinitionSchema } from '@authe/fe-commons/models';
import { parseBytecodeFacts, ScopedStorage, orderBytecodeCapabilities, bytecodeCapabilityLabel, rawOf, STORAGE_LEVELS } from '@authe/fe-commons/storage';
import { parseAbi, encodeFunctionData, concat, encodeAbiParameters, size, slice, hexToBigInt, getAddress, isAddress, parseAbiItem, decodeAbiParameters, zeroAddress } from 'viem';
import * as abi_star from '@authe/fe-commons/abi';
import { syntheticSelector, normalizeAbiParameter, functionSignature, recordedAbiConfidence, describeUnscoredAbi } from '@authe/fe-commons/abi';
import { createLogger } from '@authe/fe-commons/logging';
import { unwrapData, ApiError } from '@authe/fe-commons/api';
import { normalizeAddress, shortenAddress, registry, isValidAddress, getChainMetadata, toCanonicalChainId, isEVM, chainsEqual, shortenHash } from '@authe/fe-commons/chains';
import * as contracts_star from '@authe/fe-commons/contracts';
import { scopeRungLabel, directChain, proxyOverrideRequestBody, windowCovers, toProvenance, formatBlockWindow, windowViewBlock, ProxyKind } from '@authe/fe-commons/contracts';
export { scopeRungLabel } from '@authe/fe-commons/contracts';
import { z } from 'zod';
import * as React50 from 'react';
import { createRpcContractReader, useContractWrite } from '@authe/web3';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useDepsChanged } from '@authe/ui-core/api';
import { EmptyState, Accordion, AccordionItem, AccordionTrigger, Badge, AccordionContent, cn, useLadderShared, NativeSelect, Field, Input, JsonEditor, Button, Alert, AlertDescription, Separator, Label, Switch, Skeleton, ErrorState, ReadConfidenceBoundary, PencilIcon, Spinner, AddressDisplay, useTierCutoffs, useScopedOverride, notifySuccess, notifyError, BlockPinControl, WhyPanel, AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, Textarea, ScrollArea, CompareView, Table, TableHeader, TableRow, TableHead, TableBody, TableCell, Tabs, TabsList, TabsTrigger, TabsContent, CopyButton, Card, CardContent, useReadConfidence, useOverrideWriteScope, notifyWarning, notifyInfo, AddressDisplayProvider, CardHeader, ChainAvatar, CardTitle, ReadConfidenceBadge, Checkbox, usePersistedCollection, HashDisplay, ExpandableText, Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@authe/ui-core';
import { safeChainText } from '@authe/fe-commons/display';
import { isUnlimitedAllowance } from '@authe/fe-commons/value';
import { ProtocolStateSummary } from '@authe/protocol-ui';
import { contractPredicateLabel } from '@authe/fe-commons/related-addresses';
import { humanizeFieldName } from '@authe/fe-commons/metadata-analytics';
import { canonicalRung } from '@authe/fe-commons/tenancy';
import { boundOverlayRefs } from '@authe/fe-commons/overlays';

var __defProp = Object.defineProperty;
var __getOwnPropDesc = Object.getOwnPropertyDescriptor;
var __getOwnPropNames = Object.getOwnPropertyNames;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __copyProps = (to, from, except, desc) => {
  if (from && typeof from === "object" || typeof from === "function") {
    for (let key of __getOwnPropNames(from))
      if (!__hasOwnProp.call(to, key) && key !== except)
        __defProp(to, key, { get: () => from[key], enumerable: !(desc = __getOwnPropDesc(from, key)) || desc.enumerable });
  }
  return to;
};
var __reExport = (target, mod, secondTarget) => (__copyProps(target, mod, "default"), secondTarget);

// src/config.ts
var CONTRACT_EXPLORER_PACKAGE = "contract-explorer";
var ABI_OPTIONS_NAME = `${CONTRACT_EXPLORER_PACKAGE}:abi-options`;
var PINNED_ABIS_NAME = `${CONTRACT_EXPLORER_PACKAGE}:pinned-abis`;
var INLINE_ABIS_NAME = `${CONTRACT_EXPLORER_PACKAGE}:inline-abis`;
var ABI_CACHE_MAX_ENTRIES = 64;
var ABI_CACHE_TTL_MS = 5 * 6e4;
var EVENT_LOG_CACHE_MAX_ENTRIES = 48;
var EVENT_LOG_CACHE_TTL_MS = 6e4;
var PINNED_ABIS_LIMIT = 8;

// src/model/options.ts
var AbiSourceKind = {
  /** User-supplied / edited JSON ABI. */
  INLINE: "inline",
  /** Previously resolved + cached ABI (in-memory or scoped storage). */
  CACHE: "cache",
  /** Backend catalog ABI, resolved through org/tenant/user precedence. */
  CATALOG: "catalog",
  /** Well-known default ABIs (ERC-20/721/1155, EIP-1967, …). */
  DEFAULTS: "defaults",
  /** External explorer sources (Etherscan-like). */
  EXTERNAL: "external"
};
var ALL_ABI_SOURCES = [
  AbiSourceKind.INLINE,
  AbiSourceKind.CACHE,
  AbiSourceKind.CATALOG,
  AbiSourceKind.DEFAULTS,
  AbiSourceKind.EXTERNAL
];
var DEFAULT_ABI_FETCH_OPTIONS = {
  useDefaults: true,
  useExternalSources: false,
  useCatalog: true,
  useProxies: true,
  discoverProxies: true,
  sourcePriority: [
    AbiSourceKind.INLINE,
    AbiSourceKind.CACHE,
    AbiSourceKind.CATALOG,
    AbiSourceKind.DEFAULTS,
    AbiSourceKind.EXTERNAL
  ]
};
var PROVIDER_ABI_SOURCES = [
  AbiSourceKind.CATALOG,
  AbiSourceKind.DEFAULTS,
  AbiSourceKind.EXTERNAL
];
function isProviderSource(source) {
  return PROVIDER_ABI_SOURCES.includes(source);
}
function providerSourceOrder(priority) {
  const kept = priority.filter(isProviderSource);
  const seen = new Set(kept);
  return [...kept, ...PROVIDER_ABI_SOURCES.filter((s) => !seen.has(s))];
}
function withProviderOrder(priority, order) {
  const head = priority.filter((s) => !isProviderSource(s));
  return [...head, ...order];
}
function moveAbiSource(priority, source, delta) {
  const order = providerSourceOrder(priority);
  const from = order.indexOf(source);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= order.length) return withProviderOrder(priority, order);
  const next = [...order];
  next[from] = order[to];
  next[to] = order[from];
  return withProviderOrder(priority, next);
}
function optionsKey(opts) {
  return [
    opts.useDefaults ? "d" : "",
    opts.useExternalSources ? "x" : "",
    opts.useCatalog ? "c" : "",
    opts.useProxies ? "p" : "",
    opts.discoverProxies ? "P" : "",
    opts.sourcePriority.join(">")
  ].join("|");
}
function isStateExploration(value) {
  return typeof value === "string" && ["profile", "none", "1", "2", "3", "4"].includes(value);
}
function stateExplorationOptions(mode) {
  if (mode === "profile") return { budget: RpcBudgetSchema.parse({
    max_drilldown_addresses: 8,
    max_calls_total: 192,
    max_calls_per_contract: 128,
    max_lookup_calls: 64
  }) };
  return {
    drilldown: DrillDownRuleSchema.parse({
      enabled: mode !== "none",
      max_depth: mode === "none" ? 0 : Number(mode),
      inherit_variables: false
    }),
    budget: RpcBudgetSchema.parse({
      max_drilldown_addresses: 16,
      max_calls_total: 192,
      max_calls_per_contract: 128,
      max_lookup_calls: 96
    })
  };
}
function blockToBlockSpec(block) {
  if (!block) return void 0;
  if (block.blockNumber != null) {
    const n = Number(block.blockNumber);
    if (Number.isSafeInteger(n) && n >= 0) return { number: n, block_hash: null, timestamp: null, tag: null };
    throw new RangeError("Block number must be a nonnegative safe integer");
  }
  if (block.blockTag === "pending") throw new Error("Pending state cannot provide a coherent contract snapshot");
  if (block.blockTag) return { number: null, block_hash: null, timestamp: null, tag: block.blockTag };
  return void 0;
}
function buildContractStateRequest(params) {
  const target = {
    address: params.address,
    name: null,
    category: params.category ?? null,
    variables: params.variables ?? [],
    use_category_defaults: params.useCategoryDefaults ?? true,
    auto_detect_proxy: params.autoDetectProxy ?? true,
    drilldown: params.drilldown ?? null,
    combine: null
  };
  return {
    request_id: params.requestId ?? null,
    chains: [
      {
        chain_id: params.chainId,
        block: blockToBlockSpec(params.block),
        contracts: [target]
      }
    ],
    drilldown_defaults: null,
    combine: null,
    budget: params.budget,
    include_source_code: params.includeSourceCode ?? false
  };
}

// src/model/profileLabel.ts
function profileLabel(category, match) {
  const id2 = (category ?? match?.name ?? "").trim();
  const stated = match?.label?.trim();
  if (stated && (!category || !match?.name || match.name === category)) return stated;
  return readProfileId(id2);
}
function readProfileId(id2) {
  const words = id2.split("_").filter(Boolean).map(
    (word) => /^erc\d+$/.test(word) ? `ERC-${word.slice(3)}` : /^v\d+$/.test(word) ? `V${word.slice(1)}` : word
  );
  if (words.length === 0) return id2;
  const [first, ...rest] = words;
  return [first.charAt(0).toUpperCase() + first.slice(1), ...rest].join(" ");
}
var ADDRESS = /^0x[0-9a-fA-F]{40}$/;
var UINT = /^\d{1,78}$/;
var same = (a, b) => Boolean(a) && Boolean(b) && a.toLowerCase() === b.toLowerCase();
var short = (address) => `${address.slice(0, 6)}\u2026${address.slice(-4)}`;
function variable(key, params, label) {
  return { key, params, label, kind: "function", slot: null, storage_offset: 0, value_type: null, ref: null };
}
function argument(param, shape) {
  if (!param) return void 0;
  const raw = rawOf(param.value) ?? param.value;
  if (typeof raw === "number" && !Number.isSafeInteger(raw)) return void 0;
  const text2 = typeof raw === "string" ? raw.trim() : typeof raw === "number" || typeof raw === "bigint" ? String(raw) : "";
  return shape.test(text2) ? text2 : void 0;
}
function shapeOf(call) {
  const signature = call.signature?.trim();
  if (signature && signature.includes("(")) return signature.replace(/\s+/g, "");
  return `${call.label ?? ""}(${call.params.map((p) => p.type ?? "").join(",")})`;
}
function failureReads(call, caller) {
  const reads = [];
  const from = caller && ADDRESS.test(caller) ? caller : void 0;
  if (call) {
    const [a, , c] = call.params;
    switch (shapeOf(call)) {
      case "transfer(address,uint256)":
      case "burn(uint256)":
      case "withdraw(uint256)":
        if (from) reads.push(variable("balanceOf", [from], `Balance of the sender ${short(from)}`));
        break;
      case "transferFrom(address,address,uint256)":
      case "safeTransferFrom(address,address,uint256)":
      case "safeTransferFrom(address,address,uint256,bytes)": {
        const owner2 = argument(a, ADDRESS);
        const third = argument(c, UINT);
        if (owner2) reads.push(variable("balanceOf", [owner2], `Balance of the owner ${short(owner2)}`));
        if (owner2 && from) reads.push(variable("allowance", [owner2, from], `Allowance ${short(owner2)} granted the caller ${short(from)}`));
        if (third) {
          reads.push(variable("ownerOf", [third], "Owner of the token id"));
          reads.push(variable("getApproved", [third], "Approved for the token id"));
        }
        break;
      }
      case "burnFrom(address,uint256)": {
        const owner2 = argument(a, ADDRESS);
        if (owner2) reads.push(variable("balanceOf", [owner2], `Balance of the owner ${short(owner2)}`));
        if (owner2 && from) reads.push(variable("allowance", [owner2, from], `Allowance ${short(owner2)} granted the caller ${short(from)}`));
        break;
      }
    }
  }
  reads.push(variable("paused", [], "Paused"));
  return reads;
}
function revertLine(call, error) {
  const revert = call?.revert;
  const message = revert?.params.find((p) => p.name === "message" || p.name === "reason") ?? revert?.params[0];
  const raw = message ? rawOf(message.value) ?? message.value : void 0;
  const shown = typeof message?.display === "string" ? message.display : typeof raw === "string" ? raw : void 0;
  const text2 = shown?.trim() || void 0;
  if (revert?.error && revert.error !== "Error" && revert.error !== "Panic") {
    return text2 ? `${revert.error}: ${text2}` : revert.error;
  }
  return text2 ?? (error?.trim() || void 0);
}
function mergeReads(into, more) {
  const seen = new Set(into.map((v) => `${v.key}:${JSON.stringify(v.params ?? [])}`));
  for (const read of more) {
    const id2 = `${read.key}:${JSON.stringify(read.params ?? [])}`;
    if (!seen.has(id2)) {
      seen.add(id2);
      into.push(read);
    }
  }
}
function failureSubjects(input) {
  const limit = input.limit ?? 2;
  const failed = input.internalTxs.filter((frame) => frame.success === false && !frame.isCreation && frame.to && ADDRESS.test(frame.to));
  const subjects = /* @__PURE__ */ new Map();
  const add = (address, call, caller, error) => {
    const key = address.toLowerCase();
    const reads = failureReads(call, caller);
    const existing = subjects.get(key);
    if (existing) {
      mergeReads(existing.variables, reads);
      existing.reason ??= revertLine(call, error);
      return;
    }
    subjects.set(key, { kind: "failure", address, reason: revertLine(call, error), variables: reads });
  };
  const frameTo = (address) => [...failed].reverse().find((frame) => same(frame.to, address));
  if (input.reverted) {
    const raiser = input.call?.revert?.raisedBy;
    const address = raiser && ADDRESS.test(raiser) ? raiser : input.to;
    if (address && ADDRESS.test(address)) {
      const frame = frameTo(address);
      if (frame) add(address, frame.call, frame.from, frame.error);
      else add(address, same(address, input.to) ? input.call : null, same(address, input.to) ? input.from : void 0);
    }
  }
  const origins = failed.filter((frame) => {
    const raiser = frame.call?.revert?.raisedBy;
    return !raiser || same(raiser, frame.to);
  });
  for (const frame of failed) {
    const raiser = frame.call?.revert?.raisedBy;
    if (raiser && ADDRESS.test(raiser) && !same(raiser, frame.to) && !failed.some((other) => same(other.to, raiser))) {
      const asked = [...input.internalTxs].reverse().find((other) => same(other.to, raiser) && other.success === void 0 && !other.isCreation);
      add(raiser, asked?.call ?? null, asked?.from, frame.error);
    }
  }
  for (const frame of [...origins].reverse()) add(frame.to, frame.call, frame.from, frame.error);
  return [...subjects.values()].slice(0, limit);
}
var PROXY_CHANGES = {
  Upgraded: { arity: 1, says: "Implementation upgraded" },
  AdminChanged: { arity: 2, says: "Proxy admin changed" },
  BeaconUpgraded: { arity: 1, says: "Beacon changed" },
  NewImplementation: { arity: 2, says: "Implementation changed" },
  NewAdmin: { arity: 2, says: "Admin changed" },
  ChangedMasterCopy: { arity: 1, says: "Singleton changed" }
};
function proxyChangeSubjects(events, limit = 3) {
  const subjects = /* @__PURE__ */ new Map();
  for (const event of events) {
    const known = event.name && Object.prototype.hasOwnProperty.call(PROXY_CHANGES, event.name) ? PROXY_CHANGES[event.name] : void 0;
    if (!known || !event.address || !ADDRESS.test(event.address)) continue;
    if (event.params.length !== known.arity || !event.params.every((p) => (p.type ?? "address") === "address")) continue;
    const key = event.address.toLowerCase();
    const existing = subjects.get(key);
    if (existing) {
      if (!existing.reason?.includes(known.says)) existing.reason = `${existing.reason} \xB7 ${known.says}`;
      continue;
    }
    subjects.set(key, { kind: "proxy_change", address: event.address, reason: known.says, variables: [] });
  }
  return [...subjects.values()].slice(0, limit);
}
var STATE_CHANGE_ATTRIBUTE = "state_change";
var STATE_CHANGE_CONFIGURATION = "configuration";
var STATE_READ_ATTRIBUTE = "state_read";
var STATE_READ_KEY_ATTRIBUTE = "state_read_key";
var GETTER = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;
var READ_KEY = /^(\d{1,78}|0x[0-9a-fA-F]{1,64})$/;
var DECLARED_READ_LIMIT = 4;
function plain(value) {
  if (typeof value === "string") return value.trim() || void 0;
  if (typeof value === "number") return Number.isSafeInteger(value) ? String(value) : void 0;
  if (typeof value === "bigint") return String(value);
  return void 0;
}
function declaredRead(node) {
  const name = plain(node.attributes?.[STATE_READ_ATTRIBUTE]);
  if (!name || !GETTER.test(name)) return void 0;
  const raw = node.attributes?.[STATE_READ_KEY_ATTRIBUTE];
  if (raw === void 0 || raw === null) return variable(name, [], name);
  const key = plain(raw);
  if (!key || !READ_KEY.test(key)) return void 0;
  return variable(name, [key], `${name}(${key})`);
}
function configChangeSubjects(nodes, limit = 3) {
  const subjects = /* @__PURE__ */ new Map();
  for (const node of nodes ?? []) {
    if (node.attributes?.[STATE_CHANGE_ATTRIBUTE] !== STATE_CHANGE_CONFIGURATION) continue;
    if (!node.address || !ADDRESS.test(node.address)) continue;
    const key = node.address.toLowerCase();
    const says = node.label?.trim() || void 0;
    const read = declaredRead(node);
    const existing = subjects.get(key);
    if (existing) {
      if (says && !existing.reason?.split(" \xB7 ").includes(says)) existing.reason = existing.reason ? `${existing.reason} \xB7 ${says}` : says;
      if (read) mergeReads(existing.variables, [read]);
      continue;
    }
    subjects.set(key, { kind: "config_change", address: node.address, reason: says, variables: read ? [read] : [] });
  }
  for (const subject of subjects.values()) subject.variables = subject.variables.slice(0, DECLARED_READ_LIMIT);
  return [...subjects.values()].slice(0, limit);
}
var NOT_IN_ABI = "function not in ABI";
function text(value) {
  if (value === null || value === void 0) return "\u2014";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") return String(value);
  return JSON.stringify(value);
}
function readName(key, params) {
  const name = key.replace(/\(\)$/, "");
  return params?.length ? `${name.split("(")[0]}(${params.map(text).join(", ")})` : name;
}
var LIST_MEMBER_LIMIT = 64;
var LIST_MEMBER_PRESENT = "listed";
function listMembers(decoded) {
  if (!Array.isArray(decoded) || decoded.length > LIST_MEMBER_LIMIT) return void 0;
  if (!decoded.every((member) => typeof member === "string" || typeof member === "number" || typeof member === "bigint")) return void 0;
  return decoded.map((member) => String(member));
}
function writeValue(into, field, value, options = {}) {
  if (value.error) {
    const absent = value.error === NOT_IN_ABI || value.key.includes("(") && value.error === "read-only function missing or overloaded; use a canonical signature";
    if (!absent) (into.errors ??= {})[field] = value.error;
    return;
  }
  const decoded = value.value_decoded;
  const members = options.listMembers ? listMembers(decoded) : void 0;
  if (members) {
    into.values[`${field} \xB7 count`] = String(members.length);
    for (const member of members) into.values[`${field} \u203A ${member}`] = LIST_MEMBER_PRESENT;
    return;
  }
  if (!value.display && decoded && typeof decoded === "object" && !Array.isArray(decoded)) {
    for (const [name, member] of Object.entries(decoded)) {
      const inner = options.listMembers ? listMembers(member) : void 0;
      if (inner) {
        into.values[`${field} \u203A ${name} \xB7 count`] = String(inner.length);
        for (const each of inner) into.values[`${field} \u203A ${name} \u203A ${each}`] = LIST_MEMBER_PRESENT;
        continue;
      }
      into.values[`${field} \u203A ${name}`] = text(member);
    }
    return;
  }
  into.values[field] = value.display ?? text(decoded);
}
var PROXY_ROLE_FIELDS = ["Implementation", "Proxy admin", "Proxy pattern"];
function stateSnapshot(result, options) {
  const snapshot = { label: options.label, values: {} };
  const node = result?.nodes[0];
  if (!node) return snapshot;
  if (!node.is_contract) {
    snapshot.values["Contract code"] = "None at this block";
    return snapshot;
  }
  if (node.is_proxy || options.proxyRoles) {
    snapshot.values["Implementation"] = node.is_proxy ? node.implementation_addresses.join(", ") || "Not resolved" : "Not a proxy at this block";
    if (node.is_proxy) {
      snapshot.values["Proxy admin"] = node.proxy_admin ?? "None reported";
      if (node.proxy_pattern) snapshot.values["Proxy pattern"] = node.proxy_pattern;
    }
  }
  const how = { listMembers: options.listMembers };
  for (const value of node.values) writeValue(snapshot, value.label ?? readName(value.key, value.params), value, how);
  const children = new Map(node.children.map((child) => [child.ref, child]));
  for (const policy of node.relationship_policies ?? []) {
    for (const ref of policy.value_refs) {
      const cut = ref.indexOf("#");
      const child = cut > 0 ? children.get(ref.slice(0, cut)) : void 0;
      const value = child?.values.find((candidate) => candidate.ref === ref.slice(cut + 1));
      if (!child || !value) continue;
      const owner2 = child.name?.trim() || `${child.address.slice(0, 6)}\u2026${child.address.slice(-4)}`;
      writeValue(snapshot, `${owner2} \u203A ${value.label ?? readName(value.key, value.params)}`, value, how);
    }
  }
  return snapshot;
}
function inferredSelector(name) {
  return syntheticSelector(name);
}
function isInferredFunction(fn) {
  return inferredSelector(fn.name) !== void 0;
}
function returnsRawData(fn) {
  return (fn.outputs ?? []).length === 0;
}
var ARRAY_DIMENSIONS = /^(?:\[(?:[1-9]\d*)?\])*$/;
function encodableParameter(param) {
  if (param) param = normalizeAbiParameter(param);
  const type = typeof param?.type === "string" ? param.type.trim() : "";
  const base = type.replace(/(?:\[\d*\])+$/, "");
  if (!base || !ARRAY_DIMENSIONS.test(type.slice(base.length))) return false;
  if (base === "tuple") {
    const components2 = param.components;
    return Array.isArray(components2) && components2.every(encodableParameter);
  }
  if (base === "address" || base === "bool" || base === "string" || base === "bytes") return true;
  const fixed = /^bytes(\d+)$/.exec(base);
  if (fixed) return Number(fixed[1]) >= 1 && Number(fixed[1]) <= 32;
  const integer = /^u?int(\d*)$/.exec(base);
  if (integer) {
    if (!integer[1]) return true;
    const bits = Number(integer[1]);
    return bits >= 8 && bits <= 256 && bits % 8 === 0;
  }
  return false;
}
function inferredCallProblem(fn) {
  if (!isInferredFunction(fn)) return void 0;
  const inputs = fn.inputs;
  if (!Array.isArray(inputs) || !inputs.every((param) => encodableParameter(param))) {
    return "This function was inferred from bytecode, but its argument types could not be recovered, so a call to it cannot be encoded. Supply a verified ABI to call it.";
  }
  return void 0;
}
function encodeFunctionCall(fn, args = []) {
  const selector = inferredSelector(fn.name);
  if (!selector) {
    return encodeFunctionData({ abi: [fn], functionName: fn.name, args });
  }
  const problem = inferredCallProblem(fn);
  if (problem) throw new Error(problem);
  return fn.inputs.length === 0 ? selector : concat([selector, encodeAbiParameters(fn.inputs.map(normalizeAbiParameter), args)]);
}
function inferredReturnValue(fn, raw) {
  const outputs = (fn.outputs ?? []).map(normalizeAbiParameter);
  if (outputs.length === 0) return raw;
  const values = decodeAbiParameters(outputs, raw);
  return outputs.length === 1 ? values[0] : values;
}
function requestedFunction(req) {
  const arity = req.args?.length ?? 0;
  return req.abi.find(
    (entry) => entry.type === "function" && entry.name === req.functionName && (entry.inputs?.length ?? 0) === arity
  );
}
var RAW_CALL_UNSUPPORTED = "This function needs a raw call to preserve its selector or undeclared return data, but the active contract reader cannot send raw calls.";
function requiresRawRead(req) {
  const fn = requestedFunction(req);
  return Boolean(inferredSelector(req.functionName) || fn && returnsRawData(fn));
}
async function readContractFunction(reader, ref, req, opts) {
  if (!requiresRawRead(req)) return reader.read(ref, req, opts);
  const fn = requestedFunction(req);
  if (!fn) throw new Error(`${req.functionName} is not in the ABI this read was given.`);
  if (!reader.call) throw new Error(RAW_CALL_UNSUPPORTED);
  const raw = await reader.call(ref, encodeFunctionCall(fn, req.args ?? []), opts);
  return inferredReturnValue(fn, raw ?? "0x");
}
async function readContractFunctions(reader, ref, reqs, opts) {
  const plain2 = [];
  const raw = [];
  reqs.forEach((req, i) => (requiresRawRead(req) ? raw : plain2).push(i));
  if (raw.length === 0) return reader.readMany(ref, reqs, opts);
  const [batched, singles] = await Promise.all([
    plain2.length > 0 ? reader.readMany(ref, plain2.map((i) => reqs[i]), opts) : Promise.resolve([]),
    Promise.allSettled(raw.map((i) => readContractFunction(reader, ref, reqs[i], opts)))
  ]);
  const out = new Array(reqs.length);
  plain2.forEach((index, j) => {
    out[index] = batched[j];
  });
  raw.forEach((index, j) => {
    const settled = singles[j];
    out[index] = settled.status === "fulfilled" ? { status: "success", data: settled.value } : {
      status: "failure",
      error: settled.reason instanceof Error ? settled.reason : new Error(String(settled.reason))
    };
  });
  return out;
}
function contractWriteCall(address, abi, fn, args) {
  const call = { address, abi, functionName: fn.name, args };
  return isInferredFunction(fn) ? { ...call, data: encodeFunctionCall(fn, args) } : call;
}
var ADDRESS_HEADROOM = 1n << 160n;
function splitReturnData(raw) {
  const hex = /^0x[0-9a-f]*$/i.test(raw) && raw.length % 2 === 0 ? raw.toLowerCase() : "0x";
  const byteLength = size(hex);
  const words = [];
  const whole = Math.floor(byteLength / 32);
  for (let index = 0; index < whole; index++) {
    const offset = index * 32;
    const word = slice(hex, offset, offset + 32);
    const uint = hexToBigInt(word);
    words.push({
      index,
      offset,
      hex: word,
      uint,
      ...uint !== 0n && uint < ADDRESS_HEADROOM ? { address: getAddress(slice(word, 12)) } : {},
      ...uint === 0n || uint === 1n ? { bool: uint === 1n } : {}
    });
  }
  const tail = byteLength % 32 ? slice(hex, whole * 32) : void 0;
  return { hex, byteLength, words, ...tail ? { tail } : {} };
}

// src/model/explorerTabs.ts
var CONTRACT_EXPLORER_TABS = [
  "fields",
  "onchain",
  "read",
  "write",
  "events",
  "abi",
  "source",
  "sources",
  "proxy",
  "state",
  "edit",
  "compare",
  "options"
];
var DEFAULT_CONTRACT_EXPLORER_TAB = "fields";
function isContractExplorerTab(value) {
  return typeof value === "string" && CONTRACT_EXPLORER_TABS.includes(value);
}
var EVM_EXPLORER_TABS = [
  { id: "fields", label: "Fields" },
  { id: "onchain", label: "On-chain state" },
  { id: "read", label: "Read" },
  { id: "write", label: "Write" },
  { id: "events", label: "Events" },
  { id: "abi", label: "ABI" },
  { id: "source", label: "Verified source" },
  { id: "sources", label: "ABI sources" },
  { id: "proxy", label: "Proxies" },
  { id: "state", label: "Compare blocks" },
  { id: "edit", label: "Edit" },
  { id: "compare", label: "Compare ABIs" },
  { id: "options", label: "Options" }
];
function availableExplorerTabs(tabs, services) {
  return tabs.filter(({ id: id2 }) => {
    if (id2 === "onchain") return services.state;
    if (id2 === "events") return Boolean(services.events);
    if (id2 === "source") return Boolean(services.source);
    if (id2 === "sources") return Boolean(services.sources);
    if (id2 === "proxy") return Boolean(services.proxy);
    return true;
  });
}
function resolveExplorerTab(tabs, requested) {
  const match = tabs.find(({ id: id2 }) => id2 === requested);
  return { active: match ?? tabs[0], unavailable: Boolean(requested && !match) };
}
function explorerTabLabel(id2) {
  return EVM_EXPLORER_TABS.find((tab) => tab.id === id2)?.label ?? "Requested view";
}
var ADDRESS2 = /^0x[\da-fA-F]{40}$/;
var UINT2 = /^\d{1,78}$/;
var ZERO = /^0x0{40}$/i;
function tokenIds(param) {
  const raw = rawOf(param?.value) ?? param?.value;
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== "string" || raw.length > 42e4) return void 0;
  const text2 = raw.trim();
  if (!text2.startsWith("[") || !text2.endsWith("]")) return void 0;
  const body = text2.slice(1, -1).trim();
  if (!body) return [];
  const parts = body.split(",");
  if (parts.some((part) => !/^\s*(?:\d{1,78}|"\d{1,78}"|'\d{1,78}')\s*$/.test(part))) return void 0;
  return parts.map((part) => part.trim().replace(/^["']|["']$/g, ""));
}
function boundedLimit(value, fallback, maximum) {
  return Number.isFinite(value) ? Math.max(0, Math.min(maximum, Math.floor(value))) : fallback;
}
function account(value) {
  return value && ADDRESS2.test(value) && !ZERO.test(value) ? value.toLowerCase() : void 0;
}
function id(param) {
  const value = argument(param, UINT2);
  return value && BigInt(value) < 2n ** 256n ? BigInt(value).toString() : void 0;
}
var owner = (p) => account(argument(p, ADDRESS2));
var TOKEN_GETTERS = {
  "balanceOf(address)": (p) => `Balance \xB7 ${p[0]}`,
  "allowance(address,address)": (p) => `Allowance \xB7 ${p[0]} \u2192 ${p[1]}`,
  "isApprovedForAll(address,address)": (p) => `Operator approval \xB7 ${p[0]} \u2192 ${p[1]}`,
  "ownerOf(uint256)": (p) => `Owner \xB7 token #${p[0]}`,
  "getApproved(uint256)": (p) => `Approved address \xB7 token #${p[0]}`,
  "balanceOf(address,uint256)": (p) => `Balance \xB7 token #${p[1]} \xB7 ${p[0]}`
};
function tokenStateVariable(key, params) {
  if (!Object.hasOwn(TOKEN_GETTERS, key) || !Array.isArray(params)) return void 0;
  const types = key.slice(key.indexOf("(") + 1, -1).split(",");
  if (types.length !== params.length) return void 0;
  const normalized = [];
  for (let i = 0; i < types.length; i++) {
    const value = params[i];
    if (typeof value !== "string") return void 0;
    const valid = types[i] === "address" ? account(value) : id({ value });
    if (valid === void 0) return void 0;
    normalized.push(valid);
  }
  return variable(key, normalized, TOKEN_GETTERS[key](normalized));
}
var tokenRead = (key, params) => tokenStateVariable(key, params);
var balances = (b) => [...new Set([b.owner, b.recipient].filter((v) => Boolean(v)))].map(
  (a) => tokenRead("balanceOf(address)", [a])
);
var allowance = (b) => b.owner && b.spender ? [tokenRead("allowance(address,address)", [b.owner, b.spender])] : [];
var operator = (b) => b.owner && b.spender ? [tokenRead("isApprovedForAll(address,address)", [b.owner, b.spender])] : [];
var transferOperator = (b) => b.owner === b.spender ? [] : operator(b);
var nft = (b) => b.tokenId === void 0 ? [] : [
  tokenRead("ownerOf(uint256)", [b.tokenId]),
  tokenRead("getApproved(uint256)", [b.tokenId])
];
var multi = (b) => b.tokenId === void 0 ? [] : [...new Set([b.owner, b.recipient].filter((v) => Boolean(v)))].map(
  (a) => tokenRead("balanceOf(address,uint256)", [a, b.tokenId])
);
var candidateNft = (b) => nft({ tokenId: b.candidateTokenId });
var TOKEN_STATE_RULES = [
  {
    id: "erc20",
    label: "Token balances",
    calls: ["transfer(address,uint256)", "withdraw(uint256)"],
    bind: (p, c) => ({ owner: account(c), recipient: owner(p[0]) }),
    reads: balances
  },
  {
    id: "allowance",
    label: "Token approval",
    calls: [
      "increaseAllowance(address,uint256)",
      "decreaseAllowance(address,uint256)"
    ],
    bind: (p, c) => ({ owner: account(c), spender: owner(p[0]) }),
    reads: allowance
  },
  {
    id: "approve",
    label: "Token approval",
    calls: ["approve(address,uint256)"],
    bind: (p, c) => ({ owner: account(c), spender: owner(p[0]), candidateTokenId: id(p[1]) }),
    reads: (b) => [...allowance(b), ...candidateNft(b)]
  },
  {
    id: "burn",
    label: "Token burn state",
    calls: ["burn(uint256)"],
    bind: (p, c) => ({ owner: account(c), candidateTokenId: id(p[0]) }),
    reads: (b) => [...balances(b), ...candidateNft(b)]
  },
  {
    id: "operator",
    label: "Operator approval",
    calls: ["setApprovalForAll(address,bool)"],
    bind: (p, c) => ({ owner: account(c), spender: owner(p[0]) }),
    reads: operator
  },
  {
    id: "transfer_from",
    label: "Transfer state",
    calls: ["transferFrom(address,address,uint256)"],
    bind: (p, c) => ({ owner: owner(p[0]), recipient: owner(p[1]), spender: account(c), candidateTokenId: id(p[2]) }),
    reads: (b) => [...balances(b), ...allowance(b), ...transferOperator(b), ...candidateNft(b)]
  },
  {
    id: "erc721",
    label: "NFT ownership and approval",
    calls: ["safeTransferFrom(address,address,uint256)", "safeTransferFrom(address,address,uint256,bytes)"],
    bind: (p, c) => ({ owner: owner(p[0]), recipient: owner(p[1]), spender: account(c), tokenId: id(p[2]) }),
    reads: (b) => [...nft(b), ...transferOperator(b)]
  },
  {
    id: "erc1155",
    label: "Multi-token balances",
    calls: ["safeTransferFrom(address,address,uint256,uint256,bytes)"],
    bind: (p, c) => ({ owner: owner(p[0]), recipient: owner(p[1]), spender: account(c), tokenId: id(p[2]) }),
    reads: (b) => [...multi(b), ...transferOperator(b)]
  },
  {
    id: "erc1155_batch",
    label: "Multi-token balances",
    calls: ["safeBatchTransferFrom(address,address,uint256[],uint256[],bytes)"],
    tokenIdsParam: 2,
    bind: (p, c) => ({ owner: owner(p[0]), recipient: owner(p[1]), spender: account(c) }),
    reads: (b) => [...multi(b), ...transferOperator(b)]
  }
];
var DEFAULT_TOKEN_STATE_POLICY = {
  rules: TOKEN_STATE_RULES,
  maxItems: 1e3,
  maxSubjects: 200,
  maxReadsPerSubject: 12
};
function tokenStateSubjects(input, policy = DEFAULT_TOKEN_STATE_POLICY) {
  const subjects = /* @__PURE__ */ new Map();
  let count = 0, limited = false;
  const maxItems = boundedLimit(policy.maxItems, DEFAULT_TOKEN_STATE_POLICY.maxItems, 5e3);
  const maxSubjects = boundedLimit(policy.maxSubjects, DEFAULT_TOKEN_STATE_POLICY.maxSubjects, 500);
  const maxReads = boundedLimit(policy.maxReadsPerSubject, DEFAULT_TOKEN_STATE_POLICY.maxReadsPerSubject, 24);
  const add = (address, b, reads, label, source) => {
    address = account(address);
    if (!address || !reads.length) return;
    const key = `${address}:${b.tokenId ?? "token"}`;
    let item = subjects.get(key);
    if (!item) {
      if (subjects.size >= maxSubjects) {
        limited = true;
        return;
      }
      item = {
        id: key,
        address,
        label: `${label}${b.tokenId !== void 0 ? ` \xB7 #${b.tokenId}` : ""}`,
        variables: [],
        sources: [],
        limited: false
      };
      subjects.set(key, item);
    }
    mergeReads(item.variables, reads);
    if (item.variables.length > maxReads) {
      item.variables.length = maxReads;
      item.limited = limited = true;
    }
    if (!item.sources.includes(source) && item.sources.length < 4) item.sources.push(source);
  };
  const take = () => {
    if (count++ >= maxItems) {
      limited = true;
      return false;
    }
    return true;
  };
  const call = (decoded, address, caller, source) => {
    if (!decoded) return;
    const rule = policy.rules.find((r) => r.calls.includes(shapeOf(decoded)));
    if (rule?.tokenIdsParam !== void 0) {
      const p = decoded.params, ids = tokenIds(p[rule.tokenIdsParam]);
      if (ids)
        for (const value of ids) {
          if (!take()) break;
          const b = { ...rule.bind(p, caller), tokenId: id({ value }) };
          if (b.tokenId !== void 0) add(address, b, rule.reads(b), rule.label, source);
        }
      else limited = true;
      return;
    }
    if (rule) {
      const b = rule.bind(decoded.params, caller);
      add(address, b, rule.reads(b), rule.label, source);
    }
  };
  for (const transfer of input.transfers ?? []) {
    if (!take()) break;
    const b = { owner: account(transfer.from), recipient: account(transfer.to) };
    if (transfer.kind === "erc20") add(transfer.token, b, balances(b), "Token balances", "Decoded transfer");
    if (transfer.kind === "nft") {
      b.tokenId = id({ value: transfer.tokenId });
      add(transfer.token, b, [...nft(b), ...multi(b)], "NFT state", "Decoded transfer");
    }
  }
  for (const event of input.events) {
    if (!take()) break;
    if (event.raw) continue;
    const p = event.params;
    const signature = event.signature?.replace(/\s+/g, "") ?? `${event.name}(${p.map((v) => v.type).join(",")})`;
    if (signature === "Approval(address,address,uint256)" && (event.topics.length === 3 || event.topics.length === 4)) {
      const b = {
        owner: owner(p[0]),
        spender: owner(p[1]),
        ...event.topics.length === 4 ? { tokenId: id(p[2]) } : {}
      };
      add(
        event.address,
        b,
        b.tokenId !== void 0 ? nft(b) : allowance(b),
        "Approval state",
        `Event ${event.index ?? ""} \xB7 Approval`
      );
    } else if (signature === "Transfer(address,address,uint256)" && (event.topics.length === 3 || event.topics.length === 4)) {
      const b = {
        owner: owner(p[0]),
        recipient: owner(p[1]),
        ...event.topics.length === 4 ? { tokenId: id(p[2]) } : {}
      };
      if (event.topics.length === 3 || b.tokenId !== void 0)
        add(
          event.address,
          b,
          event.topics.length === 4 ? nft(b) : balances(b),
          event.topics.length === 4 ? "NFT state" : "Token balances",
          `Event ${event.index ?? ""} \xB7 Transfer`
        );
    } else if (signature === "ApprovalForAll(address,address,bool)") {
      const b = { owner: owner(p[0]), spender: owner(p[1]) };
      add(event.address, b, operator(b), "Operator approval", `Event ${event.index ?? ""} \xB7 ApprovalForAll`);
    } else if (signature === "TransferSingle(address,address,address,uint256,uint256)") {
      const b = { owner: owner(p[1]), recipient: owner(p[2]), tokenId: id(p[3]) };
      add(event.address, b, multi(b), "Multi-token balances", `Event ${event.index ?? ""} \xB7 TransferSingle`);
    } else if (signature === "TransferBatch(address,address,address,uint256[],uint256[])") {
      const ids = tokenIds(p[3]);
      if (ids)
        for (const value of ids) {
          if (!take()) break;
          const b = {
            owner: owner(p[1]),
            recipient: owner(p[2]),
            tokenId: id({ value })
          };
          add(
            event.address,
            b,
            multi(b),
            "Multi-token balances",
            `Event ${event.index ?? ""} \xB7 TransferBatch`
          );
        }
      else limited = true;
    }
  }
  if (input.decodedCall && take()) call(input.decodedCall, input.to, input.from, "Function call (intent)");
  for (const frame of input.internalTxs) {
    if (!take()) break;
    if (frame.isCreation || frame.callType?.toLowerCase() === "delegatecall" || frame.callType?.toLowerCase() === "callcode")
      continue;
    call(frame.call, frame.to, frame.from, `Internal call ${frame.index ?? ""} (intent)`);
  }
  return { subjects: [...subjects.values()], limited };
}

// src/model/tokenStateContext.ts
var MAX_CONTEXT_LENGTH = 12e3;
var MAX_READS = 24;
function parseTokenStateContext(value) {
  if (typeof value !== "string" || value.length > MAX_CONTEXT_LENGTH) return void 0;
  try {
    const data = JSON.parse(value);
    if (data?.v !== 1 || typeof data.address !== "string" || !/^0x[0-9a-f]{40}$/i.test(data.address) || !Array.isArray(data.reads) || !data.reads.length || data.reads.length > MAX_READS) return void 0;
    const variables = [];
    for (const read of data.reads) {
      if (typeof read?.key !== "string") return void 0;
      const variable2 = tokenStateVariable(read.key, read.params);
      if (!variable2) return void 0;
      mergeReads(variables, [variable2]);
    }
    return { address: data.address.toLowerCase(), variables };
  } catch {
    return void 0;
  }
}
function serializeTokenStateContext(context) {
  const value = JSON.stringify({ v: 1, address: context.address, reads: context.variables.map(({ key, params }) => ({ key, params })) });
  return parseTokenStateContext(value) ? value : void 0;
}

// src/model/abi.ts
var abi_exports = {};
__reExport(abi_exports, abi_star);

// src/provider/source.ts
var log = createLogger(`${CONTRACT_EXPLORER_PACKAGE}:provider`);
function describeAbiFailures(failures) {
  const message = failures.map((f) => f.error instanceof Error ? f.error.message : String(f.error ?? "")).find((m) => m.trim().length > 0);
  if (message) return message;
  const names = [...new Set(failures.map((f) => f.provider))].join(", ");
  return `The ABI lookup failed (${names || "unknown source"}).`;
}
function sourceEnabled(source, options) {
  switch (source) {
    case AbiSourceKind.CATALOG:
      return options.useCatalog;
    case AbiSourceKind.DEFAULTS:
      return options.useDefaults;
    case AbiSourceKind.EXTERNAL:
      return options.useExternalSources;
    default:
      return true;
  }
}
var AbiSourcesUnavailableError = class extends Error {
  constructor(failures) {
    const names = [...new Set(failures.map((f) => f.provider))].join(", ");
    super(`No ABI source could be reached (${names}). This is not the same as the contract having no ABI.`);
    this.failures = failures;
    this.name = "AbiSourcesUnavailableError";
  }
  failures;
};
function createCompositeAbiProvider(providers) {
  return {
    id: "composite",
    sources: [AbiSourceKind.CATALOG, AbiSourceKind.DEFAULTS, AbiSourceKind.EXTERNAL],
    async fetch(ref, options, ctx) {
      const failures = [];
      for (const source of options.sourcePriority) {
        if (!sourceEnabled(source, options)) continue;
        for (const provider of providers.filter((p) => p.sources.includes(source))) {
          try {
            const result = await provider.fetch(ref, options, ctx);
            if (result && result.abi.entries.length > 0) {
              return {
                ...result,
                origin: result.origin || source,
                ...failures.length > 0 ? { failures: [...failures] } : {}
              };
            }
          } catch (err) {
            log.warn("provider fetch failed", { provider: provider.id, source, error: String(err) });
            failures.push({ provider: provider.id, source, error: err });
          }
        }
      }
      if (failures.length > 0) throw new AbiSourcesUnavailableError(failures);
      return null;
    }
  };
}
function createInMemoryAbiProvider(seeds, config = {}) {
  const id2 = config.id ?? "in-memory";
  const source = config.source ?? AbiSourceKind.CATALOG;
  const index = /* @__PURE__ */ new Map();
  for (const seed of seeds) index.set(`${seed.chainId.toLowerCase()}:${seed.address.toLowerCase()}`, seed);
  return {
    id: id2,
    sources: [source],
    async fetch(ref) {
      const seed = index.get(`${ref.chainId.toLowerCase()}:${ref.address.toLowerCase()}`);
      if (!seed) return null;
      if (seed.dto) {
        return { abi: (0, abi_exports.normalizedAbiFromDto)(seed.dto), meta: (0, abi_exports.abiMetaFromDto)(seed.dto), origin: id2 };
      }
      return {
        abi: (0, abi_exports.normalizeAbi)(seed.entries ?? []),
        meta: { chainId: ref.chainId, address: ref.address, ...seed.meta },
        origin: id2
      };
    }
  };
}
function createDefaultAbiProvider() {
  return {
    id: "defaults",
    sources: [AbiSourceKind.DEFAULTS],
    async fetch(ref) {
      return {
        abi: (0, abi_exports.defaultErcAbi)(),
        meta: {
          chainId: ref.chainId,
          address: ref.address,
          source: "defaults",
          // A fixed floor, not a measurement. It exists to rank this BELOW every
          // real source; there is nothing about this contract being scored, and
          // the number rendered bare — "confidence 20%" beside no explanation —
          // read as a judgement about USDT rather than about the fallback.
          confidence: 0.2,
          explanation: [
            "No stored, catalogued or verified ABI was found for this address.",
            "Showing the well-known ERC-20 and ERC-721 interfaces as a fallback.",
            "Functions listed here are the standard ones, not this contract's \u2014 some may not exist on it."
          ]
        },
        origin: AbiSourceKind.DEFAULTS,
        isGuess: true
      };
    }
  };
}

// src/model/contract.ts
var contract_exports = {};
__reExport(contract_exports, contracts_star);

// src/model/proxyEvidence.ts
function record(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value) ? value : void 0;
}
function hasIncompleteProxyEvidence(evidence, conclusive) {
  if (conclusive === false) return true;
  return hasFailedProxyEvidence(evidence);
}
function hasFailedProxyEvidence(evidence) {
  return (evidence ?? []).some((entry) => {
    const row = record(entry);
    const status = record(row?.signals)?.status ?? row?.status;
    return typeof status === "string" && ["failed", "inconclusive"].includes(status.toLowerCase());
  });
}
function proxyEvidenceDetails(entry) {
  const row = record(entry);
  return record(row?.metadata) ?? record(row?.details);
}

// src/provider/apiAbi.ts
var log2 = createLogger(`${CONTRACT_EXPLORER_PACKAGE}:api-abi`);
var THIRD_PARTY_INFERRED_SOURCE = "third_party_inferred";
function abiContractPath(base, ref) {
  return `${base}/${encodeURIComponent(ref.chainId)}/${encodeURIComponent(ref.address)}`;
}
function metaFromWire(wire) {
  return {
    chainId: wire.chain_id,
    address: wire.address,
    contractName: wire.name ?? null,
    source: wire.source ?? null,
    updatedAt: wire.updated_at ?? null,
    orgId: wire.owner?.org_id ?? null,
    tenantId: wire.owner?.tenant_id ?? null,
    abiId: wire.abi_id,
    scope: wire.scope ?? null,
    isDefault: Boolean(wire.is_default),
    inherited: Boolean(wire.inherited),
    confidence: wire.confidence ?? void 0,
    confidenceStatus: wire.confidence_status,
    confidenceScored: wire.confidence_scored,
    lifecycle: wire.lifecycle ?? void 0,
    explanation: wire.explanation ?? null,
    evidence: wire.evidence ?? null,
    validFromBlock: wire.valid_from_block ?? null,
    validToBlock: wire.valid_to_block ?? null
  };
}
function blockQuery(ref) {
  const height = ref.block?.blockNumber;
  return height == null ? void 0 : { block: height.toString() };
}
function createApiAbiProvider(client, options = {}) {
  const base = options.basePath ?? "/abis";
  const id2 = options.id ?? "abi-mgmt-api";
  return {
    id: id2,
    sources: [AbiSourceKind.CATALOG],
    async fetch(ref, _options, ctx) {
      let wire;
      try {
        wire = unwrapData(
          await client.get(abiContractPath(base, ref), {
            query: blockQuery(ref),
            signal: ctx?.signal
          })
        );
      } catch (err) {
        if (err instanceof ApiError && err.statusCode === 404) {
          if (err.details?.reason === "lookup_failed") throw err;
          return null;
        }
        throw err;
      }
      const entries = Array.isArray(wire?.entries) ? wire.entries : [];
      if (entries.length === 0 && (wire?.entries_count ?? 0) > 0) {
        throw new Error("Stored ABI entries are unavailable. Retry or choose another usable ABI source.");
      }
      if (entries.length === 0) return null;
      if (wire?.source === THIRD_PARTY_INFERRED_SOURCE) return null;
      const bytecodeFacts = bytecodeFactsOf(wire?.bytecode_facts);
      return {
        abi: (0, abi_exports.normalizeAbi)(entries),
        meta: metaFromWire(wire),
        origin: id2,
        ...bytecodeFacts ? { bytecodeFacts } : {}
      };
    }
  };
}
function withBlockRange(body, opts) {
  if (opts?.validFromBlock != null) body.valid_from_block = opts.validFromBlock;
  if (opts?.validToBlock != null) body.valid_to_block = opts.validToBlock;
  return body;
}
function bytecodeFactsOf(raw) {
  if (!raw || typeof raw !== "object") return null;
  const [record2] = parseBytecodeFacts({ extras: { bytecode: { contract: raw } } });
  return record2 ?? null;
}
function abiSourceMeta(entry) {
  return {
    chainId: entry.chainId,
    address: entry.address,
    contractName: entry.name,
    abiId: entry.abiId,
    scope: entry.scope,
    source: entry.source,
    isDefault: entry.isDefault,
    inherited: entry.inherited,
    confidence: entry.confidence,
    confidenceStatus: entry.confidenceStatus,
    confidenceScored: entry.confidenceScored,
    evidence: entry.evidence,
    explanation: entry.explanation,
    validFromBlock: entry.validFromBlock,
    validToBlock: entry.validToBlock
  };
}
function abiSourceId(entry) {
  if (entry.origin === "catalog") return "catalog";
  return entry.abiId ?? `scope:${entry.scope ?? ""}`;
}
function abiSourceScopeLabel(entry) {
  if (entry.origin === "catalog") return "Catalog";
  if (entry.isDefault) return "System default";
  const rung = scopeRungLabel(entry.scope);
  if (entry.inherited) return rung ? `Inherited \xB7 ${rung}` : "Inherited";
  return rung ? `Your ${rung.toLowerCase()} scope` : "Your workspace";
}
function entriesOf(wire) {
  return Array.isArray(wire.entries) ? wire.entries : void 0;
}
function overrideToEntry(w) {
  return {
    origin: "override",
    abiId: w.abi_id,
    chainId: w.chain_id,
    address: w.address,
    name: w.name ?? null,
    source: w.source ?? null,
    status: w.status ?? null,
    scope: w.scope ?? null,
    isDefault: Boolean(w.is_default),
    inherited: Boolean(w.inherited),
    entriesCount: typeof w.entries_count === "number" ? w.entries_count : entriesOf(w)?.length ?? 0,
    entries: entriesOf(w),
    confidence: w.confidence ?? void 0,
    confidenceStatus: w.confidence_status,
    confidenceScored: w.confidence_scored,
    explanation: w.explanation,
    evidence: w.evidence,
    updatedAt: w.updated_at ?? null,
    validFromBlock: w.valid_from_block ?? null,
    validToBlock: w.valid_to_block ?? null
  };
}
function createApiAbiByAddressClient(client, options = {}) {
  const base = options.basePath ?? "/abis";
  return async (ref, opts) => {
    const path = `${base}/by-address/${encodeURIComponent(ref.chainId)}/${encodeURIComponent(ref.address)}`;
    const query = {};
    if (opts?.includeEntries !== false) query.include = "entries";
    if (opts?.block != null) query.block = opts.block.toString();
    const wire = unwrapData(
      await client.get(path, {
        query: Object.keys(query).length > 0 ? query : void 0,
        signal: opts?.signal
      })
    );
    const sources = (wire?.overrides ?? []).map(overrideToEntry);
    if (wire?.catalog) {
      const c = wire.catalog;
      sources.push({
        origin: "catalog",
        chainId: c.chain_id ?? ref.chainId,
        address: c.address ?? ref.address,
        name: c.name ?? c.contract_name ?? null,
        source: c.source ?? null,
        scope: null,
        isDefault: false,
        inherited: true,
        isProxy: Boolean(c.is_proxy),
        entriesCount: typeof c.entries_count === "number" ? c.entries_count : entriesOf(c)?.length ?? 0,
        entries: entriesOf(c),
        confidence: c.confidence ?? void 0,
        confidenceStatus: c.confidence_status,
        confidenceScored: c.confidence_scored,
        evidence: c.evidence,
        explanation: c.explanation,
        updatedAt: c.updated_at ?? null,
        validFromBlock: c.valid_from_block ?? null,
        validToBlock: c.valid_to_block ?? null,
        ...c.inferred ? { inferred: true } : {}
      });
    }
    return {
      chainId: wire?.chain_id ?? ref.chainId,
      address: wire?.address ?? ref.address,
      block: wire?.block ?? null,
      sources
    };
  };
}
function createApiAbiWriter(client, options = {}) {
  const base = options.basePath ?? "/abis";
  const canEdit = Boolean(options.canEdit);
  const canCreate = options.canCreate === void 0 ? canEdit : Boolean(options.canCreate);
  const canUpdate = options.canUpdate ?? canEdit;
  const scopeQuery = (opts) => opts?.scope ? { scope: opts.scope } : void 0;
  const create = async (ref, entries, opts) => {
    if (!canCreate) throw new Error("Registering an ABI requires the abis:create permission.");
    const body = { chain_id: ref.chainId, address: ref.address, entries };
    if (opts?.name !== void 0) body.name = opts.name;
    if (opts?.description !== void 0) body.description = opts.description;
    withBlockRange(body, opts);
    const created = unwrapData(
      await client.post(base, body, { query: scopeQuery(opts), signal: opts?.signal })
    );
    const replaced = created?.replaced;
    return {
      imported: false,
      replaced: typeof replaced === "boolean" ? replaced : void 0,
      meta: metaFromWire(created)
    };
  };
  return {
    canEdit,
    canCreate,
    create,
    async save(ref, entries, opts) {
      if (!canEdit) throw new Error("Saving an ABI requires abis:edit or abis:create permission.");
      if (!canUpdate) return create(ref, entries, opts);
      const body = { entries };
      if (opts?.name !== void 0) body.name = opts.name;
      if (opts?.description !== void 0) body.description = opts.description;
      withBlockRange(body, opts);
      try {
        const wire = unwrapData(
          await client.patch(abiContractPath(base, ref), body, {
            query: scopeQuery(opts),
            signal: opts?.signal
          })
        );
        return { imported: Boolean(wire?.imported), meta: metaFromWire(wire) };
      } catch (err) {
        if (err instanceof ApiError && err.statusCode === 404) {
          if (!canCreate) {
            throw new Error(
              "No ABI exists for this contract yet, and registering a new one needs the abis:create permission."
            );
          }
          return create(ref, entries, opts);
        }
        throw err;
      }
    },
    async rename(ref, name, opts) {
      if (!canEdit || !canUpdate) throw new Error("Renaming an ABI requires the abis:edit permission.");
      const body = { name };
      withBlockRange(body, opts);
      const wire = unwrapData(
        await client.patch(abiContractPath(base, ref), body, {
          query: scopeQuery(opts),
          signal: opts?.signal
        })
      );
      return { imported: Boolean(wire?.imported), meta: metaFromWire(wire) };
    },
    async refresh(ref, opts) {
      const wire = unwrapData(
        await client.post(`${abiContractPath(base, ref)}/refresh`, void 0, {
          // Opt-in last rung: only asked for when the caller wants the code read
          // if no provider has an ABI. Omitted entirely otherwise, so an
          // ordinary refresh is byte-for-byte the request it always was.
          query: opts?.bytecode ? { bytecode: "1" } : void 0,
          signal: opts?.signal
        })
      );
      const raw = wire?.bytecode;
      return {
        refreshed: wire?.refreshed !== false,
        found: Boolean(wire?.found),
        // Deliberately not defaulted to false: the miss branch omits the key
        // entirely, and "we didn't say" is not "we didn't save it".
        persisted: typeof wire?.persisted === "boolean" ? wire.persisted : void 0,
        providersQueried: wire?.providers_queried ?? null,
        ...typeof wire?.providers_errored === "number" && wire.providers_errored > 0 ? { providersErrored: wire.providers_errored } : {},
        ...wire?.kept_existing ? { keptExisting: true } : {},
        contractName: wire?.contract_name ?? null,
        entriesCount: wire?.entries_count ?? null,
        ...wire?.inferred_only ? { inferredOnly: true } : {},
        ...raw ? {
          bytecode: {
            status: typeof raw.status === "string" ? raw.status : "failed",
            stored: Boolean(raw.stored),
            // Absent stays absent: an older backend said nothing about either.
            ...typeof raw.source === "string" ? { source: raw.source } : {},
            ...typeof raw.implementation === "string" ? { implementation: raw.implementation } : {},
            entriesCount: Number(raw.entries_count ?? 0),
            entryPoints: Number(raw.entry_points ?? 0),
            typed: Number(raw.typed ?? 0),
            named: Number(raw.named ?? 0),
            analyzers: Array.isArray(raw.analyzers) ? raw.analyzers.filter((a) => typeof a === "string") : [],
            codeHash: raw.code_hash ?? null,
            blockNumber: typeof raw.block_number === "number" ? raw.block_number : null,
            detail: raw.detail ?? null,
            // Reuse the transaction page's parser rather than a second
            // reader of the same record: it already drops malformed
            // entries instead of inventing them, and one shape cannot
            // drift from itself. It expects the record under an address,
            // so the address this refresh was for is the key.
            facts: bytecodeFactsOf(raw.facts)
          }
        } : {}
      };
    },
    async delete(ref, opts) {
      unwrapData(
        await client.delete(abiContractPath(base, ref), {
          query: opts?.block != null ? { block: opts.block.toString() } : void 0,
          signal: opts?.signal
        })
      );
    }
  };
}
function proxyKindFromPattern(pattern) {
  switch ((pattern ?? "").toLowerCase()) {
    case "eip1967":
      return ProxyKind.EIP1967;
    case "uups":
    case "eip1822":
      return ProxyKind.UUPS;
    case "beacon":
      return ProxyKind.BEACON;
    case "transparent":
      return ProxyKind.TRANSPARENT;
    case "diamond":
    case "eip2535":
      return ProxyKind.DIAMOND;
    case "none":
    case "":
      return ProxyKind.UNKNOWN;
    default:
      return ProxyKind.CUSTOM;
  }
}
function createApiProxyResolver(client, options = {}) {
  const base = options.basePath ?? "/proxy-overrides";
  return async (ref, opts) => {
    let wire;
    try {
      wire = unwrapData(
        await client.get(abiContractPath(base, ref), {
          query: blockQuery(ref),
          signal: opts?.signal
        })
      );
    } catch (err) {
      log2.warn("proxy resolve failed", { chain: ref.chainId, address: ref.address, error: String(err) });
      throw err;
    }
    return proxyChainFromWire(wire, ref.address, ref.chainId);
  };
}
function proxyChainFromWire(wire, root, chainId) {
  const explanation = (wire?.explanation ?? []).filter(Boolean);
  const probeFailed = hasIncompleteProxyEvidence(wire?.evidence, wire?.conclusive);
  if (!wire?.is_proxy) {
    const chain = directChain(root);
    return {
      ...chain,
      ...probeFailed ? { truncated: hasFailedProxyEvidence(wire?.evidence) ? "probe-failed" : "not-detected" } : {},
      ...explanation.length > 0 ? { explanation } : {},
      ...wire?.resolved_by ? { resolvedBy: wire.resolved_by } : {}
    };
  }
  const impls = wire.implementations ?? [];
  const hops = impls.map((impl) => ({
    address: impl.address,
    kind: proxyKindFromPattern(impl.pattern ?? wire?.pattern)
  }));
  const effectiveAddress = hops[hops.length - 1]?.address ?? root;
  const beaconUnresolved = wire.evidence?.some((e) => {
    const details = proxyEvidenceDetails(e);
    return details?.fallback === "returned_beacon_address" && typeof details.beacon === "string" && normalizeAddress(details.beacon, chainId) === normalizeAddress(effectiveAddress, chainId);
  });
  const truncated = beaconUnresolved ? "beacon-unresolved" : probeFailed ? "probe-failed" : void 0;
  return {
    root,
    hops,
    effectiveAddress,
    isProxy: hops.length > 0,
    ...truncated ? { truncated } : {},
    ...explanation.length > 0 ? { explanation } : {},
    ...wire.resolved_by ? { resolvedBy: wire.resolved_by } : {}
  };
}
function batchEntryKey(address, block) {
  return `${address.toLowerCase()}@${block ?? "latest"}`;
}
function errorFromEntry(entry) {
  const stage = entry.error?.stage ?? entry.status ?? "unknown";
  const message = entry.error?.message ?? `proxy resolution ${entry.status ?? "failed"}`;
  return new Error(`${stage}: ${message}`);
}
function createApiProxyBatchResolver(client, options = {}) {
  const base = options.basePath ?? "/proxy-overrides";
  return async (refs, opts) => {
    if (refs.length === 0) return [];
    const unique = [];
    const seen = /* @__PURE__ */ new Set();
    for (const ref of refs) {
      const key = (0, contract_exports.contractRefKey)(ref);
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(ref);
    }
    const body = {
      refs: unique.map((ref) => {
        const height = ref.block?.blockNumber;
        return height == null ? { chain: ref.chainId, address: ref.address } : { chain: ref.chainId, address: ref.address, block: Number(height) };
      })
    };
    let wire;
    try {
      wire = unwrapData(
        await client.post(`${base}/resolve`, body, { signal: opts?.signal })
      );
    } catch (err) {
      log2.warn("proxy batch resolve failed", { refs: unique.length, error: String(err) });
      throw err;
    }
    const entries = Array.isArray(wire?.resolved) ? wire.resolved : [];
    const positional = entries.length === unique.length;
    const byKey = /* @__PURE__ */ new Map();
    if (!positional) {
      for (const entry of entries) {
        byKey.set(batchEntryKey(entry.address ?? "", entry.block_number), entry);
      }
    }
    return unique.map((ref, index) => {
      const entry = positional ? entries[index] : byKey.get(batchEntryKey(ref.address, ref.block?.blockNumber == null ? null : Number(ref.block.blockNumber)));
      if (!entry) {
        return {
          ref,
          chain: null,
          error: new Error("proxy.batch.missing: the service returned no entry for this ref")
        };
      }
      if (entry.status !== "resolved" || !entry.resolution) {
        return { ref, chain: null, error: errorFromEntry(entry) };
      }
      return { ref, chain: proxyChainFromWire(entry.resolution, ref.address, ref.chainId), error: null };
    });
  };
}
function sourceFromWire(w, ref) {
  const out = {
    chainId: w.chain_id ?? ref.chainId,
    address: w.address ?? ref.address,
    origin: w.origin ?? "third_party",
    found: Boolean(w.found),
    verified: w.verified,
    sourceAvailable: w.source_available,
    contractName: w.contract_name ?? null,
    compilerVersion: w.compiler_version ?? null,
    optimizationUsed: w.optimization_used ?? null,
    runs: w.runs ?? null,
    license: w.license ?? null,
    language: w.language ?? null,
    isProxy: w.is_proxy,
    implementationAddresses: w.implementation_addresses,
    sourceCode: w.source_code ?? null,
    abiEntriesCount: w.abi_entries_count,
    confidence: w.confidence ?? null,
    providersQueried: w.providers_queried ?? null,
    contributors: w.contributors ?? null
  };
  if (Array.isArray(w.abi)) out.abi = w.abi;
  if (w.bytecode) {
    out.bytecode = {
      present: w.bytecode.present ?? null,
      bytecode: w.bytecode.bytecode ?? null,
      sizeBytes: w.bytecode.size_bytes,
      block: w.bytecode.block ?? null,
      error: w.bytecode.error
    };
  }
  return out;
}
function createApiContractSourceClient(client, options = {}) {
  const base = options.basePath ?? "/source";
  return async (ref, opts) => {
    const include = [];
    if (opts?.includeBytecode) include.push("bytecode");
    if (opts?.includeAbi) include.push("abi");
    const query = {};
    if (include.length) query.include = include.join(",");
    if (opts?.providers?.length) query.providers = opts.providers.join(",");
    if (opts?.skipCache) query.skip_cache = "true";
    if (opts?.block != null) query.block = String(opts.block);
    const path = `${base}/${encodeURIComponent(ref.chainId)}/${encodeURIComponent(ref.address)}`;
    const wire = unwrapData(
      await client.get(path, {
        query: Object.keys(query).length ? query : void 0,
        signal: opts?.signal
      })
    );
    return sourceFromWire(wire ?? {}, ref);
  };
}

// src/provider/cache.ts
function identityKey(identity) {
  if (!identity) return "app";
  return [
    identity.app ? `app=${identity.app}` : "",
    identity.customerId ? `cust=${identity.customerId}` : "",
    identity.orgId ? `org=${identity.orgId}` : "",
    identity.tenantId ? `tenant=${identity.tenantId}` : "",
    identity.userId ? `user=${identity.userId}` : ""
  ].filter(Boolean).join("&");
}
function abiCacheKey(ref, options, identity) {
  return `${identityKey(identity)}::${(0, contract_exports.contractRefKey)(ref)}::${optionsKey(options)}`;
}
var AbiCache = class {
  constructor(max = ABI_CACHE_MAX_ENTRIES, ttlMs = ABI_CACHE_TTL_MS) {
    this.max = max;
    this.ttlMs = ttlMs;
  }
  max;
  ttlMs;
  map = /* @__PURE__ */ new Map();
  get(key) {
    const entry = this.map.get(key);
    if (!entry) return void 0;
    if (Date.now() - entry.at > this.ttlMs) {
      this.map.delete(key);
      return void 0;
    }
    this.map.delete(key);
    this.map.set(key, entry);
    return entry.result;
  }
  set(key, result) {
    this.map.delete(key);
    this.map.set(key, { result, at: Date.now() });
    while (this.map.size > this.max) {
      const oldest = this.map.keys().next().value;
      if (oldest === void 0) break;
      this.map.delete(oldest);
    }
  }
  delete(key) {
    this.map.delete(key);
  }
  /** Drop every entry for a contract ref (all option/identity variants). */
  invalidateRef(ref) {
    const needle = (0, contract_exports.contractRefKey)(ref);
    for (const key of [...this.map.keys()]) {
      if (key.includes(needle)) this.map.delete(key);
    }
  }
  clear() {
    this.map.clear();
  }
};
var sharedAbiCache = new AbiCache();

// src/provider/validatingClient.ts
function createValidatingClient(schema, options) {
  const { fetcher, id: id2 = "backend", validate = true } = options;
  return {
    id: id2,
    async read(request, opts) {
      const raw = await fetcher(request, opts);
      if (!validate) return raw;
      try {
        return schema.parse(raw);
      } catch (cause) {
        throw new Error("The server response could not be validated. Refresh to retry; if this continues, contact support.", { cause });
      }
    }
  };
}
function createContractStateClient(options) {
  return createValidatingClient(
    ContractStateResultSchema,
    options
  );
}
var DiscoveryRunSchema = z.object({
  run_id: z.string(),
  status: z.enum(["running", "finished"]),
  complete: z.boolean(),
  results: z.array(z.object({
    status: z.enum(["queued", "running", "retrying", "complete", "incomplete", "failed"]),
    routing: z.string().nullish(),
    pattern: z.string().nullish(),
    implementations: z.array(z.string()).default([]),
    subject: z.object({ chain_id: z.string(), address: z.string(), block_number: z.number().nullish(), block_hash: z.string().nullish() }).nullish()
  }))
});
function createProxyDiscoveryClient(client) {
  const path = "/proxy-overrides/discovery-request";
  return {
    async request(subject, signal) {
      return DiscoveryRunSchema.parse(unwrapData(await client.post(path, { subject }, { signal })));
    },
    async read(runId, signal) {
      return DiscoveryRunSchema.parse(unwrapData(await client.get(path, { query: { run_id: runId }, signal })));
    }
  };
}
function createApiContractStateClient(client, options = {}) {
  const { path = "/abis/contract-state", validate, id: id2 = "abi-mgmt-api" } = options;
  const state = createContractStateClient({
    id: id2,
    validate,
    fetcher: async (request, opts) => unwrapData(
      await client.post(path, request, { signal: opts?.signal })
    )
  });
  return { ...state, discovery: createProxyDiscoveryClient(client), profiles: {
    async list(scope, opts) {
      return ProfileSettingsSchema.parse(unwrapData(await client.get(`${path}/profiles`, { query: { scope }, signal: opts?.signal, cacheTtlMs: 0 })));
    },
    async save(scope, edit, opts) {
      return ProfileSettingsSchema.parse(unwrapData(await client.put(`${path}/profiles`, edit, { query: { scope }, signal: opts?.signal })));
    }
  } };
}
function createTransactionInsightClient(options) {
  return createValidatingClient(
    TransactionStateInsightSchema,
    options
  );
}
function createApiTransactionInsightClient(client, options = {}) {
  const { path = "/abis/transaction-insight", validate, id: id2 = "abi-mgmt-api" } = options;
  return createTransactionInsightClient({
    id: id2,
    validate,
    fetcher: async (request, opts) => unwrapData(
      await client.post(path, request, {
        signal: opts?.signal
      })
    )
  });
}
var ReaderContext = React50.createContext(null);
var fallbackReader = null;
function defaultContractReader() {
  fallbackReader ??= createRpcContractReader();
  return fallbackReader;
}
function ContractReaderProvider({ reader, children }) {
  const value = reader ?? defaultContractReader();
  return /* @__PURE__ */ jsx(ReaderContext.Provider, { value, children });
}
function useContractReader(override) {
  const ctx = React50.useContext(ReaderContext);
  return override ?? ctx ?? defaultContractReader();
}
var ContractStateClientContext = React50.createContext(null);
function ContractStateClientProvider({
  client,
  children
}) {
  return /* @__PURE__ */ jsx(ContractStateClientContext.Provider, { value: client, children });
}
function useContractStateClient(override) {
  const ctx = React50.useContext(ContractStateClientContext);
  return override ?? ctx ?? null;
}
var TransactionInsightClientContext = React50.createContext(null);
function TransactionInsightClientProvider({
  client,
  children
}) {
  return /* @__PURE__ */ jsx(TransactionInsightClientContext.Provider, { value: client, children });
}
function useTransactionInsightClient(override) {
  const ctx = React50.useContext(TransactionInsightClientContext);
  return override ?? ctx ?? null;
}
function requestKey(request) {
  if (request === null || request === void 0) return "";
  return JSON.stringify(request, (_k, v) => typeof v === "bigint" ? `${v.toString()}n` : v);
}
function useClientRead(params) {
  const { client, request, enabled = true } = params;
  const ready = enabled && request !== null && client !== null;
  const [data, setData] = React50.useState(null);
  const [error, setError] = React50.useState(null);
  const [isPending, setIsPending] = React50.useState(ready);
  const [nonce, setNonce] = React50.useState(0);
  const key = requestKey(request);
  if (useDepsChanged([client, key, ready])) {
    setData(null);
    setError(null);
    setIsPending(ready);
  }
  React50.useEffect(() => {
    if (!ready || !client || request === null) {
      return void 0;
    }
    const controller = new AbortController();
    Promise.resolve().then(() => client.read(request, { signal: controller.signal })).then((res) => {
      if (controller.signal.aborted) return;
      setData(res);
      setIsPending(false);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsPending(false);
    });
    return () => controller.abort();
  }, [client, ready, key, nonce]);
  const refetch = React50.useCallback(() => {
    if (!ready) return;
    setIsPending(true);
    setError(null);
    setNonce((n) => n + 1);
  }, [ready]);
  return { data, error, isPending, unwired: client === null, refetch };
}

// src/data/useReaderRead.ts
function useReaderRead({ reader, ref, abi, functionName, args, enabled = true }) {
  const client = React50.useMemo(() => ({
    async read(request, options) {
      return { value: await readContractFunction(reader, request.ref, request.call, options) };
    }
  }), [reader]);
  const { data, error, isPending, refetch } = useClientRead({
    client,
    enabled,
    request: args === null ? null : { ref, call: { abi: abi.entries, functionName, args } }
  });
  return { data: data?.value, hasResult: data !== null, error, isPending, refetch };
}

// src/data/useContractState.ts
function useContractState(params) {
  const { request, client: override, enabled = true } = params;
  const client = useContractStateClient(override);
  const { data, ...rest } = useClientRead({
    client,
    request,
    enabled
  });
  return { result: data, ...rest };
}

// src/data/useTransactionInsight.ts
function useTransactionInsight(params) {
  const { request, client: override, enabled = true } = params;
  const client = useTransactionInsightClient(override);
  const { data, ...rest } = useClientRead({ client, request, enabled });
  return { insight: data, ...rest };
}
function useAbiStorage(identity) {
  const key = JSON.stringify(identity ?? {});
  return React50.useMemo(() => new ScopedStorage(identity ?? {}), [key]);
}
function useAbiOptions(identity, initial) {
  const storage = useAbiStorage(identity);
  const initialRef = React50.useRef(initial);
  initialRef.current = initial;
  const load = React50.useCallback(
    () => ({
      ...DEFAULT_ABI_FETCH_OPTIONS,
      ...initialRef.current,
      ...storage.get(ABI_OPTIONS_NAME, {})
    }),
    [storage]
  );
  const [options, setState] = React50.useState(load);
  React50.useEffect(() => {
    setState(load());
  }, [load]);
  const setOptions = React50.useCallback(
    (next) => {
      setState((prev) => {
        const merged = { ...prev, ...next };
        storage.set(ABI_OPTIONS_NAME, merged);
        return merged;
      });
    },
    [storage]
  );
  const reset = React50.useCallback(() => {
    storage.remove(ABI_OPTIONS_NAME);
    setState({ ...DEFAULT_ABI_FETCH_OPTIONS, ...initialRef.current });
  }, [storage]);
  return { options, setOptions, reset };
}
var CHANGE_EVENT = "authe:contract-explorer-storage";
function useAbiCollection(storage, name, empty, cap) {
  const subscribe = React50.useCallback((onChange) => {
    const keys = new Set(STORAGE_LEVELS.map((level) => storage.keyAt(name, level)));
    const changed = (event) => {
      const key = event.type === "storage" ? event.key : event.detail;
      if (key === null || keys.has(key)) onChange();
    };
    window.addEventListener("storage", changed);
    window.addEventListener(CHANGE_EVENT, changed);
    return () => {
      window.removeEventListener("storage", changed);
      window.removeEventListener(CHANGE_EVENT, changed);
    };
  }, [storage, name]);
  return usePersistedCollection({
    read: () => storage.get(name, empty),
    write: (next) => {
      const saved = storage.set(name, next);
      if (saved) window.dispatchEvent(new CustomEvent(CHANGE_EVENT, {
        detail: storage.keyAt(name, storage.defaultLevel)
      }));
      return saved;
    },
    empty,
    cap,
    resetKey: storage,
    readBeforeCommit: true,
    subscribe
  });
}

// src/data/useInlineAbis.ts
var NO_OVERRIDES = {};
function useInlineAbis(identity) {
  const storage = useAbiStorage(identity);
  const [all, commit, clearAll] = useAbiCollection(storage, INLINE_ABIS_NAME, NO_OVERRIDES);
  const get = React50.useCallback(
    (ref) => all[(0, contract_exports.addressKey)(ref)]?.entries ?? null,
    [all]
  );
  const set = React50.useCallback(
    (ref, entries, meta) => {
      commit((prev) => ({ ...prev, [(0, contract_exports.addressKey)(ref)]: { entries, meta, updatedAt: Date.now() } }));
    },
    [commit]
  );
  const remove = React50.useCallback(
    (ref) => {
      commit((prev) => {
        const key = (0, contract_exports.addressKey)(ref);
        if (!(key in prev)) return prev;
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    [commit]
  );
  const getMeta = React50.useCallback((ref) => all[(0, contract_exports.addressKey)(ref)]?.meta, [all]);
  return { all, get, getMeta, set, remove, clearAll };
}
function toRecord(p) {
  return {
    id: p.id,
    label: p.label,
    chainId: p.ref.chainId,
    address: p.ref.address,
    block: (0, contract_exports.blockToJson)(p.ref.block),
    entries: p.entries,
    meta: p.meta
  };
}
function fromRecord(r) {
  return {
    id: r.id,
    label: r.label,
    ref: { chainId: r.chainId, address: r.address, block: (0, contract_exports.blockFromJson)(r.block), label: r.label },
    entries: r.entries,
    meta: r.meta
  };
}
var NO_PINS = [];
function usePinnedAbis(identity) {
  const storage = useAbiStorage(identity);
  const [records, commit, clear] = useAbiCollection(
    storage,
    PINNED_ABIS_NAME,
    NO_PINS,
    { limit: PINNED_ABIS_LIMIT, keep: "last" }
  );
  const pin = React50.useCallback(
    (entry) => {
      const id2 = entry.id ?? `${entry.ref.chainId}:${entry.ref.address}:${Date.now()}`;
      const record2 = toRecord({ ...entry, id: id2 });
      commit((prev) => [...prev.filter((r) => r.id !== id2), record2]);
      return id2;
    },
    [commit]
  );
  const unpin = React50.useCallback(
    (id2) => commit((prev) => prev.filter((r) => r.id !== id2)),
    [commit]
  );
  const isPinned = React50.useCallback(
    (chainId, address) => records.some((r) => r.chainId.toLowerCase() === chainId.toLowerCase() && r.address.toLowerCase() === address.toLowerCase()),
    [records]
  );
  const pinned = React50.useMemo(() => records.map(fromRecord), [records]);
  return { pinned, pin, unpin, clear, isPinned };
}
var log3 = createLogger(`${CONTRACT_EXPLORER_PACKAGE}:resolver`);
function useAbiResolver(params) {
  const { ref, provider, identity, inlineEntries, inlineMeta, enabled = true } = params;
  const options = params.options ?? DEFAULT_ABI_FETCH_OPTIONS;
  const cache = params.cache ?? sharedAbiCache;
  const [resolved, setResolved] = React50.useState(null);
  const [status, setStatus] = React50.useState(enabled && ref ? "loading" : "idle");
  const [error, setError] = React50.useState(null);
  const [nonce, setNonce] = React50.useState(0);
  const refKey = ref ? (0, contract_exports.contractRefKey)(ref) : "";
  const optsKey = optionsKey(options);
  const idKey = identityKey(identity);
  const inlineKey = inlineEntries && inlineEntries.length > 0 ? JSON.stringify([inlineEntries, inlineMeta]) : "";
  if (useDepsChanged([refKey, optsKey, idKey, inlineKey, enabled])) {
    setResolved(null);
    setError(null);
    setStatus(enabled && ref ? "loading" : "idle");
  }
  React50.useEffect(() => {
    if (!enabled || !ref) {
      setResolved(null);
      setStatus("idle");
      setError(null);
      return void 0;
    }
    if (inlineEntries && inlineEntries.length > 0) {
      setResolved({
        ref,
        abi: (0, abi_exports.normalizeAbi)(inlineEntries),
        meta: { ...inlineMeta, chainId: ref.chainId, address: ref.address, source: inlineMeta?.source ?? "inline" },
        origin: "inline"
      });
      setStatus("ready");
      setError(null);
      return void 0;
    }
    const key = abiCacheKey(ref, options, identity);
    const cached = cache.get(key);
    if (cached) {
      setResolved({
        ref,
        abi: cached.abi,
        meta: cached.meta,
        origin: cached.origin || "cache",
        isGuess: cached.isGuess,
        failures: cached.failures,
        bytecodeFacts: cached.bytecodeFacts
      });
      setStatus("ready");
      setError(null);
      return void 0;
    }
    if (!provider) {
      setResolved(null);
      setStatus("empty");
      return void 0;
    }
    const controller = new AbortController();
    setStatus("loading");
    setError(null);
    provider.fetch(ref, options, { identity, signal: controller.signal }).then((result) => {
      if (controller.signal.aborted) return;
      if (!result || result.abi.entries.length === 0) {
        setResolved(null);
        setStatus("empty");
        return;
      }
      cache.set(key, result);
      setResolved({
        ref,
        abi: result.abi,
        meta: result.meta,
        origin: result.origin,
        isGuess: result.isGuess,
        failures: result.failures,
        bytecodeFacts: result.bytecodeFacts
      });
      setStatus("ready");
    }).catch((err) => {
      if (controller.signal.aborted) return;
      log3.warn("abi resolve failed", { ref: refKey, error: String(err) });
      setError(err instanceof Error ? err : new Error(String(err)));
      setStatus("error");
    });
    return () => controller.abort();
  }, [enabled, refKey, optsKey, idKey, inlineKey, provider, cache, nonce]);
  const refetch = React50.useCallback(() => {
    if (ref) cache.invalidateRef(ref);
    setStatus(enabled && ref ? "loading" : "idle");
    setError(null);
    setNonce((n) => n + 1);
  }, [cache, ref, enabled]);
  return { resolved, status, error, origin: resolved?.origin ?? null, refetch };
}
var log4 = createLogger(`${CONTRACT_EXPLORER_PACKAGE}:abi-sources`);
function useAbiSources(ref, client, opts = true) {
  const { enabled = true, block = null } = typeof opts === "boolean" ? { enabled: opts } : opts;
  const [sources, setSources] = React50.useState([]);
  const [isLoading, setIsLoading] = React50.useState(Boolean(enabled && client && ref));
  const [error, setError] = React50.useState(null);
  const [nonce, setNonce] = React50.useState(0);
  const blockKey7 = block == null ? "" : block.toString();
  const key = ref ? `${ref.chainId.toLowerCase()}:${ref.address.toLowerCase()}@${blockKey7}` : "";
  if (useDepsChanged([key, enabled])) {
    setSources([]);
    setError(null);
    setIsLoading(Boolean(enabled && client && ref));
  }
  React50.useEffect(() => {
    if (!enabled || !client || !ref) {
      setSources([]);
      setError(null);
      setIsLoading(false);
      return void 0;
    }
    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    client(ref, { includeEntries: true, block, signal: controller.signal }).then((res) => {
      if (controller.signal.aborted) return;
      setSources(res.sources);
      setIsLoading(false);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      log4.warn("abi sources fetch failed", { ref: key, error: String(err) });
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsLoading(false);
    });
    return () => controller.abort();
  }, [enabled, client, key, nonce]);
  const refetch = React50.useCallback(() => {
    setIsLoading(Boolean(enabled && client && ref));
    setError(null);
    setNonce((n) => n + 1);
  }, [enabled, client, ref]);
  return { sources, isLoading, error, refetch };
}
var log5 = createLogger(`${CONTRACT_EXPLORER_PACKAGE}:proxy`);
function useProxyChain(ref, opts = {}) {
  const { discover = true, enabled = true, maxHops, resolver } = opts;
  const reader = useContractReader(opts.reader);
  const [chain, setChain] = React50.useState(null);
  const [isResolving, setIsResolving] = React50.useState(Boolean(enabled && ref && discover));
  const [error, setError] = React50.useState(null);
  const [nonce, setNonce] = React50.useState(0);
  const refKey = ref ? (0, contract_exports.contractRefKey)(ref) : "";
  if (useDepsChanged([refKey, enabled, discover, maxHops])) {
    setChain(null);
    setError(null);
    setIsResolving(Boolean(enabled && ref && discover));
  }
  React50.useEffect(() => {
    if (!enabled || !ref) {
      setChain(null);
      setError(null);
      setIsResolving(false);
      return void 0;
    }
    if (!discover) {
      setChain((0, contract_exports.directChain)(ref.address));
      setIsResolving(false);
      return void 0;
    }
    let cancelled = false;
    let abort;
    setIsResolving(true);
    setError(null);
    let work;
    if (resolver) {
      const controller = new AbortController();
      abort = () => controller.abort();
      work = resolver(ref, { signal: controller.signal });
    } else {
      const deps = (0, contract_exports.readerProxyDeps)(reader, ref, maxHops);
      if (!deps) {
        setChain((0, contract_exports.directChain)(ref.address));
        setIsResolving(false);
        return void 0;
      }
      work = (0, contract_exports.resolveProxyChain)(ref.address, deps);
    }
    work.then((result) => {
      if (!cancelled) {
        setChain(result);
        setIsResolving(false);
      }
    }).catch((err) => {
      if (cancelled) return;
      log5.warn("proxy resolve failed", { ref: refKey, error: String(err) });
      setChain(null);
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsResolving(false);
    });
    return () => {
      cancelled = true;
      abort?.();
    };
  }, [enabled, discover, reader, resolver, refKey, maxHops, nonce]);
  const refetch = React50.useCallback(() => {
    setIsResolving(Boolean(enabled && ref && discover));
    setError(null);
    setNonce((n) => n + 1);
  }, [enabled, ref, discover]);
  return { chain, isResolving, error, refetch };
}

// src/proxy/detect.ts
var KNOWN_PROXY_SLOTS = [
  {
    id: "eip1967.impl",
    label: "Implementation",
    slot: contract_exports.EIP1967_IMPL_SLOT,
    decode: "address",
    description: "EIP-1967 implementation slot (transparent / UUPS)."
  },
  {
    id: "eip1967.beacon",
    label: "Beacon",
    slot: contract_exports.EIP1967_BEACON_SLOT,
    decode: "address",
    description: "EIP-1967 beacon slot \u2014 beacon returns the implementation."
  },
  {
    id: "eip1967.admin",
    label: "Admin",
    slot: contract_exports.EIP1967_ADMIN_SLOT,
    decode: "address",
    description: "EIP-1967 admin slot (present on transparent proxies)."
  },
  {
    id: "oz.legacy.impl",
    label: "Legacy implementation",
    slot: contract_exports.OZ_LEGACY_IMPL_SLOT,
    decode: "address",
    description: "Legacy OpenZeppelin (zeppelinos) implementation slot."
  }
];
var UUPS_ABI = [
  { type: "function", name: "proxiableUUID", stateMutability: "view", inputs: [], outputs: [{ type: "bytes32" }] }
];
var DIAMOND_LOUPE_ABI = [
  { type: "function", name: "facetAddresses", stateMutability: "view", inputs: [], outputs: [{ type: "address[]" }] },
  {
    type: "function",
    name: "facets",
    stateMutability: "view",
    inputs: [],
    outputs: [
      {
        type: "tuple[]",
        components: [
          { name: "facetAddress", type: "address" },
          { name: "functionSelectors", type: "bytes4[]" }
        ]
      }
    ]
  }
];
function classifyProxyKind(signals) {
  if (signals.supportsDiamondLoupe) return contract_exports.ProxyKind.DIAMOND;
  if (signals.hasImplSlot) {
    if (signals.hasAdminSlot) return contract_exports.ProxyKind.TRANSPARENT;
    if (signals.supportsProxiableUUID) return contract_exports.ProxyKind.UUPS;
    return contract_exports.ProxyKind.EIP1967;
  }
  if (signals.hasBeaconSlot) return contract_exports.ProxyKind.BEACON;
  if (signals.hasLegacySlot) return contract_exports.ProxyKind.CUSTOM;
  return contract_exports.ProxyKind.UNKNOWN;
}

// src/model/readPolicy.ts
function unavailableReadReason(fn, isProxy = false) {
  const inferred = inferredCallProblem(fn);
  if (inferred) return inferred;
  if (isProxy && fn.name === "proxiableUUID" && fn.inputs.length === 0) {
    return "proxiableUUID is an implementation-only UUPS check. It intentionally reverts when called through a proxy. Open the implementation to read it directly.";
  }
  return void 0;
}
function completeKnownRead(fn) {
  return fn.name === "proxiableUUID" && fn.inputs.length === 0 && fn.outputs.length === 0 ? UUPS_ABI[0] : fn;
}

// src/data/useContractFields.ts
function useContractFields(ref, abi, opts = {}) {
  const reader = useContractReader(opts.reader);
  const noArgReads = React50.useMemo(() => abi?.reads.filter((fn) => (fn.inputs ?? []).length === 0 && !unavailableReadReason(fn, opts.isProxy)).map(completeKnownRead) ?? [], [abi, opts.isProxy]);
  const needArguments = React50.useMemo(() => abi?.reads.filter((fn) => (fn.inputs ?? []).length > 0 && !unavailableReadReason(fn, opts.isProxy)) ?? [], [abi, opts.isProxy]);
  const client = React50.useMemo(() => ({
    read(request, options) {
      return readContractFunctions(reader, request.ref, request.calls, options);
    }
  }), [reader]);
  const { data, error, isPending, refetch } = useClientRead({
    client,
    enabled: opts.enabled ?? true,
    request: ref && abi && noArgReads.length > 0 ? { ref, calls: noArgReads.map((fn) => ({
      abi: [fn, ...abi.errors],
      functionName: fn.name,
      args: []
    })) } : null
  });
  const fields = React50.useMemo(() => noArgReads.map((fn, i) => ({ fn, outcome: data?.[i] })), [noArgReads, data]);
  return { fields, needArguments, error, isPending, refetch };
}
var ERC165_ABI = parseAbi([
  "function supportsInterface(bytes4 interfaceId) view returns (bool)"
]);
var ERC721_INTERFACE_ID = "0x80ac58cd";
var ERC1155_INTERFACE_ID = "0xd9b67a26";
var EMPTY = { erc721: false, erc1155: false, resolved: false };
function declared(outcome) {
  return outcome?.status === "success" && outcome.data === true;
}
function useNftStandard(ref, opts = {}) {
  const enabled = opts.enabled ?? true;
  const reader = useContractReader(opts.reader);
  const [standard, setStandard] = React50.useState(EMPTY);
  const key = ref ? (0, contract_exports.contractRefKey)({ chainId: ref.chainId, address: ref.address }) : "";
  React50.useEffect(() => {
    if (!enabled || !ref) {
      setStandard(EMPTY);
      return void 0;
    }
    let cancelled = false;
    setStandard(EMPTY);
    reader.readMany({ chainId: ref.chainId, address: ref.address }, [
      { abi: ERC165_ABI, functionName: "supportsInterface", args: [ERC721_INTERFACE_ID] },
      { abi: ERC165_ABI, functionName: "supportsInterface", args: [ERC1155_INTERFACE_ID] }
    ]).then((outcomes) => {
      if (cancelled) return;
      const [erc721, erc1155] = outcomes;
      setStandard({
        erc721: declared(erc721),
        erc1155: declared(erc1155),
        resolved: true
      });
    }).catch(() => {
      if (!cancelled) setStandard({ ...EMPTY, resolved: true });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, key, reader]);
  return standard;
}
var IDENTITY_ABI = parseAbi([
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function decimals() view returns (uint8)"
]);
var EMPTY2 = { name: null, symbol: null, decimals: null, resolved: false };
function asText(value) {
  if (typeof value !== "string") return null;
  const cleaned = value.replaceAll("\0", "").trim();
  return cleaned.length > 0 ? cleaned : null;
}
function useTokenIdentity(ref, opts = {}) {
  const enabled = opts.enabled ?? true;
  const reader = useContractReader(opts.reader);
  const [identity, setIdentity] = React50.useState(EMPTY2);
  const key = ref ? (0, contract_exports.contractRefKey)({ chainId: ref.chainId, address: ref.address }) : "";
  React50.useEffect(() => {
    if (!enabled || !ref) {
      setIdentity(EMPTY2);
      return void 0;
    }
    let cancelled = false;
    setIdentity(EMPTY2);
    reader.readMany({ chainId: ref.chainId, address: ref.address }, [
      { abi: IDENTITY_ABI, functionName: "name", args: [] },
      { abi: IDENTITY_ABI, functionName: "symbol", args: [] },
      { abi: IDENTITY_ABI, functionName: "decimals", args: [] }
    ]).then((outcomes) => {
      if (cancelled) return;
      const [name, symbol, decimals] = outcomes;
      setIdentity({
        name: name?.status === "success" ? asText(name.data) : null,
        symbol: symbol?.status === "success" ? asText(symbol.data) : null,
        decimals: decimals?.status === "success" && typeof decimals.data === "number" ? decimals.data : null,
        resolved: true
      });
    }).catch(() => {
      if (!cancelled) setIdentity({ ...EMPTY2, resolved: true });
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, key, reader]);
  return identity;
}

// src/data/eventLogCache.ts
function boundKey(value) {
  if (value == null) return "*";
  return typeof value === "bigint" ? `#${value.toString()}` : value;
}
var readerIds = /* @__PURE__ */ new WeakMap();
var nextReaderId = 0;
function readerKey(reader) {
  if (typeof reader === "string") return reader;
  let id2 = readerIds.get(reader);
  if (id2 === void 0) {
    id2 = ++nextReaderId;
    readerIds.set(reader, id2);
  }
  return `${reader.id}@${id2}`;
}
function eventLogCacheKey(reader, ref, query = {}) {
  const lookback = query.fromBlock == null && query.lookback != null ? `~${query.lookback}` : "";
  const range = `${boundKey(query.fromBlock)}${lookback}..${boundKey(query.toBlock)}`;
  const topics = query.topics && query.topics.length ? query.topics.join(",") : "*";
  return `${readerKey(reader)}::${(0, contract_exports.contractRefKey)(ref)}::${range}::${topics}`;
}
var EventLogCache = class {
  constructor(max = EVENT_LOG_CACHE_MAX_ENTRIES, ttlMs = EVENT_LOG_CACHE_TTL_MS) {
    this.max = max;
    this.ttlMs = ttlMs;
  }
  max;
  ttlMs;
  map = /* @__PURE__ */ new Map();
  get(key) {
    const entry = this.map.get(key);
    if (!entry) return void 0;
    if (Date.now() - entry.at > this.ttlMs) {
      this.map.delete(key);
      return void 0;
    }
    this.map.delete(key);
    this.map.set(key, entry);
    return entry.logs;
  }
  set(key, logs) {
    this.map.delete(key);
    this.map.set(key, { logs, at: Date.now() });
    while (this.map.size > this.max) {
      const oldest = this.map.keys().next().value;
      if (oldest === void 0) break;
      this.map.delete(oldest);
    }
  }
  /** Drop every cached range for a contract ref (all reader/range/topic variants). */
  invalidateRef(ref) {
    const needle = (0, contract_exports.contractRefKey)(ref);
    for (const key of [...this.map.keys()]) {
      if (key.includes(needle)) this.map.delete(key);
    }
  }
  clear() {
    this.map.clear();
  }
};
var sharedEventLogCache = new EventLogCache();

// src/data/useEventLogs.ts
var EMPTY_ABI = (0, abi_exports.normalizeAbi)([]);
function queryKey(query) {
  if (!query) return "";
  const from = query.fromBlock == null ? query.lookback == null ? "" : `~${query.lookback}` : query.fromBlock.toString();
  const to = query.toBlock == null ? "" : query.toBlock.toString();
  return `${from}..${to}::${(query.topics ?? []).join(",")}`;
}
function useEventLogs(ref, abi, opts = {}) {
  const enabled = opts.enabled ?? true;
  const reader = useContractReader(opts.reader);
  const cache = opts.cache ?? sharedEventLogCache;
  const supported = typeof reader.getLogs === "function";
  const [raw, setRaw] = React50.useState([]);
  const [isPending, setIsPending] = React50.useState(false);
  const [error, setError] = React50.useState(null);
  const [fromCache, setFromCache] = React50.useState(false);
  const [answered, setAnswered] = React50.useState(null);
  const [nonce, setNonce] = React50.useState(0);
  const lastNonce = React50.useRef(0);
  const ready = enabled && supported && Boolean(ref);
  const refKey = ref ? (0, contract_exports.contractRefKey)(ref) : "";
  const qKey = queryKey(opts.query);
  if (useDepsChanged([reader, cache, refKey, qKey, ready])) {
    setRaw([]);
    setError(null);
    setFromCache(false);
    setIsPending(ready);
  }
  React50.useEffect(() => {
    if (!ready || !ref || !reader.getLogs) {
      setRaw([]);
      setIsPending(false);
      setError(null);
      setFromCache(false);
      return void 0;
    }
    const key = eventLogCacheKey(reader, ref, opts.query);
    const force = nonce !== lastNonce.current;
    lastNonce.current = nonce;
    const cached = force ? void 0 : cache.get(key);
    if (cached) {
      setRaw(cached);
      setIsPending(false);
      setError(null);
      setFromCache(true);
      setAnswered(`${refKey}|${qKey}`);
      return void 0;
    }
    const controller = new AbortController();
    setIsPending(true);
    setError(null);
    setFromCache(false);
    reader.getLogs(ref, opts.query, { signal: controller.signal }).then((logs2) => {
      if (controller.signal.aborted) return;
      cache.set(key, logs2);
      setRaw(logs2);
      setIsPending(false);
      setAnswered(`${refKey}|${qKey}`);
    }).catch((err) => {
      if (controller.signal.aborted) return;
      setRaw([]);
      setError(err instanceof Error ? err : new Error(String(err)));
      setIsPending(false);
    });
    return () => controller.abort();
  }, [reader, cache, ready, refKey, qKey, nonce]);
  const logs = React50.useMemo(() => {
    if (raw.length === 0) return [];
    return (0, abi_exports.decodeLogs)(raw, abi ?? EMPTY_ABI).sort((a, b) => {
      const block = Number(b.blockNumber ?? 0n) - Number(a.blockNumber ?? 0n);
      return block !== 0 ? block : (b.logIndex ?? 0) - (a.logIndex ?? 0);
    });
  }, [raw, abi]);
  const summary = React50.useMemo(() => (0, abi_exports.summarizeLogs)(logs), [logs]);
  const refetch = React50.useCallback(() => setNonce((n) => n + 1), []);
  const settled = ready && !isPending && error === null && answered === `${refKey}|${qKey}`;
  return { logs, summary, isPending, error, supported, fromCache, settled, refetch };
}
function functionFormKey(fn) {
  return JSON.stringify([fn.name, fn.stateMutability, fn.inputs]);
}
function parameterKeys(inputs) {
  const names = inputs.map((param) => param.name || "");
  const reserved = new Set(names.filter(Boolean));
  return names.map((name, i) => {
    if (name && names.indexOf(name) === names.lastIndexOf(name)) return name;
    const base = `arg${i}`;
    let key = base;
    let suffix = 1;
    while (reserved.has(key)) key = `${base}_${suffix++}`;
    reserved.add(key);
    return key;
  });
}
var PROXY_KIND_LABELS = {
  [contract_exports.ProxyKind.EIP1967]: "EIP-1967",
  [contract_exports.ProxyKind.UUPS]: "UUPS",
  [contract_exports.ProxyKind.BEACON]: "Beacon",
  [contract_exports.ProxyKind.TRANSPARENT]: "Transparent",
  [contract_exports.ProxyKind.DIAMOND]: "Diamond",
  [contract_exports.ProxyKind.CUSTOM]: "Custom",
  [contract_exports.ProxyKind.UNKNOWN]: "Unknown"
};
function proxyKindLabel(kind) {
  return PROXY_KIND_LABELS[kind] ?? PROXY_KIND_LABELS[contract_exports.ProxyKind.UNKNOWN];
}
var ABI_SOURCE_LABELS = {
  [AbiSourceKind.INLINE]: "Inline",
  [AbiSourceKind.CACHE]: "Cache",
  [AbiSourceKind.CATALOG]: "Catalog",
  [AbiSourceKind.DEFAULTS]: "Defaults",
  [AbiSourceKind.EXTERNAL]: "External"
};
function abiSourceLabel(source) {
  return ABI_SOURCE_LABELS[source] ?? source;
}
var ABI_ORIGIN_LABELS = {
  bytecode_analysis: "read from bytecode",
  effect_inference: "described from the transaction's effects",
  evmole: "read from bytecode (EVMole)",
  whatsabi: "inferred (WhatsABI)",
  third_party_inferred: "guessed from bytecode by a third party",
  guess_abi: "guessed",
  openchain: "4-byte signatures (OpenChain)",
  "4byte": "4-byte signatures",
  etherscan: "Etherscan",
  blockscout: "Blockscout",
  sourcify: "Sourcify",
  codeslaw: "Codeslaw",
  third_party: "third-party explorer",
  provided: "uploaded",
  bundled: "bundled",
  default: "system default",
  customer_overlay: "workspace override",
  unknown: "unrecorded"
};
var INFERRED_ABI_ORIGINS = /* @__PURE__ */ new Set([
  "bytecode_analysis",
  "effect_inference",
  "evmole",
  "whatsabi",
  "third_party_inferred",
  "guess_abi",
  "openchain",
  "4byte"
]);
function abiOriginLabel(source) {
  if (!source) return null;
  const key = source.trim().toLowerCase();
  return key ? ABI_ORIGIN_LABELS[key] ?? source : null;
}
function isInferredAbiOrigin(source) {
  return INFERRED_ABI_ORIGINS.has((source ?? "").trim().toLowerCase());
}
function formatReadValue(value) {
  if (value === null || value === void 0) return "\u2014";
  if (typeof value === "bigint") return value.toString();
  if (typeof value === "boolean") return value ? "true" : "false";
  if (Array.isArray(value)) return `[${value.map(formatReadValue).join(", ")}]`;
  if (typeof value === "object") {
    return JSON.stringify(value, (_k, v) => typeof v === "bigint" ? v.toString() : v, 2);
  }
  return String(value);
}
function coerceArg(param, raw) {
  param = normalizeAbiParameter(param);
  const type = param.type;
  const label = param.name && param.name.length > 0 ? `${param.name} (${type})` : type;
  const trimmed = raw.trim();
  if (type !== "string" && trimmed === "") {
    throw new Error(`${label} is required.`);
  }
  if (type.endsWith("]") || type.startsWith("tuple")) {
    const parsed = JSON.parse(trimmed, (_key, value) => {
      if (typeof value === "number" && !Number.isSafeInteger(value)) {
        throw new Error(`${label}: quote large integers in JSON to preserve their exact value.`);
      }
      return value;
    });
    return coerceComposite(param, parsed, label);
  }
  if (type === "address" && !isAddress(trimmed)) {
    throw new Error(`${label} must be a valid 20-byte address with a valid checksum when mixed case is used.`);
  }
  const integer = /^(u?int)(\d*)$/.exec(type);
  if (integer) {
    let value;
    try {
      value = BigInt(trimmed);
    } catch {
      throw new Error(`${label} must be a whole number.`);
    }
    const bits = Number(integer[2] || 256);
    if (bits < 8 || bits > 256 || bits % 8 !== 0) throw new Error(`${label} has an invalid integer width.`);
    const signed = integer[1] === "int";
    const upper = 1n << BigInt(signed ? bits - 1 : bits);
    const minimum = signed ? -upper : 0n;
    const maximum = upper - 1n;
    if (value < minimum || value > maximum) throw new Error(`${label} must be between ${minimum} and ${maximum}.`);
    return value;
  }
  const bytes = /^bytes(\d*)$/.exec(type);
  if (bytes) {
    if (!/^0x(?:[0-9a-fA-F]{2})*$/.test(trimmed)) {
      throw new Error(`${label} must contain complete hexadecimal bytes prefixed with 0x (for example, 0x1234).`);
    }
    if (bytes[1] && trimmed.length !== 2 + Number(bytes[1]) * 2) {
      throw new Error(`${label} requires exactly ${bytes[1]} bytes.`);
    }
    return trimmed;
  }
  if (type === "bool") {
    const lowered = trimmed.toLowerCase();
    if (lowered === "true" || lowered === "1") return true;
    if (lowered === "false" || lowered === "0") return false;
    throw new Error(`${label} must be true or false.`);
  }
  return type === "string" ? raw : trimmed;
}
function coerceComposite(param, value, label) {
  const array = /^(.*)\[(\d*)\]$/.exec(param.type);
  if (array) {
    if (!Array.isArray(value)) throw new Error(`${label} must be a JSON array.`);
    if (array[2] && value.length !== Number(array[2])) {
      throw new Error(`${label} requires exactly ${array[2]} items.`);
    }
    const item = { ...param, type: array[1] };
    return value.map((entry, i) => coerceComposite(item, entry, `${label}[${i}]`));
  }
  if (param.type === "tuple" && "components" in param) {
    const components2 = param.components;
    if (Array.isArray(value)) {
      if (value.length !== components2.length) throw new Error(`${label} requires ${components2.length} tuple items.`);
      return components2.map((component, i) => coerceComposite(component, value[i], `${label}[${i}]`));
    }
    if (!value || typeof value !== "object" || components2.some((component) => !component.name)) {
      throw new Error(`${label} must be a JSON array or an object with named tuple fields.`);
    }
    if (new Set(components2.map((component) => component.name)).size !== components2.length) {
      throw new Error(`${label} has duplicate tuple field names; use a positional JSON array.`);
    }
    const record2 = value;
    if (Object.keys(record2).some((key) => !components2.some((component) => component.name === key))) {
      throw new Error(`${label} contains an unknown tuple field.`);
    }
    return Object.fromEntries(components2.map((component) => [
      component.name,
      coerceComposite(component, Object.prototype.hasOwnProperty.call(record2, component.name) ? record2[component.name] : void 0, `${label}.${component.name}`)
    ]));
  }
  if (value === null || value === void 0 || typeof value === "object") throw new Error(`${label} is required and must be a scalar value.`);
  if (param.type === "string" && typeof value !== "string") throw new Error(`${label} must be a string.`);
  return coerceArg({ ...param, name: label }, String(value));
}
function buildArgs(inputs, values) {
  if (!inputs || inputs.length === 0) return [];
  const keys = parameterKeys(inputs);
  return inputs.map((param, i) => {
    const key = keys[i];
    return coerceArg(param, Object.prototype.hasOwnProperty.call(values, key) ? values[key] : "");
  });
}
var OMITTED_READS_NAMED = 4;
function omittedReadsNote(needArguments) {
  if (needArguments.length === 0) return null;
  const named = needArguments.slice(0, OMITTED_READS_NAMED).map(functionSignature).join(", ");
  const more = needArguments.length > OMITTED_READS_NAMED ? ", \u2026" : "";
  const plural2 = needArguments.length === 1;
  return `${needArguments.length} more read ${plural2 ? "function takes" : "functions take"} arguments (${named}${more}) \u2014 call ${plural2 ? "it" : "them"} on the Read tab.`;
}
function FnRow({ fn }) {
  let selector = "";
  try {
    selector = (0, abi_exports.functionSelector)(fn);
  } catch {
    selector = "";
  }
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3 border-b py-1.5 last:border-0", children: [
    /* @__PURE__ */ jsx("code", { className: "truncate font-mono text-xs", children: (0, abi_exports.functionSignature)(fn) }),
    /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-2", children: [
      /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "font-mono text-3xs", children: fn.stateMutability }),
      selector && /* @__PURE__ */ jsx("code", { className: "text-3xs text-muted-foreground", children: selector })
    ] })
  ] });
}
function EventRow({ ev }) {
  let topic = "";
  try {
    topic = (0, abi_exports.eventTopic)(ev);
  } catch {
    topic = "";
  }
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3 border-b py-1.5 last:border-0", children: [
    /* @__PURE__ */ jsx("code", { className: "truncate font-mono text-xs", children: (0, abi_exports.eventSignature)(ev) }),
    topic && /* @__PURE__ */ jsx("code", { className: "shrink-0 text-3xs text-muted-foreground", children: shortenHash(topic) })
  ] });
}
function AbiViewer({ abi, className, collapsed = false }) {
  if (!abi || abi.entries.length === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No ABI", description: "Resolve, paste, or pin an ABI to inspect its interface.", size: "sm" });
  }
  const sections = [
    {
      id: "reads",
      label: "Read functions",
      count: abi.reads.length,
      body: abi.reads.map((fn, i) => /* @__PURE__ */ jsx(FnRow, { fn }, `${fn.name}-${i}`))
    },
    {
      id: "writes",
      label: "Write functions",
      count: abi.writes.length,
      body: abi.writes.map((fn, i) => /* @__PURE__ */ jsx(FnRow, { fn }, `${fn.name}-${i}`))
    },
    {
      id: "events",
      label: "Events",
      count: abi.events.length,
      body: abi.events.map((ev, i) => /* @__PURE__ */ jsx(EventRow, { ev }, `${ev.name}-${i}`))
    },
    {
      id: "errors",
      label: "Errors",
      count: abi.errors.length,
      // The signature, not the bare name: a custom error is what a revert
      // decodes against, and its parameter types are the half that tells you
      // whether the revert you are holding matches this one.
      body: abi.errors.map((err, i) => /* @__PURE__ */ jsx("div", { className: "border-b py-1.5 last:border-0", children: /* @__PURE__ */ jsx("code", { className: "font-mono text-xs", children: (0, abi_exports.errorSignature)(err) }) }, `${err.name}-${i}`))
    }
  ];
  const defaultValue = collapsed ? [] : ["reads", "writes"];
  return /* @__PURE__ */ jsx(Accordion, { type: "multiple", defaultValue, className: cn("w-full", className), children: sections.map((s) => /* @__PURE__ */ jsxs(AccordionItem, { value: s.id, children: [
    /* @__PURE__ */ jsx(AccordionTrigger, { className: "text-sm", children: /* @__PURE__ */ jsxs("span", { className: "flex items-center gap-2", children: [
      s.label,
      /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "text-3xs", children: s.count })
    ] }) }),
    /* @__PURE__ */ jsx(AccordionContent, { children: s.count === 0 ? /* @__PURE__ */ jsx("p", { className: "py-2 text-xs text-muted-foreground", children: "None." }) : /* @__PURE__ */ jsx("div", { className: "px-1", children: s.body }) })
  ] }, s.id)) });
}
var ABI_SCOPE_LABELS = {
  user: "Only me",
  tenant: "Tenant",
  org: "Organization",
  customer: "Entire customer"
};
function abiScopeDescription(scope, shared = true) {
  if (!shared) return scope === "user" ? "Saved only for you. Shared pipeline runs do not use it; personal reprocessing does." : scope === "tenant" ? "Shared with your workspace. Its pipeline runs can decode using this ABI." : "Saved with its existing shared visibility. A more specific ABI can override it.";
  return scope === "user" ? "Saved for you alone. Tenant pipeline runs do not use it; a re-process with body scope \u201CUser \u2014 private to me\u201D does." : scope === "tenant" ? "Shared with this tenant. Its pipeline runs can decode using this ABI." : scope === "org" ? "Shared across this organization's tenants, unless a more specific ABI overrides it." : scope === "customer" ? "Shared across this customer's organizations, unless a more specific ABI overrides it." : "Saved at your current workspace scope.";
}
function AbiSaveScopeField({ options, scope, onChange, disabled }) {
  const shared = useLadderShared();
  if (options.length <= 1) return null;
  return /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-1.5 text-xs text-muted-foreground", children: [
    /* @__PURE__ */ jsx("span", { className: "whitespace-nowrap", children: "Save for" }),
    /* @__PURE__ */ jsx(
      NativeSelect,
      {
        value: scope ?? options[0]?.value,
        onChange: (e) => onChange?.(e.target.value),
        disabled,
        density: "sm",
        "aria-label": "Scope to save the ABI for",
        children: options.map((option) => /* @__PURE__ */ jsx("option", { value: option.value, children: !shared && option.value === "tenant" ? "Workspace" : option.label }, option.value))
      }
    )
  ] });
}
function InlineAbiEditor({
  initialEntries,
  onSave,
  onClear,
  hasOverride = false,
  height = 280,
  onPersist,
  persistLabel = "Save to workspace",
  persistBusy = false,
  name,
  onNameChange,
  scopeOptions,
  scope,
  onScopeChange,
  scopeDescription,
  notice,
  className
}) {
  const seed = React50.useMemo(
    () => initialEntries && initialEntries.length > 0 ? JSON.stringify(initialEntries, null, 2) : "",
    [initialEntries]
  );
  const [draft, setDraft] = React50.useState({ seed, text: seed });
  const text2 = draft.text;
  const setText = (next) => setDraft((current) => ({ ...current, text: next }));
  const { abi, error } = React50.useMemo(() => (0, abi_exports.parseAbiJson)(text2), [text2]);
  const matchesLoaded = Boolean(abi && JSON.stringify(abi.entries) === JSON.stringify(initialEntries));
  if (seed !== draft.seed && (text2 === draft.seed || matchesLoaded)) {
    setDraft({ seed, text: matchesLoaded ? text2 : seed });
  }
  const showError = text2.trim().length > 0 && error != null;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    onPersist && onNameChange && /* @__PURE__ */ jsx(Field, { label: "Name", labelClassName: "text-xs", children: /* @__PURE__ */ jsx(
      Input,
      {
        value: name ?? "",
        onChange: (e) => onNameChange(e.target.value),
        placeholder: "e.g. USDC, MyToken \u2014 labels this ABI in your registry",
        disabled: persistBusy,
        className: "h-8 text-sm"
      }
    ) }),
    /* @__PURE__ */ jsx(JsonEditor, { value: text2, onChange: setText, height, readOnly: persistBusy }),
    seed !== draft.seed && text2 !== draft.seed && /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
      "A newer ABI loaded. Your draft is preserved.",
      " ",
      /* @__PURE__ */ jsx(Button, { size: "sm", variant: "ghost", disabled: persistBusy, onClick: () => setDraft({ seed, text: seed }), children: "Use loaded ABI" })
    ] }),
    showError && /* @__PURE__ */ jsx(Alert, { variant: "destructive", children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: error?.message }) }),
    notice,
    onPersist && scopeDescription && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: scopeDescription }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: abi ? `${abi.reads.length + abi.writes.length} functions \xB7 ${abi.events.length} events \xB7 ${abi.errors.length} errors` : "Paste a JSON ABI array." }),
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
        hasOverride && onClear && /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", disabled: persistBusy, onClick: onClear, children: "Remove override" }),
        /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", disabled: !abi || persistBusy, onClick: () => abi && onSave(abi.entries), children: "Use locally" }),
        onPersist && scopeOptions && /* @__PURE__ */ jsx(AbiSaveScopeField, { options: scopeOptions, scope, onChange: onScopeChange, disabled: persistBusy }),
        onPersist && /* @__PURE__ */ jsx(Button, { size: "sm", disabled: !abi || persistBusy, onClick: async () => {
          if (!abi) return;
          await onPersist(abi.entries);
        }, children: persistBusy ? "Saving\u2026" : persistLabel })
      ] })
    ] })
  ] });
}
var SOURCE_ORDER_HINTS = {
  [AbiSourceKind.CATALOG]: "Your org / tenant / user ABIs.",
  [AbiSourceKind.DEFAULTS]: "Generic ERC interfaces \u2014 a guess, never this contract's own.",
  [AbiSourceKind.EXTERNAL]: "Etherscan-like explorers."
};
var SOURCE_TOGGLE = {
  [AbiSourceKind.CATALOG]: "useCatalog",
  [AbiSourceKind.DEFAULTS]: "useDefaults",
  [AbiSourceKind.EXTERNAL]: "useExternalSources"
};
var TOGGLES = [
  { key: "useCatalog", label: "Catalog ABIs", hint: "Org / tenant / user catalog (identity precedence)." },
  { key: "useDefaults", label: "Default ABIs", hint: "Fallback to ERC-20/721 and well-known interfaces." },
  { key: "useExternalSources", label: "External sources", hint: "Etherscan-like explorers." },
  { key: "useProxies", label: "Follow proxies", hint: "Use the implementation ABI for calls." },
  { key: "discoverProxies", label: "Discover proxies", hint: "Detect proxies via storage-slot reads." }
];
function AbiSourcePanel({
  options,
  onChange,
  onReset,
  origin,
  meta,
  isGuess = false,
  className
}) {
  const order = React50.useMemo(
    () => providerSourceOrder(options.sourcePriority),
    [options.sourcePriority]
  );
  const score = React50.useMemo(() => recordedAbiConfidence(meta), [meta]);
  const scoreNote = React50.useMemo(
    () => score == null ? describeUnscoredAbi(meta) : null,
    [meta, score]
  );
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold", children: "Resolution" }),
      onReset && /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: onReset, children: "Reset" })
    ] }),
    (origin || meta) && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 text-xs text-muted-foreground", children: [
        origin && /* @__PURE__ */ jsx(Badge, { variant: isGuess ? "destructive" : "secondary", children: abiSourceLabel(origin) }),
        meta?.contractName && /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: meta.contractName }),
        meta?.isProxy && /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "proxy" }),
        score != null ? /* @__PURE__ */ jsxs("span", { title: isGuess ? "A fixed floor for the fallback, not a measurement of this contract." : void 0, children: [
          isGuess ? "fallback confidence" : "confidence",
          " ",
          Math.round(score * 100),
          "%"
        ] }) : scoreNote ? /* @__PURE__ */ jsx("span", { title: scoreNote, children: "not scored" }) : null,
        meta?.lifecycle && /* @__PURE__ */ jsxs("span", { children: [
          "\xB7 ",
          meta.lifecycle
        ] })
      ] }),
      (meta?.explanation ?? []).length > 0 && /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-0.5 text-xs text-muted-foreground", children: (meta?.explanation ?? []).map((line, i) => /* @__PURE__ */ jsxs("li", { children: [
        "\u2014 ",
        line
      ] }, `${i}-${line}`)) })
    ] }),
    /* @__PURE__ */ jsx(Separator, {}),
    /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-3", children: TOGGLES.map((row) => /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "space-y-0.5", children: [
        /* @__PURE__ */ jsx(Label, { htmlFor: `abi-opt-${String(row.key)}`, className: "text-sm", children: row.label }),
        /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: row.hint })
      ] }),
      /* @__PURE__ */ jsx(
        Switch,
        {
          id: `abi-opt-${String(row.key)}`,
          checked: Boolean(options[row.key]),
          onCheckedChange: (checked) => onChange({ [row.key]: checked })
        }
      )
    ] }, String(row.key))) }),
    /* @__PURE__ */ jsx(Separator, {}),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "space-y-0.5", children: [
        /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold", children: "Source order" }),
        /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "First match wins. A local edit and the session cache are always checked before any of these." })
      ] }),
      /* @__PURE__ */ jsx("ol", { className: "flex flex-col divide-y rounded-md border", children: order.map((source, i) => {
        const toggle = SOURCE_TOGGLE[source];
        const off = toggle ? !options[toggle] : false;
        const label = abiSourceLabel(source);
        return /* @__PURE__ */ jsxs("li", { className: "flex items-center justify-between gap-3 px-3 py-2", children: [
          /* @__PURE__ */ jsxs("div", { className: "min-w-0 space-y-0.5", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx("span", { className: "font-mono text-2xs tabular-nums text-muted-foreground", children: i + 1 }),
              /* @__PURE__ */ jsx("span", { className: cn("text-sm", off && "text-muted-foreground line-through"), children: label }),
              off && /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-3xs", children: "off" })
            ] }),
            /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: SOURCE_ORDER_HINTS[source] })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-1", children: [
            /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "sm",
                className: "size-7 p-0",
                "aria-label": `Move ${label} earlier`,
                disabled: i === 0,
                onClick: () => onChange({ sourcePriority: moveAbiSource(options.sourcePriority, source, -1) }),
                children: /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: "\u2191" })
              }
            ),
            /* @__PURE__ */ jsx(
              Button,
              {
                variant: "ghost",
                size: "sm",
                className: "size-7 p-0",
                "aria-label": `Move ${label} later`,
                disabled: i === order.length - 1,
                onClick: () => onChange({ sourcePriority: moveAbiSource(options.sourcePriority, source, 1) }),
                children: /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: "\u2193" })
              }
            )
          ] })
        ] }, source);
      }) })
    ] })
  ] });
}
function abiAssessment(meta) {
  const record2 = [];
  const source = abiOriginLabel(meta?.source);
  if (source) record2.push(`ABI source: ${source}.`);
  const rung = scopeRungLabel(meta?.scope);
  if (meta?.isDefault) record2.push("System default: used when no override in your tenancy ladder takes precedence.");
  else if (rung) record2.push(`${meta?.inherited ? "Inherited from" : "Saved at"} ${rung.toLowerCase()} scope.`);
  if (meta?.validFromBlock != null || meta?.validToBlock != null) {
    record2.push(`ABI version: ${formatBlockWindow({ valid_from_block: meta.validFromBlock ?? 0, valid_to_block: meta.validToBlock ?? null })}.`);
  }
  return {
    score: recordedAbiConfidence(meta),
    evidence: meta?.evidence,
    explanation: meta?.explanation,
    lifecycle: meta?.lifecycle,
    confidenceNote: "No confidence assessment was recorded for this ABI.",
    record: record2
  };
}
function windowLabel(entry) {
  if (entry.validFromBlock == null && entry.validToBlock == null) return null;
  return formatBlockWindow({
    valid_from_block: entry.validFromBlock ?? 0,
    valid_to_block: entry.validToBlock ?? null
  });
}
function badgeVariant(entry) {
  if (entry.origin === "catalog") return "outline";
  if (entry.isDefault) return "secondary";
  if (entry.inherited) return "outline";
  return "default";
}
function AbiSourcesPanel({
  sources,
  isLoading = false,
  error = null,
  onRetry,
  onUse,
  onTogglePin,
  onRename,
  activeSourceId,
  pinnedIds,
  block = null,
  listedAddress = null,
  proxyAddress = null,
  className
}) {
  const viaProxy = listedAddress != null && proxyAddress != null && normalizeAddress(listedAddress) !== normalizeAddress(proxyAddress);
  const subject = viaProxy ? `the implementation ${shortenAddress(listedAddress)}` : "this contract";
  if (isLoading) {
    return /* @__PURE__ */ jsx("div", { className: cn("space-y-2", className), children: Array.from({ length: 3 }).map((_, i) => /* @__PURE__ */ jsx(Skeleton, { className: "h-14 w-full" }, i)) });
  }
  if (error) return /* @__PURE__ */ jsx(ErrorState, { error, onRetry });
  if (sources.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: block == null ? `No ABIs registered for ${viaProxy ? "the implementation" : "this contract"}` : `No ABIs live at block ${block.toLocaleString()}`,
        description: block == null ? viaProxy ? `No saved or catalog ABIs for ${subject}, which is what this page reads through. An ABI saved for the proxy ${shortenAddress(proxyAddress)} is listed here only with proxy following turned off, under Options.` : "No saved or catalog ABIs for this contract yet. Paste one in the Edit tab and save it to your workspace." : `${viaProxy ? "The implementation" : "This contract"} has no ABI whose block window covers the pinned block. Clear the block pin to see every version.`,
        size: "sm"
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-1", className), children: [
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: block == null ? `Every ABI version visible to you for ${subject}, newest catalog row included.` : `ABIs live at block ${block.toLocaleString()} for ${subject}.` }),
    /* @__PURE__ */ jsx("ul", { className: "divide-y divide-border", children: sources.map((entry) => {
      const id2 = abiSourceId(entry);
      const isActive = activeSourceId != null && id2 === activeSourceId;
      const isPinned = pinnedIds?.has(id2) ?? false;
      const hasEntries = Boolean(entry.entries && entry.entries.length > 0);
      const entriesMissing = !hasEntries && entry.entriesCount > 0;
      const entriesEmpty = !hasEntries && entry.entriesCount === 0;
      return /* @__PURE__ */ jsx("li", { className: "py-2.5", children: /* @__PURE__ */ jsx(ReadConfidenceBoundary, { ...abiAssessment(abiSourceMeta(entry)), subject: "ABI", children: /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3", children: [
        /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
            /* @__PURE__ */ jsx(AbiName, { entry, onRename }),
            /* @__PURE__ */ jsx(Badge, { variant: badgeVariant(entry), className: "text-3xs", children: abiSourceScopeLabel(entry) })
          ] }),
          /* @__PURE__ */ jsxs("div", { className: "mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground", children: [
            /* @__PURE__ */ jsxs("span", { children: [
              entry.entriesCount,
              " items"
            ] }),
            windowLabel(entry) && /* @__PURE__ */ jsxs("span", { className: "tabular-nums", children: [
              "\xB7 ",
              windowLabel(entry)
            ] }),
            entry.source && /* @__PURE__ */ jsxs("span", { children: [
              "\xB7 ",
              abiOriginLabel(entry.source)
            ] }),
            entry.isProxy && /* @__PURE__ */ jsx("span", { children: "\xB7 proxy" })
          ] }),
          entry.inferred && /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-xs text-muted-foreground", children: "A third party's guess from the contract's bytecode, not a published ABI, and not used as this contract's ABI. Stored before guesses were kept out of the catalog: its entries have no names, and a constant in the code can pass for an event." }),
          !entry.inferred && isInferredAbiOrigin(entry.source) && /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-xs text-muted-foreground", children: "Inferred from the contract's code, not a published ABI: argument types are read from the bytecode and function names are not recoverable from it." }),
          entriesMissing && /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-xs text-destructive", children: "Entries couldn't be loaded, so this ABI can't be used or pinned. Retry, or check the stored blob for this contract." }),
          entriesEmpty && /* @__PURE__ */ jsx("p", { className: "mt-0.5 text-xs text-muted-foreground", children: "This ABI is registered but declares nothing, so there is nothing to use or pin." })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-2", children: [
          onTogglePin && /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: isPinned ? "secondary" : "ghost",
              disabled: !hasEntries,
              onClick: () => hasEntries && onTogglePin(entry),
              children: isPinned ? "Pinned" : "Pin"
            }
          ),
          /* @__PURE__ */ jsx(
            Button,
            {
              size: "sm",
              variant: isActive ? "secondary" : "outline",
              disabled: !hasEntries || !onUse || isActive,
              onClick: () => hasEntries && onUse?.(entry),
              children: isActive ? "In use" : "Use"
            }
          )
        ] })
      ] }) }) }, id2);
    }) })
  ] });
}
function renameableSource(entry) {
  return entry.origin === "override" && !entry.inherited && !entry.isDefault;
}
function AbiName({
  entry,
  onRename
}) {
  const [editing, setEditing] = React50.useState(false);
  const [draft, setDraft] = React50.useState(entry.name ?? "");
  const [saving, setSaving] = React50.useState(false);
  const [error, setError] = React50.useState(null);
  const shown = entry.name || (entry.origin === "catalog" ? "Catalog ABI" : "Unnamed ABI");
  const canRename = Boolean(onRename) && renameableSource(entry);
  if (!editing) {
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("span", { className: cn("truncate text-sm font-medium", entry.name ? "text-foreground" : "text-muted-foreground"), children: shown }),
      canRename ? /* @__PURE__ */ jsx(
        Button,
        {
          type: "button",
          size: "sm",
          variant: "ghost",
          className: "size-6 shrink-0 p-0",
          "aria-label": entry.name ? `Rename ${entry.name}` : "Name this ABI",
          title: entry.name ? "Rename" : "Name this ABI",
          onClick: () => {
            setDraft(entry.name ?? "");
            setError(null);
            setEditing(true);
          },
          children: /* @__PURE__ */ jsx(PencilIcon, { className: "size-3", "aria-hidden": true })
        }
      ) : null
    ] });
  }
  const save = async () => {
    const next = draft.trim() || null;
    if (next === (entry.name || null)) {
      setEditing(false);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onRename(entry, next);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "Couldn't rename this ABI.");
    } finally {
      setSaving(false);
    }
  };
  return /* @__PURE__ */ jsxs(
    "form",
    {
      className: "flex min-w-0 flex-wrap items-center gap-1",
      onSubmit: (e) => {
        e.preventDefault();
        void save();
      },
      children: [
        /* @__PURE__ */ jsx(
          Input,
          {
            autoFocus: true,
            "aria-label": "ABI name",
            value: draft,
            maxLength: 256,
            placeholder: "Name this ABI",
            disabled: saving,
            className: "h-7 w-48 text-sm",
            onChange: (e) => setDraft(e.target.value),
            onKeyDown: (e) => {
              if (e.key === "Escape") {
                e.preventDefault();
                setEditing(false);
              }
            }
          }
        ),
        /* @__PURE__ */ jsx(Button, { type: "submit", size: "sm", className: "h-7", disabled: saving, children: saving ? "Saving\u2026" : "Save" }),
        /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "ghost", className: "h-7", disabled: saving, onClick: () => setEditing(false), children: "Cancel" }),
        error ? /* @__PURE__ */ jsx("span", { role: "alert", className: "basis-full text-xs text-destructive", children: error }) : null
      ]
    }
  );
}
function NoAbiState({ subject, failures, resolving, onFetch, fetching = false, className }) {
  if (resolving) {
    return /* @__PURE__ */ jsxs("p", { role: "status", className: cn("flex items-center gap-2 py-6 text-sm text-muted-foreground", className), children: [
      /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      " Resolving the ABI before listing ",
      subject,
      "\u2026"
    ] });
  }
  const failed = failures && failures.length > 0;
  return /* @__PURE__ */ jsxs("div", { className, children: [
    /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: failed ? "Couldn't load an ABI" : "No ABI resolved",
        description: failed ? `${describeAbiFailures(failures)} So we couldn't list ${subject} for this contract. Retry, or paste an ABI in the Edit tab.` : `An ABI is needed to list ${subject} for this contract. Resolve or paste one in the Edit tab.`,
        size: "sm"
      }
    ),
    onFetch && /* @__PURE__ */ jsx(FetchAbiAction, { onFetch, fetching })
  ] });
}
var GuessFitContext = React50.createContext(null);
function GuessedAbiNotice({
  failures,
  consequence,
  onFetch,
  fetching = false,
  className
}) {
  const fit = React50.useContext(GuessFitContext);
  const lookupFailed = Boolean(failures?.length);
  if (fit && fit.kept === 0) {
    return /* @__PURE__ */ jsx(Alert, { className: cn("border-risk-medium/40 bg-risk-medium/5", className), children: /* @__PURE__ */ jsxs(AlertDescription, { className: "flex flex-col gap-2 text-xs", children: [
      /* @__PURE__ */ jsxs("span", { children: [
        /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: "No standard ERC-20/721 selectors were detected in this contract's code." }),
        " ",
        lookupFailed ? "An ABI lookup failed, so its own interface is still unknown." : "No stored, catalogued or verified ABI was found for it either, so none of its own functions can be listed yet."
      ] }),
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "This scan checks selector constants. Custom dispatch and forwarding can hide functions, so it does not establish whether this contract is a token or an NFT." }),
      onFetch && /* @__PURE__ */ jsx(FetchAbiAction, { onFetch, fetching })
    ] }) });
  }
  return /* @__PURE__ */ jsx(Alert, { className: cn("border-risk-medium/40 bg-risk-medium/5", className), children: /* @__PURE__ */ jsxs(AlertDescription, { className: "flex flex-col gap-2 text-xs", children: [
    /* @__PURE__ */ jsxs("span", { children: [
      /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: "Showing the standard ERC-20/721 interfaces \u2014 not this contract's own ABI." }),
      " ",
      lookupFailed ? "An ABI lookup failed, so we could not establish whether a stored or verified ABI is available." : "No stored, catalogued or verified ABI was found for this address."
    ] }),
    /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
      "Values are read from the real contract using guessed return types; a successful read does not verify those types. What is missing is everything beyond the standard: this contract's own functions, events and errors aren't listed here, and its logs can't be decoded.",
      consequence ? ` ${consequence}` : "",
      fit && fit.dropped > 0 ? ` Only entries with matching constants are listed \u2014 ${fit.dropped} without matches were left out of this guess.` : ""
    ] }),
    onFetch && /* @__PURE__ */ jsx(FetchAbiAction, { onFetch, fetching })
  ] }) });
}
function FetchAbiAction({ onFetch, fetching }) {
  return /* @__PURE__ */ jsxs("span", { className: "flex flex-col gap-1", children: [
    /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", disabled: fetching, onClick: onFetch, children: fetching ? "Looking\u2026" : "Look for this contract's ABI" }),
    /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "Queries the explorers, and can read the contract's own deployed bytecode when none of them has it." })
  ] });
}
var TRUNCATION_NOTE = {
  cycle: "The implementation pointer loops back to an address already in this chain. The last new implementation is shown; anything past it repeats.",
  "max-hops": "The hop limit was reached with another proxy still ahead. There may be a further implementation this view does not show.",
  "beacon-unresolved": "A beacon was found but this reader cannot call it for its implementation, so the address below is the BEACON \u2014 its ABI is a stub, not the contract's.",
  "not-detected": "No supported proxy pattern matched. Delegation can still occur, including library calls; no proxy conclusion was established.",
  "probe-failed": "At least one detection strategy failed or was inconclusive. Proxy detection is incomplete; review the available evidence before relying on this result."
};
function TruncationNote({ chain }) {
  if (!chain.truncated) return null;
  return /* @__PURE__ */ jsx("p", { className: "rounded-md border border-risk-medium/40 bg-risk-medium/5 p-2 text-xs text-muted-foreground", children: TRUNCATION_NOTE[chain.truncated] });
}
function ExplanationNote({ chain }) {
  if (!chain.explanation || chain.explanation.length === 0) return null;
  return /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-0.5 text-xs text-muted-foreground", children: chain.explanation.map((line, i) => /* @__PURE__ */ jsxs("li", { children: [
    "\u2014 ",
    line
  ] }, `${i}-${line}`)) });
}
function ProxyChainView({
  chain,
  chainId,
  isResolving = false,
  error = null,
  onRetry,
  onSelectImplementation,
  className
}) {
  if (isResolving) {
    return /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 py-6 text-sm text-muted-foreground", children: [
      /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      " Resolving proxy chain\u2026"
    ] });
  }
  if (error) {
    const forbidden = /403|forbidden|scope/i.test(error.message);
    return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
      /* @__PURE__ */ jsx(
        EmptyState,
        {
          title: forbidden ? "You can't see proxy data" : "Couldn't resolve the proxy chain",
          description: forbidden ? "Reading proxy resolutions needs the proxy:resolve permission. Whether this contract delegates to an implementation is unknown from here \u2014 it is not a finding that it doesn't." : `${error.message} \u2014 so we don't know whether this contract is a proxy. This is not the same as it not being one.`,
          size: "sm"
        }
      ),
      onRetry && !forbidden && /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: onRetry, children: "Retry" }) })
    ] });
  }
  if (!chain) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No contract", description: "Select a contract to inspect its proxy chain.", size: "sm" });
  }
  if (!chain.isProxy) {
    const incomplete = Boolean(chain.truncated);
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 py-2 text-sm", children: [
        /* @__PURE__ */ jsx(Badge, { variant: "outline", children: incomplete ? "inconclusive" : "direct" }),
        /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: incomplete ? "No implementation found." : "Not a proxy." }),
        /* @__PURE__ */ jsx(AddressDisplay, { address: chain.root, chain: chainId })
      ] }),
      /* @__PURE__ */ jsx(TruncationNote, { chain }),
      /* @__PURE__ */ jsx(ExplanationNote, { chain })
    ] });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-1.5", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "rounded-md border bg-muted/30 p-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "mb-1 flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: "proxy" }),
        /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "root" })
      ] }),
      /* @__PURE__ */ jsx(AddressDisplay, { address: chain.root, chain: chainId })
    ] }),
    chain.hops.map((hop, i) => {
      const isEffective = hop.address.toLowerCase() === chain.effectiveAddress.toLowerCase();
      return /* @__PURE__ */ jsxs(React50.Fragment, { children: [
        /* @__PURE__ */ jsx("span", { className: "mx-auto text-sm leading-none text-muted-foreground", "aria-hidden": true, children: "\u2193" }),
        /* @__PURE__ */ jsxs("div", { className: cn("rounded-md border p-2", isEffective && "border-primary/50 bg-primary/5"), children: [
          /* @__PURE__ */ jsxs("div", { className: "mb-1 flex items-center justify-between gap-2", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
              /* @__PURE__ */ jsx(Badge, { variant: "outline", children: proxyKindLabel(hop.kind) }),
              isEffective && /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "effective" })
            ] }),
            onSelectImplementation && !isEffective && /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: () => onSelectImplementation(hop.address), children: "Use" })
          ] }),
          /* @__PURE__ */ jsx(AddressDisplay, { address: hop.address, chain: chainId }),
          hop.beacon && /* @__PURE__ */ jsxs("p", { className: "mt-1 text-3xs text-muted-foreground", children: [
            "via beacon ",
            /* @__PURE__ */ jsx(AddressDisplay, { address: hop.beacon, chain: chainId })
          ] })
        ] })
      ] }, `${hop.address}-${i}`);
    }),
    /* @__PURE__ */ jsx(TruncationNote, { chain }),
    /* @__PURE__ */ jsx(ExplanationNote, { chain })
  ] });
}
function proxyOverrideFormError(isProxy, implementations, fromText, toText, proxyAddress) {
  if (isProxy && implementations.length === 0) return "A proxy needs at least one implementation address.";
  if (!isProxy && implementations.length > 0) return "Remove the implementations to pin a non-proxy.";
  const invalid = implementations.find((address) => !isAddress(address, { strict: false }));
  if (invalid) return `Invalid implementation address: ${invalid}. Enter a 0x-prefixed, 40-digit hexadecimal address.`;
  if (implementations.some((address) => normalizeAddress(address) === zeroAddress)) return "The zero address cannot be an implementation.";
  if (proxyAddress && implementations.some((address) => normalizeAddress(address) === normalizeAddress(proxyAddress))) return "A proxy cannot use itself as its implementation.";
  if (new Set(implementations.map((address) => normalizeAddress(address))).size !== implementations.length) return "Remove duplicate implementation addresses.";
  const from = fromText.trim();
  const to = toText.trim();
  for (const [label, value] of [["From-block", from], ["To-block", to]]) {
    if (value && (!/^\d+$/.test(value) || !Number.isSafeInteger(Number(value)))) {
      return `${label} must be a non-negative, safe integer block number.`;
    }
  }
  if (!from) return "Enter a From block explicitly (0 means genesis), or prepare a pin from a storage observation.";
  if (to && Number(to) <= Number(from || "0")) return "To-block must be greater than from-block.";
  return null;
}
async function inspectProxyStorage(reader, ref, signal) {
  if (!reader.getStorageAt || !reader.getBytecode) {
    throw new Error("This reader cannot inspect storage and validate contract bytecode. Use a reader with both capabilities.");
  }
  const block = ref.block?.blockNumber ?? await reader.resolveBlockNumber?.(ref, { signal });
  if (block == null) throw new Error("Pin a numeric block first; this reader cannot resolve block tags.");
  if (block < 0n || block >= BigInt(Number.MAX_SAFE_INTEGER)) throw new Error("The observation block cannot be represented by the proxy pin API.");
  const atBlock = { ...ref, block: { blockNumber: block } };
  const slots = await Promise.all(KNOWN_PROXY_SLOTS.map(async (slot) => {
    try {
      const word = await reader.getStorageAt(atBlock, slot.slot, { signal });
      if (!word || !/^0x[0-9a-f]{64}$/i.test(word) || !/^0x0{24}/i.test(word)) {
        return { slot, word, error: "Storage did not return a zero-padded address word." };
      }
      return { slot, word, address: (0, contract_exports.addressFromStorageWord)(word) };
    } catch (error) {
      return { slot, error: error instanceof Error ? error.message : String(error) };
    }
  }));
  const result = (message, draft) => ({ block, slots, message, draft });
  const impl = slots.find((row) => row.slot.id === "eip1967.impl");
  const beacon = slots.find((row) => row.slot.id === "eip1967.beacon");
  const legacy = slots.find((row) => row.slot.id === "oz.legacy.impl");
  const selected = impl.error || impl.address ? impl : beacon.error || beacon.address ? beacon : legacy;
  if (selected.error) return result("Inspection is incomplete. Resolve the storage error before preparing a pin.");
  if (!selected.address) return result("No implementation found in these standard slots. This does not rule out clones, Safe proxies, diamonds, or custom proxy layouts.");
  const requireContract = async (address) => {
    if (!isAddress(address, { strict: false }) || normalizeAddress(address, ref.chainId) === zeroAddress) throw new Error("The implementation must be a nonzero EVM address.");
    if (normalizeAddress(address, ref.chainId) === normalizeAddress(ref.address, ref.chainId)) throw new Error("A proxy cannot use itself as its implementation or beacon.");
    const code = await reader.getBytecode({ ...atBlock, address }, { signal });
    if (!code || code === "0x") throw new Error(`No contract bytecode at ${address} at block ${block}.`);
    return address;
  };
  try {
    let implementation = await requireContract(selected.address);
    const pattern = selected === beacon ? "beacon" : selected === legacy ? "custom" : "eip1967";
    if (selected === beacon) {
      const value = await reader.read({ ...atBlock, address: implementation }, { abi: contract_exports.BEACON_ABI, functionName: "implementation" }, { signal });
      implementation = await requireContract(String(value));
      if (normalizeAddress(implementation, ref.chainId) === normalizeAddress(beacon.address, ref.chainId)) throw new Error("The beacon returned itself as its implementation.");
    }
    const reason = `Observed ${selected.slot.label.toLowerCase()} slot ${selected.slot.slot} = ${selected.word} on ${ref.chainId} ${ref.address} at block ${block}${selected === beacon ? `; beacon implementation() = ${implementation}` : ""}; target bytecode present at that block.`;
    return result("Confirm the contract code delegates through this slot. This observation supports one block only. Extend the interval only when upgrade history or other evidence establishes the boundary.", {
      implementation,
      pattern,
      fromBlock: block.toString(),
      toBlock: (block + 1n).toString(),
      reason
    });
  } catch (error) {
    return result(`Cannot prepare a pin: ${error instanceof Error ? error.message : String(error)}`);
  }
}
function ProxyStorageInspector({ contract, onPrepare, onAddressesFound }) {
  const reader = useContractReader();
  const key = `${contract.chainId}|${normalizeAddress(contract.address, contract.chainId)}|${(0, contract_exports.blockKey)(contract.block)}`;
  const [state, setState] = React50.useState();
  const pending = React50.useRef(null);
  React50.useEffect(() => {
    setState(void 0);
    return () => {
      pending.current?.abort();
    };
  }, [key, reader]);
  const inspect = async () => {
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    setState({ key, loading: true });
    try {
      const result = await inspectProxyStorage(reader, contract, controller.signal);
      if (!controller.signal.aborted) setState({ key, result });
    } catch (error) {
      if (!controller.signal.aborted) setState({ key, error: error instanceof Error ? error.message : String(error) });
    }
  };
  const current = state?.key === key ? state : void 0;
  const foundKey = [
    ...current?.result?.slots.flatMap((row) => row.address ? [row.address] : []) ?? [],
    ...current?.result?.draft ? [current.result.draft.implementation] : []
  ].join(",");
  React50.useEffect(() => {
    onAddressesFound?.(foundKey ? foundKey.split(",") : [], "proxy:storage");
    return () => onAddressesFound?.([], "proxy:storage");
  }, [foundKey, onAddressesFound]);
  return /* @__PURE__ */ jsxs("section", { "aria-label": "Proxy storage inspection", className: "rounded-md border p-3 space-y-2", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx("span", { className: "text-xs font-medium", children: "Inspect proxy storage" }),
      /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", disabled: current?.loading, onClick: () => void inspect(), children: current?.loading ? "Reading storage\u2026" : "Read proxy slots" })
    ] }),
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Read standard implementation slots at the selected block and prepare an explorer mapping. This does not upgrade the on-chain contract." }),
    current?.error && /* @__PURE__ */ jsx("p", { role: "alert", className: "text-xs text-destructive", children: current.error }),
    current?.result && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs("p", { className: "text-xs font-medium", children: [
        "Observed at block ",
        current.result.block.toString()
      ] }),
      /* @__PURE__ */ jsx("ul", { className: "space-y-2 text-xs", children: current.result.slots.map((row) => /* @__PURE__ */ jsxs("li", { className: "space-y-1", children: [
        /* @__PURE__ */ jsxs("span", { className: "font-medium", children: [
          row.slot.label,
          ": "
        ] }),
        row.error ? /* @__PURE__ */ jsx("span", { className: "text-destructive", children: "Read unavailable" }) : row.address ? /* @__PURE__ */ jsx(AddressDisplay, { address: row.address, chain: contract.chainId }) : /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "Empty" }),
        /* @__PURE__ */ jsxs("details", { className: "text-muted-foreground", children: [
          /* @__PURE__ */ jsx("summary", { children: "Storage evidence" }),
          /* @__PURE__ */ jsxs("p", { className: "break-all font-mono", children: [
            "Slot: ",
            row.slot.slot
          ] }),
          row.word && /* @__PURE__ */ jsxs("p", { className: "break-all font-mono", children: [
            "Value: ",
            row.word
          ] }),
          row.error && /* @__PURE__ */ jsx("p", { children: row.error })
        ] })
      ] }, row.slot.id)) }),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: current.result.message }),
      current.result.draft && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 text-xs", children: [
        /* @__PURE__ */ jsx("span", { children: "Implementation:" }),
        /* @__PURE__ */ jsx(AddressDisplay, { address: current.result.draft.implementation, chain: contract.chainId }),
        onPrepare && /* @__PURE__ */ jsx(Button, { size: "sm", onClick: () => onPrepare(current.result.draft), children: "Use as implementation" })
      ] })
    ] })
  ] });
}
var RUNG_OPTIONS = [
  { value: "user", label: "Only me" },
  { value: "tenant", label: "Tenant" },
  { value: "org", label: "Organization" },
  { value: "customer", label: "Entire customer" }
];
var PATTERNS = ["manual", "eip1967", "uups", "beacon", "transparent", "diamond", "custom"];
function implAddresses(impls) {
  if (!Array.isArray(impls)) return [];
  return impls.map((i) => typeof i === "string" ? i : i.address).filter(Boolean);
}
function discoveredRow(w, i) {
  return {
    key: `d-${w.valid_from_block}-${i}`,
    kind: "discovered",
    valid_from_block: w.valid_from_block,
    valid_to_block: w.valid_to_block,
    implementations: implAddresses(w.implementations),
    pattern: w.pattern ?? null,
    scope: null,
    ...toProvenance(w)
  };
}
function overrideRow(o) {
  return {
    key: `o-${o.override_id}`,
    kind: "override",
    valid_from_block: o.valid_from_block,
    valid_to_block: o.valid_to_block,
    implementations: o.implementations ?? [],
    pattern: o.pattern ?? null,
    scope: o.scope,
    ...toProvenance(o)
  };
}
function viewAtLabel(window2) {
  const target = windowViewBlock(window2);
  return target === null ? "View at current" : `View at ${Number(target).toLocaleString()}`;
}
function timelineRows(history) {
  if (!history) return [];
  const rows = [
    ...history.windows.map(discoveredRow),
    ...history.overrides.map(overrideRow)
  ];
  return rows.sort((a, b) => {
    if (a.valid_from_block !== b.valid_from_block) return a.valid_from_block - b.valid_from_block;
    return a.kind === b.kind ? 0 : a.kind === "discovered" ? -1 : 1;
  });
}
function ProxyOverridePanel(props) {
  return /* @__PURE__ */ jsx(ProxyOverridePanelContent, { ...props }, `${props.chainId}|${normalizeAddress(props.address, props.chainId)}`);
}
function ProxyOverridePanelContent({
  chainId,
  address,
  client,
  canEdit = false,
  canPurge = false,
  canIndex = false,
  writeScopes = [],
  block = null,
  inspectionBlock,
  onBlockChange,
  onWritten,
  onAddressesFound,
  className
}) {
  const tierCutoffs = useTierCutoffs();
  const resolution = useScopedOverride(
    {
      cacheFragment: "/proxy-overrides/",
      read: (c, { scope: scope2, block: at, signal }) => c.get(`/proxy-overrides/${encodeURIComponent(chainId)}/${encodeURIComponent(address)}`, {
        query: {
          ...scope2 ? { scope: scope2 } : {},
          ...at != null ? { block: at.toString() } : {}
        },
        signal,
        cacheTtlMs: 0
      }),
      write: canEdit ? (c, { input, scope: scope2 }) => c.put(
        `/proxy-overrides/${encodeURIComponent(chainId)}/${encodeURIComponent(address)}`,
        // The client's own body builder, not the raw form state: an empty
        // "Until block" is `null` here, and API Gateway's draft-4 validator
        // rejects an explicit null before the handler ever runs.
        proxyOverrideRequestBody(input),
        { query: scope2 ? { scope: scope2 } : {} }
      ) : void 0,
      // `?block=` picks ONE window; without it the service deletes every window
      // this rung holds. The button says "Remove my pin", singular, so it has to
      // send the block it is showing.
      remove: (c, { scope: scope2, block: at }) => c.delete(`/proxy-overrides/${encodeURIComponent(chainId)}/${encodeURIComponent(address)}`, {
        query: {
          ...scope2 ? { scope: scope2 } : {},
          ...at != null ? { block: at.toString() } : {}
        }
      })
    },
    { initialBlock: block }
  );
  const { setBlock } = resolution;
  React50.useEffect(() => {
    setBlock(block);
  }, [block, setBlock]);
  const setPin = React50.useCallback(
    (next) => {
      setBlock(next);
      onBlockChange?.(next);
    },
    [setBlock, onBlockChange]
  );
  const scope = resolution.scope;
  const historyKey = `${chainId}|${address}|${scope ?? ""}`;
  const [loaded, setLoaded] = React50.useState(
    void 0
  );
  const [historyError, setHistoryError] = React50.useState(null);
  const [loadingHistory, setLoadingHistory] = React50.useState(false);
  const history = loaded?.key === historyKey ? loaded.data : void 0;
  const historySeq = React50.useRef(0);
  const loadHistory = React50.useCallback(async () => {
    const seq = ++historySeq.current;
    const key = historyKey;
    setLoadingHistory(true);
    setHistoryError(null);
    try {
      const data = await client.history(chainId, address, { scope });
      if (seq !== historySeq.current) return;
      setLoaded({ key, data });
    } catch (err) {
      if (seq !== historySeq.current) return;
      setHistoryError(err instanceof Error ? err.message : String(err));
    } finally {
      if (seq === historySeq.current) setLoadingHistory(false);
    }
  }, [client, chainId, address, scope, historyKey]);
  const loadHistoryRef = React50.useRef(loadHistory);
  React50.useEffect(() => {
    loadHistoryRef.current = loadHistory;
  }, [loadHistory]);
  React50.useEffect(() => {
    void loadHistoryRef.current();
  }, [historyKey]);
  const rows = React50.useMemo(() => timelineRows(history), [history]);
  const historyAddressKey = rows.flatMap((row) => row.implementations).join(",");
  React50.useEffect(() => {
    onAddressesFound?.(historyAddressKey ? historyAddressKey.split(",") : [], "proxy:history");
    return () => onAddressesFound?.([], "proxy:history");
  }, [historyAddressKey, onAddressesFound]);
  const [isProxy, setIsProxy] = React50.useState(true);
  const [implText, setImplText] = React50.useState("");
  const [pattern, setPattern] = React50.useState("manual");
  const [fromBlock, setFromBlock] = React50.useState(block?.toString() ?? "");
  const [toBlock, setToBlock] = React50.useState(block == null ? "" : (block + 1n).toString());
  const [reason, setReason] = React50.useState("");
  const [evidenceUrl, setEvidenceUrl] = React50.useState("");
  const [targetRung, setTargetRung] = React50.useState(void 0);
  const draftForm = React50.useRef(null);
  const prepareDraft = (draft) => {
    setIsProxy(true);
    setImplText(draft.implementation);
    setPattern(draft.pattern);
    setFromBlock(draft.fromBlock);
    setToBlock(draft.toBlock);
    setReason(draft.reason);
    setEvidenceUrl("");
    setTargetRung(RUNG_OPTIONS.find((option) => writeScopes.includes(option.value))?.value);
    draftForm.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  };
  const implementations = React50.useMemo(
    () => implText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean),
    [implText]
  );
  const formError = React50.useMemo(
    () => proxyOverrideFormError(isProxy, implementations, fromBlock, toBlock, address),
    [isProxy, implementations, fromBlock, toBlock, address]
  );
  const windowNote = React50.useMemo(() => {
    if (formError) return null;
    const from = Number(fromBlock.trim());
    return !toBlock.trim() ? `Applies from block ${from} onward with no end block. Future upgrades will remain overridden in this scope until this pin is changed or removed.` : `Applies to blocks ${from}-${Number(toBlock.trim()) - 1} only. Until block is excluded; outside this interval, other pins or discovery apply.`;
  }, [fromBlock, toBlock, formError]);
  const submit = React50.useCallback(async () => {
    if (formError) return;
    const input = {
      is_proxy: isProxy,
      implementations,
      pattern,
      valid_from_block: fromBlock.trim() ? Number(fromBlock.trim()) : 0,
      valid_to_block: toBlock.trim() ? Number(toBlock.trim()) : null,
      reason: reason.trim() || void 0,
      evidence_url: evidenceUrl.trim() || void 0
    };
    const writeRung = targetRung ?? null;
    if (targetRung) resolution.setScope(targetRung);
    const written = await resolution.save(input, { scope: writeRung });
    if (written) {
      const rung = writeRung ? RUNG_OPTIONS.find((o) => o.value === writeRung)?.label : null;
      notifySuccess(rung ? `Proxy pinned \xB7 ${rung}` : "Proxy pinned", {
        description: windowNote ?? void 0
      });
      onWritten?.();
      void loadHistoryRef.current();
    }
  }, [
    formError,
    windowNote,
    isProxy,
    implementations,
    pattern,
    fromBlock,
    toBlock,
    reason,
    evidenceUrl,
    targetRung,
    resolution,
    loadHistory,
    onWritten
  ]);
  const myPins = React50.useMemo(() => rows.filter((r) => r.kind === "override"), [rows]);
  const pinnedAt = resolution.block;
  const removesEverything = pinnedAt == null && myPins.length > 1;
  const [unpinOpen, setUnpinOpen] = React50.useState(false);
  const unpin = React50.useCallback(async () => {
    if (await resolution.remove({ block: pinnedAt })) {
      notifySuccess(
        pinnedAt == null ? "Pins removed" : `Pin removed \xB7 block ${pinnedAt.toLocaleString()}`,
        {
          description: pinnedAt == null ? "Every pin your scope held for this contract is gone; resolution falls back to on-chain discovery." : "Resolution at this block falls back to the next rung down, then to on-chain discovery."
        }
      );
      onWritten?.();
      void loadHistoryRef.current();
    }
  }, [resolution, loadHistory, pinnedAt, onWritten]);
  const [indexing, setIndexing] = React50.useState(false);
  const requestIndex = React50.useCallback(async () => {
    setIndexing(true);
    try {
      const result = await client.index(chainId, address);
      if (result.accepted) {
        notifySuccess("History build queued", {
          description: "Scanning upgrade events takes a moment \u2014 reload the timeline shortly."
        });
      } else {
        notifyError(new Error("The service did not accept the request."), {
          title: "Couldn't queue the history build."
        });
      }
    } catch (err) {
      notifyError(err, { title: "Couldn't queue the history build." });
    } finally {
      setIndexing(false);
    }
  }, [client, chainId, address]);
  const [purging, setPurging] = React50.useState(false);
  const [purgeOpen, setPurgeOpen] = React50.useState(false);
  const purge = React50.useCallback(async () => {
    setPurging(true);
    try {
      await client.purge(chainId, address);
      notifySuccess("Discovery cache purged", { description: "The next read re-detects from chain." });
      resolution.refetch();
      onWritten?.();
      void loadHistoryRef.current();
    } catch (err) {
      notifyError(err, { title: "Couldn't purge the discovery cache." });
    } finally {
      setPurging(false);
    }
  }, [client, chainId, address, resolution, loadHistory, onWritten]);
  const elsewhere = React50.useMemo(
    () => rows.filter((r) => r.implementations.length > 0 && !windowCovers(r, resolution.block)).sort((a, b) => b.valid_from_block - a.valid_from_block),
    [rows, resolution.block]
  );
  const myElsewhere = React50.useMemo(() => elsewhere.filter((r) => r.kind === "override"), [elsewhere]);
  const nearest = myElsewhere[0] ?? elsewhere[0];
  const resolved = resolution.data;
  const incomplete = hasIncompleteProxyEvidence(resolved?.evidence, resolved?.conclusive);
  const fromOverride = resolved?.resolved_by === "proxy_override";
  const rungLabel = resolved?.scope ? scopeRungLabel(resolved.scope) : resolved?.scope_rung ?? null;
  const provenance = toProvenance(resolved);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-4", className), children: [
    /* @__PURE__ */ jsx(
      ProxyStorageInspector,
      {
        contract: { chainId, address, block: inspectionBlock ?? (block == null ? void 0 : { blockNumber: block }) },
        onPrepare: canEdit ? prepareDraft : void 0,
        onAddressesFound
      }
    ),
    /* @__PURE__ */ jsxs("div", { className: "rounded-md border p-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "mb-2 flex flex-wrap items-center justify-between gap-2", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx("span", { className: "text-xs font-medium", children: "Resolved" }),
          resolution.isFetching && /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
          rungLabel && /* @__PURE__ */ jsx(Badge, { variant: fromOverride ? "secondary" : "outline", className: "text-3xs", children: fromOverride ? `Pinned \xB7 ${rungLabel}` : rungLabel })
        ] }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx(BlockPinControl, { id: "proxy-block", value: resolution.block, onChange: setPin }),
          writeScopes.length > 1 && /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-1.5 text-xs text-muted-foreground", children: [
            /* @__PURE__ */ jsx("span", { className: "whitespace-nowrap", children: "View as" }),
            /* @__PURE__ */ jsxs(
              NativeSelect,
              {
                value: scope ?? "",
                onChange: (e) => resolution.setScope(e.target.value || null),
                density: "sm",
                "aria-label": "Tenancy rung to read the proxy at",
                children: [
                  /* @__PURE__ */ jsx("option", { value: "", children: "My scope" }),
                  RUNG_OPTIONS.filter((o) => writeScopes.includes(o.value)).map((o) => /* @__PURE__ */ jsx("option", { value: o.value, children: o.label }, o.value))
                ]
              }
            )
          ] })
        ] })
      ] }),
      resolution.error ? /* @__PURE__ */ jsx("p", { className: "text-xs text-destructive", children: resolution.error.error.message }) : resolved?.is_proxy === false ? /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: incomplete ? hasFailedProxyEvidence(resolved?.evidence) ? "Some proxy checks failed; no implementation was resolved at this block." : "No supported proxy pattern matched at this block; delegation remains unresolved." : "Not a proxy at this block." }),
        loadingHistory && !history && /* @__PURE__ */ jsxs("p", { className: "flex items-center gap-1.5 text-xs text-muted-foreground", children: [
          /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
          " Checking other blocks\u2026"
        ] }),
        nearest && /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
          myElsewhere.length > 0 ? `Your pin applies to ${formatBlockWindow(nearest)}, which does not include this block.` : `${elsewhere.length} other window${elsewhere.length === 1 ? "" : "s"} name an implementation.`,
          " ",
          /* @__PURE__ */ jsx(
            Button,
            {
              variant: "ghost",
              size: "sm",
              className: "h-5 px-1 text-2xs",
              onClick: () => setPin(windowViewBlock(nearest)),
              children: viewAtLabel(nearest)
            }
          )
        ] })
      ] }) : /* @__PURE__ */ jsx("div", { className: "flex flex-col gap-1.5", children: (resolved?.implementations ?? []).map((impl, i) => /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-3xs", children: impl.pattern ?? resolved?.pattern ?? "proxy" }),
        /* @__PURE__ */ jsx(AddressDisplay, { address: impl.address, chain: chainId })
      ] }, `${impl.address}-${i}`)) }),
      /* @__PURE__ */ jsx(
        WhyPanel,
        {
          className: "mt-3 border-t pt-2",
          explanation: provenance.explanation,
          evidence: provenance.evidence,
          confidence: provenance.confidence,
          cutoffs: tierCutoffs,
          lifecycle: provenance.lifecycle
        }
      ),
      fromOverride && canEdit && /* @__PURE__ */ jsx("div", { className: "mt-2 flex justify-end", children: removesEverything ? /* @__PURE__ */ jsxs(AlertDialog, { open: unpinOpen, onOpenChange: (next) => !resolution.isSaving && setUnpinOpen(next), children: [
        /* @__PURE__ */ jsx(AlertDialogTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", disabled: resolution.isSaving, children: "Remove my pins" }) }),
        /* @__PURE__ */ jsxs(AlertDialogContent, { children: [
          /* @__PURE__ */ jsxs(AlertDialogHeader, { children: [
            /* @__PURE__ */ jsxs(AlertDialogTitle, { children: [
              "Remove all ",
              myPins.length,
              " of your pins for this contract?"
            ] }),
            /* @__PURE__ */ jsxs(AlertDialogDescription, { children: [
              "You are viewing the current block, so there is no single window to remove \u2014 this deletes every pin your scope holds for ",
              /* @__PURE__ */ jsx(AddressDisplay, { address, chain: chainId }),
              ", across its whole upgrade history. To remove just one, pin the block it covers first."
            ] })
          ] }),
          /* @__PURE__ */ jsxs(AlertDialogFooter, { children: [
            /* @__PURE__ */ jsx(AlertDialogCancel, { disabled: resolution.isSaving, children: "Cancel" }),
            /* @__PURE__ */ jsxs(
              Button,
              {
                type: "button",
                variant: "destructive",
                disabled: resolution.isSaving,
                onClick: () => {
                  setUnpinOpen(false);
                  void unpin();
                },
                children: [
                  "Remove ",
                  myPins.length,
                  " pins"
                ]
              }
            )
          ] })
        ] })
      ] }) : /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", disabled: resolution.isSaving, onClick: () => void unpin(), children: pinnedAt == null ? "Remove my pin" : `Remove my pin at ${pinnedAt.toLocaleString()}` }) }),
      fromOverride && !canEdit && /* @__PURE__ */ jsxs("p", { className: "mt-2 text-right text-2xs text-muted-foreground", children: [
        "Removing a pin needs the ",
        /* @__PURE__ */ jsx("code", { className: "font-mono", children: "proxy:edit" }),
        " permission."
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "rounded-md border p-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-center justify-between gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "text-xs font-medium", children: "Upgrade timeline" }),
        /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
          loadingHistory && /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
          /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", disabled: loadingHistory, onClick: () => void loadHistory(), children: "Reload" })
        ] })
      ] }),
      historyError && /* @__PURE__ */ jsx("p", { className: "text-xs text-destructive", children: historyError }),
      loadingHistory && !history && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Loading upgrade history and your pins\u2026" }),
      history && rows.length === 0 && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
        /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
          "No upgrade history indexed for this contract yet.",
          !canIndex && /* @__PURE__ */ jsxs(Fragment, { children: [
            " ",
            "Building one needs the ",
            /* @__PURE__ */ jsx("code", { className: "font-mono", children: "proxy:index" }),
            " permission."
          ] })
        ] }),
        canIndex && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", disabled: indexing, onClick: () => void requestIndex(), children: indexing ? "Requesting\u2026" : "Build history" })
      ] }),
      /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-1.5", children: rows.map((row) => {
        const active = windowCovers(row, resolution.block);
        return /* @__PURE__ */ jsxs(
          "li",
          {
            className: cn(
              "rounded-md border p-2 text-xs",
              row.kind === "override" && "border-primary/40 bg-primary/5",
              active && "ring-1 ring-primary/40"
            ),
            children: [
              /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
                /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
                  /* @__PURE__ */ jsx(Badge, { variant: row.kind === "override" ? "secondary" : "outline", className: "text-3xs", children: row.kind === "override" ? scopeRungLabel(row.scope) ?? "Pinned" : "Chain" }),
                  /* @__PURE__ */ jsx("span", { className: "tabular-nums text-muted-foreground", children: formatBlockWindow(row) })
                ] }),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    variant: "ghost",
                    size: "sm",
                    className: "h-6 px-2 text-2xs",
                    onClick: () => setPin(windowViewBlock(row)),
                    children: viewAtLabel(row)
                  }
                )
              ] }),
              /* @__PURE__ */ jsx("div", { className: "mt-1 flex flex-col gap-1", children: row.implementations.length === 0 ? /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: row.kind === "override" ? "pinned as not a proxy" : "no proxy detected" }) : row.implementations.map((impl, i) => /* @__PURE__ */ jsx(AddressDisplay, { address: impl, chain: chainId }, `${impl}-${i}`)) }),
              /* @__PURE__ */ jsx(
                WhyPanel,
                {
                  className: "mt-2",
                  title: "Why this window?",
                  explanation: row.explanation,
                  evidence: row.evidence,
                  confidence: row.confidence,
                  cutoffs: tierCutoffs,
                  lifecycle: row.lifecycle
                }
              )
            ]
          },
          row.key
        );
      }) }),
      (history?.overrides_truncated_rungs?.length ?? 0) > 0 && /* @__PURE__ */ jsxs("p", { className: "mt-2 rounded-md border border-risk-medium/40 bg-risk-medium/5 p-2 text-xs text-muted-foreground", children: [
        "More pins exist than are shown \u2014 the",
        " ",
        history?.overrides_truncated_rungs?.map((rung) => scopeRungLabel(rung) ?? rung).join(", "),
        " ",
        (history?.overrides_truncated_rungs?.length ?? 0) === 1 ? "rung was" : "rungs were",
        " truncated at the service's cap. Narrow the view with \u201CView as\u201D to see a single rung in full."
      ] })
    ] }),
    canEdit && /* @__PURE__ */ jsxs("div", { ref: draftForm, className: "rounded-md border p-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-center justify-between gap-2", children: [
        /* @__PURE__ */ jsx("span", { className: "text-xs font-medium", children: "Pin an implementation" }),
        /* @__PURE__ */ jsxs("label", { className: "flex items-center gap-1.5 text-xs text-muted-foreground", children: [
          /* @__PURE__ */ jsx(
            "input",
            {
              type: "checkbox",
              checked: !isProxy,
              onChange: (e) => {
                setIsProxy(!e.target.checked);
                if (e.target.checked) setImplText("");
              }
            }
          ),
          "Not a proxy"
        ] })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "grid gap-2 sm:grid-cols-2", children: [
        /* @__PURE__ */ jsx(Field, { label: "Implementation address(es)", className: "sm:col-span-2", labelClassName: "text-xs", children: /* @__PURE__ */ jsx(
          Textarea,
          {
            value: implText,
            onChange: (e) => setImplText(e.target.value),
            disabled: !isProxy,
            rows: 2,
            placeholder: "0x\u2026 (several for a diamond, comma or space separated)",
            className: "font-mono text-xs"
          }
        ) }),
        /* @__PURE__ */ jsx(Field, { label: "Pattern", labelClassName: "text-xs", children: /* @__PURE__ */ jsx(
          NativeSelect,
          {
            value: pattern,
            onChange: (e) => setPattern(e.target.value),
            density: "sm",
            children: PATTERNS.map((p) => /* @__PURE__ */ jsx("option", { value: p, children: p }, p))
          }
        ) }),
        writeScopes.length > 1 && /* @__PURE__ */ jsx(Field, { label: "Applies to", labelClassName: "text-xs", children: /* @__PURE__ */ jsxs(
          NativeSelect,
          {
            value: targetRung ?? "",
            onChange: (e) => setTargetRung(e.target.value || void 0),
            density: "sm",
            children: [
              /* @__PURE__ */ jsx("option", { value: "", children: "My scope" }),
              RUNG_OPTIONS.filter((o) => writeScopes.includes(o.value)).map((o) => /* @__PURE__ */ jsx("option", { value: o.value, children: o.label }, o.value))
            ]
          }
        ) }),
        /* @__PURE__ */ jsx(Field, { label: "From block", labelClassName: "text-xs", children: /* @__PURE__ */ jsx(
          Input,
          {
            value: fromBlock,
            onChange: (e) => setFromBlock(e.target.value),
            inputMode: "numeric",
            placeholder: "Required (0 = genesis)",
            className: "h-8 font-mono text-xs"
          }
        ) }),
        /* @__PURE__ */ jsx(
          Field,
          {
            label: /* @__PURE__ */ jsxs(Fragment, { children: [
              "Until block",
              " ",
              /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "(exclusive, blank = no end)" })
            ] }),
            labelClassName: "text-xs",
            children: /* @__PURE__ */ jsx(
              Input,
              {
                value: toBlock,
                onChange: (e) => setToBlock(e.target.value),
                inputMode: "numeric",
                placeholder: "No end block",
                className: "h-8 font-mono text-xs"
              }
            )
          }
        ),
        windowNote && /* @__PURE__ */ jsx("p", { className: "text-2xs text-muted-foreground sm:col-span-2", children: windowNote }),
        /* @__PURE__ */ jsx(Field, { label: "Reason", className: "sm:col-span-2", labelClassName: "text-xs", children: /* @__PURE__ */ jsx(
          Input,
          {
            value: reason,
            onChange: (e) => setReason(e.target.value),
            placeholder: "Why the detectors are wrong here",
            className: "h-8 text-xs"
          }
        ) }),
        /* @__PURE__ */ jsx(
          Field,
          {
            label: /* @__PURE__ */ jsxs(Fragment, { children: [
              "Evidence link ",
              /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "(optional)" })
            ] }),
            className: "sm:col-span-2",
            labelClassName: "text-xs",
            children: /* @__PURE__ */ jsx(
              Input,
              {
                value: evidenceUrl,
                onChange: (e) => setEvidenceUrl(e.target.value),
                placeholder: "https://\u2026 upgrade tx, ticket, explorer page",
                className: "h-8 text-xs"
              }
            )
          }
        )
      ] }),
      formError && /* @__PURE__ */ jsx(Alert, { variant: "destructive", className: "mt-2", children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: formError }) }),
      resolution.saveError && /* @__PURE__ */ jsx(Alert, { variant: "destructive", className: "mt-2", children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: resolution.saveError.error.message }) }),
      /* @__PURE__ */ jsx("p", { className: "mt-2 text-2xs text-muted-foreground", children: "A pin decides which ABI your transactions decode against, and reaches everyone at the rung you choose. Its confidence is set by your scope's policy, not by this form." }),
      /* @__PURE__ */ jsxs("div", { className: "mt-2 flex items-center justify-end gap-2", children: [
        canPurge && /* @__PURE__ */ jsxs(AlertDialog, { open: purgeOpen, onOpenChange: (next) => !purging && setPurgeOpen(next), children: [
          /* @__PURE__ */ jsx(AlertDialogTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", disabled: purging, children: purging ? "Purging\u2026" : "Purge shared cache" }) }),
          /* @__PURE__ */ jsxs(AlertDialogContent, { children: [
            /* @__PURE__ */ jsxs(AlertDialogHeader, { children: [
              /* @__PURE__ */ jsx(AlertDialogTitle, { children: "Purge the shared discovery cache for this contract?" }),
              /* @__PURE__ */ jsxs(AlertDialogDescription, { children: [
                "This deletes the stored detection rows for",
                " ",
                /* @__PURE__ */ jsx(AddressDisplay, { address, chain: chainId }),
                " for everyone, not just your scope. The next read re-detects from chain, which costs RPC and can resolve differently. Pins \u2014 yours and other scopes' \u2014 are not touched."
              ] })
            ] }),
            /* @__PURE__ */ jsxs(AlertDialogFooter, { children: [
              /* @__PURE__ */ jsx(AlertDialogCancel, { disabled: purging, children: "Cancel" }),
              /* @__PURE__ */ jsx(
                Button,
                {
                  type: "button",
                  variant: "destructive",
                  disabled: purging,
                  onClick: () => {
                    setPurgeOpen(false);
                    void purge();
                  },
                  children: "Purge cache"
                }
              )
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsx(Button, { size: "sm", disabled: Boolean(formError) || resolution.isSaving, onClick: () => void submit(), children: resolution.isSaving ? "Pinning\u2026" : "Pin implementation" })
      ] })
    ] }),
    !canEdit && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(Separator, {}),
      /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
        "You need the ",
        /* @__PURE__ */ jsx("code", { className: "font-mono", children: "proxy:edit" }),
        " permission to pin an implementation."
      ] })
    ] })
  ] });
}
function useFunctionDrafts(scopeKey) {
  const [store, setStore] = React50.useState(() => /* @__PURE__ */ new Map());
  if (useDepsChanged([scopeKey])) {
    const next = /* @__PURE__ */ new Map();
    setStore(next);
    return next;
  }
  return store;
}
function parameterShape(param, depth = 0) {
  if (!("components" in param)) return `${param.name || "(unnamed)"}: ${param.type}`;
  const indent = "  ".repeat(depth);
  return `${param.name || "(unnamed)"}: ${param.type} {
${param.components.map((component) => `${indent}  ${parameterShape(component, depth + 1)}`).join("\n")}
${indent}}`;
}
function FunctionForm({
  fn,
  actionLabel,
  busy = false,
  disabled = false,
  onDraftChange,
  onValidationError,
  drafts,
  onSubmit,
  className
}) {
  const draftKey = functionFormKey(fn);
  const [values, setValues] = React50.useState(() => drafts?.get(draftKey)?.values ?? {});
  const [valueWei, setValueWei] = React50.useState(() => drafts?.get(draftKey)?.valueWei ?? "");
  const draftRef = React50.useRef({ values, valueWei });
  const [error, setError] = React50.useState(null);
  const inputs = React50.useMemo(() => fn.inputs ?? [], [fn.inputs]);
  const keys = React50.useMemo(() => parameterKeys(inputs), [inputs]);
  const isPayable = fn.stateMutability === "payable";
  const edit = (patch) => {
    const next = { ...draftRef.current, ...patch };
    draftRef.current = next;
    drafts?.set(draftKey, next);
    setValues(next.values);
    setValueWei(next.valueWei);
    setError(null);
    onDraftChange?.();
  };
  const submit = React50.useCallback(() => {
    if (disabled || busy) return;
    setError(null);
    try {
      const current = draftRef.current;
      const args = buildArgs(inputs, current.values);
      const value = isPayable && current.valueWei.trim() ? coerceArg({ type: "uint256", name: "value (wei)" }, current.valueWei) : void 0;
      onSubmit(args, value);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      onValidationError?.();
    }
  }, [inputs, isPayable, onSubmit, onValidationError, disabled, busy]);
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), onKeyDown: (event) => {
    if (event.key === "Enter" && event.target instanceof HTMLInputElement && !event.nativeEvent.isComposing) {
      event.preventDefault();
      event.stopPropagation();
      submit();
    }
  }, children: [
    inputs.length > 0 && /* @__PURE__ */ jsx("div", { className: "grid gap-2", children: inputs.map((param, i) => {
      const key = keys[i];
      return /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
        /* @__PURE__ */ jsx(
          Field,
          {
            label: /* @__PURE__ */ jsxs(Fragment, { children: [
              param.name && param.name !== key ? `${param.name} (${key})` : key,
              " ",
              /* @__PURE__ */ jsx("span", { className: "font-mono", children: param.type })
            ] }),
            labelClassName: "text-xs text-muted-foreground",
            hint: param.type.endsWith("]") || param.type.startsWith("tuple") ? 'Enter JSON. Quote large integers (for example, "9007199254740993") to keep them exact.' : void 0,
            children: /* @__PURE__ */ jsx(
              Input,
              {
                value: Object.prototype.hasOwnProperty.call(values, key) ? values[key] : "",
                placeholder: param.type,
                onChange: (e) => {
                  edit({ values: { ...draftRef.current.values, [key]: e.target.value } });
                },
                className: "h-8 font-mono text-xs"
              }
            )
          }
        ),
        "components" in param && /* @__PURE__ */ jsxs("details", { className: "text-xs text-muted-foreground", children: [
          /* @__PURE__ */ jsxs("summary", { className: "cursor-pointer", children: [
            "Tuple fields for ",
            key
          ] }),
          /* @__PURE__ */ jsx("pre", { className: "mt-1 whitespace-pre-wrap break-words font-mono", children: parameterShape(param) })
        ] })
      ] }, key);
    }) }),
    isPayable && /* @__PURE__ */ jsx(
      Field,
      {
        label: /* @__PURE__ */ jsxs(Fragment, { children: [
          "value ",
          /* @__PURE__ */ jsx("span", { className: "font-mono", children: "wei" })
        ] }),
        labelClassName: "text-xs text-muted-foreground",
        children: /* @__PURE__ */ jsx(
          Input,
          {
            value: valueWei,
            placeholder: "0",
            onChange: (e) => edit({ valueWei: e.target.value }),
            className: "h-8 font-mono text-xs"
          }
        )
      }
    ),
    error && /* @__PURE__ */ jsx(Alert, { variant: "destructive", children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: error }) }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsx("code", { className: "truncate text-3xs text-muted-foreground", children: (0, abi_exports.functionSignature)(fn) }),
      /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", onClick: submit, disabled: disabled || busy, children: busy ? "\u2026" : actionLabel })
    ] })
  ] });
}

// src/model/readError.ts
function formatReadError(error) {
  if (/historical state .*?(?:not available|unavailable)|missing trie node|state (?:data )?(?:has been |is )?pruned|no state available for block/i.test(error.message)) {
    return "The current data provider cannot read state at this historical block. Try On-chain state for profile reads, or select a newer block for this call.";
  }
  if (/must not be called through delegatecall|UUPSUnauthorizedCallContext/i.test(error.message)) {
    return "This UUPS function cannot be called in the current proxy context. Read proxiableUUID on the implementation directly.";
  }
  const details = error.details ?? "";
  if (!/revert/i.test(error.message) && /upstream overloaded|\boverloaded\b|rate.?limit|too many requests|capacity exceeded|service (?:temporarily )?unavailable|endpoint has been discontinued/i.test(`${error.message}
${details}`)) {
    return "This chain's RPC provider is overloaded or unavailable right now, so the value couldn't be read. It is not a problem with the call \u2014 try Refresh shortly.";
  }
  const shortMessage = error.shortMessage;
  return shortMessage || error.message.split(/\n(?:Contract Call:|Docs:|Details:|Version:)/)[0].trim();
}
function formatStateDiagnostic(message) {
  const miss = message.match(/ABI cached not-found(?: in DDB)? for (\S+) (0x[0-9a-f]{40})/i);
  return miss ? `No ABI was found for ${miss[2]} on ${miss[1]}. This lookup was cached temporarily. Available profile reads are shown; supply an ABI for additional functions.` : message;
}
var PREVIEW = 5;
var SCROLL_AFTER = 12;
function plural(noun, count) {
  if (count === 1) return noun;
  return /(s|x|z|ch|sh)$/i.test(noun) ? `${noun}es` : `${noun}s`;
}
function CollapsibleList({
  items,
  render,
  noun = "value",
  previewCount = PREVIEW,
  className
}) {
  const [expanded, setExpanded] = React50.useState(false);
  const shown = expanded ? items : items.slice(0, previewCount);
  const hidden = items.length - shown.length;
  return /* @__PURE__ */ jsxs("div", { className: cn("flex min-w-0 flex-col items-end gap-1", className), children: [
    /* @__PURE__ */ jsxs("span", { className: "text-2xs uppercase tracking-wide text-muted-foreground", children: [
      items.length,
      " ",
      plural(noun, items.length)
    ] }),
    /* @__PURE__ */ jsx(
      "div",
      {
        className: cn(
          "flex w-full min-w-0 flex-col items-end gap-1.5",
          expanded && items.length > SCROLL_AFTER && "max-h-64 overflow-y-auto pr-1"
        ),
        children: shown.map((item, i) => /* @__PURE__ */ jsx(React50.Fragment, { children: render(item, i) }, i))
      }
    ),
    (hidden > 0 || expanded) && /* @__PURE__ */ jsx(
      "button",
      {
        type: "button",
        className: "text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground",
        onClick: () => setExpanded((open) => !open),
        "aria-expanded": expanded,
        children: expanded ? "Show fewer" : `Show all ${items.length}`
      }
    )
  ] });
}
var DEFAULT_DECORATE_LIMIT = 12;
var ARRAY_SUFFIX = /^(.*)\[\d*\]$/;
function elementType(type) {
  const match = ARRAY_SUFFIX.exec(type);
  return match?.[1] ?? null;
}
function components(param) {
  return param.components ?? [];
}
function componentValue(value, component, i) {
  if (Array.isArray(value)) return value[i];
  if (value && typeof value === "object") {
    const name = component.name ?? "";
    return Object.prototype.hasOwnProperty.call(value, name) ? value[name] : void 0;
  }
  return void 0;
}
function addressEntries(outputs, data) {
  const out = [];
  const walk = (param, value, path) => {
    if (param.type === "address") {
      if (typeof value === "string" && value.length > 0) out.push({ address: value, path });
      return;
    }
    const element = elementType(param.type);
    if (element !== null) {
      if (Array.isArray(value)) value.forEach((item, i) => walk({ ...param, type: element }, item, `${path}[${i}]`));
      return;
    }
    if (param.type.startsWith("tuple")) {
      components(param).forEach((component, i) => {
        const name = component.name || `[${i}]`;
        walk(component, componentValue(value, component, i), path ? `${path}.${name}` : name);
      });
    }
  };
  if (outputs.length === 1) walk(outputs[0], data, outputs[0].name ?? "");
  else outputs.forEach((param, i) => walk(param, componentValue(data, param, i), param.name || `[${i}]`));
  return out;
}
function addressLeaves(outputs, data) {
  return addressEntries(outputs, data).map((entry) => entry.address);
}
function nodeFor(param, value, next) {
  if (param.type === "address" && typeof value === "string" && value.length > 0) {
    return { kind: "address", address: value, ord: next() };
  }
  const element = elementType(param.type);
  if (element !== null && Array.isArray(value)) {
    return {
      kind: "list",
      value,
      items: value.map((item) => nodeFor({ ...param, type: element }, item, next))
    };
  }
  if (param.type.startsWith("tuple") && value !== null && value !== void 0) {
    const parts = components(param);
    if (parts.length > 0) {
      return {
        kind: "rows",
        entries: parts.map((component, i) => ({
          name: component.name || `[${i}]`,
          node: nodeFor(component, componentValue(value, component, i), next)
        }))
      };
    }
  }
  return { kind: "text", value };
}
function counter() {
  let n = 0;
  return () => n++;
}
function hasAddress(node) {
  if (node.kind === "address") return true;
  if (node.kind === "list") return node.items.some(hasAddress);
  if (node.kind === "rows") return node.entries.some((entry) => hasAddress(entry.node));
  return false;
}
function hasTuple(node) {
  return node.kind === "rows" || node.kind === "list" && node.items.some(hasTuple);
}
function Text({ value }) {
  const text2 = formatReadValue(value);
  return text2.length > 512 ? /* @__PURE__ */ jsx(
    ExpandableText,
    {
      text: text2,
      monospace: true,
      lines: 2,
      expandLabel: `Show all ${text2.length} characters`,
      className: "min-w-0 max-w-full [&>p]:break-all [&>p]:max-h-64 [&>p]:overflow-y-auto"
    },
    text2
  ) : /* @__PURE__ */ jsx("code", { className: "break-all text-xs", children: text2 });
}
function ReadValue({
  outputs,
  data,
  renderAddress,
  decorateLimit = DEFAULT_DECORATE_LIMIT
}) {
  const draw = (node, key) => {
    switch (node.kind) {
      case "address":
        return /* @__PURE__ */ jsx(React50.Fragment, { children: renderAddress(node.address, { decorated: node.ord < decorateLimit }) }, key);
      case "list":
        return hasAddress(node) || hasTuple(node) ? /* @__PURE__ */ jsx(
          CollapsibleList,
          {
            items: node.items,
            noun: node.items.every((item) => item.kind === "address") ? "address" : "value",
            render: (item, i) => draw(item, `${key}.${i}`)
          },
          key
        ) : /* @__PURE__ */ jsx(Text, { value: node.value }, key);
      case "rows":
        return /* @__PURE__ */ jsx("div", { className: "flex flex-col items-end gap-1.5", children: node.entries.map((entry, i) => /* @__PURE__ */ jsxs("div", { className: "flex w-full items-baseline justify-end gap-2", children: [
          /* @__PURE__ */ jsx("span", { className: "shrink-0 font-mono text-2xs text-muted-foreground", children: entry.name }),
          /* @__PURE__ */ jsx("div", { className: "min-w-0 text-right", children: draw(entry.node, `${key}.${i}`) })
        ] }, i)) }, key);
      default:
        return /* @__PURE__ */ jsx(Text, { value: node.value }, key);
    }
  };
  const ord = counter();
  if (outputs.length === 1) return /* @__PURE__ */ jsx(Fragment, { children: draw(nodeFor(outputs[0], data, ord), "0") });
  return /* @__PURE__ */ jsx(Fragment, { children: draw(
    {
      kind: "rows",
      entries: outputs.map((param, i) => ({
        name: param.name || `[${i}]`,
        node: nodeFor(param, Array.isArray(data) ? data[i] : void 0, ord)
      }))
    },
    "0"
  ) });
}
var RAW_RETURN_LABEL = "Raw return data \u2014 no return type is known";
var MAX_WORDS = 16;
function InferredBadge({ name }) {
  const selector = inferredSelector(name);
  if (!selector) return null;
  return /* @__PURE__ */ jsx(
    Badge,
    {
      variant: "secondary",
      className: "text-3xs font-normal",
      title: `Inferred from bytecode. Called by its selector ${selector}; its real name is not recoverable.`,
      children: "inferred"
    }
  );
}
function RawReturnDataView({ data, chainId, compact = false, className }) {
  const parsed = React50.useMemo(() => typeof data === "string" ? splitReturnData(data) : null, [data]);
  if (!parsed) return /* @__PURE__ */ jsx("code", { className: "break-all text-xs", children: formatReadValue(data) });
  const words = parsed.words.slice(0, MAX_WORDS);
  const hidden = parsed.words.length - words.length;
  const readings = (word) => /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("span", { children: [
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "uint256 " }),
      /* @__PURE__ */ jsx("code", { className: "break-all", children: word.uint.toString() })
    ] }),
    word.address && /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "address " }),
      /* @__PURE__ */ jsx(AddressDisplay, { address: word.address, chain: chainId, copyable: !compact })
    ] }),
    word.bool !== void 0 && /* @__PURE__ */ jsxs("span", { children: [
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "bool " }),
      /* @__PURE__ */ jsx("code", { children: String(word.bool) })
    ] })
  ] });
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-1 text-left text-xs", className), children: [
    /* @__PURE__ */ jsxs("p", { className: "text-muted-foreground", children: [
      RAW_RETURN_LABEL,
      parsed.byteLength > 0 ? ` \xB7 ${parsed.byteLength} byte${parsed.byteLength === 1 ? "" : "s"}` : ""
    ] }),
    parsed.byteLength === 0 ? /* @__PURE__ */ jsx("p", { children: "The call succeeded and returned no data (0x)." }) : /* @__PURE__ */ jsxs(Fragment, { children: [
      compact ? /* @__PURE__ */ jsxs("details", { children: [
        /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-muted-foreground", children: "Hex" }),
        /* @__PURE__ */ jsx("code", { className: "block break-all font-mono", children: parsed.hex })
      ] }) : /* @__PURE__ */ jsx("code", { className: "block break-all font-mono", children: parsed.hex }),
      words.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx("p", { className: "text-muted-foreground", children: "Possible interpretations of each word; these return types are not verified." }),
        /* @__PURE__ */ jsx("ol", { className: cn(compact ? "space-y-0.5" : "space-y-1"), "aria-label": "Return data words", children: words.map((word) => /* @__PURE__ */ jsxs(
          "li",
          {
            className: cn("flex flex-wrap items-baseline gap-x-3 gap-y-0.5", !compact && "rounded-sm border px-2 py-1"),
            children: [
              /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", title: `Bytes ${word.offset}\u2013${word.offset + 31}`, children: [
                "Word ",
                word.index
              ] }),
              readings(word)
            ]
          },
          word.index
        )) })
      ] }),
      hidden > 0 && /* @__PURE__ */ jsxs("p", { className: "text-muted-foreground", children: [
        hidden,
        " more word",
        hidden === 1 ? "" : "s",
        " \u2014 see the hex",
        compact ? "" : " above",
        "."
      ] }),
      parsed.tail && /* @__PURE__ */ jsxs("p", { className: "text-muted-foreground", children: [
        "Trailing bytes not in a whole word: ",
        /* @__PURE__ */ jsx("code", { className: "break-all", children: parsed.tail })
      ] })
    ] })
  ] });
}
var IDENTITY_ABI2 = parseAbi([
  "function name() view returns (string)",
  "function symbol() view returns (string)"
]);
var NEIGHBOUR_PROBE_LIMIT = 8;
function mergeAddressReports(reports, limit = 16, preferred = "read") {
  const sources = Object.keys(reports);
  const explicitReads = sources.filter((key) => key.startsWith("read:args:"));
  const proxyReads = sources.filter((key) => key.startsWith("proxy:"));
  const ordered = [
    ...preferred === "proxy" ? proxyReads : [],
    ...preferred === "state" ? ["state", ...explicitReads, "fields"] : preferred === "fields" ? ["fields", ...explicitReads, "state"] : [...explicitReads, "fields", "state"],
    ...sources.filter((key) => key.startsWith("read:constant:")),
    ...preferred === "proxy" ? [] : proxyReads,
    ...sources
  ];
  const seen = /* @__PURE__ */ new Map();
  for (const source of ordered) for (const address of reports[source] ?? []) {
    if (!/^0x[\da-f]{40}$/i.test(address) || /^0x0{40}$/i.test(address)) continue;
    const key = normalizeAddress(address);
    if (seen.has(key)) continue;
    if (seen.size >= Math.max(0, limit)) return [...seen.values()];
    seen.set(key, address);
  }
  return [...seen.values()];
}
function unnamedAddresses(addresses, opts = {}) {
  const limit = opts.limit ?? NEIGHBOUR_PROBE_LIMIT;
  const out = [];
  for (const address of addresses) {
    if (out.length >= Math.max(0, limit)) break;
    const overlay = opts.resolveOverlay?.(address, opts.chainId);
    if (!overlay?.label && !overlay?.primaryEntityName) out.push(address);
  }
  return out;
}
function nameBehindCatalog(overlay, probed) {
  return overlay?.label || overlay?.primaryEntityName ? void 0 : probed;
}
var CONCURRENCY = 3;
var EMPTY3 = Object.freeze({});
function useNeighbourNames(chainId, addresses, opts = {}) {
  const enabled = opts.enabled ?? true;
  const reader = useContractReader(opts.reader);
  const [names, setNames] = React50.useState(EMPTY3);
  const limit = opts.limit ?? NEIGHBOUR_PROBE_LIMIT;
  const probe = React50.useMemo(
    () => [...new Set(addresses.map((a) => normalizeAddress(a, chainId)))].slice(0, Math.max(0, limit)),
    [addresses, chainId, limit]
  );
  const key = `${chainId}|${requestKey(opts.block)}|${probe.join(",")}`;
  if (useDepsChanged([enabled, key, reader])) setNames(EMPTY3);
  React50.useEffect(() => {
    if (!enabled || probe.length === 0) {
      return void 0;
    }
    let cancelled = false;
    const runBounded = async (items, run) => {
      const out = new Array(items.length);
      let next = 0;
      const worker = async () => {
        for (let i = next++; !cancelled && i < items.length; i = next++) out[i] = await run(items[i]);
      };
      await Promise.all(Array.from({ length: Math.min(CONCURRENCY, items.length) }, worker));
      return out;
    };
    runBounded(
      probe,
      async (address) => {
        try {
          const ref = { chainId, address, block: opts.block };
          const [name, symbol] = await reader.readMany(ref, [
            { abi: IDENTITY_ABI2, functionName: "name", args: [] },
            { abi: IDENTITY_ABI2, functionName: "symbol", args: [] }
          ]);
          const answer = (name?.status === "success" ? asText(name.data) : null) ?? (symbol?.status === "success" ? asText(symbol.data) : null);
          return [address, answer ? safeChainText(answer, { maxChars: 60 }) : ""];
        } catch {
          return [address, ""];
        }
      }
    ).then((pairs) => {
      if (cancelled) return;
      const next = {};
      for (const [address, name] of pairs) if (name) next[address] = name;
      setNames(Object.keys(next).length > 0 ? next : EMPTY3);
    });
    return () => {
      cancelled = true;
    };
  }, [enabled, key, reader]);
  return names;
}
function ReadFunctionCard({
  fn,
  contractRef,
  abi,
  reader,
  drafts,
  resolveOverlay,
  resolveName,
  onAddressesFound,
  maxNamedAddresses = 16,
  onExploreAddress,
  implementation
}) {
  const unavailable = unavailableReadReason(fn, Boolean(implementation));
  const [args, setArgs] = React50.useState(
    !unavailable && (fn.inputs ?? []).length === 0 ? [] : null
  );
  const [draftChanged, setDraftChanged] = React50.useState(false);
  const selectedAbi = React50.useMemo(() => (0, abi_exports.normalizeAbi)([fn, ...abi.errors]), [fn, abi.errors]);
  const { data, hasResult, error, isPending, refetch } = useReaderRead({
    reader,
    ref: contractRef,
    abi: selectedAbi,
    functionName: fn.name,
    args: unavailable ? null : args
  });
  const foundKey = React50.useMemo(() => {
    const seen = /* @__PURE__ */ new Map();
    if (hasResult) for (const address of addressLeaves(fn.outputs, data)) {
      if (!/^0x[\da-f]{40}$/i.test(address) || /^0x0{40}$/i.test(address)) continue;
      if (seen.size >= maxNamedAddresses) break;
      seen.set(normalizeAddress(address, contractRef.chainId), address);
    }
    return [...seen.values()].join(",");
  }, [fn.outputs, data, hasResult, maxNamedAddresses, contractRef.chainId]);
  const source = `read:${fn.inputs.length ? "args" : "constant"}:${functionFormKey(fn)}`;
  React50.useEffect(() => {
    onAddressesFound?.(foundKey ? foundKey.split(",") : [], source);
    return () => onAddressesFound?.([], source);
  }, [foundKey, onAddressesFound, source]);
  const renderAddress = (address, { decorated }) => {
    const overlay = decorated ? resolveOverlay?.(address, contractRef.chainId) : void 0;
    return /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-end gap-1", children: [
      /* @__PURE__ */ jsx(
        AddressDisplay,
        {
          address,
          chain: contractRef.chainId,
          copyable: decorated,
          label: decorated ? nameBehindCatalog(overlay, resolveName?.(address)) : void 0,
          overlay: decorated ? overlay : null
        }
      ),
      onExploreAddress && /^0x[\da-f]{40}$/i.test(address) && !/^0x0{40}$/i.test(address) && /* @__PURE__ */ jsx(
        Button,
        {
          size: "sm",
          variant: "ghost",
          className: "h-6 px-1.5 text-xs",
          "aria-label": `Explore address ${address}`,
          onClick: () => onExploreAddress({ ...contractRef, address }),
          children: "Explore"
        }
      )
    ] });
  };
  return /* @__PURE__ */ jsxs("section", { "aria-label": fn.name, className: "rounded-md border p-3", children: [
    /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-center gap-2", children: [
      /* @__PURE__ */ jsx("code", { className: "font-mono text-sm font-medium", children: fn.name }),
      /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-3xs", children: fn.stateMutability }),
      /* @__PURE__ */ jsx(InferredBadge, { name: fn.name })
    ] }),
    unavailable ? /* @__PURE__ */ jsxs("div", { className: "space-y-2 text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsx("p", { children: unavailable }),
      implementation && fn.name === "proxiableUUID" && onExploreAddress && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: () => onExploreAddress(implementation), children: "Open implementation" })
    ] }) : /* @__PURE__ */ jsx(
      FunctionForm,
      {
        fn,
        drafts,
        actionLabel: "Call",
        busy: isPending,
        onDraftChange: () => setDraftChanged(true),
        onValidationError: () => setDraftChanged(true),
        onSubmit: (next) => {
          setDraftChanged(false);
          if (requestKey(next) === requestKey(args)) refetch();
          else setArgs(next);
        }
      }
    ),
    hasResult && /* @__PURE__ */ jsxs("div", { className: "mt-2", children: [
      draftChanged && /* @__PURE__ */ jsx("p", { role: "status", className: "text-xs text-muted-foreground", children: "Previous result; edited inputs have not been called." }),
      error && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Previous result; the latest call failed." }),
      returnsRawData(fn) ? /* @__PURE__ */ jsx("div", { className: "rounded-sm bg-muted/50 p-2", children: /* @__PURE__ */ jsx(RawReturnDataView, { data, chainId: contractRef.chainId }) }) : fn.outputs.length > 1 && Array.isArray(data) && data.length === fn.outputs.length ? /* @__PURE__ */ jsx("dl", { className: "space-y-2 rounded-sm bg-muted/50 p-2 text-xs", children: fn.outputs.map((output, i) => /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsxs("dt", { className: "mb-1 text-muted-foreground", children: [
          output.name || `Output ${i + 1}`,
          " ",
          /* @__PURE__ */ jsx("code", { children: output.type })
        ] }),
        /* @__PURE__ */ jsx("dd", { children: /* @__PURE__ */ jsx(ReadValue, { outputs: [output], data: data[i], renderAddress, decorateLimit: maxNamedAddresses }) })
      ] }, i)) }) : /* @__PURE__ */ jsx("div", { className: "overflow-x-auto rounded-sm bg-muted/50 p-2 text-xs", children: /* @__PURE__ */ jsx(ReadValue, { outputs: fn.outputs, data, renderAddress, decorateLimit: maxNamedAddresses }) })
    ] }),
    error && /* @__PURE__ */ jsx("p", { role: "alert", className: "mt-2 text-xs text-destructive", children: formatReadError(error) })
  ] });
}
function ReadPanel({
  contract: contractRef,
  abi,
  isGuess = false,
  failures,
  abiResolving,
  onFetchAbi,
  fetchingAbi,
  reader: readerOverride,
  className,
  resolveOverlay,
  resolveName,
  onAddressesFound,
  maxNamedAddresses,
  onExploreAddress,
  implementation
}) {
  const reader = useContractReader(readerOverride);
  const drafts = useFunctionDrafts(`${contractRef.chainId}:${normalizeAddress(contractRef.address, contractRef.chainId)}`);
  const [filter, setFilter] = React50.useState("");
  const reads = React50.useMemo(() => {
    if (!abi) return [];
    const q = filter.trim().toLowerCase();
    return q ? abi.reads.filter((fn) => fn.name.toLowerCase().includes(q)) : abi.reads;
  }, [abi, filter]);
  if (!abi || abiResolving && !isGuess && abi.reads.length === 0) return /* @__PURE__ */ jsx(NoAbiState, { subject: "read functions", failures, resolving: abiResolving, onFetch: onFetchAbi, fetching: fetchingAbi, className });
  if (abi.reads.length === 0 && isGuess) {
    return /* @__PURE__ */ jsx(GuessedAbiNotice, { failures, onFetch: onFetchAbi, fetching: fetchingAbi, className });
  }
  if (abi.reads.length === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No read functions", description: "This ABI exposes no view/pure functions.", size: "sm" });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    isGuess && /* @__PURE__ */ jsx(GuessedAbiNotice, { failures, onFetch: onFetchAbi, fetching: fetchingAbi, consequence: "Reads the contract doesn't implement will revert." }),
    /* @__PURE__ */ jsx(
      Input,
      {
        "aria-label": "Filter read functions",
        value: filter,
        placeholder: "Filter functions\u2026",
        onChange: (e) => setFilter(e.target.value),
        className: "h-8"
      }
    ),
    /* @__PURE__ */ jsx(ScrollArea, { className: "max-h-[480px] pr-2", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      reads.length === 0 && /* @__PURE__ */ jsx(EmptyState, { title: "No matching read functions", description: "Try a different function name.", size: "sm" }),
      abi.reads.map((fn) => /* @__PURE__ */ jsx("div", { hidden: !reads.includes(fn), children: /* @__PURE__ */ jsx(
        ReadFunctionCard,
        {
          fn: completeKnownRead(fn),
          implementation,
          contractRef,
          abi,
          reader,
          drafts,
          resolveOverlay,
          resolveName,
          onAddressesFound,
          maxNamedAddresses,
          onExploreAddress
        }
      ) }, `${contractRef.chainId}:${normalizeAddress(contractRef.address, contractRef.chainId)}:${functionFormKey(fn)}`))
    ] }) })
  ] });
}
var STATUS_LABEL = {
  idle: "",
  signing: "Awaiting signature\u2026",
  pending: "Pending\u2026",
  sent: "Sent \u2014 not confirmed",
  success: "Confirmed",
  reverted: "Reverted on chain",
  error: "Failed"
};
var FAILED_STATUSES = /* @__PURE__ */ new Set([
  "error",
  "reverted"
]);
function WriteFunctionCard({
  fn,
  contractRef,
  abi,
  chainId,
  disabled = false,
  active,
  matches,
  drafts
}) {
  const { write, status, hash, error } = useContractWrite(chainId);
  const selectedAbi = React50.useMemo(() => [fn, ...abi.errors], [fn, abi.errors]);
  const [submitted, setSubmitted] = React50.useState(null);
  const [draftChanged, setDraftChanged] = React50.useState(false);
  const [encodeError, setEncodeError] = React50.useState(null);
  const busy = status === "signing" || status === "pending";
  const unavailable = unavailableReadReason(fn);
  const onSubmit = React50.useCallback(
    (args, value) => {
      if (disabled || !active || busy || unavailable) return;
      let call;
      try {
        call = contractWriteCall(contractRef.address, selectedAbi, fn, args);
      } catch (err) {
        setEncodeError(err instanceof Error ? err.message : String(err));
        return;
      }
      setEncodeError(null);
      setSubmitted(JSON.stringify({
        function: (0, abi_exports.functionSignature)(fn),
        args,
        ...value != null ? { valueWei: value } : {},
        // Shown for an inferred function: what is signed is these bytes, not a name.
        ...call.data ? { calldata: call.data } : {}
      }, (_, item) => typeof item === "bigint" ? item.toString() : item, 2));
      setDraftChanged(false);
      void write(call, value);
    },
    [write, contractRef.address, selectedAbi, fn, disabled, active, busy, unavailable]
  );
  if (!active && status === "idle") return null;
  return /* @__PURE__ */ jsxs("section", { "aria-label": active ? fn.name : `${fn.name} \u2014 previous interface`, hidden: active && !matches, className: "rounded-md border p-3", children: [
    /* @__PURE__ */ jsxs("div", { className: "mb-2 flex items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("code", { className: "font-mono text-sm font-medium", children: fn.name }),
        /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-3xs", children: fn.stateMutability }),
        /* @__PURE__ */ jsx(InferredBadge, { name: fn.name })
      ] }),
      status !== "idle" && /* @__PURE__ */ jsx(
        Badge,
        {
          variant: FAILED_STATUSES.has(status) ? "destructive" : status === "success" ? "secondary" : "outline",
          className: "text-3xs",
          children: STATUS_LABEL[status]
        }
      )
    ] }),
    active ? /* @__PURE__ */ jsx(FunctionForm, { fn, drafts, actionLabel: "Send", busy, disabled: disabled || Boolean(unavailable), onSubmit, onDraftChange: () => setDraftChanged(true) }) : /* @__PURE__ */ jsx("p", { role: "note", className: "text-xs text-muted-foreground", children: "Previous interface: this function changed or was removed. Its transaction is still tracked below." }),
    submitted && /* @__PURE__ */ jsxs("div", { className: "mt-2 text-xs", children: [
      draftChanged && active && /* @__PURE__ */ jsx("p", { role: "note", className: "mb-1 text-muted-foreground", children: "Inputs changed; the transaction below uses the submitted values." }),
      /* @__PURE__ */ jsxs("details", { open: draftChanged || !active, children: [
        /* @__PURE__ */ jsx("summary", { className: "cursor-pointer", children: "Submitted call" }),
        /* @__PURE__ */ jsx("pre", { className: "mt-1 whitespace-pre-wrap break-words font-mono", children: submitted })
      ] })
    ] }),
    unavailable && /* @__PURE__ */ jsx("p", { className: "mt-2 text-xs text-muted-foreground", children: unavailable }),
    encodeError && /* @__PURE__ */ jsx("p", { role: "alert", className: "mt-2 whitespace-pre-wrap break-words text-xs text-destructive", children: encodeError }),
    hash && /* @__PURE__ */ jsxs("div", { className: "mt-2 flex items-center gap-1 text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsx("span", { children: "tx" }),
      /* @__PURE__ */ jsx(HashDisplay, { hash, chain: contractRef.chainId, type: "tx", copyable: true })
    ] }),
    error && /* @__PURE__ */ jsx("p", { role: "alert", className: "mt-2 whitespace-pre-wrap break-words text-xs text-destructive", children: error.message })
  ] });
}
function WritePanel({
  contract: contractRef,
  abi,
  isGuess = false,
  failures,
  abiResolving,
  onFetchAbi,
  fetchingAbi,
  className
}) {
  const [filter, setFilter] = React50.useState("");
  const scope = `${contractRef.chainId}:${normalizeAddress(contractRef.address, contractRef.chainId)}`;
  const drafts = useFunctionDrafts(scope);
  const [history, setHistory] = React50.useState(() => ({ scope, abi, entries: new Map(
    abi?.writes.map((fn) => [functionFormKey(fn), { fn, abi }]) ?? []
  ) }));
  if (history.scope !== scope || abi && history.abi !== abi) {
    const entries = history.scope === scope ? new Map(history.entries) : /* @__PURE__ */ new Map();
    for (const fn of abi?.writes ?? []) entries.set(functionFormKey(fn), { fn, abi });
    setHistory({ scope, abi, entries });
  }
  const displayAbi = abi ?? (history.scope === scope ? history.abi : null);
  const activeKeys = new Set(displayAbi?.writes.map(functionFormKey));
  const q = filter.trim().toLowerCase();
  const writes = displayAbi?.writes.filter((fn) => fn.name.toLowerCase().includes(q)) ?? [];
  const chainIdNum = (0, contract_exports.chainIdToNumber)(contractRef.chainId);
  if (!displayAbi || abiResolving && !isGuess && displayAbi.writes.length === 0 && history.entries.size === 0) return /* @__PURE__ */ jsx(NoAbiState, { subject: "write functions", failures, resolving: abiResolving, onFetch: onFetchAbi, fetching: fetchingAbi, className });
  if (displayAbi.writes.length === 0 && history.entries.size === 0 && isGuess) {
    return /* @__PURE__ */ jsx(GuessedAbiNotice, { failures, onFetch: onFetchAbi, fetching: fetchingAbi, className });
  }
  if (displayAbi.writes.length === 0 && history.entries.size === 0) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No write functions", description: "This ABI exposes no state-changing functions.", size: "sm" });
  }
  if (chainIdNum === void 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Can't sign on this chain",
        description: `"${contractRef.chainId}" doesn't resolve to a known EVM chain id, so there is no way to be sure a signature would go to this network rather than whichever one your wallet is on. Reads are unaffected.`,
        size: "sm",
        className
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    contractRef.block && /* @__PURE__ */ jsxs("p", { role: "note", className: "text-xs text-muted-foreground", children: [
      "Reads are pinned to block ",
      contractRef.block.blockNumber?.toString() ?? contractRef.block.blockTag,
      ". Transactions execute against the current chain state."
    ] }),
    !abi && /* @__PURE__ */ jsx(NoAbiState, { subject: "new transactions", failures, resolving: abiResolving }),
    isGuess && /* @__PURE__ */ jsx(GuessedAbiNotice, { failures, onFetch: onFetchAbi, fetching: fetchingAbi, consequence: "Sending is disabled until a real ABI is resolved \u2014 these selectors may not exist on this contract." }),
    /* @__PURE__ */ jsx(Input, { "aria-label": "Filter write functions", value: filter, placeholder: "Filter functions\u2026", onChange: (e) => setFilter(e.target.value), className: "h-8" }),
    /* @__PURE__ */ jsx(ScrollArea, { className: "max-h-[480px] pr-2", children: /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-2", children: [
      writes.length === 0 && /* @__PURE__ */ jsx(EmptyState, { title: displayAbi.writes.length ? "No matching write functions" : "No write functions", description: displayAbi.writes.length ? "Try a different function name." : "This ABI exposes no state-changing functions.", size: "sm" }),
      [...history.entries].map(([key, entry]) => /* @__PURE__ */ jsx(
        WriteFunctionCard,
        {
          fn: entry.fn,
          contractRef,
          abi: entry.abi,
          chainId: chainIdNum,
          disabled: isGuess || !abi || abiResolving,
          active: activeKeys.has(key),
          matches: entry.fn.name.toLowerCase().includes(q),
          drafts
        },
        `${scope}:${key}`
      ))
    ] }) })
  ] });
}
function fieldAddresses(field) {
  if (field.outcome?.status !== "success") return [];
  return addressLeaves(field.fn.outputs ?? [], field.outcome.data);
}
var DEFAULT_MAX_NAMED = 16;
function ContractFieldsGrid({
  contract: contractRef,
  abi,
  isGuess = false,
  isProxy = false,
  failures,
  abiResolving,
  onFetchAbi,
  fetchingAbi,
  resolveOverlay,
  onAddressesFound,
  maxNamedAddresses = DEFAULT_MAX_NAMED,
  resolveName,
  className
}) {
  const { fields, needArguments, isPending, error, refetch } = useContractFields(
    contractRef,
    abi,
    { isProxy }
  );
  const found = React50.useMemo(() => {
    const seen = /* @__PURE__ */ new Map();
    for (const field of fields) {
      for (const address of fieldAddresses(field)) {
        const key = normalizeAddress(address, contractRef.chainId);
        if (seen.has(key)) continue;
        if (seen.size >= maxNamedAddresses) break;
        seen.set(key, address);
      }
    }
    return [...seen.values()];
  }, [fields, maxNamedAddresses, contractRef.chainId]);
  const foundKey = found.join(",");
  React50.useEffect(() => {
    if (!onAddressesFound) return;
    onAddressesFound(foundKey ? foundKey.split(",") : [], "fields");
    return () => onAddressesFound([], "fields");
  }, [foundKey, onAddressesFound]);
  const renderFieldAddress = React50.useCallback(
    (address, { decorated }) => {
      const overlay = decorated ? resolveOverlay?.(address, contractRef.chainId) : void 0;
      return /* @__PURE__ */ jsx(
        AddressDisplay,
        {
          address,
          chain: contractRef.chainId,
          copyable: decorated,
          label: decorated ? nameBehindCatalog(overlay, resolveName?.(address)) : void 0,
          overlay: decorated ? overlay : null
        }
      );
    },
    [contractRef.chainId, resolveName, resolveOverlay]
  );
  const skipped = abi?.reads.filter((fn) => unavailableReadReason(fn, isProxy)) ?? [];
  const skippedNote = skipped.length ? `${skipped.length} functions require a different call context or a verified signature. See the Read tab for details.` : void 0;
  const argNote = omittedReadsNote(needArguments);
  if (!abi || abiResolving && !isGuess && fields.length === 0) return /* @__PURE__ */ jsx(NoAbiState, { subject: "fields", failures, resolving: abiResolving, onFetch: onFetchAbi, fetching: fetchingAbi, className });
  if (fields.length === 0 && isGuess) {
    return /* @__PURE__ */ jsx(GuessedAbiNotice, { failures, onFetch: onFetchAbi, fetching: fetchingAbi, className });
  }
  if (fields.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No fields",
        description: argNote ? `Nothing here can be read without arguments. ${argNote}` : skippedNote ?? "No zero-argument view functions to read.",
        size: "sm"
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    skippedNote && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: skippedNote }),
    isGuess && /* @__PURE__ */ jsx(GuessedAbiNotice, { failures, onFetch: onFetchAbi, fetching: fetchingAbi, consequence: "Getters this contract doesn't implement will fail; open their error details to see why." }),
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold", children: "Fields" }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-xs text-muted-foreground", children: [
        isPending && /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
        /* @__PURE__ */ jsx("button", { type: "button", className: "hover:text-foreground disabled:opacity-50", disabled: isPending, onClick: refetch, children: "Refresh" })
      ] })
    ] }),
    error && /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2", children: [
      /* @__PURE__ */ jsxs("p", { className: "text-xs text-destructive", children: [
        "Couldn't read these fields: ",
        formatReadError(error),
        fields.some((field) => field.outcome !== void 0) && " Displayed values are from the previous successful read."
      ] }),
      /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", disabled: isPending, onClick: refetch, children: "Retry" })
    ] }),
    argNote && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: argNote }),
    /* @__PURE__ */ jsx("div", { className: "divide-y rounded-md border", children: fields.map((field, i) => {
      const { fn, outcome } = field;
      const hasAddress2 = fieldAddresses(field).length > 0;
      return /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-3 px-3 py-2", children: [
        /* @__PURE__ */ jsxs("div", { className: "flex shrink-0 items-center gap-1.5", children: [
          /* @__PURE__ */ jsx("code", { className: "font-mono text-xs font-medium", children: fn.name }),
          /* @__PURE__ */ jsx(InferredBadge, { name: fn.name })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "min-w-0 flex-1 text-right", children: outcome?.status === "success" && returnsRawData(fn) ? (
          // A getter recovered from bytecode, called by its selector: the
          // bytes it returned and their readings, never a claimed type.
          /* @__PURE__ */ jsx(RawReturnDataView, { data: outcome.data, chainId: contractRef.chainId, compact: true })
        ) : outcome?.status === "success" && hasAddress2 ? /* @__PURE__ */ jsx("div", { className: "flex justify-end", children: /* @__PURE__ */ jsx(
          ReadValue,
          {
            outputs: fn.outputs ?? [],
            data: outcome.data,
            renderAddress: renderFieldAddress
          }
        ) }) : outcome?.status === "success" ? /* @__PURE__ */ jsx("code", { className: "break-all text-xs", children: formatReadValue(outcome.data) }) : outcome?.status === "failure" ? /* @__PURE__ */ jsxs("details", { className: "text-xs text-destructive", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer", children: "Read failed" }),
          /* @__PURE__ */ jsx("p", { className: "mt-1 whitespace-pre-wrap break-words text-left", children: outcome.error ? formatReadError(outcome.error) : "The reader did not provide error details." })
        ] }) : /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "\u2014" }) })
      ] }, `${fn.name}-${i}`);
    }) })
  ] });
}
var GROUPS = {
  function: "Functions",
  event: "Events",
  error: "Errors"
};
function CompareAbisView({ items, className }) {
  const { columns, rows } = React50.useMemo(() => {
    const sets = /* @__PURE__ */ new Map();
    const union = /* @__PURE__ */ new Set();
    for (const item of items) {
      const set = (0, abi_exports.signatureSet)(item.abi);
      sets.set(item.id, set);
      for (const sig of set) union.add(sig);
    }
    const cols = items.map((item) => ({
      id: item.id,
      header: item.label,
      subject: item.id
    }));
    const sigRows = [...union].sort().map((sig) => {
      const [kind, ...rest] = sig.split(":");
      const signature = rest.join(":");
      return {
        id: sig,
        // Three kinds, not two. Errors were absent from `signatureSet` entirely,
        // so a version that added a revert error compared as identical; grouping
        // them with functions would have hidden the difference a second way.
        group: GROUPS[kind ?? ""] ?? "Functions",
        label: /* @__PURE__ */ jsx("code", { className: "text-xs", children: signature }),
        value: (subject) => sets.get(subject)?.has(sig) ? "1" : "0",
        render: (subject) => sets.get(subject)?.has(sig) ? /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "text-3xs", children: "\u2713" }) : /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "\u2014" })
      };
    });
    return { columns: cols, rows: sigRows };
  }, [items]);
  if (items.length < 2) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Pin at least two ABIs",
        description: "Pin ABIs (different contracts, blocks, or chains) to diff their interfaces.",
        size: "sm"
      }
    );
  }
  return /* @__PURE__ */ jsx(CompareView, { columns, rows, className });
}

// src/state/diff.ts
function diffContractState(before, after) {
  return diffContractStates([before, after]).map((row) => ({
    field: row.field,
    before: row.cells[0]?.value,
    after: row.cells[1]?.value,
    change: row.cells[1].change
  }));
}
function changedRows(rows) {
  return rows.filter((r) => isChange(r.change));
}
function changeCount(rows) {
  return changedRows(rows).length;
}
function diffContractStates(snapshots) {
  const fields = /* @__PURE__ */ new Set();
  for (const snap of snapshots) {
    for (const key of [...Object.keys(snap.values), ...Object.keys(snap.errors ?? {})]) fields.add(key);
  }
  const rows = [];
  for (const field of fields) {
    const cells = [];
    let changed = false;
    let prev;
    let prevAvailable = false;
    snapshots.forEach((snap, i) => {
      const cur = snap.values[field];
      const error = snap.error ?? snap.errors?.[field];
      const available = !snap.pending && error === void 0;
      let change;
      if (!available || i > 0 && !prevAvailable) {
        change = "unavailable";
      } else if (i === 0) {
        change = "unchanged";
      } else {
        const hasPrev = prev !== void 0;
        const hasCur = cur !== void 0;
        if (hasPrev && !hasCur) change = "removed";
        else if (!hasPrev && hasCur) change = "added";
        else if (cur !== prev) change = "changed";
        else change = "unchanged";
      }
      if (isChange(change)) changed = true;
      cells.push({ value: cur, change, error, pending: snap.pending });
      prev = cur;
      prevAvailable = available;
    });
    rows.push({ field, cells, changed });
  }
  return rows.sort((a, b) => a.field.localeCompare(b.field));
}
function isChange(change) {
  return change === "added" || change === "removed" || change === "changed";
}
function changedMultiRows(rows) {
  return rows.filter((r) => r.changed);
}
var CELL_STYLES = {
  added: "bg-finality-finalized/10",
  removed: "bg-risk-high/10",
  changed: "bg-risk-medium/10",
  unchanged: "",
  unavailable: "bg-muted/30"
};
function resolveSnapshots(props) {
  if (props.snapshots && props.snapshots.length > 0) return props.snapshots;
  return [props.before, props.after, props.latest].filter(
    (s) => s != null
  );
}
function StateDiffTable({
  rows,
  headers,
  incomplete,
  pending,
  renderHeader,
  renderValue,
  renderField,
  renderAbsent
}) {
  if (rows.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: pending ? "Reading snapshots\u2026" : incomplete ? "No confirmed differences" : "No differences",
        description: pending ? "The comparison will update as the selected block reads finish." : incomplete ? "Some reads are unavailable. Review All fields for their status." : "The snapshots have identical field values.",
        size: "sm"
      }
    );
  }
  return /* @__PURE__ */ jsx("div", { className: "scroll-fade-y max-h-[480px] overflow-auto rounded-md border", children: /* @__PURE__ */ jsxs(Table, { className: "w-full text-sm", children: [
    /* @__PURE__ */ jsx(TableHeader, { className: "sticky top-0 bg-card", children: /* @__PURE__ */ jsxs(TableRow, { className: "border-b text-left text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsx(TableHead, { className: "px-3 py-2 font-medium", children: "Field" }),
      headers.map((h, i) => /* @__PURE__ */ jsx(TableHead, { className: "min-w-36 px-3 py-2 font-medium", children: renderHeader?.(i, h) ?? h }, i))
    ] }) }),
    /* @__PURE__ */ jsx(TableBody, { children: rows.map((row) => /* @__PURE__ */ jsxs(TableRow, { className: "border-b align-top", children: [
      /* @__PURE__ */ jsx(TableCell, { className: "min-w-48 px-3 py-2", children: /* @__PURE__ */ jsx("div", { className: "max-w-sm break-words [overflow-wrap:anywhere]", children: renderField?.(row.field) ?? /* @__PURE__ */ jsx("code", { className: "text-xs", children: row.field }) }) }),
      row.cells.map((cell, i) => /* @__PURE__ */ jsx(TableCell, { className: cn("px-3 py-2", CELL_STYLES[cell.change]), children: cell.pending ? /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "Loading\u2026" }) : cell.error ? /* @__PURE__ */ jsxs("details", { className: "max-w-xs text-xs text-destructive", children: [
        /* @__PURE__ */ jsx("summary", { className: "cursor-pointer", children: "Read failed" }),
        /* @__PURE__ */ jsx("p", { className: "mt-1 whitespace-pre-wrap break-words", children: cell.error })
      ] }) : /* @__PURE__ */ jsx(
        "code",
        {
          className: cn(
            "break-all text-xs",
            cell.change === "unchanged" ? "text-muted-foreground" : "text-foreground"
          ),
          children: cell.value === void 0 ? renderAbsent?.(row.field, i) ?? "\u2014" : renderValue?.(row.field, i, cell.value) ?? cell.value
        }
      ) }, i))
    ] }, row.field)) })
  ] }) });
}
function ContractStateHighlighter(props) {
  const { className, snapshots: given, before, after, latest } = props;
  const snapshots = React50.useMemo(
    () => resolveSnapshots({ snapshots: given, before, after, latest }),
    [given, before, after, latest]
  );
  const rows = React50.useMemo(() => diffContractStates(snapshots), [snapshots]);
  const changed = React50.useMemo(() => changedMultiRows(rows), [rows]);
  const pending = snapshots.some((s) => s.pending);
  const incomplete = snapshots.some((s) => s.pending || s.error || Object.keys(s.errors ?? {}).length > 0);
  if (snapshots.length < 2) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Pick at least two snapshots",
        description: "Compare a contract's state across blocks (before / after / latest).",
        size: "sm",
        className
      }
    );
  }
  const headers = snapshots.map((snap, i) => snap.label ?? ["Before", "After", "Latest"][i] ?? `#${i + 1}`);
  if (rows.length === 0 && !incomplete) {
    return /* @__PURE__ */ jsx(EmptyState, { title: "No fields to compare", description: "This ABI has no readable no-argument fields. Functions with arguments can be called on the Read tab.", size: "sm", className });
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-2 text-sm", children: [
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: headers.join(" \u2192 ") }),
      /* @__PURE__ */ jsxs(Badge, { variant: changed.length > 0 ? "secondary" : "outline", children: [
        changed.length,
        " ",
        incomplete ? "confirmed " : "",
        changed.length === 1 ? "change" : "changes"
      ] })
    ] }),
    incomplete && /* @__PURE__ */ jsxs("div", { role: "status", className: "space-y-1 text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsx("p", { children: "Comparison incomplete. Failed or unfinished reads are not counted as changes." }),
      snapshots.map((snapshot, i) => snapshot.pending ? /* @__PURE__ */ jsxs("p", { children: [
        headers[i],
        ": loading\u2026"
      ] }, i) : snapshot.error ? /* @__PURE__ */ jsxs("p", { className: "text-destructive", children: [
        headers[i],
        ": ",
        snapshot.error
      ] }, i) : Object.keys(snapshot.errors ?? {}).length > 0 ? /* @__PURE__ */ jsxs("p", { className: "text-destructive", children: [
        headers[i],
        ": ",
        Object.keys(snapshot.errors ?? {}).length,
        " field reads failed. Open All fields for details."
      ] }, i) : null)
    ] }),
    /* @__PURE__ */ jsxs(Tabs, { defaultValue: props.defaultTab ?? "changes", children: [
      /* @__PURE__ */ jsxs(TabsList, { children: [
        /* @__PURE__ */ jsxs(TabsTrigger, { value: "changes", children: [
          "Changes (",
          changed.length,
          ")"
        ] }),
        /* @__PURE__ */ jsxs(TabsTrigger, { value: "all", children: [
          "All fields (",
          rows.length,
          ")"
        ] })
      ] }),
      /* @__PURE__ */ jsx(TabsContent, { value: "changes", className: "mt-3", children: /* @__PURE__ */ jsx(StateDiffTable, { rows: changed, headers, incomplete, pending, renderHeader: props.renderHeader, renderValue: props.renderValue, renderField: props.renderField, renderAbsent: props.renderAbsent }) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "all", className: "mt-3", children: /* @__PURE__ */ jsx(StateDiffTable, { rows, headers, incomplete, pending, renderHeader: props.renderHeader, renderValue: props.renderValue, renderField: props.renderField, renderAbsent: props.renderAbsent }) })
    ] })
  ] });
}
function SnapshotLoader({ id: id2, column, abi, resolution, onState }) {
  const historical = Boolean(resolution);
  const proxy = useProxyChain(column.ref, {
    enabled: historical && !column.invalid,
    discover: resolution?.options?.useProxies !== false && resolution?.options?.discoverProxies !== false,
    resolver: resolution?.proxyResolver
  });
  const implementation = proxy.chain?.effectiveAddress ?? column.ref.address;
  const abiRef = React50.useMemo(() => ({ ...column.ref, address: implementation }), [column.ref.chainId, column.ref.address, column.ref.block, implementation]);
  const resolved = useAbiResolver({
    ...resolution,
    ref: abiRef,
    inlineEntries: resolution?.inline?.get(abiRef),
    inlineMeta: resolution?.inline?.getMeta(abiRef),
    enabled: historical && !column.invalid && !proxy.isResolving && !proxy.error && Boolean(proxy.chain)
  });
  const confidence = useReadConfidence();
  const rejected = historical && resolved.status === "ready" && (!confidence.accepts(recordedAbiConfidence(resolved.resolved?.meta)) || resolved.resolved?.isGuess);
  const selectedAbi = historical ? rejected ? null : resolved.resolved?.abi ?? null : abi;
  const resolving = historical && (proxy.isResolving || resolved.status === "loading");
  const resolutionError = React50.useMemo(
    () => historical ? proxy.error ?? resolved.error ?? (rejected ? new Error("No sufficiently trusted ABI is available at this checkpoint.") : resolved.status === "empty" ? new Error("No ABI is available at this checkpoint.") : null) : null,
    [historical, proxy.error, resolved.error, resolved.status, rejected]
  );
  const read = useContractFields(column.ref, selectedAbi, {
    enabled: !column.invalid && !resolving && !resolutionError,
    isProxy: historical ? proxy.chain?.isProxy : column.isProxy
  });
  const isPending = resolving || read.isPending;
  const error = resolutionError ?? read.error;
  const fields = read.fields;
  React50.useEffect(() => {
    const values = {};
    const errors = {};
    const data = {};
    for (const field of fields) {
      if (field.outcome?.status === "success") {
        values[field.fn.name] = formatReadValue(field.outcome.data);
        data[field.fn.name] = field.outcome.data;
      } else if (field.outcome?.status === "failure") errors[field.fn.name] = field.outcome.error ? formatReadError(field.outcome.error) : "Read failed";
      else if (!isPending && !error && !column.invalid) errors[field.fn.name] = "The reader returned no result for this field.";
    }
    onState(id2, {
      values,
      errors,
      data,
      abi: selectedAbi ?? void 0,
      implementation: historical ? implementation : void 0,
      pending: isPending,
      error: column.invalid ? "Invalid block selector." : error ? formatReadError(error) : void 0
    });
  }, [id2, fields, isPending, error, column.invalid, onState, selectedAbi, historical, implementation]);
  return null;
}
function ContractSnapshotComparison({ columns, abi, resolution, revision = 0, onAddressesFound, enrichmentChain }) {
  const reader = useContractReader();
  const [states, setStates] = React50.useState({});
  const [generation, setGeneration] = React50.useState(0);
  const keyed = columns.map((column) => ({ column, id: `${(0, contract_exports.contractRefKey)(column.ref)}:${Boolean(column.invalid)}:${Boolean(column.isProxy)}` }));
  const identity = JSON.stringify([keyed.map((c) => c.id), abi.entries, revision]);
  if (useDepsChanged([
    reader,
    identity,
    resolution?.provider,
    resolution?.proxyResolver,
    JSON.stringify(resolution?.identity),
    JSON.stringify(resolution?.options)
  ])) {
    setStates({});
    setGeneration((n) => n + 1);
  }
  const setColumn = React50.useCallback((id2, snapshot) => {
    setStates((prev) => ({ ...prev, [id2]: snapshot }));
  }, []);
  const addresses = keyed.flatMap(({ id: id2, column }) => {
    if (enrichmentChain && column.ref.chainId !== enrichmentChain) return [];
    const found = [column.ref.address];
    for (const entry of (states[id2]?.abi ?? abi).entries) {
      if (entry.type === "function" && !entry.inputs.length && Object.prototype.hasOwnProperty.call(states[id2]?.data ?? {}, entry.name)) {
        found.push(...addressLeaves(entry.outputs, states[id2]?.data?.[entry.name]));
      }
    }
    return found;
  });
  const addressKey2 = [...new Set(addresses)].slice(0, 64).join(",");
  React50.useEffect(() => {
    onAddressesFound?.(addressKey2 ? addressKey2.split(",") : [], "compare:values");
    return () => onAddressesFound?.([], "compare:values");
  }, [addressKey2, onAddressesFound]);
  const unique = [...new Map(keyed.map((c) => [c.id, c])).values()];
  const snapshots = keyed.map(({ id: id2, column }) => ({
    ...states[id2] ?? { values: {}, pending: !column.invalid, error: column.invalid ? "Invalid block selector." : void 0 },
    label: column.label
  }));
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    unique.map(({ id: id2, column }) => /* @__PURE__ */ jsx(SnapshotLoader, { id: id2, column, abi, resolution, onState: setColumn }, `${generation}:${id2}`)),
    /* @__PURE__ */ jsx(
      ContractStateHighlighter,
      {
        snapshots,
        renderHeader: (index, label) => /* @__PURE__ */ jsxs(Fragment, { children: [
          columns[index]?.heading ?? label,
          states[keyed[index]?.id ?? ""]?.implementation && /* @__PURE__ */ jsxs("span", { className: "mt-1 block text-xs text-muted-foreground", children: [
            "ABI at this block: ",
            /* @__PURE__ */ jsx(AddressDisplay, { address: states[keyed[index].id].implementation, chain: columns[index].ref.chainId })
          ] })
        ] }),
        renderValue: (field, index, fallback) => {
          const column = keyed[index];
          const data = column && states[column.id]?.data;
          const entry = (column && states[column.id]?.abi || abi).entries.find((entry2) => entry2.type === "function" && entry2.name === field && entry2.inputs.length === 0);
          if (!column || !data || !Object.prototype.hasOwnProperty.call(data, field) || !entry || entry.type !== "function") return fallback;
          if (entry.outputs.length === 0) return fallback;
          const blockNumber = column.column.ref.block?.blockNumber;
          return /* @__PURE__ */ jsx(AddressDisplayProvider, { proxyContext: blockNumber == null ? void 0 : {
            chain_id: column.column.ref.chainId,
            block_number: Number(blockNumber)
          }, children: /* @__PURE__ */ jsx(
            ReadValue,
            {
              outputs: entry.outputs,
              data: data[field],
              renderAddress: (address, { decorated }) => /* @__PURE__ */ jsx(AddressDisplay, { address, chain: column.column.ref.chainId, copyable: decorated })
            }
          ) });
        }
      }
    )
  ] });
}
function CompareFieldsView({ refs, abi, className, enrichmentChain, onAddressesFound }) {
  if (!abi || refs.length < 2) return /* @__PURE__ */ jsx(EmptyState, { title: "Pick at least two", description: "Compare field values across blocks, contracts, or chains.", size: "sm" });
  return /* @__PURE__ */ jsx("div", { className, children: /* @__PURE__ */ jsx(ContractSnapshotComparison, { abi, enrichmentChain, onAddressesFound, columns: refs.map((ref) => ({
    heading: /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(AddressDisplay, { address: ref.address, chain: ref.chainId, fallbackLabel: ref.label }),
      " \xB7 ",
      (0, contract_exports.blockKey)(ref.block)
    ] }),
    ref,
    label: ref.label ?? `${ref.chainId} \xB7 ${shortenAddress(ref.address)} @ ${(0, contract_exports.blockKey)(ref.block)}`
  })) }) });
}
function StateSnapshotContext({ node, children }) {
  return /* @__PURE__ */ jsx(AddressDisplayProvider, { proxyContext: node.block_number == null ? void 0 : {
    chain_id: node.chain_id,
    block_number: node.block_number,
    block_hash: node.block_hash ?? void 0
  }, children });
}
var ProxyDiscoveryContext = React50.createContext(null);
var diagnosticSchema = z.object({ status: z.enum(["failed", "deferred", "not_detected", "non_delegating", "resolved"]), message: z.string(), checks: z.array(z.object({ strategy: z.string(), status: z.string() })) });
function StateProxyDiscovery({ node }) {
  const client = React50.useContext(ProxyDiscoveryContext);
  const parsed = diagnosticSchema.safeParse(node.proxy_provenance?.discovery);
  const [run, setRun] = React50.useState(null);
  const [busy, setBusy] = React50.useState(false);
  const [error, setError] = React50.useState("");
  const activeClient = React50.useRef(client);
  activeClient.current = client;
  const [owner2, setOwner] = React50.useState(client);
  if (owner2 !== client) {
    setOwner(client);
    setRun(null);
    setBusy(false);
    setError("");
  }
  const alive = React50.useRef(true);
  React50.useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  const subject = node.block_number != null && node.block_hash ? { chain_id: node.chain_id, address: node.address, block_number: node.block_number, block_hash: node.block_hash } : null;
  const check = async () => {
    if (!client || !run || busy) return;
    setBusy(true);
    setError("");
    try {
      const next = await client.read(run.run_id);
      if (alive.current && activeClient.current === client) setRun(next);
    } catch (e) {
      if (alive.current && activeClient.current === client) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (alive.current && activeClient.current === client) setBusy(false);
    }
  };
  const request = async () => {
    if (!client || !subject || busy || run?.status === "running") return;
    setBusy(true);
    setError("");
    try {
      const next = await client.request(subject);
      if (alive.current && activeClient.current === client) setRun(next);
    } catch (e) {
      if (alive.current && activeClient.current === client) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (alive.current && activeClient.current === client) setBusy(false);
    }
  };
  if (!parsed.success) return null;
  const diagnostic = parsed.data;
  const outcome = run?.results[0];
  const unresolved = ["failed", "deferred", "not_detected"].includes(diagnostic.status);
  return /* @__PURE__ */ jsxs("details", { className: "mt-2 rounded border p-2 text-xs", children: [
    /* @__PURE__ */ jsxs("summary", { className: "cursor-pointer", children: [
      "Proxy discovery \xB7 ",
      diagnostic.status.replaceAll("_", " ")
    ] }),
    /* @__PURE__ */ jsx("p", { className: "my-2 text-muted-foreground", children: diagnostic.message }),
    /* @__PURE__ */ jsx("ul", { className: "mb-2 text-muted-foreground", children: diagnostic.checks.map((check2, i) => /* @__PURE__ */ jsxs("li", { children: [
      /* @__PURE__ */ jsx("code", { children: check2.strategy }),
      ": ",
      check2.status
    ] }, `${check2.strategy}-${i}`)) }),
    unresolved && client && subject && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs("p", { className: "mb-2 text-muted-foreground", children: [
        "Run the shared proxy service at block ",
        subject.block_number,
        ". This checks one snapshot; it does not pin an implementation or build upgrade history."
      ] }),
      /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", disabled: busy || run?.status === "running", onClick: () => void request(), children: "Request deeper discovery" })
    ] }),
    run && /* @__PURE__ */ jsxs("div", { role: "status", className: "mt-2 space-y-2", children: [
      /* @__PURE__ */ jsxs("p", { children: [
        "Discovery ",
        run.status === "running" ? outcome?.status ?? "queued" : outcome?.status === "complete" ? "completed" : outcome?.status === "failed" ? "failed" : "finished without a routing conclusion",
        " at block ",
        outcome?.subject?.block_number ?? subject?.block_number,
        "."
      ] }),
      run.status === "finished" && /* @__PURE__ */ jsx("p", { children: outcome?.routing === "not_detected" ? "No supported proxy pattern resolved an implementation. This does not prove that delegation is absent." : outcome?.routing ? `Routing: ${outcome.routing.replaceAll("_", " ")}.` : "The service could not complete the checks. No proxy conclusion was saved." }),
      run.status === "running" && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", disabled: busy, onClick: () => void check(), children: "Check discovery status" }),
      /* @__PURE__ */ jsxs("p", { className: "text-muted-foreground", children: [
        "Request ",
        run.run_id,
        ". Completed observations apply only to this block hash."
      ] })
    ] }),
    error && /* @__PURE__ */ jsx("p", { role: "alert", className: "mt-2 text-destructive", children: error })
  ] });
}
function ProfileEvidence({ match }) {
  return /* @__PURE__ */ jsxs("details", { className: "mt-2 rounded border px-2 py-1 text-xs", children: [
    /* @__PURE__ */ jsxs("summary", { className: "cursor-pointer", children: [
      "Profile evidence \xB7 v",
      match.version,
      " \xB7 ",
      match.origin.scope.replaceAll("_", " ")
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "mt-2 space-y-2 text-muted-foreground", children: [
      /* @__PURE__ */ jsxs("p", { children: [
        "Profile ",
        /* @__PURE__ */ jsx("code", { className: "break-all", children: match.name })
      ] }),
      /* @__PURE__ */ jsxs("p", { children: [
        match.native_schema ? "Native schema selected using the account owner or registered package; the current code is not independently verified." : match.selection === "explicit" ? "Explicitly selected profile." : match.selection === "fallback" ? "No specific interface matched; generic defaults are used." : "All required interface signals matched.",
        " Interface compatibility does not verify protocol ownership."
      ] }),
      /* @__PURE__ */ jsxs("p", { children: [
        "Revision ",
        /* @__PURE__ */ jsx("code", { className: "break-all", children: match.revision })
      ] }),
      match.native_schema ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs("p", { children: [
          "Schema: ",
          match.native_schema.name,
          " (",
          match.native_schema.kind,
          ")"
        ] }),
        /* @__PURE__ */ jsxs("p", { children: [
          "Binding ",
          /* @__PURE__ */ jsx("code", { className: "break-all", children: match.native_schema.binding })
        ] }),
        /* @__PURE__ */ jsxs("p", { children: [
          "State / module fingerprint ",
          /* @__PURE__ */ jsx("code", { className: "break-all", children: typeof match.native_schema.fingerprint === "string" ? match.native_schema.fingerprint : JSON.stringify(match.native_schema.fingerprint) })
        ] })
      ] }) : /* @__PURE__ */ jsxs("p", { children: [
        "ABI fingerprint ",
        /* @__PURE__ */ jsx("code", { className: "break-all", children: match.abi_fingerprint })
      ] }),
      match.origin.config_version !== void 0 && /* @__PURE__ */ jsxs("p", { children: [
        "Settings version ",
        match.origin.config_version,
        match.origin.updated_at ? ` \xB7 Updated ${new Date(match.origin.updated_at * 1e3).toISOString()}` : ""
      ] }),
      /* @__PURE__ */ jsx("ul", { className: "space-y-1", children: match.signals.map((signal) => /* @__PURE__ */ jsxs("li", { children: [
        /* @__PURE__ */ jsx("code", { children: signal.signal }),
        " \xB7 ",
        signal.matched ? "matched" : "not matched",
        signal.abi_entries.map((entry, i) => /* @__PURE__ */ jsxs("span", { children: [
          " \u2192 (",
          entry.outputs.join(", "),
          ")"
        ] }, i))
      ] }, signal.signal)) }),
      match.sources.length ? /* @__PURE__ */ jsx("ul", { children: match.sources.map((url) => /* @__PURE__ */ jsx("li", { children: /^https:\/\//.test(url) ? /* @__PURE__ */ jsx("a", { className: "underline break-all", href: url, target: "_blank", rel: "noreferrer", children: url }) : url }, url)) }) : /* @__PURE__ */ jsx("p", { children: "No external source was recorded for this profile." })
    ] })
  ] });
}
var DEFAULT_EXPLORER_CAPABILITIES = [
  {
    family: "evm",
    tabs: EVM_EXPLORER_TABS,
    kind: "evm",
    title: "Contract",
    addressLabel: "Contract address",
    placeholder: "0x\u2026",
    relationships: true,
    description: "Inspect ABI, source, proxy relationships and block-aware contract reads."
  },
  {
    family: "aptos",
    chains: ["aptos:1", "aptos:2"],
    tabs: [{ id: "onchain", label: "Move packages & modules" }],
    kind: "account",
    title: "Aptos Move account",
    addressLabel: "Aptos account address",
    placeholder: "0x1 or a 32-byte account address",
    relationships: true,
    observationLabel: "Ledger version",
    observationField: "ledger_version",
    description: "Published Move packages, declared package dependencies, upgrade policies and module bytecode hashes at one pinned ledger version. A ledger version counts transactions, not blocks. Package policy does not identify who controls the account. Configured Chainlink read-only views use that same ledger version and its module ABI. Historical queries, resource balance enumeration and transaction submission are not available in this explorer."
  },
  {
    family: "sui",
    tabs: [{ id: "onchain", label: "Move object state" }],
    kind: "account",
    relationshipSnapshot: "checkpoint",
    title: "Sui package / object",
    addressLabel: "Package or object ID",
    placeholder: "0x\u2026 (32-byte object ID)",
    relationships: true,
    observationLabel: "Checkpoint",
    description: "Move objects decoded from BCS with the checkpoint\u2019s native type layout. Object references do not establish ownership. Packages identify code, not a single state account. Dynamic fields and typed container relationships are read within profile limits at the same checkpoint. Partial results identify page or read limits. Package-wide enumeration, historical selection and transaction submission are not available."
  },
  {
    family: "solana",
    tabs: [{ id: "onchain", label: "Program / account state" }],
    kind: "account",
    observationLabel: "Finalized slot",
    title: "Solana program / account",
    addressLabel: "Program or account address",
    placeholder: "Base58 public key",
    relationships: true,
    description: "Current finalized account state, token data and program authorities \u2014 for a program, who can replace its code and when it was last deployed, and what a Forensic run read from its own code. Each related account reports its own observed slot. Historical snapshots and IDL-driven instruction calls are not available."
  },
  {
    family: "utxo",
    tabs: [{ id: "onchain", label: "UTXOs & scripts" }],
    chains: ["bitcoin", "bitcoin_testnet"],
    kind: "account",
    title: "Bitcoin address / script",
    addressLabel: "Bitcoin address",
    placeholder: "Bitcoin address for the selected network",
    relationships: false,
    description: "Current address balances, unspent outputs and script evidence from recent spends. Taproot may reveal one script branch or none. CoinJoin is analysed on the transaction page; an address alone does not identify a CoinJoin or link its inputs to outputs. Balances and UTXOs are separate observations and can change during a read. Historical snapshots and contract ABI calls are not available."
  },
  {
    family: "xrpl",
    tabs: [{ id: "onchain", label: "Account & ledger state" }],
    chains: ["xrp", "xrp_testnet"],
    kind: "account",
    title: "XRP account / ledger objects",
    addressLabel: "XRP classic address",
    placeholder: "r\u2026 (classic account address)",
    relationships: true,
    observationLabel: "Validated ledger",
    description: "Validated account balance, flags, signer lists and linked ledger objects. Each account's objects use its observed ledger. Up to 50 objects are shown; related accounts may use later ledgers. Historical snapshots and transaction submission are not available."
  },
  {
    family: "tron",
    tabs: [{ id: "onchain", label: "Account & contract state" }],
    chains: ["tron", "tron_shasta", "tron_nile"],
    kind: "account",
    title: "TRON contract / account",
    addressLabel: "TRON address",
    placeholder: "T\u2026 (Base58Check address)",
    relationships: true,
    description: "Current account balances, permissions and contract metadata, plus up to 12 no-argument read-only getters from the published on-chain ABI. Reads use latest state and are separate observations. Historical snapshots, parameterized calls and transaction submission are not available."
  },
  {
    family: "ton",
    tabs: [{ id: "onchain", label: "Account & contract state" }],
    chains: ["ton", "ton_testnet"],
    kind: "account",
    title: "TON contract / account",
    addressLabel: "TON address",
    placeholder: "EQ\u2026 / UQ\u2026 or workchain:hex account",
    relationships: true,
    description: "Current TON account status, exact balances, last transaction and bounded code/data BOCs, with the provider's observed block. Wallets are contracts too. Bounceable and non-bounceable forms share one catalog identity. Bounded TEP-74 get methods expose reported Jetton owner/master/admin references and wallet/master round-trip evidence as separate current observations. Historical snapshots, NFT discovery and message tracing are not available."
  },
  {
    family: "near",
    tabs: [{ id: "onchain", label: "Account & public views" }],
    kind: "account",
    title: "NEAR contract / account",
    addressLabel: "NEAR account ID",
    placeholder: "wrap.near or an implicit account ID",
    relationships: true,
    observationLabel: "Finalized block",
    description: "Finalized account balances, access-key permissions and supported public contract views. Token profiles and Ref Finance pool previews use the account's observed block. Related accounts may use later blocks. Historical snapshots and transaction submission are not available."
  },
  {
    family: "cosmos",
    tabs: [{ id: "onchain", label: "Account & CosmWasm state" }],
    chains: ["thorchain"],
    kind: "account",
    title: "THORChain CosmWasm / account",
    addressLabel: "THORChain address",
    placeholder: "thor1\u2026 (account or CosmWasm contract)",
    relationships: true,
    description: "Current native bank balances, CosmWasm metadata and up to 25 storage entries. Creator/admin references and declared protocol state fields support drill-down. Reads are separate current observations. Historical snapshots and transaction submission are not available."
  },
  // The Cosmos chains the account-state source has LCD hosts for
  // (`commons.chains.providers.cosmos_lcd`); after THORChain, whose entry
  // speaks its own words and wins for it.
  {
    family: "cosmos",
    tabs: [{ id: "onchain", label: "Account & CosmWasm state" }],
    chains: ["osmosis", "cosmoshub"],
    kind: "account",
    title: "Cosmos CosmWasm contract / account",
    addressLabel: "Bech32 address",
    placeholder: "osmo1\u2026 or cosmos1\u2026 (account or CosmWasm contract)",
    relationships: true,
    description: "Current native bank balances, CosmWasm metadata (code id, creator, admin, label) and up to 25 storage entries. Creator/admin references and declared protocol storage fields support drill-down. Reads are separate current observations. Historical snapshots and transaction submission are not available."
  },
  {
    family: "utxo",
    chains: ["zcash"],
    kind: "limited",
    tabs: [],
    title: "Zcash public-data coverage",
    addressLabel: "Zcash address",
    placeholder: "Transparent, Sapling or unified address",
    relationships: false,
    description: "Zcash does not expose EVM contract ABI, proxy or read/write calls. Transparent transaction addresses and amounts can be public; shielded recipients, amounts and memos are private. This explorer has no Zcash address-state reader, so unavailable balances are not shown as zero. Use Transactions to inspect available public transaction evidence, or open the address for catalog information."
  },
  {
    family: "utxo",
    chains: ["monero"],
    kind: "limited",
    tabs: [],
    title: "Monero public-data coverage",
    addressLabel: "Monero address",
    placeholder: "Standard, subaddress or integrated address",
    relationships: false,
    description: "Monero does not expose EVM contract ABI, proxy or read/write calls. A public address alone cannot reveal its balance, recipients or transaction history. Use Transactions to inspect available public transaction evidence, or open the address for catalog information. This explorer does not accept private viewing keys or perform wallet scans."
  }
];
function createExplorerCapabilities(entries = DEFAULT_EXPLORER_CAPABILITIES) {
  const resolve = (chainId) => {
    const chain = registry.resolve(chainId);
    if (!chain) return null;
    const listed = (spelling) => registry.resolve(spelling)?.caip2 === chain.caip2;
    return entries.find((entry) => entry.family === chain.family && (!entry.chains || entry.chains.some(listed))) ?? null;
  };
  return { resolve, chains: () => registry.all().flatMap((chain) => chain.caip2 && resolve(chain.caip2) ? [chain.caip2] : []) };
}
var explorerCapabilities = createExplorerCapabilities();
function LongValue({ text: text2 }) {
  if (text2.length > 160) return /* @__PURE__ */ jsx(
    ExpandableText,
    {
      text: text2,
      monospace: true,
      lines: 3,
      expandLabel: `Show all ${text2.length} characters`,
      className: "min-w-0 max-w-full [&>p]:break-all [&>p]:max-h-64 [&>p]:overflow-y-auto"
    },
    text2
  );
  return /* @__PURE__ */ jsx("code", { className: "break-all font-mono text-xs", title: text2, children: text2 });
}
function stateAddressChain(value, sourceChain) {
  const chain = value.value_schema?.address_chain_id;
  return typeof chain === "string" && chain ? toCanonicalChainId(chain) ?? chain : sourceChain;
}
function schemaParameter(schema, depth = 0) {
  if (!schema || typeof schema !== "object" || depth > 32) return null;
  const { type, name, components: components2 } = schema;
  if (typeof type !== "string" || !/^(?:address|bool|string|bytes(?:[1-9]|[12]\d|3[0-2])?|u?int(?:\d+)?|tuple)(?:\[\d*\])*$/.test(type)) return null;
  const base = { type, ...typeof name === "string" ? { name } : {} };
  if (!type.startsWith("tuple")) return base;
  if (!Array.isArray(components2) || components2.length > 256) return null;
  const children = components2.map((component) => schemaParameter(component, depth + 1));
  if (children.some((child) => !child)) return null;
  return { ...base, components: children };
}
function stateAddressType(value) {
  if (value.value_schema) {
    const schema = schemaParameter(value.value_schema);
    if (schema && (schema.type.startsWith("tuple") || schema.type.startsWith("address") || schema.type.endsWith("]"))) return schema;
  }
  if (/^address(?:\[\d*\])*$/.test(value.value_type ?? "")) return { type: value.value_type };
  if (!value.value_type && value.is_address) {
    if (Array.isArray(value.value_decoded)) return { type: "address[]" };
    if (typeof value.value_decoded === "string") return { type: "address" };
  }
  return null;
}
function blockLabel(node) {
  const capability = explorerCapabilities.resolve(node.chain_id);
  if (capability?.observationField) {
    const value = node.values.find((field) => field.key === capability.observationField && !field.error)?.value_decoded;
    if (typeof value === "string" && /^\d+$/.test(value)) return `${capability.observationLabel} ${value}`;
  }
  if (node.block_number != null) {
    const label = capability?.observationLabel;
    return label ? `${label} ${node.block_number}` : `#${node.block_number}`;
  }
  if (node.block_tag) return node.block_tag;
  return null;
}
function StateValueDisplay({
  value,
  chainId,
  resolveOverlay,
  resolveName
}) {
  if (value.error) {
    return /* @__PURE__ */ jsx("span", { className: "text-xs text-destructive", children: value.error });
  }
  const decoded = value.value_decoded;
  const addressType = stateAddressType(value);
  const addressChain = stateAddressChain(value, chainId);
  if (addressType) {
    return /* @__PURE__ */ jsx(ReadValue, { outputs: [addressType], data: decoded, renderAddress: (address, { decorated }) => {
      if (!registry.resolve(addressChain) || !isValidAddress(address, addressChain)) return /* @__PURE__ */ jsx(LongValue, { text: address });
      const overlay = decorated ? resolveOverlay?.(address, addressChain) : void 0;
      return /* @__PURE__ */ jsx(
        AddressDisplay,
        {
          address,
          chain: addressChain,
          copyable: decorated,
          label: decorated ? nameBehindCatalog(overlay, addressChain === chainId ? resolveName?.(address) : void 0) : void 0,
          overlay: decorated ? overlay : null
        }
      );
    } });
  }
  if (value.display) {
    const exact = decoded == null ? null : String(decoded);
    if (/^u?int\d*$/.test(value.value_type ?? "") && exact !== null && /^-?\d+$/.test(exact) && exact !== value.display) {
      return /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
        /* @__PURE__ */ jsx(LongValue, { text: value.display }),
        /* @__PURE__ */ jsxs("details", { className: "text-xs text-muted-foreground", children: [
          /* @__PURE__ */ jsx("summary", { className: "cursor-pointer", children: "Exact integer value" }),
          /* @__PURE__ */ jsx(LongValue, { text: exact })
        ] })
      ] });
    }
    return /* @__PURE__ */ jsx(LongValue, { text: value.display });
  }
  if (decoded == null) return /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: "\u2014" });
  if (typeof decoded === "object") {
    return /* @__PURE__ */ jsx(
      "pre",
      {
        tabIndex: 0,
        role: "region",
        "aria-label": `${value.label ?? value.key} details`,
        className: "max-h-64 overflow-auto rounded-sm bg-muted/40 p-1.5 font-mono text-2xs leading-snug focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        children: JSON.stringify(decoded, null, 2)
      }
    );
  }
  return /* @__PURE__ */ jsx(LongValue, { text: String(decoded) });
}
function StateValueArguments({ value, chainId, resolveOverlay, resolveName }) {
  if (!value.params?.length) return null;
  const inputs = argumentInputs(value);
  if (inputs) {
    return /* @__PURE__ */ jsxs("div", { className: "min-w-0 text-xs", children: [
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "Arguments: " }),
      /* @__PURE__ */ jsx(
        StateValueDisplay,
        {
          value: {
            ...value,
            error: null,
            display: null,
            is_address: false,
            value_decoded: value.params,
            value_type: "tuple",
            value_schema: { type: "tuple", components: inputs }
          },
          chainId,
          resolveOverlay,
          resolveName
        }
      )
    ] });
  }
  return /* @__PURE__ */ jsx(LongValue, { text: `Arguments: ${JSON.stringify(value.params)}` });
}
function argumentInputs(value) {
  try {
    const fn = parseAbiItem(`function ${value.key}`);
    return fn.type === "function" && fn.inputs.length === (value.params?.length ?? 0) ? fn.inputs : null;
  } catch {
    return null;
  }
}
function ValueRow({
  value,
  chainId,
  resolveOverlay,
  resolveName
}) {
  const name = value.label ?? value.key;
  return (
    // `truncate` here was on an INLINE span, where `text-overflow` does
    // nothing at all — so `FLASHLOAN_PREMIUM_TO_PROTOCOL` simply overflowed its
    // track and painted straight over the value beside it. These names are
    // long by nature and an ellipsis would hide the half that tells them apart,
    // so they WRAP instead — and the column is wide enough (18rem) that the
    // SCREAMING_SNAKE_CASE constants a contract actually has fit on one line.
    // Nothing breaks at an underscore, so a narrow column split them mid-word:
    // `FLASHLOAN_PREMIUM_TOT` / `AL`.
    /* @__PURE__ */ jsxs("div", { className: "grid w-full grid-cols-1 items-start gap-2 py-1 @lg/state-node:grid-cols-[minmax(8rem,18rem)_minmax(0,1fr)]", children: [
      /* @__PURE__ */ jsxs("div", { className: "min-w-0 break-words", children: [
        /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", title: name, children: name }),
        /* @__PURE__ */ jsx(StateValueArguments, { value, chainId, resolveOverlay, resolveName })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
        /* @__PURE__ */ jsx(StateValueDisplay, { value, chainId, resolveOverlay, resolveName }),
        (value.presentation ?? []).map((item, index) => /* @__PURE__ */ jsxs("div", { className: "mt-1 text-xs text-muted-foreground", children: [
          Array.isArray(item.path) && item.path.length > 0 && /* @__PURE__ */ jsxs("p", { children: [
            /* @__PURE__ */ jsx("code", { children: item.path.join(".") }),
            ": ",
            String(item.display ?? "Raw value retained")
          ] }),
          typeof item.age_seconds === "number" && /* @__PURE__ */ jsxs("p", { children: [
            "Checkpoint age: ",
            item.age_seconds.toLocaleString(),
            " seconds at the selected block",
            typeof item.max_age_seconds === "number" ? ` (threshold ${item.max_age_seconds.toLocaleString()} seconds)` : "",
            "."
          ] }),
          typeof item.warning === "string" && /* @__PURE__ */ jsx("p", { role: "note", className: "text-warning", children: item.warning })
        ] }, index))
      ] })
    ] })
  );
}
function CombinedRow({ combined }) {
  const name = combined.label ?? `${combined.strategy}${combined.over ? ` \xB7 ${combined.over}` : ""}`;
  return /* @__PURE__ */ jsxs("div", { className: "grid w-full grid-cols-1 items-start gap-2 py-1 @lg/state-node:grid-cols-[minmax(8rem,18rem)_minmax(0,1fr)]", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-1.5", children: [
      /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "shrink-0 text-3xs", children: String(combined.strategy) }),
      /* @__PURE__ */ jsx("span", { className: "min-w-0 break-words text-xs text-muted-foreground", title: name, children: name })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "min-w-0", children: /* @__PURE__ */ jsx(LongValue, { text: combined.display ?? JSON.stringify(combined.value) }) })
  ] });
}
function ContractStateNodeCard(props) {
  return /* @__PURE__ */ jsx(StateSnapshotContext, { node: props.node, children: /* @__PURE__ */ jsx(SnapshotNodeCard, { ...props }) });
}
function SnapshotNodeCard({
  node,
  depth = 0,
  className,
  onExploreContract,
  resolveOverlay,
  resolveName
}) {
  const [childrenOpen, setChildrenOpen] = React50.useState(depth < 1 && node.children.length <= 5);
  const evm = getChainMetadata(node.chain_id)?.family === "evm";
  const block = blockLabel(node);
  const hasChildren = node.children.length > 0;
  const nodeOverlay = resolveOverlay?.(node.address, node.chain_id);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "@container/state-node rounded-md border bg-card p-2.5",
        node.is_proxy && "border-primary/40",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
          /* @__PURE__ */ jsx(
            AddressDisplay,
            {
              address: node.address,
              chain: node.chain_id,
              label: nameBehindCatalog(nodeOverlay, node.name ?? resolveName?.(node.address)),
              overlay: nodeOverlay
            }
          ),
          evm && !node.is_contract && /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-3xs text-muted-foreground", children: "No contract code" }),
          (node.is_contract || !evm) && node.category && // Named, with the id one hover away: the id is what a request and an
          // override are keyed by, so it stays findable without being the label.
          /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "text-3xs", title: `Profile ${node.category}`, children: profileLabel(node.category, node.profile_match) }),
          node.is_proxy && /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-3xs", children: [
            "proxy",
            node.proxy_pattern ? ` \xB7 ${node.proxy_pattern}` : ""
          ] }),
          block && /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "font-mono text-3xs", children: block }),
          node.truncated && /* @__PURE__ */ jsx(Badge, { variant: "destructive", className: "text-3xs", children: "truncated" }),
          depth > 0 && (node.is_contract || !evm) && onExploreContract && /* @__PURE__ */ jsxs(
            Button,
            {
              size: "sm",
              variant: "ghost",
              className: "ml-auto h-6 px-1.5 text-xs",
              "aria-label": `Explore ${evm ? "contract" : "account"} ${node.address}`,
              onClick: () => onExploreContract(node),
              children: [
                "Explore ",
                evm ? "contract" : "account"
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsx(StateProxyDiscovery, { node }, `${node.chain_id}:${node.address}:${node.block_hash}`),
        node.profile_match && /* @__PURE__ */ jsx(ProfileEvidence, { match: node.profile_match }),
        evm && !node.is_contract && /* @__PURE__ */ jsx("p", { className: "mt-1.5 text-2xs text-muted-foreground", children: "No deployed contract code at this snapshot. The address may be an externally-owned account, not yet deployed, or a former contract." }),
        node.is_proxy && node.implementation_addresses.length > 0 && /* @__PURE__ */ jsxs("div", { className: "mt-1.5 flex flex-wrap items-center gap-1.5 text-2xs text-muted-foreground", children: [
          /* @__PURE__ */ jsx("span", { children: "impl:" }),
          node.implementation_addresses.map((addr, i) => /* @__PURE__ */ jsx(
            AddressDisplay,
            {
              address: addr,
              chain: node.chain_id,
              overlay: resolveOverlay?.(addr, node.chain_id),
              label: nameBehindCatalog(resolveOverlay?.(addr, node.chain_id), resolveName?.(addr))
            },
            `${addr}-${i}`
          ))
        ] }),
        node.errors.length > 0 && /* @__PURE__ */ jsx("ul", { className: "mt-1.5 space-y-0.5", children: node.errors.map((err, i) => /* @__PURE__ */ jsx("li", { className: "text-xs text-destructive", children: formatStateDiagnostic(err) }, i)) }),
        node.values.length > 0 && /* @__PURE__ */ jsx("div", { className: "mt-2 divide-y divide-border/60 text-sm", children: node.values.length > 20 ? /* @__PURE__ */ jsx(
          CollapsibleList,
          {
            items: node.values,
            previewCount: 12,
            noun: "field",
            render: (value) => /* @__PURE__ */ jsx(ValueRow, { value, chainId: node.chain_id, resolveOverlay, resolveName }, value.ref)
          }
        ) : node.values.map((value) => /* @__PURE__ */ jsx(ValueRow, { value, chainId: node.chain_id, resolveOverlay, resolveName }, value.ref)) }),
        node.is_contract && node.values.length === 0 && node.combined.length === 0 && node.errors.length === 0 && /* @__PURE__ */ jsxs("p", { className: "mt-2 text-xs text-muted-foreground", children: [
          "No state values were returned for this contract. ",
          depth === 0 ? "Use Fields or Read to inspect the available functions." : "Open this contract in the explorer to inspect its available functions."
        ] }),
        node.combined.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsx(Separator, { className: "my-2" }),
          /* @__PURE__ */ jsx("div", { className: "divide-y divide-border/60", children: node.combined.map((combined, i) => /* @__PURE__ */ jsx(CombinedRow, { combined }, `${combined.strategy}-${i}`)) })
        ] }),
        hasChildren && /* @__PURE__ */ jsxs("div", { className: "mt-2", children: [
          /* @__PURE__ */ jsxs(
            Button,
            {
              variant: "ghost",
              size: "sm",
              className: "h-6 px-1.5 text-xs text-muted-foreground",
              onClick: () => setChildrenOpen((open) => !open),
              "aria-expanded": childrenOpen,
              children: [
                /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "mr-1 inline-block w-2", children: childrenOpen ? "\u25BE" : "\u25B8" }),
                node.children.length,
                " related address",
                node.children.length === 1 ? "" : "es"
              ]
            }
          ),
          childrenOpen && /* @__PURE__ */ jsx("div", { className: "mt-1.5 space-y-1.5 border-l-2 border-border/60 pl-2.5", children: node.children.map((child) => /* @__PURE__ */ jsx(ContractStateNodeCard, { node: child, depth: depth + 1, onExploreContract, resolveOverlay, resolveName }, child.ref)) })
        ] })
      ]
    }
  );
}
var PEEK_BUDGET = {
  max_calls_total: 96,
  max_calls_per_contract: 64,
  max_drilldown_addresses: 6,
  max_batch_size: 100,
  max_variables_per_contract: 64,
  max_lookup_calls: 32
};
var POINTED_AT = {
  failure: "What this call depended on",
  proxy: "Where the proxy points",
  configuration: "What changed at this block",
  asset: "Balances, ownership and approvals"
};
function fieldOf(read) {
  return read.label ?? read.key;
}
function sideSnapshot(result, error, isPending, block, proxyRoles, listMembers2) {
  return {
    ...stateSnapshot(result, { label: `End of block ${block.toLocaleString("en-US")}`, proxyRoles, listMembers: listMembers2 }),
    pending: isPending || !result && !error,
    ...error ? { error: error.message } : {}
  };
}
var EVM_ADDRESS = /^0x[0-9a-fA-F]{40}$/;
var FIELD_ADDRESSES = /(0x[0-9a-fA-F]{40})(?![0-9a-fA-F])/;
function memberFields(rows) {
  return new Set(rows.filter((row) => row.cells.some((cell) => cell.value === LIST_MEMBER_PRESENT)).map((row) => row.field));
}
function pickRows(rows, fields) {
  return fields.flatMap((field) => rows.filter((row) => row.field === field || row.field.startsWith(`${field} \u203A `)));
}
function StateAtTransaction({
  client,
  chainId,
  address,
  blockNumber,
  beforeBlockNumber,
  summary,
  variables,
  focus,
  explorerLink,
  renderAddress,
  defaultOpen = false,
  allowLatest = false,
  className
}) {
  const [open, setOpen] = React50.useState(defaultOpen);
  const [asked, setAsked] = React50.useState(defaultOpen);
  const [includeLatest, setIncludeLatest] = React50.useState(false);
  const previous = beforeBlockNumber ?? blockNumber - 1;
  const requestAt = React50.useCallback(
    (block) => buildContractStateRequest({
      chainId,
      address,
      block: { blockNumber: BigInt(block) },
      variables,
      useCategoryDefaults: focus !== "asset",
      budget: PEEK_BUDGET,
      ...focus === "asset" ? { drilldown: DrillDownRuleSchema.parse({ enabled: false, max_depth: 0 }) } : {}
    }),
    [chainId, address, variables, focus]
  );
  const readable = Number.isSafeInteger(blockNumber) && blockNumber >= 1 && Number.isSafeInteger(previous) && previous >= 0;
  const beforeRequest = React50.useMemo(() => readable ? requestAt(previous) : null, [readable, requestAt, previous]);
  const afterRequest = React50.useMemo(() => readable ? requestAt(blockNumber) : null, [readable, requestAt, blockNumber]);
  const before = useContractState({ request: beforeRequest, client, enabled: asked });
  const after = useContractState({ request: afterRequest, client, enabled: asked });
  const latestRequest = React50.useMemo(() => beforeRequest ? {
    ...beforeRequest,
    chains: beforeRequest.chains.map((chain) => ({ ...chain, block: { number: null, block_hash: null, timestamp: null, tag: "latest" } }))
  } : null, [beforeRequest]);
  const latest = useContractState({ request: latestRequest, client, enabled: asked && allowLatest && includeLatest });
  const latestSide = React50.useMemo(() => ({
    ...stateSnapshot(latest.result, { label: `Latest snapshot${latest.result?.nodes[0]?.block_number != null ? ` \xB7 block ${latest.result.nodes[0].block_number.toLocaleString("en-US")}` : ""}` }),
    pending: latest.isPending || !latest.result && !latest.error,
    ...latest.error ? { error: latest.error.message } : {}
  }), [latest.result, latest.error, latest.isPending]);
  const proxyRoles = focus === "proxy";
  const configuration = focus === "configuration";
  const beforeSide = React50.useMemo(
    () => sideSnapshot(before.result, before.error, before.isPending, previous, proxyRoles, configuration),
    [before.result, before.error, before.isPending, previous, proxyRoles, configuration]
  );
  const afterSide = React50.useMemo(
    () => sideSnapshot(after.result, after.error, after.isPending, blockNumber, proxyRoles, configuration),
    [after.result, after.error, after.isPending, blockNumber, proxyRoles, configuration]
  );
  const snapshots = React50.useMemo(() => includeLatest && allowLatest ? [beforeSide, afterSide, latestSide] : [beforeSide, afterSide], [beforeSide, afterSide, latestSide, includeLatest, allowLatest]);
  const rows = React50.useMemo(() => diffContractStates(snapshots), [snapshots]);
  const pointedAt = React50.useMemo(() => {
    if (focus !== "configuration") return pickRows(rows, focus === "proxy" ? PROXY_ROLE_FIELDS : (variables ?? []).map(fieldOf));
    const named = pickRows(rows, (variables ?? []).map(fieldOf));
    const listed = new Set(named.map((row) => row.field));
    return [...named, ...changedMultiRows(rows).filter((row) => !listed.has(row.field))];
  }, [rows, focus, variables]);
  const movedCount = React50.useMemo(() => pointedAt.filter((row) => row.changed).length, [pointedAt]);
  const members = React50.useMemo(() => memberFields(rows), [rows]);
  const listMoves = React50.useMemo(() => {
    const moves = /* @__PURE__ */ new Map();
    for (const row of pointedAt) {
      const cut = row.field.lastIndexOf(" \u203A ");
      if (cut < 0 || !row.changed || !members.has(row.field)) continue;
      const list = row.field.slice(0, cut);
      const move = moves.get(list) ?? { added: 0, removed: 0 };
      const change = row.cells[row.cells.length - 1]?.change;
      if (change === "added") move.added += 1;
      else if (change === "removed") move.removed += 1;
      moves.set(list, move);
    }
    return [...moves.entries()].filter(([, move]) => move.added + move.removed > 0);
  }, [pointedAt, members]);
  const renderField = React50.useCallback((field) => {
    if (!renderAddress) return void 0;
    const parts = field.split(FIELD_ADDRESSES);
    if (parts.length === 1) return void 0;
    return /* @__PURE__ */ jsx("span", { className: "inline-flex max-w-full flex-wrap items-center gap-x-1.5 gap-y-0.5", children: parts.map((part, index) => /* @__PURE__ */ jsx(React50.Fragment, { children: EVM_ADDRESS.test(part) ? /* @__PURE__ */ jsx("span", { className: "min-w-0 max-w-full text-xs", children: renderAddress(part) }) : /* @__PURE__ */ jsx("span", { className: "min-w-0 max-w-full break-all", children: part }) }, index)) });
  }, [renderAddress]);
  const renderAbsent = React50.useCallback(
    (field) => members.has(field) ? /* @__PURE__ */ jsx("span", { className: "whitespace-nowrap", children: "not listed" }) : void 0,
    [members]
  );
  const allowanceFields = React50.useMemo(() => new Set((variables ?? []).filter((read) => read.key === "allowance" || read.key.startsWith("allowance(")).map(fieldOf)), [variables]);
  const renderValue = React50.useCallback(
    (field, _index, value) => allowanceFields.has(field) && isUnlimitedAllowance(value) ? /* @__PURE__ */ jsx("span", { title: `Maximum allowance (${value} base units)`, children: "Unlimited" }) : renderAddress && EVM_ADDRESS.test(value) ? renderAddress(value) : members.has(field) && value === LIST_MEMBER_PRESENT ? /* @__PURE__ */ jsx("span", { className: "whitespace-nowrap", children: value }) : void 0,
    [members, renderAddress, allowanceFields]
  );
  const renderHeader = React50.useCallback((_index, label) => {
    const at = /^(End of block) (.+)$/.exec(label);
    return at ? /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx("span", { className: "block whitespace-nowrap", children: at[1] }),
      /* @__PURE__ */ jsx("span", { className: "block whitespace-nowrap tabular-nums", children: at[2] })
    ] }) : label;
  }, []);
  const rest = React50.useMemo(() => {
    const shown = new Set(pointedAt.map((row) => row.field));
    const without = (record2) => Object.fromEntries(Object.entries(record2 ?? {}).filter(([field]) => !shown.has(field)));
    return snapshots.map((snapshot) => ({ ...snapshot, values: without(snapshot.values), errors: without(snapshot.errors) }));
  }, [snapshots, pointedAt]);
  const restCount = React50.useMemo(() => new Set(rest.flatMap((s) => [...Object.keys(s.values), ...Object.keys(s.errors ?? {})])).size, [rest]);
  const restChanged = React50.useMemo(() => changedMultiRows(diffContractStates(rest)).length, [rest]);
  if (!readable || before.unwired) return null;
  const pending = snapshots.some((snapshot) => snapshot.pending);
  const failed = snapshots.every((snapshot) => snapshot.error);
  const node = after.result?.nodes[0] ?? before.result?.nodes[0];
  const headers = snapshots.map((snapshot) => snapshot.label ?? "");
  return /* @__PURE__ */ jsxs(
    "details",
    {
      className: cn("rounded-md border border-border/60 bg-card text-sm", className),
      open,
      onToggle: (event) => {
        const next = event.currentTarget.open;
        setOpen(next);
        if (next) setAsked(true);
      },
      children: [
        /* @__PURE__ */ jsxs("summary", { className: "flex cursor-pointer list-none items-start gap-2 px-3 py-2 text-sm font-medium [&::-webkit-details-marker]:hidden", children: [
          /* @__PURE__ */ jsx("svg", { "aria-hidden": "true", viewBox: "0 0 16 16", className: cn("mt-1 h-3 w-3 shrink-0 text-muted-foreground transition-transform", open && "rotate-90"), children: /* @__PURE__ */ jsx("path", { d: "M6 3l5 5-5 5", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) }),
          /* @__PURE__ */ jsx("span", { className: "min-w-0 flex-1", children: summary })
        ] }),
        asked && /* @__PURE__ */ jsxs("div", { className: "space-y-3 border-t border-border/60 px-3 py-3", children: [
          /* @__PURE__ */ jsxs("div", { className: "space-y-1.5", children: [
            explorerLink ? /* @__PURE__ */ jsx("div", { className: "text-xs", children: explorerLink }) : null,
            /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
              node?.category ? /* @__PURE__ */ jsxs(Fragment, { children: [
                "Read as ",
                /* @__PURE__ */ jsx("span", { className: "font-medium text-foreground", children: profileLabel(node.category, node.profile_match) }),
                ". "
              ] }) : null,
              previous === blockNumber - 1 ? /* @__PURE__ */ jsxs(Fragment, { children: [
                "These are the block\u2019s boundaries, not this transaction\u2019s own before and after: a value another transaction in block ",
                blockNumber.toLocaleString("en-US"),
                " moved shows as changed here too."
              ] }) : /* @__PURE__ */ jsx(Fragment, { children: "These are block boundaries, not a transaction\u2019s own before and after. Changes include all transactions between the selected snapshots." })
            ] })
          ] }),
          allowLatest && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 text-xs", children: [
            /* @__PURE__ */ jsx(
              "button",
              {
                type: "button",
                className: "rounded border px-2 py-1 hover:bg-muted disabled:opacity-50",
                disabled: includeLatest && latest.isPending,
                onClick: () => {
                  if (includeLatest) latest.refetch();
                  else setIncludeLatest(true);
                },
                children: includeLatest ? latest.isPending ? "Reading latest\u2026" : "Refresh latest snapshot" : "Compare with latest snapshot"
              }
            ),
            /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "Latest is a separate read, not a transaction effect." })
          ] }),
          focus === "asset" && pending && /* @__PURE__ */ jsx("p", { role: "status", className: "text-xs text-muted-foreground", children: "Reading selected asset snapshots\u2026" }),
          focus === "asset" && !failed && snapshots.some((s) => s.error) && /* @__PURE__ */ jsx("p", { role: "alert", className: "text-xs text-destructive", children: snapshots.filter((s) => s.error).map((s) => `${s.label}: ${s.error}`).join("; ") }),
          focus === "asset" && !pending && pointedAt.length === 0 && !snapshots.some((s) => s.error) && /* @__PURE__ */ jsx("p", { role: "status", className: "text-xs text-muted-foreground", children: "No matching asset getters were available in the resolved ABI. Open the explorer to inspect ABI coverage." }),
          failed ? /* @__PURE__ */ jsxs("p", { role: "alert", className: "text-xs text-destructive", children: [
            "The requested snapshots could not be read: ",
            snapshots[1]?.error ?? snapshots[0]?.error
          ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
            (pointedAt.length > 0 || focus !== "failure" && focus !== "asset") && /* @__PURE__ */ jsxs("section", { "aria-label": focus === "proxy" ? "Proxy roles" : POINTED_AT[focus], className: "space-y-1.5", children: [
              /* @__PURE__ */ jsxs("h4", { className: "text-2xs font-semibold uppercase tracking-wide text-muted-foreground", children: [
                POINTED_AT[focus],
                focus === "configuration" && !pending && movedCount > 0 ? ` \xB7 ${movedCount}` : ""
              ] }),
              focus === "configuration" && !pending && movedCount === 0 ? (
                // Said outright: an empty table reads as "nothing happened", and what
                // happened may simply be a setting this contract's profile does not read.
                /* @__PURE__ */ jsx("p", { role: "status", className: "rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground", children: "None of the fields read for this contract differ between the two blocks. What the transaction set is in its highlights; a setting that is not among the fields below was not read." })
              ) : null,
              focus === "configuration" && !pending && listMoves.length > 0 ? /* @__PURE__ */ jsx("ul", { "aria-label": "List changes", className: "space-y-0.5 text-xs", children: listMoves.map(([list, move]) => /* @__PURE__ */ jsxs("li", { children: [
                /* @__PURE__ */ jsx("code", { children: list }),
                /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: ": " }),
                /* @__PURE__ */ jsx("span", { className: "font-medium", children: [move.added > 0 ? `${move.added} added` : null, move.removed > 0 ? `${move.removed} removed` : null].filter(Boolean).join(", ") })
              ] }, list)) }) : null,
              focus !== "configuration" || pending || pointedAt.length > 0 ? /* @__PURE__ */ jsx(
                StateDiffTable,
                {
                  rows: pointedAt,
                  headers,
                  incomplete: snapshots.some((s) => Boolean(s.error)),
                  pending,
                  renderField,
                  renderAbsent,
                  renderValue,
                  renderHeader
                }
              ) : null
            ] }),
            (restCount > 0 || pending && focus !== "asset") && /* @__PURE__ */ jsxs("details", { className: "rounded-md border border-border/60", open: focus === "failure" && pointedAt.length === 0, children: [
              /* @__PURE__ */ jsxs("summary", { className: "cursor-pointer px-3 py-1.5 text-xs font-medium text-muted-foreground", children: [
                focus === "configuration" ? "What stayed the same" : pointedAt.length > 0 || focus === "proxy" ? "The rest of this contract\u2019s state" : "This contract\u2019s state",
                pending ? " \xB7 reading\u2026" : ` \xB7 ${restCount} ${restCount === 1 ? "field" : "fields"}${restChanged > 0 ? `, ${restChanged} changed` : ""}`
              ] }),
              /* @__PURE__ */ jsx("div", { className: "border-t border-border/60 p-3", children: /* @__PURE__ */ jsx(
                ContractStateHighlighter,
                {
                  snapshots: rest,
                  defaultTab: restChanged > 0 ? "changes" : "all",
                  renderField,
                  renderAbsent,
                  renderValue,
                  renderHeader
                },
                pending ? "reading" : restChanged > 0 ? "changes" : "all"
              ) })
            ] })
          ] })
        ] })
      ]
    }
  );
}
var REPORT_CEILING = 64;
function hasReadIssues(nodes) {
  return nodes.some((node) => node.errors.length > 0 || node.values.some((value) => Boolean(value.error)) || hasReadIssues(node.children));
}
function stateAddressRefs(result) {
  const seen = /* @__PURE__ */ new Map();
  const add = (value, chain) => {
    if (typeof value !== "string" || !registry.resolve(chain) || !isValidAddress(value, chain) || seen.size >= REPORT_CEILING) return;
    const chainId = toCanonicalChainId(chain) ?? chain;
    const key = `${chainId}:${normalizeAddress(value, chainId)}`;
    if (!seen.has(key)) seen.set(key, { address: value, chainId });
  };
  const rows = [];
  const walk = (nodes) => {
    for (const node of nodes) {
      add(node.address, node.chain_id);
      node.implementation_addresses?.forEach((address) => add(address, node.chain_id));
      node.relationships?.forEach((edge) => add(edge.target, edge.chain_id));
      for (const value of node.values ?? []) {
        const type = stateAddressType(value);
        if (type && !value.error) rows.push({ chain: stateAddressChain(value, node.chain_id), addresses: addressLeaves([type], value.value_decoded) });
      }
      if (node.children?.length) walk(node.children);
    }
  };
  walk(result?.nodes ?? []);
  for (let i = 0; seen.size < REPORT_CEILING && rows.some((row) => i < row.addresses.length); i++) {
    for (const row of rows) add(row.addresses[i], row.chain);
  }
  return [...seen.values()];
}
function AddressReporter({
  result,
  onAddressesFound,
  onAddressRefsFound
}) {
  const key = React50.useMemo(() => JSON.stringify(stateAddressRefs(result)), [result]);
  const sourceChain = toCanonicalChainId(result?.nodes[0]?.chain_id ?? "") ?? result?.nodes[0]?.chain_id;
  React50.useEffect(() => {
    const refs = JSON.parse(key);
    onAddressesFound?.(refs.filter((ref) => ref.chainId === sourceChain).map((ref) => ref.address), "state");
    onAddressRefsFound?.(refs);
    return () => {
      onAddressesFound?.([], "state");
      onAddressRefsFound?.([]);
    };
  }, [key, sourceChain, onAddressesFound, onAddressRefsFound]);
  return null;
}
function ContractStateTree({
  result,
  isPending = false,
  error = null,
  unwired = false,
  onRefresh,
  className,
  onExploreContract,
  resolveOverlay,
  resolveName,
  onAddressesFound,
  onAddressRefsFound
}) {
  if (unwired) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Backend state not configured",
        description: "Provide a ContractStateClient (via ContractStateClientProvider or the client prop) to read on-chain state \u2014 categories, proxy-merged ABIs, drill-down and cross-chain combine \u2014 through the backend.",
        size: "sm",
        className
      }
    );
  }
  if (error && !result) {
    return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), children: [
      /* @__PURE__ */ jsx(EmptyState, { title: "Couldn't read state", description: error.message, size: "sm" }),
      onRefresh && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: onRefresh, disabled: isPending, children: "Retry" })
    ] });
  }
  if (isPending && !result) {
    return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2 py-6 text-sm text-muted-foreground", className), children: [
      /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      " Reading on-chain state\u2026"
    ] });
  }
  if (!result) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No state",
        description: "The backend returned no contract state for this request.",
        size: "sm",
        className
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), "aria-busy": isPending, children: [
    error && /* @__PURE__ */ jsxs("div", { role: "alert", className: "text-sm text-destructive", children: [
      "Couldn't refresh state. Showing the previous result. ",
      error.message
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-3xs", children: [
        result.rpc_calls,
        " RPC call",
        result.rpc_calls === 1 ? "" : "s"
      ] }),
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-3xs", children: [
        result.lookup_calls,
        " lookup",
        result.lookup_calls === 1 ? "" : "s"
      ] }),
      result.truncated && /* @__PURE__ */ jsx(Badge, { variant: "destructive", className: "text-3xs", children: "Partial result" }),
      (result.errors.length > 0 || hasReadIssues(result.nodes)) && /* @__PURE__ */ jsx(Badge, { variant: "destructive", className: "text-3xs", children: "Read issues" }),
      isPending && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
        " Refreshing\u2026"
      ] }),
      onRefresh && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "ghost", className: "ml-auto h-6 px-1.5 text-xs", onClick: onRefresh, disabled: isPending, children: "Refresh" })
    ] }),
    result.errors.length > 0 && /* @__PURE__ */ jsx("ul", { className: "space-y-0.5", children: result.errors.map((err, i) => /* @__PURE__ */ jsx("li", { className: "text-xs text-destructive", children: formatStateDiagnostic(err) }, i)) }),
    result.nodes.length === 0 && result.combined.length === 0 && /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No state",
        description: result.errors.length > 0 || result.truncated ? "No contract state was returned. See the read diagnostics above." : "The backend returned no contract state for this request.",
        size: "sm"
      }
    ),
    result.combined.length > 0 && /* @__PURE__ */ jsxs("div", { className: "rounded-md border bg-muted/30 p-2.5", children: [
      /* @__PURE__ */ jsx("p", { className: "mb-1.5 text-xs font-semibold text-muted-foreground", children: "Combined" }),
      /* @__PURE__ */ jsx("div", { className: "space-y-1 text-sm", children: result.combined.map((combined, i) => /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
        /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-3xs", children: String(combined.strategy) }),
        /* @__PURE__ */ jsx("span", { className: "text-xs text-muted-foreground", children: combined.label ?? combined.over ?? "" }),
        /* @__PURE__ */ jsx("div", { className: "min-w-0 max-w-full", children: /* @__PURE__ */ jsx(LongValue, { text: combined.display ?? JSON.stringify(combined.value) }) })
      ] }, `${combined.strategy}-${i}`)) })
    ] }),
    (onAddressesFound || onAddressRefsFound) && /* @__PURE__ */ jsx(AddressReporter, { result, onAddressesFound, onAddressRefsFound }),
    /* @__PURE__ */ jsx("div", { className: "space-y-2", children: result.nodes.map((node) => /* @__PURE__ */ jsx(ContractStateNodeCard, { node, onExploreContract, resolveOverlay, resolveName }, node.ref)) })
  ] });
}
function reconcileProfileText(input) {
  const edited = input.baseline !== null && input.text !== input.baseline;
  if (!edited) return { text: input.server, kept: "" };
  return { text: input.text, kept: input.sameScope && input.server !== input.baseline ? "changed" : "scope" };
}
function isCatchAllProfile(definition) {
  return Boolean(definition) && (definition?.signal_functions?.length ?? 0) === 0;
}
function ProfileEditor({ client, identity, name, label, onSaved }) {
  const { scopes, scope, setScope } = useOverrideWriteScope(identity);
  const [open, setOpen] = React50.useState(false);
  const [settings, setSettings] = React50.useState(null);
  const [text2, setText] = React50.useState("");
  const [error, setError] = React50.useState("");
  const [busy, setBusy] = React50.useState(false);
  const [notice, setNotice] = React50.useState("");
  const [reload, setReload] = React50.useState(0);
  const [kept, setKept] = React50.useState("");
  const identityKey2 = JSON.stringify(identity);
  const requestKey2 = JSON.stringify([identityKey2, scope, name]);
  const activeKey = React50.useRef(requestKey2);
  activeKey.current = requestKey2;
  const [loadedKey, setLoadedKey] = React50.useState("");
  const textRef = React50.useRef(text2);
  textRef.current = text2;
  const baseline = React50.useRef(null);
  React50.useEffect(() => {
    if (!open || !scope) return;
    const controller = new AbortController();
    setSettings(null);
    setError("");
    client.list(scope, { signal: controller.signal }).then((result) => {
      if (controller.signal.aborted) return;
      const row = result.profiles.find((p) => p.definition.name === name);
      if (!row) throw new Error("This profile is no longer available. Refresh the snapshot.");
      const server = JSON.stringify(row.definition, null, 2);
      const next = reconcileProfileText({
        text: textRef.current,
        baseline: baseline.current?.text ?? null,
        server,
        sameScope: baseline.current?.key === requestKey2
      });
      baseline.current = { key: requestKey2, text: server };
      setSettings(result);
      setText(next.text);
      setKept(next.kept);
      setLoadedKey(requestKey2);
    }).catch((e) => {
      if (!controller.signal.aborted) setError(String(e.message ?? e));
    });
    return () => controller.abort();
  }, [client, open, scope, name, requestKey2, reload]);
  const close = () => {
    baseline.current = null;
    setKept("");
    setOpen(false);
  };
  const current = settings && loadedKey === requestKey2 ? settings : null;
  const stored = current?.profiles.find((p) => p.definition.name === name);
  const catchAll = isCatchAllProfile(stored?.definition);
  const title = label?.trim() || stored?.label?.trim() || name;
  let parsed = null;
  let validation = "";
  try {
    if (current) {
      parsed = ProfileDefinitionSchema.parse(JSON.parse(text2));
      if (parsed.name !== name) throw new Error("Keep the selected profile name.");
    }
  } catch (e) {
    validation = e instanceof Error ? e.message : "Invalid profile JSON";
  }
  const save = async (reset = false) => {
    if (!scope || !current || busy || !reset && !parsed) return;
    const key = requestKey2;
    setBusy(true);
    setError("");
    try {
      await client.save(scope, { name, definition: reset ? null : parsed, expected_version: current.config_version });
      if (activeKey.current !== key) return;
      close();
      setNotice(reset ? "Profile now inherits from the broader scope." : `Profile saved to ${scope}.`);
      onSaved();
    } catch (e) {
      if (activeKey.current === key) setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return /* @__PURE__ */ jsxs("div", { className: "mb-3", children: [
    notice && /* @__PURE__ */ jsx("p", { role: "status", className: "mb-2 text-xs", children: notice }),
    /* @__PURE__ */ jsxs(Dialog, { open, onOpenChange: (next) => {
      if (!busy) {
        if (next) setOpen(true);
        else close();
        setNotice("");
      }
    }, children: [
      /* @__PURE__ */ jsx(DialogTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", disabled: !scope, children: "Edit profile" }) }),
      /* @__PURE__ */ jsxs(DialogContent, { className: "max-w-3xl max-h-[90vh] overflow-y-auto", children: [
        /* @__PURE__ */ jsxs(DialogHeader, { children: [
          /* @__PURE__ */ jsxs(DialogTitle, { children: [
            "Edit ",
            title
          ] }),
          /* @__PURE__ */ jsxs(DialogDescription, { children: [
            title !== name && /* @__PURE__ */ jsxs(Fragment, { children: [
              "Profile ",
              /* @__PURE__ */ jsx("code", { children: name }),
              ". "
            ] }),
            "Applies to every matching contract in the selected scope. More specific scopes take precedence. Built-in profiles remain available through inheritance."
          ] })
        ] }),
        catchAll && /* @__PURE__ */ jsxs("p", { role: "note", className: "rounded border border-warning/40 bg-warning/5 p-2 text-xs", children: [
          /* @__PURE__ */ jsx("strong", { className: "font-semibold", children: "This is a catch-all profile." }),
          " It is not matched by an interface: it is applied to every contract or account that no more specific profile claims. A change here reaches all of them in ",
          scope ? `the ${scope} scope` : "the selected scope",
          ", not one protocol."
        ] }),
        /* @__PURE__ */ jsxs("label", { className: "text-sm", children: [
          "Save profile to ",
          /* @__PURE__ */ jsx("select", { "aria-label": "Save profile to", value: scope ?? "", disabled: busy, onChange: (e) => setScope(e.target.value), className: "ml-2 rounded border bg-background p-1", children: scopes.map((s) => /* @__PURE__ */ jsx("option", { value: s, children: s }, s)) })
        ] }),
        !current && !error && /* @__PURE__ */ jsx("p", { role: "status", children: "Loading profile settings\u2026" }),
        current && /* @__PURE__ */ jsxs(Fragment, { children: [
          /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
            "Settings version ",
            current.config_version,
            ". Change variables, labels, matching signals, sources, units, relationship recipes or summary fields. Recipes bind typed results to read-only calls within the shared budgets. Version and content revision increase automatically when saved."
          ] }),
          kept && /* @__PURE__ */ jsxs("div", { role: "status", className: "flex flex-wrap items-center gap-2 rounded border border-warning/40 bg-warning/5 p-2 text-xs", children: [
            /* @__PURE__ */ jsx("span", { className: "min-w-0 flex-1", children: kept === "changed" ? "This profile was changed while you were editing. Your edits are kept \u2014 saving now replaces the newer version." : `Your unsaved edits are kept. Saving writes them to ${scope}, replacing that scope\u2019s definition.` }),
            /* @__PURE__ */ jsx(Button, { size: "sm", variant: "ghost", disabled: busy, onClick: () => {
              if (baseline.current) setText(baseline.current.text);
              setKept("");
            }, children: "Discard my edits" })
          ] }),
          /* @__PURE__ */ jsxs("label", { className: "text-sm", children: [
            "Profile JSON",
            /* @__PURE__ */ jsx("textarea", { "aria-label": "Profile JSON", className: "mt-1 block min-h-64 w-full rounded border bg-background p-2 font-mono text-xs", value: text2, disabled: busy, onChange: (e) => setText(e.target.value), spellCheck: false })
          ] })
        ] }),
        (error || validation) && /* @__PURE__ */ jsx("p", { role: "alert", className: "max-h-32 overflow-auto text-xs text-destructive", children: error || validation }),
        error && /* @__PURE__ */ jsx(Button, { variant: "outline", disabled: busy, onClick: () => setReload((v) => v + 1), children: "Reload settings" }),
        /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-2", children: [
          /* @__PURE__ */ jsx(Button, { disabled: busy || !current || !parsed || Boolean(validation), onClick: () => void save(), children: "Save profile" }),
          stored?.overridden_here && /* @__PURE__ */ jsx(Button, { variant: "outline", disabled: busy, onClick: () => void save(true), children: "Use inherited profile" }),
          /* @__PURE__ */ jsx(Button, { variant: "ghost", disabled: busy, onClick: close, children: "Cancel" })
        ] })
      ] })
    ] })
  ] });
}
function Disclosure({ summary, children, className }) {
  const [open, setOpen] = React50.useState(false);
  return /* @__PURE__ */ jsxs("details", { className, onToggle: (event) => setOpen(event.currentTarget.open), children: [
    /* @__PURE__ */ jsx("summary", { className: "cursor-pointer text-xs font-medium focus-visible:outline focus-visible:outline-2", children: summary }),
    open && children
  ] });
}
var statuses = {
  complete: "Read successfully",
  partial: "Limited coverage",
  failed: "Read failed",
  blocked: "Unavailable",
  unsupported: "Not supported",
  skipped_by_policy: "Disabled"
};
function Identity({ value, chain, resolveOverlay, resolveName }) {
  return /* @__PURE__ */ jsx(
    AddressDisplay,
    {
      address: value,
      chain,
      overlay: resolveOverlay?.(value, chain),
      label: nameBehindCatalog(resolveOverlay?.(value, chain), resolveName?.(value))
    }
  );
}
function ResultValue({ value, chain, ...decoration }) {
  const schema = stateAddressType(value);
  const references = schema ? addressEntries([schema], value.value_decoded) : [];
  const complex = !value.error && value.value_decoded !== null && typeof value.value_decoded === "object";
  const display2 = /* @__PURE__ */ jsx(StateValueDisplay, { value, chainId: chain, ...decoration });
  return /* @__PURE__ */ jsxs("div", { className: "min-w-0 space-y-1 text-xs", children: [
    /* @__PURE__ */ jsx("div", { className: "break-words font-medium", title: value.key, children: value.label ?? (value.key.includes("(") ? value.key : humanizeFieldName(value.key)) }),
    /* @__PURE__ */ jsx(StateValueArguments, { value, chainId: chain, ...decoration }),
    complex ? /* @__PURE__ */ jsxs(Fragment, { children: [
      references.length > 0 && /* @__PURE__ */ jsx("div", { className: "flex min-w-0 flex-wrap gap-2", children: references.slice(0, 3).map((entry, i) => /* @__PURE__ */ jsxs("span", { className: "inline-flex min-w-0 flex-wrap items-center gap-1", children: [
        /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
          entry.path || "address",
          ":"
        ] }),
        /* @__PURE__ */ jsx(Identity, { value: entry.address, chain: stateAddressChain(value, chain), ...decoration })
      ] }, `${entry.path}:${i}`)) }),
      /* @__PURE__ */ jsx(Disclosure, { summary: /* @__PURE__ */ jsxs(Fragment, { children: [
        "Full ",
        value.label ?? value.key,
        " result",
        references.length ? ` \xB7 ${references.length} addresses` : ""
      ] }), children: display2 })
    ] }) : display2
  ] });
}
function RelationshipSummary({ result, isPending, error, onRefresh, ...decoration }) {
  const nodes = [];
  const walk = (children) => children.forEach((node) => {
    nodes.push(node);
    walk(node.children);
  });
  walk(result?.nodes ?? []);
  const edges = nodes.flatMap((node) => (node.relationships ?? []).map((edge) => ({ node, edge })));
  const policies = nodes.flatMap((node) => (node.relationship_policies ?? []).map((policy) => ({ node, policy })));
  const coverage = [...new Set(nodes.flatMap((node) => node.relationship_coverage ?? []))];
  const incomplete = Boolean(result?.truncated || result?.errors.length || nodes.some((node) => node.errors.length || node.truncated || node.values.some((value) => value.error) || node.relationship_policies?.some((policy) => ["partial", "failed", "blocked", "unsupported"].includes(policy.status))));
  const root = result?.nodes[0];
  const contractCount = new Set(nodes.map((node) => `${node.chain_id}:${node.address}`)).size;
  return /* @__PURE__ */ jsx(AddressDisplayProvider, { presentation: "inline", size: "sm", showAvatar: false, showExplorerIcon: false, children: /* @__PURE__ */ jsxs("section", { "aria-label": "Automatic relationship summary", className: "my-3 min-w-0 space-y-3 rounded-md border bg-muted/10 p-3 text-sm", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-2", children: [
      /* @__PURE__ */ jsxs("div", { children: [
        /* @__PURE__ */ jsx("h3", { className: "font-medium", children: "Relationships at a glance" }),
        /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "State and relationships follow the selected profile and exploration depth. Expand a result to inspect its details." })
      ] }),
      onRefresh && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "ghost", onClick: onRefresh, disabled: isPending, children: "Refresh relationships" })
    ] }),
    isPending && /* @__PURE__ */ jsxs("p", { role: "status", className: "flex items-center gap-2 text-muted-foreground", children: [
      /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      result ? "Refreshing relationships; showing the previous snapshot\u2026" : "Reading relationships and their nested state\u2026"
    ] }),
    error && /* @__PURE__ */ jsxs("p", { role: "alert", className: "text-destructive", children: [
      "Relationships could not be refreshed: ",
      error.message
    ] }),
    !result && !isPending && !error && /* @__PURE__ */ jsx("p", { className: "text-muted-foreground", children: "Relationship state is unavailable." }),
    result && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 text-xs", children: [
        /* @__PURE__ */ jsxs(Badge, { variant: "outline", children: [
          "Snapshot ",
          root ? blockLabel(root) ?? "current observations" : "current observations"
        ] }),
        /* @__PURE__ */ jsxs("span", { children: [
          policies.length ? `${policies.length} relationship checks` : `${edges.length} address references`,
          " \xB7 ",
          contractCount,
          " ",
          contractCount === 1 ? "account" : "accounts",
          " read"
        ] }),
        incomplete && /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "Partial coverage" })
      ] }),
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "State belongs to this snapshot; catalog names describe current records. Reported references do not establish common ownership." }),
      incomplete && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Some reads failed, were unavailable, or reached a configured limit. Expand results for details." }),
      root && !!root.summary_fields?.length && /* @__PURE__ */ jsx(StateSnapshotContext, { node: root, children: /* @__PURE__ */ jsx(
        ProtocolStateSummary,
        {
          node: root,
          coordinate: blockLabel(root) ?? void 0,
          renderValue: (value) => /* @__PURE__ */ jsx(ResultValue, { value, chain: root.chain_id, ...decoration })
        }
      ) }),
      nodes.filter((node) => node !== root && node.profile_match?.native_schema).slice(0, 8).map((node) => /* @__PURE__ */ jsx(StateSnapshotContext, { node, children: /* @__PURE__ */ jsxs("div", { className: "min-w-0 space-y-1", children: [
        /* @__PURE__ */ jsxs("div", { className: "text-xs text-muted-foreground", children: [
          "Read from ",
          /* @__PURE__ */ jsx(Identity, { value: node.address, chain: node.chain_id, ...decoration })
        ] }),
        /* @__PURE__ */ jsx(
          ProtocolStateSummary,
          {
            node,
            coordinate: blockLabel(node) ?? void 0,
            renderValue: (value) => /* @__PURE__ */ jsx(ResultValue, { value, chain: node.chain_id, ...decoration })
          }
        )
      ] }) }, node.ref)),
      /* @__PURE__ */ jsx("div", { className: "grid min-w-0 gap-2 lg:grid-cols-2", children: policies.map(({ node, policy }) => {
        const refs = [...new Set(policy.value_refs)];
        const groups = /* @__PURE__ */ new Map();
        for (const ref of refs) {
          const value = result.index[ref];
          if (!value) continue;
          const origin = nodes.find((n) => ref.startsWith(`${n.ref}#`)) ?? node;
          groups.set(origin, [...groups.get(origin) ?? [], value]);
        }
        return /* @__PURE__ */ jsx(StateSnapshotContext, { node, children: /* @__PURE__ */ jsxs("article", { "aria-label": policy.label, className: "min-w-0 space-y-2 rounded border bg-background p-3", children: [
          /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-start justify-between gap-2", children: [
            /* @__PURE__ */ jsx("h4", { className: "min-w-0 flex-1 font-medium", children: policy.label }),
            /* @__PURE__ */ jsx(Badge, { variant: "outline", children: policy.status === "complete" && !policy.targets.length && !refs.length ? "Nothing to read" : statuses[policy.status] ?? policy.status.replaceAll("_", " ") })
          ] }),
          node !== root && /* @__PURE__ */ jsxs("div", { className: "text-xs text-muted-foreground", children: [
            "Via ",
            /* @__PURE__ */ jsx(Identity, { value: node.address, chain: node.chain_id, ...decoration })
          ] }),
          policy.targets.filter((target) => target !== node.address).map((target) => /* @__PURE__ */ jsx("div", { className: "min-w-0", children: /* @__PURE__ */ jsx(Identity, { value: target, chain: node.chain_id, ...decoration }) }, target)),
          /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-x-4 gap-y-1 text-xs", children: [...groups.values()].flat().filter((value) => !value.params?.length && !value.error && value.value_decoded != null && typeof value.value_decoded !== "object" && !stateAddressType(value) && String(value.value_decoded).length < 60).slice(0, 3).map((value) => /* @__PURE__ */ jsxs("span", { children: [
            /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
              value.label ?? value.key.replace(/\(\)$/, ""),
              ": "
            ] }),
            /* @__PURE__ */ jsx(StateValueDisplay, { value, chainId: node.chain_id, ...decoration })
          ] }, value.ref)) }),
          policy.reason && /* @__PURE__ */ jsx("p", { className: "break-words text-xs text-muted-foreground", children: policy.reason }),
          !!refs.length && /* @__PURE__ */ jsx(Disclosure, { summary: /* @__PURE__ */ jsxs(Fragment, { children: [
            "Inspect ",
            refs.length,
            " read ",
            refs.length === 1 ? "result" : "results"
          ] }), children: /* @__PURE__ */ jsxs("div", { className: "mt-3 space-y-3", children: [
            [...groups].map(([origin, values]) => /* @__PURE__ */ jsx(StateSnapshotContext, { node: origin, children: /* @__PURE__ */ jsxs("div", { className: "min-w-0 space-y-3 border-l-2 pl-3", children: [
              /* @__PURE__ */ jsxs("div", { className: "text-xs text-muted-foreground", children: [
                "Read from ",
                /* @__PURE__ */ jsx(Identity, { value: origin.address, chain: origin.chain_id, ...decoration }),
                " \xB7 ",
                blockLabel(origin)
              ] }),
              values.map((value) => /* @__PURE__ */ jsx(ResultValue, { value, chain: origin.chain_id, ...decoration }, value.ref))
            ] }) }, origin.ref)),
            refs.some((ref) => !result.index[ref]) && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Some results are absent from this response. Refresh to retry." })
          ] }) }),
          !!policy.sources.length && /* @__PURE__ */ jsxs(Disclosure, { className: "text-xs", summary: "Why this relationship?", children: [
            /* @__PURE__ */ jsx("p", { className: "my-1 text-muted-foreground", children: "Selected by this contract\u2019s configured profile. A successful read does not verify protocol identity or trust." }),
            /* @__PURE__ */ jsx("ul", { children: policy.sources.map((url, i) => /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsxs("a", { href: url, target: "_blank", rel: "noopener noreferrer", className: "break-all underline", children: [
              "Protocol source ",
              i + 1
            ] }) }, url)) })
          ] })
        ] }) }, `${node.ref}:${policy.id}`);
      }) }),
      !edges.length && !policies.length && /* @__PURE__ */ jsx("p", { className: "text-muted-foreground", children: incomplete ? "No address links resolved in this partial snapshot." : "No separate address links were reported by the configured reads." }),
      !!edges.length && /* @__PURE__ */ jsx(Disclosure, { className: "rounded border p-2", summary: /* @__PURE__ */ jsxs(Fragment, { children: [
        "All observed address references \xB7 ",
        edges.length
      ] }), children: /* @__PURE__ */ jsx("ul", { className: "mt-2 space-y-2", children: edges.map(({ node, edge }, index) => /* @__PURE__ */ jsx(StateSnapshotContext, { node, children: /* @__PURE__ */ jsxs("li", { className: "flex min-w-0 flex-wrap items-center gap-2 text-xs", children: [
        /* @__PURE__ */ jsx(Identity, { value: edge.subject, chain: edge.chain_id, ...decoration }),
        /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: contractPredicateLabel(edge.predicate).toLowerCase() }),
        /* @__PURE__ */ jsx(Identity, { value: edge.target, chain: edge.chain_id, ...decoration }),
        /* @__PURE__ */ jsxs("span", { className: "break-all text-muted-foreground", children: [
          edge.value_ref,
          edge.output_path,
          " \xB7 ",
          edge.assurance
        ] })
      ] }) }, `${node.ref}:${edge.observation_id}:${index}`)) }) }),
      /* @__PURE__ */ jsxs(Disclosure, { className: "text-xs text-muted-foreground", summary: "Snapshot evidence and coverage", children: [
        /* @__PURE__ */ jsxs("p", { children: [
          result.rpc_calls,
          " RPC calls \xB7 ",
          result.lookup_calls ?? 0,
          " lookups. Expanding these results does not make additional on-chain reads."
        ] }),
        root?.block_hash && /* @__PURE__ */ jsxs("p", { className: "break-all", children: [
          "Block hash: ",
          root.block_hash
        ] }),
        nodes.filter((node) => node.is_proxy).map((node) => /* @__PURE__ */ jsx(StateSnapshotContext, { node, children: /* @__PURE__ */ jsxs("div", { className: "my-2 min-w-0", children: [
          /* @__PURE__ */ jsx(Identity, { value: node.address, chain: node.chain_id, ...decoration }),
          /* @__PURE__ */ jsxs("p", { children: [
            "Implementation at ",
            blockLabel(node),
            " (",
            node.proxy_pattern ?? "detected proxy",
            "):"
          ] }),
          node.implementation_addresses.map((implementation) => /* @__PURE__ */ jsx(Identity, { value: implementation, chain: node.chain_id, ...decoration }, implementation))
        ] }) }, node.ref)),
        coverage.length > 0 && /* @__PURE__ */ jsxs("p", { children: [
          "Coverage: ",
          coverage.join("; ")
        ] }),
        result.errors.map((message, index) => /* @__PURE__ */ jsx("p", { children: message }, index))
      ] })
    ] })
  ] }) });
}
function ContractStatePanel({
  canEditProfiles = false,
  canDiscoverProxy = false,
  identity = {},
  chainId,
  address,
  block,
  includeSourceCode,
  client,
  className,
  active = true,
  summaryOnly = false,
  showSummary = true,
  wrapDetails,
  relationships = true,
  accountMode = false,
  exploration: controlledExploration,
  onExplorationChange,
  onExploreContract,
  resolveOverlay,
  resolveName,
  onAddressesFound,
  onAddressRefsFound
}) {
  const [localExploration, setLocalExploration] = React50.useState("profile");
  const exploration = controlledExploration ?? localExploration;
  const setExploration = (next) => {
    if (controlledExploration === void 0) setLocalExploration(next);
    onExplorationChange?.(next);
  };
  const resolvedClient = useContractStateClient(client);
  const scope = `${chainId}:${normalizeAddress(address, chainId)}`;
  const prepared = React50.useMemo(
    () => {
      try {
        return { request: buildContractStateRequest({ chainId, address, block, includeSourceCode, ...stateExplorationOptions(relationships ? exploration : "none") }), error: null };
      } catch (cause) {
        return { request: null, error: cause instanceof Error ? cause : new Error(String(cause)) };
      }
    },
    [chainId, address, block, includeSourceCode, exploration, relationships]
  );
  const [applied, setApplied] = React50.useState(() => ({ scope, client: resolvedClient, prepared: active ? prepared : null }));
  if (applied.scope !== scope || applied.client !== resolvedClient || active && applied.prepared !== prepared) {
    setApplied({ scope, client: resolvedClient, prepared: active ? prepared : null });
  }
  const read = applied.prepared;
  const { result, error, isPending, unwired, refetch } = useContractState({ request: read?.request ?? null, client: resolvedClient });
  const explorationId = React50.useId();
  const observationNote = explorerCapabilities.resolve(chainId)?.relationshipSnapshot === "checkpoint" ? "Related objects and dynamic fields use the same checkpoint." : "Related accounts are separate current observations, not an atomic historical snapshot.";
  const details = /* @__PURE__ */ jsxs(Fragment, { children: [
    relationships && /* @__PURE__ */ jsxs("div", { className: "mb-3 space-y-1 text-xs", children: [
      /* @__PURE__ */ jsx("label", { htmlFor: explorationId, className: "mr-2 font-medium", children: accountMode ? "Related accounts" : "Related contracts" }),
      /* @__PURE__ */ jsxs(
        "select",
        {
          id: explorationId,
          value: exploration,
          onChange: (event) => setExploration(event.target.value),
          className: "rounded-md border bg-background px-2 py-1 text-sm",
          children: [
            /* @__PURE__ */ jsx("option", { value: "profile", children: "Profile defaults" }),
            /* @__PURE__ */ jsx("option", { value: "none", children: accountMode ? "This account only" : "This contract only" }),
            /* @__PURE__ */ jsx("option", { value: "1", children: "Explore 1 level" }),
            /* @__PURE__ */ jsx("option", { value: "2", children: "Explore 2 levels" }),
            /* @__PURE__ */ jsx("option", { value: "3", children: "Explore 3 levels" }),
            /* @__PURE__ */ jsx("option", { value: "4", children: "Explore 4 levels" })
          ]
        }
      ),
      /* @__PURE__ */ jsxs("p", { className: "text-muted-foreground", children: [
        accountMode ? exploration === "profile" ? `Uses this chain\u2019s configured relationship policy, including bounded native dependencies when supported. ${observationNote}` : exploration === "none" ? "Reads this account without expanding related addresses." : `Follows declared account references up to ${exploration} ${exploration === "1" ? "level" : "levels"} and 16 related accounts. ${observationNote}` : exploration === "profile" ? "Uses this contract type\u2019s default relationships." : exploration === "none" ? "Reads this contract without expanding related addresses." : `Follows address outputs up to ${exploration} ${exploration === "1" ? "level" : "levels"} and 16 related contracts. Each child uses its own profile; budget limits may return partial results. Contracts at the depth limit may have further relationships.`,
        " ",
        "Expanding rows shows the fetched snapshot. ",
        accountMode ? "Refresh reads current state again." : "Refresh fetches state again at the selected block."
      ] })
    ] }),
    canEditProfiles && resolvedClient?.profiles && result?.nodes[0]?.profile_match && /* @__PURE__ */ jsx(
      ProfileEditor,
      {
        client: resolvedClient.profiles,
        identity,
        name: result.nodes[0].profile_match.name,
        label: profileLabel(result.nodes[0].profile_match.name, result.nodes[0].profile_match),
        onSaved: refetch
      },
      JSON.stringify([scope, identity, resolvedClient.id])
    ),
    /* @__PURE__ */ jsx(ProxyDiscoveryContext.Provider, { value: canDiscoverProxy ? resolvedClient?.discovery ?? null : null, children: /* @__PURE__ */ jsx(
      ContractStateTree,
      {
        result,
        error: read?.error ?? error,
        isPending,
        unwired,
        onRefresh: read?.error ? void 0 : refetch,
        onExploreContract,
        resolveOverlay,
        resolveName
      }
    ) })
  ] });
  return /* @__PURE__ */ jsxs("div", { className, children: [
    /* @__PURE__ */ jsx(AddressReporter, { result, onAddressesFound, onAddressRefsFound }),
    showSummary && !unwired && (relationships || accountMode) && /* @__PURE__ */ jsx(
      RelationshipSummary,
      {
        result,
        error: read?.error ?? error,
        isPending,
        onRefresh: read?.error ? void 0 : refetch,
        resolveOverlay,
        resolveName
      }
    ),
    wrapDetails ? wrapDetails(details) : !summaryOnly && details
  ] });
}
function invalidBlock(text2) {
  const value = text2.trim().toLowerCase();
  if (!value || value === "latest") return false;
  const block = (0, contract_exports.parseBlock)(value);
  return !block || block.blockNumber !== void 0 && block.blockNumber < 0n;
}
function ContractStateView({ chainId, address, abi, isProxy, resolution, defaultBeforeBlock = "", defaultAfterBlock = "", includeLatest = true, assetContext, contractStateClient, className, onAddressesFound }) {
  const [beforeText, setBeforeText] = React50.useState(defaultBeforeBlock);
  const [afterText, setAfterText] = React50.useState(defaultAfterBlock);
  const [applied, setApplied] = React50.useState({ before: defaultBeforeBlock, after: defaultAfterBlock });
  const [revision, setRevision] = React50.useState(0);
  const column = (text2, emptyLabel) => {
    const block = (0, contract_exports.parseBlock)(text2);
    const invalid = invalidBlock(text2);
    return {
      invalid,
      isProxy,
      label: invalid ? `${emptyLabel} \xB7 unreadable block` : block ? `Block ${(0, contract_exports.blockKey)(block).replace(/^#/, "")}` : emptyLabel,
      ref: { chainId, address, block }
    };
  };
  const columns = [column(applied.before, "Before (latest)"), column(applied.after, "After (latest)")];
  if (includeLatest) columns.push({ label: "Latest", ref: { chainId, address }, isProxy });
  const invalidBefore = invalidBlock(beforeText);
  const invalidAfter = invalidBlock(afterText);
  const sameBlock = !invalidBlock(applied.before) && !invalidBlock(applied.after) && (0, contract_exports.blockKey)((0, contract_exports.parseBlock)(applied.before)) === (0, contract_exports.blockKey)((0, contract_exports.parseBlock)(applied.after));
  const commit = (key, text2) => setApplied((prev) => ({ ...prev, [key]: text2 }));
  const selectedAsset = assetContext?.address.toLowerCase() === address.toLowerCase() ? assetContext : void 0;
  const numericBlocks = /^\d+$/.test(applied.before) && /^\d+$/.test(applied.after) && Number.isSafeInteger(Number(applied.before)) && Number.isSafeInteger(Number(applied.after)) && Number(applied.after) >= 1;
  if (assetContext && !selectedAsset) return /* @__PURE__ */ jsx("p", { role: "alert", children: "This asset comparison belongs to a different contract. Reopen it from the transaction asset picker." });
  if (!abi && !selectedAsset) return /* @__PURE__ */ jsx(EmptyState, { title: "No ABI", description: "Resolve or paste an ABI to read the contract's state.", size: "sm", className });
  const unavailable = selectedAsset ? [] : abi.reads.filter((fn) => !fn.inputs.length && unavailableReadReason(fn, isProxy));
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-4", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-end gap-3", children: [
      /* @__PURE__ */ jsx(Field, { label: "Before block", labelClassName: "text-xs text-muted-foreground", children: /* @__PURE__ */ jsx(
        Input,
        {
          value: beforeText,
          onChange: (e) => setBeforeText(e.target.value),
          onBlur: () => commit("before", beforeText),
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit("before", beforeText);
            }
          },
          "aria-invalid": invalidBefore || void 0,
          placeholder: "latest",
          className: "h-8 w-36 font-mono text-xs"
        }
      ) }),
      /* @__PURE__ */ jsx(Field, { label: "After block", labelClassName: "text-xs text-muted-foreground", children: /* @__PURE__ */ jsx(
        Input,
        {
          value: afterText,
          onChange: (e) => setAfterText(e.target.value),
          onBlur: () => commit("after", afterText),
          onKeyDown: (e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              commit("after", afterText);
            }
          },
          "aria-invalid": invalidAfter || void 0,
          placeholder: "latest",
          className: "h-8 w-36 font-mono text-xs"
        }
      ) }),
      /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: () => {
        setApplied({ before: beforeText, after: afterText });
        setRevision((n) => n + 1);
      }, children: "Refresh" })
    ] }),
    /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
      "Press Enter or leave a field to apply its block. ",
      selectedAsset ? "This comparison preserves the selected asset and holders. Each snapshot resolves its ABI at that block through the state service using your current tenancy. Only the requested token getters are read." : resolution ? "Each column resolves its proxy and ABI at that block, using your current tenancy and source settings. Inline overrides remain explicit overrides." : "Reads use the selected ABI at every block; proxy upgrades may require a different historical ABI."
    ] }),
    unavailable.length > 0 && /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
      unavailable.length,
      " ",
      unavailable.length === 1 ? "function requires" : "functions require",
      " a different call context or a verified signature and ",
      unavailable.length === 1 ? "is" : "are",
      " excluded from comparison. See the Read tab for details."
    ] }),
    (invalidBefore || invalidAfter) && /* @__PURE__ */ jsxs("p", { role: "alert", className: "text-xs text-destructive", children: [
      "Enter a block height or tag (",
      contract_exports.BLOCK_TAGS.join(", "),
      "). Invalid columns cannot be compared."
    ] }),
    sameBlock && /* @__PURE__ */ jsxs("p", { role: "status", className: "rounded-md border border-dashed px-3 py-2 text-xs text-muted-foreground", children: [
      "Both sides read the same block (",
      (0, contract_exports.blockKey)((0, contract_exports.parseBlock)(applied.before)).replace(/^#/, ""),
      "), so nothing can differ yet. Set an earlier Before block \u2014 the one before an upgrade, an exploit or a governance change \u2014 to see what changed since."
    ] }),
    selectedAsset ? numericBlocks ? /* @__PURE__ */ jsx(
      StateAtTransaction,
      {
        client: contractStateClient,
        chainId,
        address,
        beforeBlockNumber: Number(applied.before),
        blockNumber: Number(applied.after),
        variables: selectedAsset.variables,
        focus: "asset",
        summary: "Selected asset state",
        defaultOpen: true,
        allowLatest: true,
        renderAddress: (value) => /* @__PURE__ */ jsx(AddressDisplay, { address: value, chain: chainId })
      },
      `${chainId}:${address}:${applied.before}:${applied.after}:${revision}:${JSON.stringify(selectedAsset.variables)}`
    ) : /* @__PURE__ */ jsx("p", { role: "alert", className: "text-xs text-destructive", children: "Selected asset comparisons need numeric block heights within the supported range. Use \u201CCompare with latest snapshot\u201D for a separate current read." }) : /* @__PURE__ */ jsx(ContractSnapshotComparison, { columns, abi, resolution, revision, enrichmentChain: chainId, onAddressesFound })
  ] });
}
function AddressIdentityChip({
  identity,
  className
}) {
  if (!identity) return null;
  const labels = identity.labels ?? [];
  const tags = identity.tags ?? [];
  const hasAny = identity.entity_name || labels.length > 0 || tags.length > 0;
  if (!hasAny) return null;
  const sourceTitle = identity.source ? `identity source: ${identity.source}` : void 0;
  return /* @__PURE__ */ jsxs("span", { className: cn("inline-flex flex-wrap items-center gap-1", className), title: sourceTitle, children: [
    identity.entity_name && /* @__PURE__ */ jsx(Badge, { variant: "default", className: "text-3xs", children: identity.entity_name }),
    labels.map((label) => /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "text-3xs", children: label }, `l-${label}`)),
    tags.map((tag) => /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-3xs", children: [
      "#",
      tag
    ] }, `t-${tag}`))
  ] });
}
function deltaClass(delta) {
  if (!delta || delta === "0") return "text-muted-foreground";
  return delta.startsWith("-") ? "text-flow-out" : "text-flow-in";
}
function display(raw, formatted) {
  return formatted ?? raw ?? "\u2014";
}
function tokenKey(change) {
  return change.is_native ? "native" : change.token_address ?? "?";
}
function identityKeyLookup(identities, chainId, holder) {
  const address = holder.toLowerCase();
  return identities[`${chainId}:${address}`] ?? identities[address] ?? null;
}
function group(changes) {
  const byChain = /* @__PURE__ */ new Map();
  for (const change of changes) {
    const byHolder = byChain.get(change.chain_id) ?? /* @__PURE__ */ new Map();
    const list = byHolder.get(change.holder) ?? [];
    list.push(change);
    byHolder.set(change.holder, list);
    byChain.set(change.chain_id, byHolder);
  }
  return byChain;
}
function TokenCell({ change }) {
  if (change.is_native) {
    return /* @__PURE__ */ jsxs("span", { className: "inline-flex items-center gap-1", children: [
      /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-3xs", children: "native" }),
      change.symbol && /* @__PURE__ */ jsx("span", { className: "text-xs font-medium", children: change.symbol })
    ] });
  }
  return /* @__PURE__ */ jsx(
    AddressDisplay,
    {
      address: change.token_address ?? "",
      chain: change.chain_id,
      label: change.symbol ?? void 0
    }
  );
}
function DuringTimeline({ change }) {
  const [open, setOpen] = React50.useState(false);
  if (change.during.length === 0) return null;
  return /* @__PURE__ */ jsxs("div", { className: "mt-1", children: [
    /* @__PURE__ */ jsxs(
      Button,
      {
        variant: "ghost",
        size: "sm",
        className: "h-5 px-1 text-2xs text-muted-foreground",
        onClick: () => setOpen((o) => !o),
        "aria-expanded": open,
        children: [
          /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "mr-1 inline-block w-2", children: open ? "\u25BE" : "\u25B8" }),
          change.timeline_scope === "transaction" ? "transaction timeline" : "estimated timeline",
          " \xB7 ",
          change.during.length,
          " step",
          change.during.length === 1 ? "" : "s"
        ]
      }
    ),
    open && /* @__PURE__ */ jsx("ol", { className: "ml-3 mt-0.5 space-y-0.5 border-l border-border/60 pl-2 font-mono text-2xs", children: change.during.map((point) => /* @__PURE__ */ jsxs("li", { className: "flex items-center gap-2", children: [
      point.log_index != null && /* @__PURE__ */ jsxs("span", { className: "text-muted-foreground", children: [
        "log#",
        point.log_index
      ] }),
      /* @__PURE__ */ jsxs("span", { className: deltaClass(point.delta_raw), children: [
        point.delta_raw.startsWith("-") ? "" : "+",
        point.delta_raw
      ] }),
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: "\u2192" }),
      /* @__PURE__ */ jsx("span", { children: point.display ?? point.balance_raw })
    ] }, point.seq)) })
  ] });
}
function ChangeRow({ change }) {
  return /* @__PURE__ */ jsxs("div", { className: "grid grid-cols-[minmax(7rem,10rem)_1fr_auto] items-start gap-2 py-1.5", children: [
    /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
      /* @__PURE__ */ jsx(TokenCell, { change }),
      /* @__PURE__ */ jsxs("span", { className: "text-3xs text-muted-foreground", children: [
        change.balance_scope === "transaction" ? "Transaction change" : "Whole-block change",
        change.before_block != null && change.after_block != null && ` \xB7 ${change.before_block} \u2192 ${change.after_block}`
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-1.5 font-mono text-xs", children: [
      /* @__PURE__ */ jsx("span", { className: "text-muted-foreground", children: display(change.before_raw, change.before_display) }),
      /* @__PURE__ */ jsx("span", { "aria-hidden": true, className: "text-muted-foreground", children: "\u2192" }),
      /* @__PURE__ */ jsx("span", { children: display(change.after_raw, change.after_display) }),
      (change.warnings ?? []).map((warning) => /* @__PURE__ */ jsx("span", { className: "w-full text-2xs text-muted-foreground", children: warning }, warning)),
      change.error && /* @__PURE__ */ jsxs("span", { className: "text-2xs text-destructive", children: [
        "(",
        change.error,
        ")"
      ] })
    ] }),
    /* @__PURE__ */ jsxs("div", { className: cn("text-right font-mono text-xs", deltaClass(change.delta_raw)), children: [
      change.delta_raw && change.delta_raw !== "0" ? change.delta_display ?? `${change.delta_raw.startsWith("-") ? "" : "+"}${change.delta_raw}` : change.delta_raw === "0" ? "\xB10" : "\u2014",
      change.truncated && /* @__PURE__ */ jsx(Badge, { variant: "destructive", className: "ml-1 text-3xs", children: "truncated" }),
      /* @__PURE__ */ jsx(DuringTimeline, { change })
    ] })
  ] });
}
function HolderCard({
  chainId,
  holder,
  changes,
  identity
}) {
  return /* @__PURE__ */ jsxs("div", { className: "rounded-md border bg-card p-2.5", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
      /* @__PURE__ */ jsx(AddressDisplay, { address: holder, chain: chainId }),
      /* @__PURE__ */ jsx(AddressIdentityChip, { identity })
    ] }),
    /* @__PURE__ */ jsx("div", { className: "mt-1.5 divide-y divide-border/60", children: changes.slice().sort((a, b) => tokenKey(a).localeCompare(tokenKey(b))).map((change) => /* @__PURE__ */ jsx(ChangeRow, { change }, tokenKey(change))) })
  ] });
}
function BalanceChangesPanel({
  insight,
  isPending = false,
  error = null,
  unwired = false,
  onRefresh,
  className
}) {
  if (unwired) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Transaction insight not configured",
        description: "Provide a TransactionInsightClient (via TransactionInsightClientProvider or the client prop) to read balance diffs + identity through the backend.",
        size: "sm",
        className
      }
    );
  }
  if (error) {
    return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), children: [
      /* @__PURE__ */ jsx(EmptyState, { title: "Couldn't read balances", description: error.message, size: "sm" }),
      onRefresh && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: onRefresh, children: "Retry" })
    ] });
  }
  if (isPending && !insight) {
    return /* @__PURE__ */ jsxs("div", { className: cn("flex items-center gap-2 py-6 text-sm text-muted-foreground", className), children: [
      /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      " Reading balances\u2026"
    ] });
  }
  if (!insight || insight.balance_changes.length === 0) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "No balance changes",
        description: "The backend returned no balance changes for this transaction.",
        size: "sm",
        className
      }
    );
  }
  const grouped = group(insight.balance_changes);
  const identityFor = (chainId, holder) => identityKeyLookup(insight.identities, chainId, holder);
  return /* @__PURE__ */ jsxs("div", { className: cn("space-y-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2 text-xs text-muted-foreground", children: [
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-3xs", children: [
        insight.rpc_calls,
        " RPC call",
        insight.rpc_calls === 1 ? "" : "s"
      ] }),
      /* @__PURE__ */ jsxs(Badge, { variant: "outline", className: "text-3xs", children: [
        insight.balance_changes.length,
        " balance change",
        insight.balance_changes.length === 1 ? "" : "s"
      ] }),
      insight.truncated && /* @__PURE__ */ jsx(Badge, { variant: "destructive", className: "text-3xs", children: "truncated (budget hit)" }),
      isPending && /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      onRefresh && /* @__PURE__ */ jsx(Button, { size: "sm", variant: "ghost", className: "ml-auto h-6 px-1.5 text-xs", onClick: onRefresh, children: "Refresh" })
    ] }),
    insight.errors.length > 0 && /* @__PURE__ */ jsx("ul", { className: "space-y-0.5", children: insight.errors.map((err, i) => /* @__PURE__ */ jsx("li", { className: "text-xs text-destructive", children: err }, i)) }),
    Array.from(grouped.entries()).map(([chainId, byHolder]) => /* @__PURE__ */ jsxs("div", { className: "space-y-2", children: [
      /* @__PURE__ */ jsxs("p", { className: "text-xs font-semibold text-muted-foreground", children: [
        "Chain ",
        chainId
      ] }),
      Array.from(byHolder.entries()).map(([holder, changes]) => /* @__PURE__ */ jsx(
        HolderCard,
        {
          chainId,
          holder,
          changes,
          identity: identityFor(chainId, holder)
        },
        holder
      ))
    ] }, chainId))
  ] });
}

// src/ui/sourceFiles.ts
function sourceFiles(source) {
  const trimmed = source.trim();
  try {
    const parsed = JSON.parse(trimmed.startsWith("{{") && trimmed.endsWith("}}") ? trimmed.slice(1, -1) : trimmed);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      const record2 = parsed;
      const standard = record2.sources && typeof record2.sources === "object" && !Array.isArray(record2.sources);
      const entries = Object.entries(standard ? record2.sources : record2);
      const files = entries.map(([path, value]) => ({ path, content: typeof value === "string" ? value : value && typeof value === "object" && "content" in value && typeof value.content === "string" ? value.content : null }));
      if (files.length && files.every((file) => file.content !== null && (standard || /[./\\]/.test(file.path)))) {
        return files;
      }
    }
  } catch {
  }
  return [{ path: "Source", content: source }];
}
function isUnverifiedGuess(data) {
  return !data.verified && !data.sourceCode && !data.contractName && !data.compilerVersion;
}
function unverifiedDescription(data) {
  const who = data.contributors?.length ? data.contributors.join(", ") : "a verifier";
  const entries = data.abiEntriesCount ?? 0;
  const guess = entries ? ` ${who} recovered ${entries} ${entries === 1 ? "entry" : "entries"} from its deployed code \u2014 a guess from the bytecode, not source.` : "";
  return `No explorer we asked (Codeslaw, Etherscan, Blockscout, Sourcify) has verified source for this contract.${guess} Reading the contract's own code (Look for this contract's ABI) recovers what can be recovered.`;
}
function SourceCodeView({ source, selectedPath, onSelectPath }) {
  const files = React50.useMemo(() => sourceFiles(source), [source]);
  const selected = files.find((file) => file.path === selectedPath) ?? files[0];
  return /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-col gap-1.5", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsx("span", { className: "text-sm font-medium", children: "Source code" }),
      /* @__PURE__ */ jsx(CopyButton, { value: selected.content })
    ] }),
    (files.length > 1 || selected.path !== "Source") && /* @__PURE__ */ jsxs("label", { className: "flex min-w-0 flex-col gap-1 text-xs text-muted-foreground", children: [
      "Source file (",
      files.length,
      ")",
      /* @__PURE__ */ jsx(
        "select",
        {
          "aria-label": "Source file",
          value: selected.path,
          onChange: (event) => onSelectPath(event.target.value),
          className: "h-8 w-full min-w-0 rounded-md border bg-background px-2 text-foreground",
          children: files.map((file) => /* @__PURE__ */ jsx("option", { value: file.path, children: file.path }, file.path))
        }
      )
    ] }),
    /* @__PURE__ */ jsx("pre", { className: "max-h-96 overflow-auto rounded-md border bg-muted/40 p-3 text-xs leading-relaxed", children: /* @__PURE__ */ jsx("code", { children: selected.content }) })
  ] });
}
function MetaItem({ label, children }) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-0.5", children: [
    /* @__PURE__ */ jsx("dt", { className: "text-xs uppercase tracking-wide text-muted-foreground", children: label }),
    /* @__PURE__ */ jsx("dd", { className: "text-sm text-foreground", children })
  ] });
}
function ContractSourcePanel({
  client,
  chainId,
  address,
  block = null,
  className
}) {
  const [withBytecode, setWithBytecode] = React50.useState(false);
  const [selectedSourcePath, setSelectedSourcePath] = React50.useState("");
  const reader = React50.useMemo(() => client ? {
    read(request, { signal }) {
      const { chainId: chain, address: target, ...options } = request;
      return client({ chainId: chain, address: target }, { ...options, signal });
    }
  } : null, [client]);
  const invalidBlock2 = block != null && (!Number.isSafeInteger(Number(block)) || Number(block) < 0);
  const { data, isPending: isLoading, error, refetch: refresh } = useClientRead({
    client: reader,
    request: invalidBlock2 ? null : {
      chainId,
      address,
      includeBytecode: withBytecode,
      ...block != null ? { block: Number(block) } : {}
    }
  });
  if (!client) {
    return /* @__PURE__ */ jsx("div", { className, children: /* @__PURE__ */ jsx(
      EmptyState,
      {
        size: "sm",
        title: "Source lookup not configured",
        description: "The host didn't wire a contract-source client. Verified source code and bytecode are unavailable here."
      }
    ) });
  }
  if (invalidBlock2) return /* @__PURE__ */ jsx(Alert, { variant: "destructive", className, children: /* @__PURE__ */ jsx(AlertDescription, { children: "Bytecode block must be a non-negative safe integer." }) });
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-3", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-3", children: [
      /* @__PURE__ */ jsx(
        Field,
        {
          label: /* @__PURE__ */ jsxs(Fragment, { children: [
            "Include bytecode",
            block != null ? ` at block ${block.toLocaleString()}` : ""
          ] }),
          orientation: "horizontal",
          reverse: true,
          labelClassName: "text-sm text-muted-foreground",
          children: /* @__PURE__ */ jsx(Switch, { checked: withBytecode, onCheckedChange: setWithBytecode })
        }
      ),
      /* @__PURE__ */ jsx(Button, { type: "button", variant: "outline", size: "sm", disabled: isLoading, onClick: refresh, children: isLoading ? /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsx(Spinner, { size: "sm", className: "mr-2 text-current" }),
        "Fetching\u2026"
      ] }) : "Refresh" })
    ] }),
    /* @__PURE__ */ jsxs("p", { className: "break-all text-xs text-muted-foreground", children: [
      "Source and bytecode address: ",
      /* @__PURE__ */ jsx(AddressDisplay, { address, chain: chainId })
    ] }),
    error && data && /* @__PURE__ */ jsx(Alert, { variant: "destructive", children: /* @__PURE__ */ jsxs(AlertDescription, { className: "text-xs", children: [
      "Couldn't refresh: ",
      error.message,
      ". Showing the previously loaded result."
    ] }) }),
    isLoading && !data ? /* @__PURE__ */ jsxs("p", { className: "flex items-center gap-2 text-sm text-muted-foreground", children: [
      /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
      " Fetching verified source\u2026"
    ] }) : error && !data ? /* @__PURE__ */ jsx(ErrorState, { error, onRetry: refresh }) : !data || !data.found ? /* @__PURE__ */ jsx(
      EmptyState,
      {
        size: "sm",
        title: "No verified source found",
        description: "No third-party verifier (Codeslaw, Etherscan, Blockscout, Sourcify) returned verified source for this address. It may be unverified, or you can retry."
      }
    ) : isUnverifiedGuess(data) ? (
      // Something answered, but not with source: a verifier's bytecode-derived
      // guess (Codeslaw's WhatsABI-style read) for a contract nobody verified.
      // It used to render as a metadata sheet of dashes under "Unverified ·
      // confidence 25%" and "Metadata was found…", which read as a partial
      // verification. It is none — say so, and name the guess as a guess.
      /* @__PURE__ */ jsx(
        EmptyState,
        {
          size: "sm",
          title: "Not verified",
          description: unverifiedDescription(data)
        }
      )
    ) : /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-4", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
        data.verified ? /* @__PURE__ */ jsx(Badge, { variant: "default", children: "Verified" }) : /* @__PURE__ */ jsx(Badge, { variant: "outline", children: "Unverified" }),
        data.isProxy && /* @__PURE__ */ jsx(Badge, { variant: "secondary", children: "Proxy" }),
        data.language && /* @__PURE__ */ jsx(Badge, { variant: "outline", children: data.language }),
        typeof data.confidence === "number" && /* @__PURE__ */ jsxs(Badge, { variant: "outline", title: "How much the providers that answered agree on this ABI", children: [
          "ABI confidence ",
          Math.round(data.confidence * 100),
          "%"
        ] })
      ] }),
      /* @__PURE__ */ jsxs("dl", { className: "grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3", children: [
        /* @__PURE__ */ jsx(MetaItem, { label: "Contract", children: data.contractName || "\u2014" }),
        /* @__PURE__ */ jsx(MetaItem, { label: "Compiler", children: data.compilerVersion || "\u2014" }),
        /* @__PURE__ */ jsx(MetaItem, { label: "License", children: data.license || "\u2014" }),
        /* @__PURE__ */ jsx(MetaItem, { label: "Optimization", children: data.optimizationUsed == null ? "\u2014" : data.optimizationUsed ? `Yes${data.runs != null ? ` (${data.runs} runs)` : ""}` : "No" }),
        /* @__PURE__ */ jsx(MetaItem, { label: "ABI entries", children: data.abiEntriesCount ?? "\u2014" }),
        /* @__PURE__ */ jsx(MetaItem, { label: "Source", children: data.origin })
      ] }),
      data.isProxy && data.implementationAddresses && data.implementationAddresses.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1", children: [
        /* @__PURE__ */ jsx("span", { className: "text-xs uppercase tracking-wide text-muted-foreground", children: "Implementations" }),
        /* @__PURE__ */ jsx("ul", { className: "flex flex-col gap-1", children: data.implementationAddresses.map((impl) => /* @__PURE__ */ jsxs("li", { className: "flex items-center gap-2 font-mono text-xs", children: [
          /* @__PURE__ */ jsx("span", { className: "truncate", children: impl }),
          /* @__PURE__ */ jsx(CopyButton, { value: impl })
        ] }, impl)) })
      ] }),
      data.sourceAvailable && data.sourceCode ? /* @__PURE__ */ jsx(SourceCodeView, { source: data.sourceCode, selectedPath: selectedSourcePath, onSelectPath: setSelectedSourcePath }) : /* @__PURE__ */ jsx(Alert, { children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: "The verifier returned this contract's metadata but not its source code." }) })
    ] }),
    withBytecode && data?.bytecode && /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
        /* @__PURE__ */ jsxs("span", { className: "text-sm font-medium", children: [
          "Deployed bytecode",
          typeof data.bytecode.sizeBytes === "number" && /* @__PURE__ */ jsxs("span", { className: "ml-2 text-xs font-normal text-muted-foreground", children: [
            data.bytecode.sizeBytes.toLocaleString(),
            " bytes"
          ] })
        ] }),
        data.bytecode.bytecode && /* @__PURE__ */ jsx(CopyButton, { value: data.bytecode.bytecode })
      ] }),
      data.bytecode.error ? /* @__PURE__ */ jsxs("p", { className: "text-xs text-destructive", children: [
        "Couldn't read bytecode: ",
        data.bytecode.error
      ] }) : data.bytecode.present === false ? /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "No code at this address (EOA or not deployed)." }) : data.bytecode.bytecode ? /* @__PURE__ */ jsx("pre", { className: "max-h-48 overflow-auto break-all rounded-md border bg-muted/40 p-3 font-mono text-xs", children: data.bytecode.bytecode }) : null
    ] })
  ] });
}

// src/ui/eventArguments.ts
function eventArguments(log6, abi) {
  if (!log6.args) return [];
  const event = log6.decoded && log6.topic0 ? abi?.topicIndex[log6.topic0] : void 0;
  if (event) return event.inputs.map((input, index) => {
    const value = Array.isArray(log6.args) ? log6.args[index] : Object.prototype.hasOwnProperty.call(log6.args, input.name || "") ? log6.args[input.name] : void 0;
    const hashed = input.indexed && (input.type === "string" || input.type === "bytes" || input.type.startsWith("tuple") || input.type.endsWith("]"));
    return {
      name: input.name || `#${index}`,
      value,
      parameter: hashed ? { name: input.name, type: "bytes32" } : input
    };
  });
  return Array.isArray(log6.args) ? log6.args.map((value, index) => ({ name: `#${index}`, value })) : Object.entries(log6.args).filter(([name]) => !/^\d+$/.test(name)).map(([name, value]) => ({ name, value }));
}
var UNKNOWN = "(unknown)";
var WIDEN_STEPS = [100n, 1000n];
function nextWiden(current) {
  return WIDEN_STEPS.find((step) => current === null || step > current) ?? null;
}
function eventsErrorText(error) {
  const details = error.details ?? error.message;
  if (/archive/i.test(details)) {
    return "The data provider refused this range: blocks this far back need archive access. Try a narrower range, or a more recent one.";
  }
  return formatReadError(error);
}
function scannedBlockPhrase(block) {
  if (block == null || typeof block === "string") return `Only the ${block ?? "latest"} block`;
  return `Only block ${block.toLocaleString("en-US")}`;
}
function logBound(text2) {
  const value = text2.trim().toLowerCase();
  if (!value) return void 0;
  if (value === "latest") return "latest";
  const parsed = (0, contract_exports.parseBlock)(value);
  if (!parsed || parsed.blockNumber !== void 0 && parsed.blockNumber < 0n) {
    throw new Error(`Enter a non-negative block height or tag (${contract_exports.BLOCK_TAGS.join(", ")}).`);
  }
  return parsed.blockNumber ?? parsed.blockTag;
}
function LogRow({
  log: log6,
  chain,
  onDrilldown,
  entries,
  namedAddresses
}) {
  return /* @__PURE__ */ jsxs("div", { className: "flex flex-col gap-1.5 px-3 py-2.5", children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between gap-3", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 items-center gap-2", children: [
        /* @__PURE__ */ jsx(Badge, { variant: log6.decoded ? "secondary" : "outline", className: "whitespace-nowrap text-3xs", children: log6.eventName ?? UNKNOWN }),
        log6.blockNumber != null && /* @__PURE__ */ jsxs("span", { className: "whitespace-nowrap text-xs text-muted-foreground", children: [
          "block ",
          log6.blockNumber.toString()
        ] })
      ] }),
      log6.transactionHash ? /* @__PURE__ */ jsx(
        HashDisplay,
        {
          hash: log6.transactionHash,
          chain,
          type: "tx",
          onDrilldown
        }
      ) : null
    ] }),
    entries.length > 0 ? /* @__PURE__ */ jsx("dl", { className: "grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5", children: entries.map((entry, index) => /* @__PURE__ */ jsxs(React50.Fragment, { children: [
      /* @__PURE__ */ jsx("dt", { className: "font-mono text-2xs text-muted-foreground", children: entry.name }),
      /* @__PURE__ */ jsx("dd", { className: "min-w-0", children: entry.parameter ? /* @__PURE__ */ jsx(
        ReadValue,
        {
          outputs: [entry.parameter],
          data: entry.value,
          renderAddress: (address, { decorated }) => /* @__PURE__ */ jsx(
            AddressDisplay,
            {
              address,
              chain,
              copyable: decorated,
              overlay: decorated && namedAddresses.has(normalizeAddress(address, chain)) ? void 0 : null
            }
          )
        }
      ) : /* @__PURE__ */ jsx("code", { className: "break-all font-mono text-2xs", children: formatReadValue(entry.value) }) })
    ] }, `${index}:${entry.name}`)) }) : !log6.decoded ? /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-2xs text-muted-foreground", children: [
      /* @__PURE__ */ jsx("code", { className: "break-all", children: log6.topic0 ?? "no topic0" }),
      log6.reason ? /* @__PURE__ */ jsxs("span", { className: "italic", children: [
        "\u2014 ",
        log6.reason
      ] }) : null,
      log6.topic0 ? /* @__PURE__ */ jsx(CopyButton, { value: log6.topic0 }) : null
    ] }) : null
  ] });
}
function EventLogPanel({
  contract,
  abi,
  abiResolving = false,
  query,
  reader,
  onDrilldown,
  onAddressesFound,
  maxNamedAddresses = 16,
  className
}) {
  const selectedBlock = contract.block?.blockNumber ?? contract.block?.blockTag ?? "latest";
  const baseQuery = query ?? { fromBlock: selectedBlock, toBlock: selectedBlock };
  const [fromText, setFromText] = React50.useState(() => baseQuery.fromBlock?.toString() ?? "");
  const [toText, setToText] = React50.useState(() => baseQuery.toBlock?.toString() ?? "");
  const [range, setRange] = React50.useState(null);
  const [rangeError, setRangeError] = React50.useState(null);
  const [autoWidened, setAutoWidened] = React50.useState(false);
  if (useDepsChanged([(0, contract_exports.contractRefKey)(contract), query?.fromBlock, query?.toBlock])) {
    setFromText(baseQuery.fromBlock?.toString() ?? "");
    setToText(baseQuery.toBlock?.toString() ?? "");
    setRange(null);
    setRangeError(null);
    setAutoWidened(false);
  }
  const effectiveQuery = range ? { ...baseQuery, ...range } : baseQuery;
  const singleBlock = effectiveQuery?.fromBlock != null && effectiveQuery.fromBlock.toString() === (effectiveQuery.toBlock ?? "").toString();
  const lookback = effectiveQuery?.fromBlock == null ? effectiveQuery?.lookback ?? null : null;
  const widenTo = singleBlock ? nextWiden(null) : lookback != null ? nextWiden(lookback) : null;
  const widen = (blocks) => {
    setFromText("");
    setRange({ fromBlock: void 0, toBlock: effectiveQuery?.toBlock, lookback: blocks });
    setRangeError(null);
  };
  const { logs, summary, isPending, error, supported, fromCache, settled, refetch } = useEventLogs(contract, abi, {
    reader,
    query: effectiveQuery
  });
  const autoRange = React50.useRef(null);
  const mayAutoWiden = !query && range === null && selectedBlock === "latest" && !autoWidened && supported;
  React50.useEffect(() => {
    if (!mayAutoWiden || !settled || logs.length > 0) return;
    const widened = { fromBlock: void 0, toBlock: "latest", lookback: WIDEN_STEPS[0] };
    autoRange.current = widened;
    setAutoWidened(true);
    setFromText("");
    setRange(widened);
  }, [mayAutoWiden, settled, logs.length]);
  const [selected, setSelected] = React50.useState(/* @__PURE__ */ new Set());
  const applyRange = (from, to) => {
    try {
      const fromBlock = logBound(from);
      const toBlock = logBound(to);
      if (typeof fromBlock === "bigint" && typeof toBlock === "bigint" && fromBlock > toBlock) {
        throw new Error("From block must not be after To block.");
      }
      setRange({ fromBlock, toBlock });
      setRangeError(null);
      refetch();
    } catch (cause) {
      setRangeError(cause instanceof Error ? cause.message : String(cause));
    }
  };
  const toggle = React50.useCallback((name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);
  const active = React50.useMemo(() => {
    const names = new Set(summary.map((s) => s.name));
    return new Set([...selected].filter((name) => names.has(name)));
  }, [selected, summary]);
  const visible = React50.useMemo(
    () => active.size === 0 ? logs : logs.filter((log6) => active.has(log6.eventName ?? UNKNOWN)),
    [logs, active]
  );
  const rows = React50.useMemo(() => visible.map((log6) => ({ log: log6, entries: eventArguments(log6, abi) })), [visible, abi]);
  const addressKey2 = React50.useMemo(() => {
    const found = /* @__PURE__ */ new Set();
    for (const { entries } of rows) for (const entry of entries) {
      if (!entry.parameter) continue;
      for (const address of addressLeaves([entry.parameter], entry.value)) {
        if (found.size >= maxNamedAddresses) return [...found].join(",");
        if (/^0x[\da-f]{40}$/i.test(address) && !/^0x0{40}$/i.test(address)) found.add(normalizeAddress(address, contract.chainId));
      }
    }
    return [...found].join(",");
  }, [rows, maxNamedAddresses, contract.chainId]);
  const namedAddresses = React50.useMemo(() => new Set(addressKey2 ? addressKey2.split(",") : []), [addressKey2]);
  React50.useEffect(() => {
    onAddressesFound?.([...namedAddresses], "events:values");
    return () => onAddressesFound?.([], "events:values");
  }, [namedAddresses, onAddressesFound]);
  if (!supported) {
    return /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Event logs unavailable",
        description: "The active reader can't fetch logs. Use the RPC reader or a backend reader with a logs transport.",
        size: "sm"
      }
    );
  }
  return /* @__PURE__ */ jsxs("div", { className: cn("flex flex-col gap-2", className), children: [
    /* @__PURE__ */ jsxs("div", { className: "flex items-center justify-between", children: [
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2", children: [
        /* @__PURE__ */ jsx("h4", { className: "text-sm font-semibold", children: "Events" }),
        /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-3xs", children: logs.length }),
        fromCache && /* @__PURE__ */ jsx(Badge, { variant: "outline", className: "text-3xs text-muted-foreground", children: "cached" })
      ] }),
      /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-2 text-xs text-muted-foreground", children: [
        isPending && /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
        /* @__PURE__ */ jsx("button", { type: "button", className: "hover:text-foreground", onClick: refetch, children: "Refresh" })
      ] })
    ] }),
    /* @__PURE__ */ jsxs("form", { className: "flex flex-wrap items-end gap-2", onSubmit: (event) => {
      event.preventDefault();
      applyRange(fromText, toText);
    }, children: [
      /* @__PURE__ */ jsx(Field, { label: "From block", labelClassName: "text-xs text-muted-foreground", children: /* @__PURE__ */ jsx(Input, { value: fromText, onChange: (event) => setFromText(event.target.value), placeholder: "recent window", className: "h-8 w-36 font-mono text-xs" }) }),
      /* @__PURE__ */ jsx(Field, { label: "To block", labelClassName: "text-xs text-muted-foreground", children: /* @__PURE__ */ jsx(Input, { value: toText, onChange: (event) => setToText(event.target.value), placeholder: (0, contract_exports.blockKey)(contract.block).replace(/^#/, ""), className: "h-8 w-36 font-mono text-xs" }) }),
      /* @__PURE__ */ jsx(Button, { type: "submit", size: "sm", variant: "outline", children: "Apply range" }),
      /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "ghost", onClick: () => {
        setFromText("latest");
        setToText("latest");
        applyRange("latest", "latest");
      }, children: "Latest block only" }),
      range && /* @__PURE__ */ jsx(Button, { type: "button", size: "sm", variant: "ghost", onClick: () => {
        setFromText(baseQuery.fromBlock?.toString() ?? "");
        setToText(baseQuery.toBlock?.toString() ?? "");
        setRange(null);
        setRangeError(null);
      }, children: "Reset range" })
    ] }),
    /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
      "Scanning ",
      effectiveQuery?.fromBlock?.toString() ?? (lookback != null ? `the last ${lookback.toLocaleString("en-US")} blocks` : "a recent window"),
      " \u2192 ",
      effectiveQuery?.toBlock?.toString() ?? (0, contract_exports.blockKey)(contract.block).replace(/^#/, ""),
      ".",
      " ",
      "Older ranges require an RPC provider with archive access.",
      range !== null && range === autoRange.current ? " The latest block had no events, so the scan was widened once." : ""
    ] }),
    /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Choose an earlier From block to explore history. Clear it to scan a recent window." }),
    rangeError && /* @__PURE__ */ jsxs("p", { role: "alert", className: "text-xs text-destructive", children: [
      "Range not applied. ",
      rangeError
    ] }),
    summary.length > 0 && /* @__PURE__ */ jsxs("div", { className: "scroll-fade-x flex flex-wrap items-center gap-1.5", children: [
      summary.map((item) => {
        const on = active.has(item.name);
        return /* @__PURE__ */ jsxs(
          "button",
          {
            type: "button",
            onClick: () => toggle(item.name),
            className: cn(
              "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-2xs transition-colors",
              on ? "border-primary bg-primary/10 text-foreground" : "text-muted-foreground hover:text-foreground"
            ),
            "aria-pressed": on,
            children: [
              /* @__PURE__ */ jsx("span", { className: "font-medium", children: item.name }),
              /* @__PURE__ */ jsx("span", { className: "tabular-nums text-muted-foreground", children: item.count })
            ]
          },
          item.name
        );
      }),
      active.size > 0 && /* @__PURE__ */ jsx(
        "button",
        {
          type: "button",
          onClick: () => setSelected(/* @__PURE__ */ new Set()),
          className: "px-1 text-2xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline",
          children: "Show all"
        }
      )
    ] }),
    !abi && logs.length > 0 && (abiResolving ? /* @__PURE__ */ jsx("p", { role: "status", className: "text-xs text-muted-foreground", children: "Resolving ABI\u2026 Events are shown as raw logs until it loads." }) : /* @__PURE__ */ jsxs("p", { className: "rounded-md border border-risk-medium/40 bg-risk-medium/5 p-2 text-xs text-muted-foreground", children: [
      "No ABI resolved for this contract, so nothing below is decoded \u2014 each row shows its raw",
      " ",
      /* @__PURE__ */ jsx("code", { className: "font-mono", children: "topic0" }),
      ". Resolve or paste an ABI in the Edit tab to name these events."
    ] })),
    error ? /* @__PURE__ */ jsx(EmptyState, { title: "Couldn't load events", description: eventsErrorText(error), size: "sm" }) : visible.length === 0 && !isPending && active.size === 0 && widenTo != null ? (
      // The default range is ONE block, on purpose (see `baseQuery`): a busy
      // exchange emits dozens of logs a block and the reader's recent window
      // can time out before the first one appears. But one block is also a
      // quiet moment for almost everything else — Aave's V3 Pool, among the
      // busiest contracts on the chain, opened on "No events" — and the way
      // out was a sentence asking the reader to clear a field by hand. Say
      // what was actually scanned, and make the wider scan one click.
      /* @__PURE__ */ jsx(
        EmptyState,
        {
          title: singleBlock ? "No events in this block" : `No events in the last ${lookback?.toLocaleString("en-US")} blocks`,
          description: singleBlock ? `${scannedBlockPhrase(effectiveQuery?.toBlock)} was scanned. One block is often a quiet moment, even for a busy contract.` : "Quieter contracts can go longer than that between events.",
          action: /* @__PURE__ */ jsxs(Button, { type: "button", size: "sm", variant: "outline", onClick: () => widen(widenTo), children: [
            "Scan the last ",
            widenTo.toLocaleString("en-US"),
            " blocks"
          ] }),
          size: "sm"
        }
      )
    ) : visible.length === 0 ? /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: isPending ? "Loading events\u2026" : "No events",
        description: isPending ? void 0 : active.size > 0 ? "No logs match the selected events." : "No logs in the scanned block range.",
        size: "sm"
      }
    ) : /* @__PURE__ */ jsx("div", { className: "scroll-fade-y max-h-[480px] divide-y overflow-auto rounded-md border", children: rows.map(({ log: log6, entries }, i) => /* @__PURE__ */ jsx(LogRow, { log: log6, entries, namedAddresses, chain: contract.chainId, onDrilldown }, `${log6.transactionHash ?? "?"}-${log6.logIndex ?? i}`)) })
  ] });
}
function InteractionTab({ active, value, children }) {
  const [visited, setVisited] = React50.useState(active);
  if (active && !visited) setVisited(true);
  return /* @__PURE__ */ jsx(
    TabsContent,
    {
      value,
      forceMount: visited || active ? true : void 0,
      hidden: !active,
      className: "pt-3",
      children
    }
  );
}
var BYTECODE_OUTCOME_TITLES = {
  stored: "Built an ABI from the contract's own code",
  no_code: "Nothing is deployed at this address",
  no_entries: "No usable function signatures were recovered",
  already_stored: "An ABI is already saved for this contract",
  unreadable: "Could not read the contract's code",
  failed: "Could not save the ABI built from the code",
  /** Not a read made now: the analysis stored with the ABI in use. */
  on_record: "This ABI was read from the contract's own code"
};
function storedBytecodeOutcome(facts, abi) {
  const functions = abi.entries.filter((entry) => entry.type === "function");
  return {
    status: "on_record",
    stored: true,
    entriesCount: functions.length,
    entryPoints: Math.max(facts.entryPoints, functions.length),
    typed: functions.length,
    named: functions.filter((fn) => !isInferredFunction(fn)).length,
    analyzers: facts.analyzers.filter((run) => run.status === "ok").map((run) => run.id),
    codeHash: facts.codeHash ?? null,
    blockNumber: null,
    detail: null,
    facts
  };
}
function BytecodeReadResult({
  outcome,
  chainId,
  onAddressesFound,
  onDismiss,
  className
}) {
  const facts = outcome.facts ?? null;
  const addressesKey = facts?.constants.join(",") ?? "";
  React50.useEffect(() => {
    onAddressesFound?.(addressesKey ? addressesKey.split(",") : [], "bytecode:constants");
    return () => onAddressesFound?.([], "bytecode:constants");
  }, [addressesKey, onAddressesFound]);
  const ran = facts?.analyzers.filter((a) => a.status === "ok") ?? [];
  const missed = facts?.analyzers.filter((a) => a.status !== "ok") ?? [];
  const analyzerNames = facts ? ran.map((a) => a.id) : outcome.analyzers;
  const compilerAbi = outcome.source === "source_metadata";
  return /* @__PURE__ */ jsxs(
    "div",
    {
      className: cn(
        "space-y-2 rounded-lg border border-border bg-muted/30 p-3 text-sm",
        className
      ),
      role: "status",
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex items-start justify-between gap-2", children: [
          /* @__PURE__ */ jsxs("div", { children: [
            /* @__PURE__ */ jsx("p", { className: "font-medium", children: compilerAbi && outcome.stored ? "Found the compiler's own ABI for this contract" : BYTECODE_OUTCOME_TITLES[outcome.status] ?? "Read the contract's code" }),
            /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
              compilerAbi && outcome.stored ? `${outcome.entriesCount} ABI entries recovered from compiler metadata matched against the deployed code.` : outcome.stored ? `${outcome.entriesCount} function${outcome.entriesCount === 1 ? "" : "s"} recovered from ${outcome.entryPoints} entry point${outcome.entryPoints === 1 ? "" : "s"}.` : outcome.detail ?? "Nothing was saved.",
              outcome.stored && !compilerAbi && outcome.named === 0 ? " Function names are not in the bytecode, so each is named after its selector." : null
            ] })
          ] }),
          onDismiss && /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: onDismiss, className: "shrink-0", children: "Dismiss" })
        ] }),
        facts && facts.capabilities.length > 0 && /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsxs("p", { className: "text-xs font-medium", children: [
            "What the code can do",
            /* @__PURE__ */ jsxs("span", { className: "font-normal text-muted-foreground", children: [
              " ",
              "\u2014 things the code contains, not things it has done"
            ] })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "flex flex-wrap gap-1.5", children: orderBytecodeCapabilities(facts.capabilities).map((slug) => {
            const { label, notable } = bytecodeCapabilityLabel(slug);
            return /* @__PURE__ */ jsx(Badge, { variant: notable ? "default" : "secondary", className: "font-normal", children: label }, slug);
          }) })
        ] }),
        facts && facts.notes.length > 0 && /* @__PURE__ */ jsx("ul", { className: "space-y-1 text-xs text-muted-foreground", children: facts.notes.map((note) => /* @__PURE__ */ jsxs("li", { className: "flex gap-1.5", children: [
          /* @__PURE__ */ jsx("span", { "aria-hidden": true, children: "\xB7" }),
          /* @__PURE__ */ jsx("span", { children: note })
        ] }, note)) }),
        facts && facts.constants.length > 0 && /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
          /* @__PURE__ */ jsx("p", { className: "text-xs font-medium", children: "Addresses in the code" }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap gap-1.5", children: [
            facts.constants.map((address) => /* @__PURE__ */ jsx("span", { className: "font-mono text-2xs text-muted-foreground", title: address, children: /* @__PURE__ */ jsx(AddressDisplay, { address, chain: chainId }) }, address)),
            facts.constantsTotal > facts.constants.length && /* @__PURE__ */ jsxs("span", { className: "text-2xs text-muted-foreground", children: [
              "+",
              facts.constantsTotal - facts.constants.length,
              " more"
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsxs("p", { className: "text-2xs text-muted-foreground", children: [
          facts ? `${facts.codeSize.toLocaleString()} bytes \xB7 ` : null,
          analyzerNames.length > 0 ? `Read by ${analyzerNames.join(", ")}` : "No successful analyzer recorded",
          missed.length > 0 ? ` \xB7 not run: ${missed.map((a) => `${a.id} (${a.reason ?? a.status})`).join(", ")}` : "",
          outcome.blockNumber ? ` \xB7 at block ${outcome.blockNumber.toLocaleString()}` : ""
        ] })
      ]
    }
  );
}

// src/model/codeFit.ts
var PUSH1 = 96;
var PUSH3 = 98;
var PUSH4 = 99;
var PUSH32 = 127;
var DELEGATECALL = 244;
var CALLCODE = 242;
function codeConstants(code) {
  const hex = (code ?? "").replace(/^0x/i, "");
  if (!hex || /^0*$/.test(hex) || hex.length % 2 !== 0) return null;
  const bytes = new Uint8Array(hex.length / 2);
  for (let i = 0; i < bytes.length; i += 1) bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  const selectors = /* @__PURE__ */ new Set();
  const words = /* @__PURE__ */ new Set();
  let delegates = false;
  const toHex = (from, width, pad) => "0x" + "00".repeat(pad - width) + Array.from(bytes.subarray(from, from + width), (b) => b.toString(16).padStart(2, "0")).join("");
  for (let i = 0; i < bytes.length; ) {
    const op = bytes[i];
    if (op >= PUSH1 && op <= PUSH32) {
      const width = op - PUSH1 + 1;
      if (i + width < bytes.length) {
        if (op === PUSH3 || op === PUSH4) selectors.add(toHex(i + 1, width, 4));
        if (op === PUSH32) words.add(toHex(i + 1, width, 32));
      }
      i += 1 + width;
      continue;
    }
    if (op === DELEGATECALL || op === CALLCODE) delegates = true;
    i += 1;
  }
  return { selectors, words, delegates };
}
function fitGuessToCode(abi, constants) {
  if (!constants || constants.delegates) return null;
  const idOf = (entry) => {
    try {
      if (entry.type === "function") return (0, abi_exports.functionSelector)(entry).toLowerCase();
      if (entry.type === "event") return (0, abi_exports.eventTopic)(entry).toLowerCase();
    } catch {
    }
    return null;
  };
  let dropped = 0;
  const entries = abi.entries.filter((entry) => {
    const id2 = idOf(entry);
    if (id2 === null) return true;
    const fits = entry.type === "function" ? constants.selectors.has(id2) : constants.words.has(id2);
    if (!fits) dropped += 1;
    return fits;
  });
  const narrowed = (0, abi_exports.normalizeAbi)(entries);
  return { abi: narrowed, kept: narrowed.reads.length + narrowed.writes.length + narrowed.events.length, dropped };
}

// src/data/useCodeConstants.ts
function useCodeConstants(ref, opts) {
  const reader = useContractReader(opts.reader);
  const key = `${ref.chainId}:${normalizeAddress(ref.address, ref.chainId)}@${(0, contract_exports.blockKey)(ref.block)}`;
  const [state, setState] = React50.useState(
    null
  );
  const canRead = Boolean(reader.getBytecode);
  React50.useEffect(() => {
    if (!opts.enabled || !reader.getBytecode) return;
    const controller = new AbortController();
    void reader.getBytecode(ref, { signal: controller.signal }).then((code) => {
      if (controller.signal.aborted) return;
      const constants = codeConstants(code ?? null);
      setState({ key, constants, codeless: constants === null });
    }).catch(() => {
      if (!controller.signal.aborted) setState({ key, constants: null, codeless: false });
    });
    return () => controller.abort();
  }, [opts.enabled, reader, key]);
  const current = state?.key === key ? state : null;
  return {
    constants: current?.constants ?? null,
    codeless: current?.codeless ?? false,
    loading: opts.enabled && canRead && current === null
  };
}
function ContractExplorer({
  contract,
  resolveOverlay,
  onAddressesFound,
  onAddressRefsFound,
  maxNamedAddresses,
  maxNameProbes,
  provider,
  identity,
  cache = sharedAbiCache,
  reader,
  contractStateClient,
  onExploreContract,
  onExploreAddress,
  stateExploration,
  onStateExplorationChange,
  writer,
  proxyResolver,
  abiByAddress,
  contractSource,
  proxyOverrides,
  canEditProxy,
  canPurgeProxy,
  canIndexProxy,
  onBlockChange,
  compareBeforeBlock,
  assetContext,
  tab,
  onTabChange,
  headerActions,
  onAbiChange,
  onProxyChange,
  className
}) {
  if (!isEVM(contract.chainId)) {
    return /* @__PURE__ */ jsx(Card, { className: cn("w-full", className), children: /* @__PURE__ */ jsx(CardContent, { className: "pt-6", children: /* @__PURE__ */ jsx(
      EmptyState,
      {
        title: "Unsupported chain family",
        description: "The contract explorer is EVM-only (ABIs + EIP-1967 proxies). Bitcoin, Solana and other non-EVM chains need a family-specific explorer.",
        size: "sm"
      }
    ) }) });
  }
  return /* @__PURE__ */ jsx(
    ContractExplorerInner,
    {
      ...{ contract, provider, identity, cache, reader, contractStateClient, onExploreContract, onExploreAddress, stateExploration, onStateExplorationChange, writer, proxyResolver, resolveOverlay, onAddressesFound, onAddressRefsFound, maxNamedAddresses, maxNameProbes, abiByAddress, contractSource, proxyOverrides, canEditProxy, canPurgeProxy, canIndexProxy, onBlockChange, compareBeforeBlock, assetContext, tab, onTabChange, headerActions, onAbiChange, onProxyChange, className }
    }
  );
}
function ContractExplorerInner({
  contract,
  resolveOverlay,
  onAddressesFound,
  onAddressRefsFound,
  maxNamedAddresses,
  maxNameProbes,
  provider,
  identity,
  cache = sharedAbiCache,
  reader,
  contractStateClient,
  onExploreContract,
  onExploreAddress,
  stateExploration,
  onStateExplorationChange,
  writer,
  proxyResolver,
  abiByAddress,
  contractSource,
  proxyOverrides,
  canEditProxy = false,
  canPurgeProxy = false,
  canIndexProxy = false,
  onBlockChange,
  compareBeforeBlock,
  assetContext,
  tab,
  onTabChange,
  headerActions,
  onAbiChange,
  onProxyChange,
  className
}) {
  const resolvedReader = useContractReader(reader);
  const resolvedStateClient = useContractStateClient(contractStateClient);
  const { options, setOptions, reset } = useAbiOptions(identity);
  const inline = useInlineAbis(identity);
  const pins = usePinnedAbis(identity);
  const [blockText, setBlockText] = React50.useState(() => {
    if (contract.block?.blockNumber != null) return contract.block.blockNumber.toString();
    return contract.block?.blockTag ?? "latest";
  });
  const block = React50.useMemo(() => (0, contract_exports.parseBlock)(blockText), [blockText]);
  const [implOverride, setImplOverride] = React50.useState(null);
  const contractKey = `${contract.chainId.toLowerCase()}:${normalizeAddress(contract.address, contract.chainId)}`;
  if (useDepsChanged([contractKey])) setImplOverride(null);
  const [ownTab, setOwnTab] = React50.useState(DEFAULT_CONTRACT_EXPLORER_TAB);
  const nameSurface = (tab ?? ownTab) === "onchain" ? "state" : (tab ?? ownTab) === "read" ? "read" : (tab ?? ownTab) === "proxy" ? "proxy" : "fields";
  const [foundBySurface, setFoundBySurface] = React50.useState({});
  const [stateRefs, setStateRefs] = React50.useState([]);
  if (useDepsChanged([contractKey])) {
    setFoundBySurface({});
    setStateRefs([]);
  }
  const reportStateRefs = React50.useCallback((refs) => {
    setStateRefs((prev) => JSON.stringify(prev) === JSON.stringify(refs) ? prev : refs);
  }, []);
  const reportAddresses = React50.useCallback((addresses, surface) => {
    setFoundBySurface((prev) => {
      const current = prev[surface] ?? [];
      const same2 = current.length === addresses.length && current.every((a, i) => a === addresses[i]);
      if (same2) return prev;
      const next = { ...prev };
      if (addresses.length) next[surface] = addresses;
      else delete next[surface];
      return next;
    });
  }, []);
  const foundAddresses = React50.useMemo(() => mergeAddressReports(foundBySurface, maxNamedAddresses, nameSurface), [foundBySurface, maxNamedAddresses, nameSurface]);
  const foundKey = foundAddresses.join(",");
  React50.useEffect(() => {
    onAddressesFound?.(foundKey ? foundKey.split(",") : []);
  }, [foundKey, onAddressesFound]);
  const sameChainRefs = foundAddresses.map((address) => ({ address, chainId: contract.chainId }));
  const catalogRefKey = JSON.stringify(boundOverlayRefs(
    nameSurface === "state" ? [...stateRefs, ...sameChainRefs] : [...sameChainRefs, ...stateRefs],
    // Keep enough references for the host to detect a smaller lookup budget.
    // Silently clipping to that budget here makes omitted catalog items look absent.
    Math.max(64, maxNamedAddresses ?? 64)
  ).refs);
  React50.useEffect(() => {
    onAddressRefsFound?.(JSON.parse(catalogRefKey));
    return () => onAddressRefsFound?.([]);
  }, [catalogRefKey, onAddressRefsFound]);
  const unnamed = React50.useMemo(
    () => unnamedAddresses(foundAddresses, {
      resolveOverlay,
      chainId: contract.chainId,
      limit: maxNameProbes
    }),
    [foundAddresses, maxNameProbes, resolveOverlay, contract.chainId]
  );
  const probedNames = useNeighbourNames(contract.chainId, unnamed, {
    reader,
    limit: maxNameProbes,
    block
  });
  const resolveName = React50.useCallback(
    (address) => probedNames[normalizeAddress(address, contract.chainId)],
    [probedNames, contract.chainId]
  );
  const resolveFallbackLabel = React50.useCallback(
    (address, chain) => !chain || chainsEqual(chain, contract.chainId) ? resolveName(address) : void 0,
    [resolveName, contract.chainId]
  );
  const pinnedBlock = block?.blockNumber ?? null;
  const setBlockPin = React50.useCallback(
    (next) => {
      setBlockText(next === null ? "" : next.toString());
      onBlockChange?.(next);
    },
    [onBlockChange]
  );
  const hostBlockKey = (0, contract_exports.blockKey)(contract.block);
  if (useDepsChanged([hostBlockKey, contractKey]) && (0, contract_exports.blockKey)(block) !== hostBlockKey) {
    setBlockText(contract.block?.blockNumber != null ? contract.block.blockNumber.toString() : contract.block?.blockTag ?? "");
  }
  const rootRef = React50.useMemo(
    () => ({ chainId: contract.chainId, address: contract.address, block, label: contract.label }),
    [contract.chainId, contract.address, contract.label, block]
  );
  const {
    chain: proxyChain,
    isResolving: proxyResolving,
    error: proxyError,
    refetch: refetchProxy
  } = useProxyChain(rootRef, {
    discover: options.discoverProxies && options.useProxies,
    resolver: proxyResolver ?? void 0,
    reader
  });
  const awaitingImplementation = !implOverride && options.useProxies && options.discoverProxies && (proxyResolving || !proxyChain && !proxyError);
  const proxyAddressKey = proxyChain?.hops.flatMap((hop) => [hop.address, ...hop.beacon ? [hop.beacon] : []]).join(",") ?? "";
  React50.useEffect(() => {
    reportAddresses(proxyAddressKey ? proxyAddressKey.split(",") : [], "proxy:chain");
    return () => reportAddresses([], "proxy:chain");
  }, [proxyAddressKey, reportAddresses]);
  const effectiveAddress = implOverride ?? (options.useProxies && proxyChain?.isProxy ? proxyChain.effectiveAddress : contract.address);
  const abiRef = React50.useMemo(
    () => ({ chainId: contract.chainId, address: effectiveAddress, block }),
    [contract.chainId, effectiveAddress, block]
  );
  const inlineEntries = inline.get({ chainId: contract.chainId, address: effectiveAddress });
  const { resolved, status, error, origin, refetch: refetchAbi } = useAbiResolver({
    ref: abiRef,
    provider,
    options,
    identity,
    inlineEntries,
    inlineMeta: inline.getMeta(abiRef),
    cache,
    enabled: !awaitingImplementation
  });
  const resolverPending = awaitingImplementation || status === "loading";
  const readConfidence = useReadConfidence();
  const abiScore = recordedAbiConfidence(resolved?.meta);
  const abiVisible = readConfidence.accepts(abiScore);
  const guessedAbi = abiVisible ? resolved?.abi ?? null : null;
  const isGuessedAbi = Boolean(resolved?.isGuess) && origin !== "inline";
  const code = useCodeConstants(abiRef, { reader, enabled: isGuessedAbi && !resolverPending });
  const checkingGuess = isGuessedAbi && code.loading;
  const abiResolving = resolverPending || checkingGuess;
  const guessFit = isGuessedAbi && guessedAbi ? fitGuessToCode(guessedAbi, code.constants) : null;
  const abi = checkingGuess ? null : guessFit ? guessFit.abi : guessedAbi;
  const abiFailures = error ? [{ provider: "ABI resolver", source: "ABI lookup", error }] : resolved?.failures;
  const abiMeta = resolved?.meta;
  const isSystemAbi = Boolean(abiMeta?.isDefault);
  const isInheritedAbi = Boolean(abiMeta?.inherited) && !isSystemAbi;
  const scopeRung = scopeRungLabel(abiMeta?.scope);
  const inferredOrigin = origin !== "inline" && isInferredAbiOrigin(abiMeta?.source) ? abiOriginLabel(abiMeta?.source) : null;
  const guessWithdrawn = isGuessedAbi && guessFit !== null && guessFit.kept === 0;
  const scopeLabel = origin === "inline" ? "Local override" : guessWithdrawn ? "No ABI" : isGuessedAbi ? "Generic ERC guess" : isSystemAbi ? "System default" : isInheritedAbi ? scopeRung ? `Inherited \xB7 ${scopeRung}` : "Inherited" : abiMeta?.abiId ? scopeRung ? `Your ${scopeRung.toLowerCase()} scope` : "Your workspace" : null;
  const headerLabel = scopeLabel && inferredOrigin && !isGuessedAbi ? `${scopeLabel} \xB7 ${inferredOrigin}` : scopeLabel;
  const canWrite = Boolean(writer?.canEdit);
  const [persisting, setPersisting] = React50.useState(false);
  const [refreshPhase, setRefreshPhase] = React50.useState(null);
  const [refreshError, setRefreshError] = React50.useState(null);
  const [refreshNotice, setRefreshNotice] = React50.useState(null);
  const refreshRequest = React50.useRef(null);
  const refreshing = refreshPhase !== null;
  const [readBytecode, setReadBytecode] = React50.useState(false);
  const [bytecodeRead, setBytecodeRead] = React50.useState(null);
  const storedBytecodeRead = React50.useMemo(
    () => resolved?.bytecodeFacts && origin !== "inline" && resolved.meta?.source === "bytecode_analysis" ? storedBytecodeOutcome(resolved.bytecodeFacts, resolved.abi) : null,
    [resolved, origin]
  );
  const [refreshOpen, setRefreshOpen] = React50.useState(false);
  React50.useEffect(() => {
    setRefreshPhase(null);
    setRefreshOpen(false);
    setBytecodeRead(null);
    setRefreshError(null);
    setRefreshNotice(null);
    return () => {
      refreshRequest.current?.abort();
      refreshRequest.current = null;
    };
  }, [contract.chainId, effectiveAddress, identityKey(identity)]);
  const openRefresh = React50.useCallback(() => {
    if (refreshRequest.current || abiResolving) return;
    setReadBytecode(isGuessedAbi || !abi || resolved?.meta?.source === "bytecode_analysis");
    setRefreshOpen(true);
  }, [abiResolving, isGuessedAbi, abi, resolved?.meta?.source]);
  const savedRung = canonicalRung(scopeRungLabel(abiMeta?.scope));
  const preferredWriteScope = abiMeta?.abiId && !isInheritedAbi && !isSystemAbi && savedRung && savedRung !== "global" ? savedRung : "tenant";
  const { scopes: writeScopes, scope: targetScope, setScope: setTargetScope } = useOverrideWriteScope(identity, preferredWriteScope);
  const sharedLadder = useLadderShared();
  const [saveReceipt, setSaveReceipt] = React50.useState(null);
  React50.useEffect(() => setSaveReceipt(null), [contractKey, effectiveAddress, identityKey(identity)]);
  const editorKey = `${(0, contract_exports.contractRefKey)(abiRef)}:${identityKey(identity)}`;
  const nameSeed = abiMeta?.contractName ?? "";
  const [nameState, setNameState] = React50.useState({ key: editorKey, seed: nameSeed, value: nameSeed });
  if (nameState.key !== editorKey || nameState.seed !== nameSeed && nameState.value === nameState.seed) {
    setNameState({ key: editorKey, seed: nameSeed, value: nameSeed });
  }
  const nameDraft = nameState.key === editorKey ? nameState.value : nameSeed;
  const setNameDraft = (value) => setNameState({ key: editorKey, seed: nameSeed, value });
  const abiWindow = React50.useMemo(
    () => abiMeta?.validFromBlock != null || abiMeta?.validToBlock != null ? { validFromBlock: abiMeta?.validFromBlock ?? 0, validToBlock: abiMeta?.validToBlock ?? null } : void 0,
    [abiMeta?.validFromBlock, abiMeta?.validToBlock]
  );
  const abiSources = useAbiSources(
    { chainId: contract.chainId, address: effectiveAddress },
    abiByAddress,
    { enabled: Boolean(abiByAddress) && !awaitingImplementation, block: pinnedBlock }
  );
  const persistAbi = React50.useCallback(
    async (entries, scope, name) => {
      if (!writer?.canEdit || !scope || !writeScopes.includes(scope)) return false;
      setPersisting(true);
      setSaveReceipt(null);
      try {
        const trimmed = name?.trim();
        const result = await writer.save(
          { chainId: contract.chainId, address: effectiveAddress },
          entries,
          { scope, name: trimmed ? trimmed : void 0, ...abiWindow }
        );
        inline.remove({ chainId: contract.chainId, address: effectiveAddress });
        onAbiChange?.();
        cache.invalidateRef(abiRef);
        refetchAbi();
        abiSources.refetch();
        const where = !sharedLadder && scope && scope !== "user" ? scope === "tenant" ? "your workspace" : "the selected shared audience" : scope ? ABI_SCOPE_LABELS[scope].toLowerCase() : "your workspace";
        setSaveReceipt(`Saved for ${where}. ${abiScopeDescription(scope, sharedLadder)}`);
        notifySuccess(
          result.imported ? `Saved a copy for ${where}` : "ABI saved",
          { description: result.imported ? "The shared default is left unchanged." : void 0 }
        );
        return true;
      } catch (err) {
        notifyError(err, { title: "Could not save the ABI." });
        return false;
      } finally {
        setPersisting(false);
      }
    },
    [writer, writeScopes, contract.chainId, effectiveAddress, inline, cache, abiRef, refetchAbi, abiWindow, onAbiChange, abiSources.refetch]
  );
  const refreshAbi = React50.useCallback(async (withBytecode) => {
    if (!writer || !canWrite || refreshRequest.current) return;
    const request = new AbortController();
    refreshRequest.current = request;
    setRefreshPhase("lookup");
    setBytecodeRead(null);
    setRefreshError(null);
    setRefreshNotice(null);
    try {
      const result = await writer.refresh(
        { chainId: contract.chainId, address: effectiveAddress },
        { bytecode: withBytecode, signal: request.signal }
      );
      if (request.signal.aborted) return;
      if (!result.found && !result.bytecode) {
        setRefreshNotice(result.providersErrored ? "Some ABI providers could not be reached. No published ABI was recovered; try again when they are available." : result.providersQueried === 0 ? "No ABI providers are configured for this chain. You can paste an ABI in the Edit tab." : "The queried providers returned no published ABI. You can try bytecode analysis or paste an ABI in the Edit tab.");
      }
      onAbiChange?.();
      cache.invalidateRef(abiRef);
      const discoveredImplementation = result.bytecode?.implementation;
      if (discoveredImplementation && /^0x[0-9a-f]{40}$/i.test(discoveredImplementation) && normalizeAddress(discoveredImplementation, contract.chainId) !== normalizeAddress(effectiveAddress, contract.chainId)) {
        cache.invalidateRef({ ...abiRef, address: discoveredImplementation });
        if (!implOverride && options.useProxies && options.discoverProxies) refetchProxy();
      }
      refetchAbi();
      abiSources.refetch();
      setRefreshPhase("reload");
      if (!result.found) {
        const bc = result.bytecode ?? null;
        setBytecodeRead(bc);
        if (bc?.stored && bc.source === "source_metadata") {
          notifySuccess("Found the compiler's own ABI for this contract", {
            description: `${bc.entriesCount} entr${bc.entriesCount === 1 ? "y" : "ies"} with their original names, published by the compiler and matched against the deployed code.`
          });
          return;
        }
        if (bc?.stored) {
          notifySuccess("Built an ABI from the contract's own code", {
            description: `${bc.entriesCount} function${bc.entriesCount === 1 ? "" : "s"} with argument types${bc.typed < bc.entryPoints ? ` (${bc.entryPoints - bc.typed} more selector${bc.entryPoints - bc.typed === 1 ? "" : "s"} exist but their types could not be read)` : ""}. Names are not recoverable from bytecode, so unnamed functions show their selector.`
          });
          return;
        }
        if (bc) {
          notifyWarning(
            BYTECODE_OUTCOME_TITLES[bc.status] ?? "Could not build an ABI from the code",
            bc.detail ?? (bc.analyzers.length ? `Read by ${bc.analyzers.join(", ")}.` : "No bytecode analyzer was available on the server.")
          );
          return;
        }
        const n = result.providersQueried;
        if (result.inferredOnly) {
          notifyWarning(
            "No published ABI \u2014 only a guess from the bytecode",
            "An explorer read entries out of this contract's code, which is not its ABI and was not saved. Refresh again with \u201CIf no explorer has it, read the contract's own code\u201D ticked to have the platform read it instead."
          );
          return;
        }
        const failed = result.providersErrored ?? 0;
        if (failed > 0) {
          notifyWarning(
            "No ABI found \u2014 not every explorer answered",
            `${failed} of ${n ?? failed} provider${(n ?? failed) === 1 ? "" : "s"} could not be reached, so this contract may still be verified. Try again shortly.`
          );
          return;
        }
        notifyWarning(
          "No ABI found",
          n === 0 ? "No ABI provider is configured for this chain, so nothing was queried. Paste the ABI in the Edit tab instead." : `${n ?? "The configured"} provider${n === 1 ? "" : "s"} had no ABI for this address. Paste one in the Edit tab instead.`
        );
        return;
      }
      if (result.persisted === false) {
        notifyInfo(
          "Found an ABI, but it wasn't saved",
          result.keptExisting ? "The catalog already holds an ABI for this contract that this workspace cannot replace. The one below is still the catalog's." : "Third-party write-back is disabled, so this result was not persisted."
        );
        return;
      }
      notifySuccess("ABI refreshed", {
        description: result.entriesCount ? `${result.entriesCount} entries from third-party explorers.` : void 0
      });
    } catch (err) {
      if (request.signal.aborted) return;
      setRefreshError(err instanceof Error ? err.message : String(err));
      notifyError(err, { title: "Could not refresh the ABI." });
      refreshRequest.current = null;
      setRefreshPhase(null);
    }
  }, [writer, canWrite, contract.chainId, effectiveAddress, cache, abiRef, refetchAbi, onAbiChange, abiSources.refetch, implOverride, options.useProxies, options.discoverProxies, refetchProxy]);
  React50.useEffect(() => {
    if (refreshPhase === "reload" && !abiResolving && !abiSources.isLoading) {
      refreshRequest.current = null;
      setRefreshPhase(null);
    }
  }, [refreshPhase, abiResolving, abiSources.isLoading]);
  const renameSource = React50.useCallback(
    async (entry, name) => {
      if (!writer?.rename) return;
      const rung = canonicalRung(scopeRungLabel(entry.scope));
      await writer.rename(
        { chainId: entry.chainId, address: entry.address },
        name,
        {
          ...rung && rung !== "global" ? { scope: rung } : {},
          validFromBlock: entry.validFromBlock ?? null,
          validToBlock: entry.validToBlock ?? null
        }
      );
      onAbiChange?.();
      abiSources.refetch();
      cache.invalidateRef(abiRef);
      refetchAbi();
    },
    [writer, abiSources, cache, abiRef, refetchAbi, onAbiChange]
  );
  const [activeSel, setActiveSel] = React50.useState(null);
  const activeSourceId = React50.useMemo(() => {
    if (activeSel && normalizeAddress(activeSel.address, contract.chainId) === normalizeAddress(effectiveAddress, contract.chainId))
      return activeSel.id;
    if (abiMeta?.abiId) {
      const match = abiSources.sources.find((s) => s.abiId === abiMeta.abiId);
      if (match) return abiSourceId(match);
    }
    if (isSystemAbi) {
      const match = abiSources.sources.find((s) => s.isDefault);
      if (match) return abiSourceId(match);
    }
    return null;
  }, [activeSel, effectiveAddress, abiMeta?.abiId, isSystemAbi, abiSources.sources]);
  const useSource = React50.useCallback(
    (entry) => {
      if (!entry.entries) return;
      inline.set({ chainId: contract.chainId, address: effectiveAddress }, entry.entries, abiSourceMeta(entry));
      cache.invalidateRef(abiRef);
      setActiveSel({ address: effectiveAddress, id: abiSourceId(entry) });
      notifySuccess("Using selected ABI", { description: abiSourceScopeLabel(entry) });
    },
    [inline, contract.chainId, effectiveAddress, cache, abiRef]
  );
  const callRef = React50.useMemo(
    () => ({ chainId: contract.chainId, address: contract.address, block }),
    [contract.chainId, contract.address, block]
  );
  const baseRefKey = React50.useMemo(() => (0, contract_exports.contractRefKey)(abiRef), [abiRef]);
  const pinIdForSource = React50.useCallback(
    (entry) => `${baseRefKey}#${abiSourceId(entry)}`,
    [baseRefKey]
  );
  const pinnedSourceIds = React50.useMemo(() => {
    const set = /* @__PURE__ */ new Set();
    for (const s of abiSources.sources) {
      if (pins.pinned.some((p) => p.id === pinIdForSource(s))) set.add(abiSourceId(s));
    }
    return set;
  }, [abiSources.sources, pins.pinned, pinIdForSource]);
  const toggleSourcePin = React50.useCallback(
    (entry) => {
      if (!entry.entries) return;
      const id2 = pinIdForSource(entry);
      if (pins.pinned.some((p) => p.id === id2)) {
        pins.unpin(id2);
        return;
      }
      const name = entry.name || resolved?.meta.contractName || shortenAddress(abiRef.address);
      pins.pin({
        id: id2,
        label: `${name} \xB7 ${abiSourceScopeLabel(entry)}`,
        ref: abiRef,
        entries: entry.entries,
        meta: abiSourceMeta(entry)
      });
    },
    [pinIdForSource, pins, resolved, abiRef]
  );
  const activeSourceEntry = React50.useMemo(
    () => abiSources.sources.find((s) => abiSourceId(s) === activeSourceId) ?? null,
    [abiSources.sources, activeSourceId]
  );
  const currentPinId = `${baseRefKey}#${activeSourceId ?? "current"}`;
  const isCurrentPinned = React50.useMemo(
    () => pins.pinned.some((p) => p.id === currentPinId),
    [pins.pinned, currentPinId]
  );
  const togglePin = React50.useCallback(() => {
    if (isCurrentPinned) {
      pins.unpin(currentPinId);
      return;
    }
    if (!abi) return;
    const name = resolved?.meta.contractName ?? shortenAddress(abiRef.address);
    const scopeText = activeSourceEntry ? abiSourceScopeLabel(activeSourceEntry) : scopeLabel;
    pins.pin({
      id: currentPinId,
      label: scopeText ? `${name} \xB7 ${scopeText}` : name,
      ref: abiRef,
      entries: abi.entries,
      meta: resolved?.meta
    });
  }, [isCurrentPinned, currentPinId, abi, pins, resolved, abiRef, activeSourceEntry, scopeLabel]);
  const visiblePins = pins.pinned.filter((pin) => readConfidence.accepts(recordedAbiConfidence(pin.meta)));
  const compareItems = visiblePins.map((p) => ({ id: p.id, label: p.label, abi: (0, abi_exports.normalizeAbi)(p.entries) }));
  const compareRefs = visiblePins.map((p) => p.ref);
  const tabs = availableExplorerTabs(explorerCapabilities.resolve(contract.chainId)?.tabs ?? EVM_EXPLORER_TABS, {
    state: Boolean(resolvedStateClient),
    events: typeof resolvedReader.getLogs === "function",
    source: Boolean(contractSource),
    sources: Boolean(abiByAddress),
    proxy: Boolean(proxyResolver || proxyOverrides || resolvedReader.getStorageAt)
  });
  const requestedTab = tab ?? ownTab;
  const selection = resolveExplorerTab(tabs, requestedTab);
  const activeTab = selection.active?.id;
  const selectTab = React50.useCallback(
    (next) => {
      if (!isContractExplorerTab(next)) return;
      setOwnTab(next);
      onTabChange?.(next);
    },
    [onTabChange]
  );
  if (!selection.active) return /* @__PURE__ */ jsx(EmptyState, { title: "No supported explorer views", description: "No reader is connected for this chain's configured views." });
  return /* @__PURE__ */ jsx(
    AddressDisplayProvider,
    {
      presentation: "inline",
      size: "sm",
      showExplorerIcon: true,
      resolveOverlay,
      resolveFallbackLabel,
      proxyContext: block?.blockNumber != null ? { chain_id: contract.chainId, block_number: Number(block.blockNumber) } : void 0,
      children: /* @__PURE__ */ jsx(ContractReaderProvider, { reader: resolvedReader, children: /* @__PURE__ */ jsx(GuessFitContext.Provider, { value: guessFit ? { kept: guessFit.kept, dropped: guessFit.dropped } : null, children: /* @__PURE__ */ jsx(ContractStateClientProvider, { client: resolvedStateClient, children: /* @__PURE__ */ jsxs(Card, { className: cn("w-full", className), children: [
        /* @__PURE__ */ jsxs(CardHeader, { className: "gap-3", children: [
          resolved && !abiVisible && /* @__PURE__ */ jsx("p", { role: "status", className: "text-sm text-muted-foreground", children: readConfidence.loading ? "Loading read confidence preference\u2026" : "ABI hidden by your read confidence preference. Change it in Settings \u2192 Defaults to review its interface." }),
          /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center justify-between gap-3", children: [
            /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-wrap items-center gap-3", children: [
              /* @__PURE__ */ jsx(ChainAvatar, { chain: contract.chainId }),
              /* @__PURE__ */ jsx(CardTitle, { className: "text-base", children: /* @__PURE__ */ jsx(
                AddressDisplay,
                {
                  presentation: "plain",
                  size: "md",
                  address: contract.address,
                  chain: contract.chainId,
                  label: nameBehindCatalog(resolveOverlay?.(contract.address, contract.chainId), abiVisible ? resolved?.meta?.contractName ?? void 0 : void 0),
                  overlay: resolveOverlay?.(contract.address, contract.chainId)
                }
              ) }),
              headerLabel && /* @__PURE__ */ jsx(
                Badge,
                {
                  variant: guessWithdrawn ? "outline" : isGuessedAbi ? "destructive" : isSystemAbi ? "secondary" : "outline",
                  className: "text-3xs",
                  title: guessWithdrawn ? "No stored or verified ABI was found, and no standard ERC-20/721 selectors were detected." : isGuessedAbi ? abiFailures?.length ? "An ABI lookup failed. The well-known ERC interfaces are standing in." : "No stored or verified ABI was found for this address. The well-known ERC interfaces are standing in." : void 0,
                  children: headerLabel
                }
              ),
              resolved && !guessWithdrawn && /* @__PURE__ */ jsx(ReadConfidenceBadge, { ...abiAssessment(abiMeta) })
            ] }),
            /* @__PURE__ */ jsxs("div", { className: "flex min-w-0 flex-wrap items-center gap-2", children: [
              headerActions,
              /* @__PURE__ */ jsx(BlockPinControl, { id: "ce-block", value: pinnedBlock, onChange: setBlockPin }),
              canWrite && targetScope && abi && !isGuessedAbi && /* @__PURE__ */ jsx(
                Button,
                {
                  size: "sm",
                  variant: "outline",
                  disabled: abiResolving || persisting,
                  onClick: () => selectTab("edit"),
                  children: isSystemAbi || isInheritedAbi || !abiMeta?.abiId ? "Save a copy\u2026" : "Edit ABI"
                }
              ),
              canWrite && writer && /* @__PURE__ */ jsxs(
                AlertDialog,
                {
                  open: refreshOpen,
                  onOpenChange: (next) => {
                    if (refreshRequest.current) return;
                    if (next) openRefresh();
                    else setRefreshOpen(false);
                  },
                  children: [
                    /* @__PURE__ */ jsx(AlertDialogTrigger, { asChild: true, children: /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", disabled: refreshing || abiResolving, children: refreshing ? "Refreshing\u2026" : "Refresh" }) }),
                    /* @__PURE__ */ jsxs(AlertDialogContent, { children: [
                      /* @__PURE__ */ jsxs(AlertDialogHeader, { children: [
                        /* @__PURE__ */ jsx(AlertDialogTitle, { children: !abi || isGuessedAbi ? "Look for this contract's ABI?" : "Re-fetch this ABI from third-party explorers?" }),
                        /* @__PURE__ */ jsxs(AlertDialogDescription, { children: [
                          "This queries the configured explorers for",
                          " ",
                          /* @__PURE__ */ jsx(AddressDisplay, { address: effectiveAddress, chain: contract.chainId }),
                          isGuessedAbi && !abiFailures?.length ? ". If one has it, it becomes this contract's shared system-default ABI (where write-back is enabled) \u2014 the one every tenant inherits. Nothing is replaced: there is no ABI on record yet." : " and, where write-back is enabled, replaces the shared system-default ABI for this contract \u2014 the one every tenant inherits when they have no override of their own. Your own overrides are not touched."
                        ] })
                      ] }),
                      /* @__PURE__ */ jsxs("div", { className: "flex items-start gap-2 rounded-md border border-border p-3", children: [
                        /* @__PURE__ */ jsx(
                          Checkbox,
                          {
                            id: "refresh-read-bytecode",
                            checked: readBytecode,
                            onCheckedChange: (next) => setReadBytecode(next === true),
                            disabled: refreshing
                          }
                        ),
                        /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
                          /* @__PURE__ */ jsx(Label, { htmlFor: "refresh-read-bytecode", className: "text-sm font-medium", children: "If no explorer has it, read the contract's own code" }),
                          /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Recovers argument types from the deployed bytecode and saves them as a candidate ABI. Function names are not in the bytecode, so unnamed entries show their 4-byte selector. Never replaces an ABI that already exists." })
                        ] })
                      ] }),
                      /* @__PURE__ */ jsxs(AlertDialogFooter, { children: [
                        /* @__PURE__ */ jsx(AlertDialogCancel, { disabled: refreshing, children: "Cancel" }),
                        /* @__PURE__ */ jsx(
                          Button,
                          {
                            type: "button",
                            disabled: refreshing,
                            onClick: () => {
                              setRefreshOpen(false);
                              void refreshAbi(readBytecode);
                            },
                            children: !abi || isGuessedAbi ? "Look for the ABI" : "Refresh ABI"
                          }
                        )
                      ] })
                    ] })
                  ]
                }
              ),
              /* @__PURE__ */ jsx(
                Button,
                {
                  size: "sm",
                  variant: isCurrentPinned ? "secondary" : "outline",
                  disabled: !abi && !isCurrentPinned,
                  onClick: togglePin,
                  children: isCurrentPinned ? "Unpin" : "Pin ABI"
                }
              )
            ] })
          ] }),
          refreshing ? /* @__PURE__ */ jsxs("p", { role: "status", "aria-live": "polite", className: "flex items-center gap-2 text-xs text-muted-foreground", children: [
            /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
            refreshPhase === "reload" ? "Loading the refreshed ABI and sources\u2026" : readBytecode ? "Looking for a published ABI and analysing bytecode if needed. This can take a minute\u2026" : "Looking for a published ABI\u2026"
          ] }) : abiResolving && /* @__PURE__ */ jsxs("p", { role: "status", className: "flex items-center gap-2 text-xs text-muted-foreground", children: [
            /* @__PURE__ */ jsx(Spinner, { size: "sm" }),
            " ",
            awaitingImplementation ? "Resolving proxy implementation before loading ABI\u2026" : checkingGuess ? "Checking standard interface candidates against the deployed bytecode\u2026" : "Resolving ABI\u2026"
          ] }),
          status === "error" && error && /* @__PURE__ */ jsx("p", { className: "text-xs text-destructive", children: error.message }),
          refreshError && /* @__PURE__ */ jsxs("p", { role: "alert", className: "text-xs text-destructive", children: [
            "ABI lookup failed: ",
            refreshError,
            ". Use Refresh to retry."
          ] }),
          !refreshing && refreshNotice && /* @__PURE__ */ jsx("p", { role: "status", className: "text-xs text-muted-foreground", children: refreshNotice }),
          !refreshing && abiSources.error && /* @__PURE__ */ jsxs("p", { role: "alert", className: "text-xs text-destructive", children: [
            "Could not load ABI sources: ",
            abiSources.error.message,
            ".",
            " ",
            /* @__PURE__ */ jsx(Button, { size: "sm", variant: "outline", onClick: abiSources.refetch, children: "Retry loading sources" })
          ] }),
          !abiResolving && !refreshing && status === "empty" && /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "No ABI found \u2014 paste one in the Edit tab." }),
          abi && inferredOrigin && /* @__PURE__ */ jsx("p", { role: "note", className: "text-xs text-muted-foreground", children: "This recovered interface may be incomplete. Fields lists recovered read functions that need no arguments; other reads appear in Read. Missing fields do not prove that the contract has no readable state." }),
          status === "ready" && abiFailures && abiFailures.length > 0 && /* @__PURE__ */ jsxs("p", { className: "text-xs text-risk-medium", children: [
            "ABI lookup failed \u2014 ",
            describeAbiFailures(abiFailures),
            " The ABI below came from a fallback source and may not be this contract's."
          ] }),
          bytecodeRead ? /* @__PURE__ */ jsx(
            BytecodeReadResult,
            {
              outcome: bytecodeRead,
              chainId: contract.chainId,
              onAddressesFound: reportAddresses,
              onDismiss: () => setBytecodeRead(null),
              className: "mb-3"
            }
          ) : storedBytecodeRead ? /* @__PURE__ */ jsx(
            BytecodeReadResult,
            {
              outcome: storedBytecodeRead,
              chainId: contract.chainId,
              onAddressesFound: reportAddresses,
              className: "mb-3"
            }
          ) : null,
          !abiResolving && status === "ready" && isGuessedAbi && // Which address has no ABI. On a proxy this resolves against the
          // IMPLEMENTATION, so "this address" named the one thing on screen it
          // was not about — while the workspace's ABI for the proxy sat in the
          // registry, and the banner read as "you have nothing for this contract".
          /* @__PURE__ */ jsxs("p", { className: "text-xs text-risk-medium", children: [
            abiFailures?.length ? "Could not finish loading this contract\u2019s ABI" : normalizeAddress(effectiveAddress, contract.chainId) === normalizeAddress(contract.address, contract.chainId) ? "No stored or verified ABI for this address" : `No stored or verified ABI for the implementation ${shortenAddress(effectiveAddress)} this page reads through`,
            guessFit && guessFit.kept === 0 ? " \u2014 no standard ERC-20/721 selector constants were detected. Custom dispatch or forwarding may hide functions." : " \u2014 showing the generic ERC-20/721 interfaces. Reads may revert and sending is disabled."
          ] })
        ] }),
        /* @__PURE__ */ jsx(CardContent, { children: /* @__PURE__ */ jsxs(Tabs, { value: activeTab, onValueChange: selectTab, children: [
          selection.unavailable && /* @__PURE__ */ jsx(Alert, { className: "mb-3", children: /* @__PURE__ */ jsxs(AlertDescription, { children: [
            explorerTabLabel(requestedTab),
            " is not available in this explorer. Showing ",
            selection.active.label,
            ".",
            (tab === void 0 || onTabChange) && /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", className: "ml-2", onClick: () => selectTab(selection.active.id), children: "Use supported view" })
          ] }) }),
          /* @__PURE__ */ jsx(TabsList, { "aria-label": "Contract views", className: "h-auto max-w-full flex-wrap justify-start", children: tabs.map(({ id: id2, label }) => {
            const count = id2 === "sources" ? abiSources.sources.length : id2 === "proxy" && proxyChain?.isProxy ? proxyChain.hops.length : id2 === "compare" ? pins.pinned.length : 0;
            return /* @__PURE__ */ jsxs(TabsTrigger, { value: id2, children: [
              label,
              count > 0 && /* @__PURE__ */ jsx(Badge, { variant: "secondary", className: "ml-1.5 text-3xs", children: count })
            ] }, id2);
          }) }),
          /* @__PURE__ */ jsx(
            ContractStatePanel,
            {
              canDiscoverProxy: canIndexProxy,
              canEditProfiles: canWrite,
              identity,
              chainId: contract.chainId,
              address: contract.address,
              block,
              summaryOnly: activeTab !== "onchain",
              showSummary: activeTab === "fields" || activeTab === "onchain",
              wrapDetails: (content) => /* @__PURE__ */ jsx(InteractionTab, { value: "onchain", active: activeTab === "onchain", children: content }),
              onExploreContract,
              exploration: stateExploration,
              onExplorationChange: onStateExplorationChange,
              resolveOverlay,
              resolveName,
              onAddressesFound: reportAddresses,
              onAddressRefsFound: reportStateRefs
            }
          ),
          /* @__PURE__ */ jsx(TabsContent, { value: "fields", className: "pt-3", children: /* @__PURE__ */ jsx(ContractFieldsGrid, { isProxy: Boolean(proxyChain?.isProxy), contract: callRef, abi, isGuess: isGuessedAbi, failures: abiFailures, abiResolving: abiResolving || refreshing, onFetchAbi: canWrite && writer ? openRefresh : void 0, fetchingAbi: refreshing || abiResolving, resolveOverlay, resolveName, onAddressesFound: reportAddresses, maxNamedAddresses }) }),
          /* @__PURE__ */ jsx(TabsContent, { value: "state", className: "pt-3", children: !abi && !assetContext ? /* @__PURE__ */ jsx(NoAbiState, { subject: "state comparisons", resolving: abiResolving || refreshing, failures: abiFailures }) : /* @__PURE__ */ jsx(ContractStateView, { resolution: { provider, options, identity, cache, proxyResolver: proxyResolver ?? void 0, inline }, assetContext, contractStateClient: resolvedStateClient, defaultBeforeBlock: compareBeforeBlock ?? blockText, defaultAfterBlock: compareBeforeBlock ? blockText : void 0, includeLatest: !compareBeforeBlock, onAddressesFound: reportAddresses, chainId: contract.chainId, address: contract.address, abi, isProxy: Boolean(proxyChain?.isProxy) }, `${contractKey}:${(0, contract_exports.blockKey)(block)}:${compareBeforeBlock ?? ""}`) }),
          /* @__PURE__ */ jsx(InteractionTab, { value: "read", active: activeTab === "read", children: /* @__PURE__ */ jsx(ReadPanel, { implementation: proxyChain?.isProxy && normalizeAddress(effectiveAddress, contract.chainId) !== normalizeAddress(callRef.address, contract.chainId) ? abiRef : void 0, contract: callRef, abi, isGuess: isGuessedAbi, failures: abiFailures, abiResolving: abiResolving || refreshing, onFetchAbi: canWrite && writer ? openRefresh : void 0, fetchingAbi: refreshing || abiResolving, resolveOverlay, resolveName, onAddressesFound: reportAddresses, maxNamedAddresses, onExploreAddress }) }),
          /* @__PURE__ */ jsx(InteractionTab, { value: "write", active: activeTab === "write", children: /* @__PURE__ */ jsx(WritePanel, { contract: callRef, abi, isGuess: isGuessedAbi, failures: abiFailures, abiResolving: abiResolving || refreshing, onFetchAbi: canWrite && writer ? openRefresh : void 0, fetchingAbi: refreshing || abiResolving }) }),
          /* @__PURE__ */ jsx(TabsContent, { value: "events", className: "pt-3", children: /* @__PURE__ */ jsx(EventLogPanel, { contract: callRef, abi, abiResolving: abiResolving || refreshing, onAddressesFound: reportAddresses, maxNamedAddresses }) }),
          /* @__PURE__ */ jsxs(TabsContent, { value: "abi", className: "pt-3", children: [
            isGuessedAbi && abi ? /* @__PURE__ */ jsx(
              GuessedAbiNotice,
              {
                failures: abiFailures,
                className: "mb-3",
                onFetch: canWrite && writer ? openRefresh : void 0,
                fetching: refreshing || abiResolving
              }
            ) : null,
            !abi ? /* @__PURE__ */ jsx(NoAbiState, { subject: "the interface", resolving: abiResolving || refreshing, failures: abiFailures, onFetch: canWrite && writer ? openRefresh : void 0, fetching: refreshing || abiResolving }) : /* @__PURE__ */ jsx(AbiViewer, { abi })
          ] }),
          contractSource && /* @__PURE__ */ jsx(TabsContent, { value: "source", className: "pt-3", children: /* @__PURE__ */ jsx(
            ContractSourcePanel,
            {
              client: contractSource,
              chainId: contract.chainId,
              address: effectiveAddress,
              block: pinnedBlock
            }
          ) }),
          abiByAddress && /* @__PURE__ */ jsx(TabsContent, { value: "sources", className: "pt-3", children: /* @__PURE__ */ jsx(
            AbiSourcesPanel,
            {
              sources: abiSources.sources,
              isLoading: awaitingImplementation || abiSources.isLoading,
              error: abiSources.error,
              onRetry: abiSources.refetch,
              activeSourceId,
              pinnedIds: pinnedSourceIds,
              block: pinnedBlock,
              listedAddress: effectiveAddress,
              proxyAddress: contract.address,
              onUse: useSource,
              onTogglePin: toggleSourcePin,
              onRename: canWrite && writer?.rename ? renameSource : void 0
            }
          ) }),
          /* @__PURE__ */ jsxs(TabsContent, { value: "proxy", className: "space-y-4 pt-3", children: [
            /* @__PURE__ */ jsx(
              ProxyChainView,
              {
                chain: proxyChain,
                chainId: contract.chainId,
                isResolving: proxyResolving,
                error: proxyError,
                onRetry: refetchProxy,
                onSelectImplementation: setImplOverride
              }
            ),
            proxyOverrides && /* @__PURE__ */ jsxs(Fragment, { children: [
              /* @__PURE__ */ jsx(Separator, {}),
              /* @__PURE__ */ jsx(
                ProxyOverridePanel,
                {
                  chainId: contract.chainId,
                  address: contract.address,
                  client: proxyOverrides,
                  canEdit: canEditProxy,
                  canPurge: canPurgeProxy,
                  canIndex: canIndexProxy,
                  writeScopes,
                  block: pinnedBlock,
                  inspectionBlock: block,
                  onBlockChange: setBlockPin,
                  onWritten: onProxyChange,
                  onAddressesFound: reportAddresses
                }
              )
            ] })
          ] }),
          /* @__PURE__ */ jsxs(InteractionTab, { value: "edit", active: activeTab === "edit", children: [
            saveReceipt && /* @__PURE__ */ jsx("p", { role: "status", className: "mb-2 text-sm", children: saveReceipt }),
            /* @__PURE__ */ jsxs("p", { className: "mb-2 text-xs text-muted-foreground", children: [
              normalizeAddress(effectiveAddress, contract.chainId) !== normalizeAddress(contract.address, contract.chainId) ? "Editing the implementation ABI used by this proxy:" : "Editing the ABI for:",
              " ",
              /* @__PURE__ */ jsx(AddressDisplay, { address: effectiveAddress, chain: contract.chainId })
            ] }),
            abiResolving && !abi ? /* @__PURE__ */ jsx(NoAbiState, { subject: "the ABI to edit", resolving: true }) : /* @__PURE__ */ jsx(
              InlineAbiEditor,
              {
                initialEntries: isGuessedAbi ? null : abi?.entries ?? null,
                hasOverride: inlineEntries != null,
                onSave: (entries) => {
                  inline.set({ chainId: contract.chainId, address: effectiveAddress }, entries);
                  cache.invalidateRef(abiRef);
                },
                onClear: () => {
                  inline.remove({ chainId: contract.chainId, address: effectiveAddress });
                  cache.invalidateRef(abiRef);
                },
                onPersist: canWrite && writer && targetScope ? (entries) => persistAbi(entries, targetScope, nameDraft) : void 0,
                persistBusy: persisting,
                name: nameDraft,
                onNameChange: canWrite && writer ? setNameDraft : void 0,
                scopeOptions: writeScopes.length > 0 ? writeScopes.map((s) => ({ value: s, label: ABI_SCOPE_LABELS[s] })) : void 0,
                scope: targetScope,
                onScopeChange: (value) => setTargetScope(value),
                scopeDescription: abiScopeDescription(targetScope, sharedLadder),
                notice: abiWindow && (abiWindow.validFromBlock > 0 || abiWindow.validToBlock != null) ? /* @__PURE__ */ jsx(Alert, { children: /* @__PURE__ */ jsxs(AlertDescription, { className: "text-xs", children: [
                  "This ABI is the version for",
                  " ",
                  /* @__PURE__ */ jsx("span", { className: "font-mono", children: abiWindow.validToBlock == null ? `blocks ${abiWindow.validFromBlock.toLocaleString()}+` : `blocks ${abiWindow.validFromBlock.toLocaleString()}\u2013${(abiWindow.validToBlock - 1).toLocaleString()}` }),
                  ". Saving updates that version, not the contract's current ABI."
                ] }) }) : isGuessedAbi ? /* @__PURE__ */ jsx(Alert, { className: "border-risk-medium/40 bg-risk-medium/5", children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: "No ABI was found for this address, so the editor starts empty rather than pre-filled with the generic ERC-20/721 stand-in \u2014 saving that would register a guess as this contract's ABI for everyone at your scope. Paste the real ABI here." }) }) : canWrite && (isSystemAbi || isInheritedAbi) ? /* @__PURE__ */ jsx(Alert, { children: /* @__PURE__ */ jsx(AlertDescription, { className: "text-xs", children: isSystemAbi ? "This is a shared system ABI. Saving creates a separate copy at the scope you pick \u2014 the shared default is left unchanged." : "This ABI is inherited from a broader scope. Saving creates a copy owned at the scope you pick." }) }) : void 0
              },
              editorKey
            )
          ] }),
          /* @__PURE__ */ jsxs(TabsContent, { value: "compare", className: "space-y-4 pt-3", children: [
            visiblePins.length < pins.pinned.length && /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
              pins.pinned.length - visiblePins.length,
              " pinned ABI(s) hidden by your read confidence preference."
            ] }),
            pins.pinned.length > 0 && /* @__PURE__ */ jsxs("div", { className: "flex flex-wrap items-center gap-2", children: [
              visiblePins.map((p) => /* @__PURE__ */ jsxs(
                "span",
                {
                  className: "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs",
                  children: [
                    /* @__PURE__ */ jsx("span", { className: "max-w-[12rem] truncate", children: p.label }),
                    /* @__PURE__ */ jsx(ReadConfidenceBadge, { ...abiAssessment(p.meta) }),
                    /* @__PURE__ */ jsx(
                      "button",
                      {
                        type: "button",
                        "aria-label": `Unpin ${p.label}`,
                        title: "Unpin",
                        className: "leading-none text-muted-foreground hover:text-foreground",
                        onClick: () => pins.unpin(p.id),
                        children: "\xD7"
                      }
                    )
                  ]
                },
                p.id
              )),
              /* @__PURE__ */ jsx(Button, { variant: "ghost", size: "sm", onClick: pins.clear, children: "Clear all" })
            ] }),
            /* @__PURE__ */ jsxs("div", { children: [
              /* @__PURE__ */ jsx("h4", { className: "mb-2 text-sm font-semibold", children: "Interface diff (pinned ABIs)" }),
              /* @__PURE__ */ jsx(CompareAbisView, { items: compareItems })
            ] }),
            /* @__PURE__ */ jsx(Separator, {}),
            /* @__PURE__ */ jsxs("div", { children: [
              /* @__PURE__ */ jsx("h4", { className: "mb-2 text-sm font-semibold", children: "Field values (pinned refs)" }),
              /* @__PURE__ */ jsx(CompareFieldsView, { refs: compareRefs, abi, enrichmentChain: contract.chainId, onAddressesFound: reportAddresses })
            ] })
          ] }),
          /* @__PURE__ */ jsx(TabsContent, { value: "options", className: "pt-3", children: /* @__PURE__ */ jsx(
            AbiSourcePanel,
            {
              options,
              onChange: setOptions,
              onReset: reset,
              origin,
              meta: resolved?.meta ?? null,
              isGuess: isGuessedAbi
            }
          ) })
        ] }, `${contractKey}:${identityKey(identity)}`) })
      ] }) }) }) })
    }
  );
}
function TokenStateAtTransaction({
  client,
  chainId,
  blockNumber,
  subjects,
  limited,
  renderAddress,
  explorerLink
}) {
  const [query, setQuery] = React50.useState("");
  const [selected, setSelected] = React50.useState("");
  const [active, setActive] = React50.useState("");
  const inputId = React50.useId();
  const shown = React50.useMemo(
    () => subjects.filter((s) => `${s.address} ${s.label}`.toLowerCase().includes(query.toLowerCase())).slice(0, 50),
    [subjects, query]
  );
  const chosen = subjects.find((s) => s.id === active);
  if (!subjects.length) return null;
  return /* @__PURE__ */ jsxs("details", { className: "rounded-md border border-border/60 bg-card text-sm", children: [
    /* @__PURE__ */ jsxs("summary", { className: "cursor-pointer px-3 py-2 font-medium", children: [
      "Token balances and approvals \xB7 ",
      subjects.length,
      " ",
      subjects.length === 1 ? "asset" : "assets",
      limited ? " \xB7 partial suggestions" : ""
    ] }),
    /* @__PURE__ */ jsxs("div", { className: "space-y-3 border-t p-3", children: [
      /* @__PURE__ */ jsx("p", { className: "text-xs text-muted-foreground", children: "Inspect one asset when needed. Transfers and events describe what was recorded; calls describe intent. Snapshots compare block boundaries, not this transaction alone. Choose an asset and press \u201CRead selected asset\u201D to fetch its state." }),
      limited && /* @__PURE__ */ jsx("p", { role: "status", className: "text-xs text-muted-foreground", children: "Some suggestions could not be included or exceed the configured limits. These suggestions are partial; use the contract explorer for assets or fields not listed." }),
      /* @__PURE__ */ jsxs("div", { className: "grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]", children: [
        /* @__PURE__ */ jsxs("div", { className: "min-w-0 space-y-1", children: [
          /* @__PURE__ */ jsx("label", { htmlFor: inputId, className: "text-xs font-medium", children: "Find an asset by contract or token ID" }),
          /* @__PURE__ */ jsx(
            "input",
            {
              id: inputId,
              value: query,
              onChange: (e) => {
                setQuery(e.target.value);
                setSelected("");
              },
              className: "w-full rounded border bg-background px-2 py-1.5",
              placeholder: "Contract address or token ID"
            }
          ),
          /* @__PURE__ */ jsxs(
            "select",
            {
              "aria-label": "Asset state to inspect",
              value: selected,
              onChange: (e) => setSelected(e.target.value),
              className: "w-full min-w-0 rounded border bg-background px-2 py-1.5",
              children: [
                /* @__PURE__ */ jsx("option", { value: "", children: "Select an asset\u2026" }),
                shown.map((s) => /* @__PURE__ */ jsxs("option", { value: s.id, children: [
                  s.label,
                  " \xB7 ",
                  s.address
                ] }, s.id))
              ]
            }
          ),
          /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
            shown.length,
            " ",
            shown.length === 1 ? "suggestion" : "suggestions",
            " shown",
            shown.length === 50 ? "; narrow your search for more" : "",
            "."
          ] })
        ] }),
        /* @__PURE__ */ jsx(
          "button",
          {
            type: "button",
            disabled: !selected || selected === active,
            onClick: () => setActive(selected),
            className: "self-end rounded border px-3 py-2 font-medium hover:bg-muted disabled:opacity-50",
            children: "Read selected asset"
          }
        )
      ] }),
      chosen && /* @__PURE__ */ jsxs(Fragment, { children: [
        /* @__PURE__ */ jsxs("p", { className: "text-xs text-muted-foreground", children: [
          "Suggested from: ",
          chosen.sources.join("; "),
          ". Unsupported getters are omitted; failed reads are shown, never treated as zero. Unformatted amounts are raw base units. Approval reads describe the token contract only, not separate permit systems."
        ] }),
        /* @__PURE__ */ jsx(
          StateAtTransaction,
          {
            client,
            chainId,
            address: chosen.address,
            blockNumber,
            variables: chosen.variables,
            focus: "asset",
            defaultOpen: true,
            allowLatest: true,
            renderAddress,
            summary: /* @__PURE__ */ jsxs("span", { className: "inline-flex max-w-full flex-wrap items-center gap-2", children: [
              /* @__PURE__ */ jsx("span", { className: "min-w-0 [overflow-wrap:anywhere]", children: chosen.label }),
              renderAddress(chosen.address)
            ] }),
            explorerLink: explorerLink(chosen.address, chosen)
          },
          `${chainId}:${blockNumber}:${chosen.id}`
        )
      ] })
    ] })
  ] });
}

// src/relationships.ts
var ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";
function isAddressLike(value) {
  return typeof value === "string" && /^0x[0-9a-fA-F]{40}$/.test(value) && value.toLowerCase() !== ZERO_ADDRESS;
}
function relatedAddressesFromFields(fields) {
  const out = [];
  const seen = /* @__PURE__ */ new Set();
  const push = (field, address, via) => {
    const key = address.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ field, address, via });
  };
  for (const { fn, outcome } of fields) {
    if (!outcome || outcome.status !== "success") continue;
    const outputs = fn.outputs ?? [];
    if (outputs.length !== 1) continue;
    const type = outputs[0].type;
    const data = outcome.data;
    if (type === "address" && isAddressLike(data)) {
      push(fn.name, data, "address");
    } else if (type === "address[]" && Array.isArray(data)) {
      for (const item of data) if (isAddressLike(item)) push(fn.name, item, "address[]");
    }
  }
  return out;
}
function AccountExplorer({ capability, historicalRequested, onUseCurrent, tab, onTabChange, ...props }) {
  if (capability.kind === "limited") return /* @__PURE__ */ jsx(Alert, { children: /* @__PURE__ */ jsx(AlertDescription, { children: capability.description }) });
  const tabs = capability.tabs.filter((view) => view.id === "onchain");
  const selection = resolveExplorerTab(tabs, tab);
  if (!selection.active) return /* @__PURE__ */ jsx(EmptyState, { title: "No supported account views", description: "No native state view is configured for this chain." });
  return /* @__PURE__ */ jsxs("div", { className: "space-y-3", children: [
    /* @__PURE__ */ jsx("p", { className: "text-sm text-muted-foreground", children: capability.description }),
    selection.unavailable && /* @__PURE__ */ jsx(Alert, { children: /* @__PURE__ */ jsxs(AlertDescription, { children: [
      explorerTabLabel(tab),
      " is not available in this chain's explorer. Showing ",
      selection.active.label,
      ".",
      onTabChange && /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", className: "ml-2", onClick: () => onTabChange("onchain"), children: "Use supported view" })
    ] }) }),
    /* @__PURE__ */ jsxs(Tabs, { value: "onchain", onValueChange: () => onTabChange?.("onchain"), children: [
      /* @__PURE__ */ jsx(TabsList, { "aria-label": "Account views", className: "h-auto max-w-full flex-wrap justify-start", children: /* @__PURE__ */ jsx(TabsTrigger, { value: "onchain", children: selection.active.label }) }),
      /* @__PURE__ */ jsx(TabsContent, { value: "onchain", className: "pt-3", children: historicalRequested ? /* @__PURE__ */ jsx(Alert, { children: /* @__PURE__ */ jsxs(AlertDescription, { children: [
        "This reader cannot retrieve state at a historical block or slot. No current values are shown as historical data.",
        onUseCurrent && /* @__PURE__ */ jsx(Button, { variant: "outline", size: "sm", className: "ml-2", onClick: onUseCurrent, children: "Read current state" })
      ] }) }) : /* @__PURE__ */ jsx(ContractStatePanel, { ...props, relationships: capability.relationships, accountMode: true }) })
    ] })
  ] });
}

export { ABI_CACHE_MAX_ENTRIES, ABI_CACHE_TTL_MS, ABI_OPTIONS_NAME, ABI_ORIGIN_LABELS, ABI_SCOPE_LABELS, ABI_SOURCE_LABELS, ALL_ABI_SOURCES, AbiCache, AbiSaveScopeField, AbiSourceKind, AbiSourcePanel, AbiSourcesPanel, AbiSourcesUnavailableError, AbiViewer, AccountExplorer, AddressIdentityChip, AddressReporter, BalanceChangesPanel, BytecodeReadResult, CONTRACT_EXPLORER_PACKAGE, CONTRACT_EXPLORER_TABS, CompareAbisView, CompareFieldsView, ContractExplorer, ContractFieldsGrid, ContractReaderProvider, ContractSourcePanel, ContractStateClientProvider, ContractStateHighlighter, ContractStateNodeCard, ContractStatePanel, ContractStateTree, ContractStateView, DEFAULT_ABI_FETCH_OPTIONS, DEFAULT_CONTRACT_EXPLORER_TAB, DEFAULT_EXPLORER_CAPABILITIES, DEFAULT_TOKEN_STATE_POLICY, DIAMOND_LOUPE_ABI, ERC1155_INTERFACE_ID, ERC721_INTERFACE_ID, EVENT_LOG_CACHE_MAX_ENTRIES, EVENT_LOG_CACHE_TTL_MS, EVM_EXPLORER_TABS, EventLogCache, EventLogPanel, FunctionForm, GuessFitContext, GuessedAbiNotice, INFERRED_ABI_ORIGINS, INLINE_ABIS_NAME, InlineAbiEditor, KNOWN_PROXY_SLOTS, LIST_MEMBER_LIMIT, LIST_MEMBER_PRESENT, NoAbiState, PINNED_ABIS_LIMIT, PINNED_ABIS_NAME, PROVIDER_ABI_SOURCES, PROXY_KIND_LABELS, PROXY_ROLE_FIELDS, ProxyChainView, ProxyOverridePanel, ReadPanel, STATE_CHANGE_ATTRIBUTE, STATE_CHANGE_CONFIGURATION, STATE_READ_ATTRIBUTE, STATE_READ_KEY_ATTRIBUTE, StateAtTransaction, StateDiffTable, StateValueArguments, StateValueDisplay, THIRD_PARTY_INFERRED_SOURCE, TOKEN_STATE_RULES, TokenStateAtTransaction, TransactionInsightClientProvider, UUPS_ABI, WritePanel, abiCacheKey, abiOriginLabel, abiScopeDescription, abiSourceId, abiSourceLabel, abiSourceMeta, abiSourceScopeLabel, asText, availableExplorerTabs, blockLabel, blockToBlockSpec, buildArgs, buildContractStateRequest, changeCount, changedMultiRows, changedRows, classifyProxyKind, coerceArg, configChangeSubjects, createApiAbiByAddressClient, createApiAbiProvider, createApiAbiWriter, createApiContractSourceClient, createApiContractStateClient, createApiProxyBatchResolver, createApiProxyResolver, createApiTransactionInsightClient, createCompositeAbiProvider, createContractStateClient, createDefaultAbiProvider, createExplorerCapabilities, createInMemoryAbiProvider, createTransactionInsightClient, createValidatingClient, defaultContractReader, describeAbiFailures, diffContractState, diffContractStates, encodeFunctionCall, eventLogCacheKey, explorerCapabilities, failureReads, failureSubjects, formatReadValue, functionFormKey, identityKey, identityKeyLookup, inferredSelector, isContractExplorerTab, isInferredAbiOrigin, isInferredFunction, isStateExploration, isUnverifiedGuess, moveAbiSource, omittedReadsNote, optionsKey, parameterKeys, parseTokenStateContext, pickRows, profileLabel, providerSourceOrder, proxyChangeSubjects, proxyKindLabel, readContractFunction, readContractFunctions, readProfileId, relatedAddressesFromFields, renameableSource, resolveExplorerTab, serializeTokenStateContext, sharedAbiCache, sharedEventLogCache, splitReturnData, stateAddressRefs, stateExplorationOptions, stateSnapshot, storedBytecodeOutcome, tokenStateSubjects, tokenStateVariable, useAbiOptions, useAbiResolver, useAbiSources, useAbiStorage, useContractFields, useContractReader, useContractState, useContractStateClient, useEventLogs, useFunctionDrafts, useInlineAbis, useNftStandard, usePinnedAbis, useProxyChain, useReaderRead, useTokenIdentity, useTransactionInsight, useTransactionInsightClient, withProviderOrder };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map